import React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  FileDown,
  FileText,
  GripVertical,
  IndentDecrease,
  IndentIncrease,
  Info,
  Minus,
  MoveHorizontal,
  Pencil,
  Plus,
  Send,
  Trash2,
  Store,
  X,
} from 'lucide-react';
import { InlineLoadingState, LoadingSpinner, PageLoadingState } from '../../../components/LoadingSpinner';
import {
  SageImportInvoiceReadView,
  type SageImportReadDocument,
} from '../../invoices/components/SageImportInvoiceReadView';
import type { OfferPdfDocumentProps } from './OfferPdfDocument';
import { PdfPreviewLoadingPanel, type PdfPreviewPhase } from './PdfPreviewLoadingPanel';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { offeringPositionsApiBase } from '../../../utils/offeringApi';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import {
  companyLogoSrc,
  fetchCompanyLogoDataUrl,
  instanceIdFromPathname,
} from '../../../utils/companyLogo';
import {
  contactDisplayName,
  leadOutboundEmail,
  type CompanySettings,
  type Lead,
  type LeadPosition,
  type PositionRowId,
  type OfferDiscount,
  type OfferVatMode,
  type OfferDocumentDraft,
  type OfferDraftRow,
  type OfferDocumentEditablePara,
  type OfferIntroBlock,
  type OfferDocumentContentBlock,
  type OfferAttachment,
  type OfferPositionSurchargeConfig,
  type OfferLeistungszeitraum,
  type OfferingPositionComponent,
} from '../../../types';
import { OfferInlineEdit } from './OfferInlineEdit';
import { OfferLeistungszeitraumEdit } from './OfferLeistungszeitraumEdit';
import { OfferDocumentIssuedContext, useOfferDocumentIssued } from './offerDocumentIssuedContext';
import {
  documentSecondaryMetaKind,
  documentLocksLeistungszeitraum,
  documentShowsLeistungszeitraum,
  formatLeistungszeitraumDisplay,
  leistungszeitraumToDateRange,
  parseOfferLeistungszeitraum,
  resolveInvoiceLeistungszeitraum,
  secondaryMetaFieldLabel,
} from '../utils/offerLeistungszeitraum';
import {
  EMPTY_OFFER_TITLE_SENTINEL,
  documentTitleShowsType,
  formatOfferTitleLine,
  inlineLabelValueSeparator,
  issueDateFieldLabel,
  labeledFieldPresentation,
  layoutAlignTextClass,
  layoutAlignToFlexJustify,
  resolveLayoutTextAlign,
  offerTitleLineParts,
  parseEditedOfferTitleLine,
  resolveOrderNumberLabel,
} from '../../settings/utils/layoutFieldPresentation';
import {
  PDF_A4_HEIGHT_PT,
  PDF_A4_WIDTH_PT,
  layoutPaymentQrAlign,
  layoutPaymentQrPlacement,
  layoutShowsPaymentQr,
  layoutShowsTitleTotalsSummary,
  layoutStartsTableOnPage2,
  resolveDocumentPageMargins,
  type DocumentLayoutTemplateRow,
  type DocumentLayoutV1,
  type LayoutElement,
} from '../../settings/utils/documentLayoutTypes';
import {
  HtmlFoldMarks,
  HtmlFooterBand,
  HtmlHeaderBand,
  OFFER_PDF_FONT_STYLE,
} from './HtmlLayoutBands';
import type { PdfLayoutBandData } from './PdfLayoutBands';
import {
  paymentQrResolveInput,
  resolvePaymentQrDataUrl,
  PAYMENT_QR_CAPTION,
  PAYMENT_QR_SIZE_PT,
} from '../../../utils/paymentQrImage';
import { OfferPositionPickerPanel } from './OfferPositionPickerPanel';
import { GaebActionButton } from './GaebActionButton';
import { OfferImportActionButton } from './OfferImportActionButton';
import {
  PositionQuoteOffersPopover,
  type QuoteOffersPositionContext,
} from '../../ausschreibungen/components/PositionQuoteOffersPopover';
import type { PositionQuoteOffer, PositionQuoteOffers } from '../../ausschreibungen/quoteComparison';
import type { QuoteOffersSummary } from './OfferCalculationTable';
import { IdsCartButton, launchIdsArticleDeeplink } from './IdsCartButton';
import { OfferAttachmentsButton } from './OfferAttachmentsButton';
import { OfferDocumentContentList } from './OfferDocumentContentList';
import { OfferImagePickerDialog } from './OfferImagePickerDialog';
import {
  deleteOfferAttachmentStorage,
  uploadOfferAttachment,
} from '../utils/offerAttachmentsApi';
import {
  clampInlineImagesAcrossZones,
  contentToLegacyBlocks,
  contentToParas,
  insertContentBlock,
  mapResolvedContent,
  newDocumentContentId,
  normalizeOfferDocumentContent,
  parasToTextBlocks,
  findImageGridBlockId,
  remainingInlineImageSlots,
  removeAttachmentFromContent,
  removeContentBlock,
  replaceGridImages,
  updateContentText,
  updateFirstTextBlock,
} from '../utils/offerDocumentContent';
import { resolveOfferImageDataUrls } from '../utils/offerFirstPageImages';
import { OfferAttachmentPreviewPages } from './OfferAttachmentPreviewPages';
import { OfferVatPickerPanel } from './OfferVatPickerPanel';
import {
  paymentTermsFromSettings,
  type OfferDocumentPaymentTerms,
} from '../utils/offerDocumentMetaPatch';
import { OfferCostKindSwitcher } from './OfferCostKindSwitcher';
import {
  OfferCalculationTable,
  type OfferCalculationPatch,
  type OfferCalculationRow,
} from './OfferCalculationTable';
import { computeOfferMarginHints, groupOfferMarginHints } from '../utils/offerMarginHints';
import { surchargeForVk } from '../utils/offerPricing';
import { OfferRowImageUpload } from './OfferRowImageUpload';
import { DraggableOfferRow } from './OfferDraggableRow';
import {
  assertDraftJsonSize,
  hydrateIntroBlocksFromDraft,
  parseOfferDocumentDraft,
  sanitizeOfferDocumentDraftForSave,
} from '../utils/offerDocumentDraft';
import { createLatestDraftPersistQueue, offerDraftSaveErrorMessage } from '../utils/offerDraftPersistQueue';
import { stripStornoFixedAfterTotals } from '../utils/stornoDocumentTexts';
import { FOLLOWUP_QUESTIONS_SENTENCE, withoutDuplicateClosingTexts } from '../utils/followupDocumentTexts';
import {
  buildOfferMergedTableRows,
  draftRowFromMergedTableRow,
  ensureDraftRowsCoverPositions,
} from '../utils/offerMergedTableRows';
import {
  DOCUMENT_VIRTUALIZE_MIN_ROWS,
  insertIndexFromPointerLayout,
  type OfferTableRowLayout,
} from '../utils/calculationRowWindow';
import { estimateDocumentRowPx, useOfferTableRowWindow } from '../utils/useOfferTableRowWindow';
import {
  contributesToOfferNetTotal,
  documentTreatsEventualAsNormal,
  EVENTUAL_POSITION_HINT,
  formatEventualGesamt,
} from '../utils/offerEventualPosition';
import { computeTitleSubtotals, groupTitleSubtotalsByAfterRowId, titleSubtotalLabel } from '../utils/offerTitleSubtotal';
import {
  maxAllowedTitleLevelAt,
  MIN_TITLE_LEVEL,
  nextTitleLevel,
  titleIndentPx,
  titleSectionRows,
} from '../utils/offerTitleHierarchy';
import {
  isLaborCostShareEligibleDocument,
  laborCostShareSettingsFromCompany,
  parseLaborCostShareMode,
  resolveLaborCostKind,
  resolveLaborCostShareFootnote,
  shouldShowLaborCostKindSwitcher,
  type LaborCostShareMode,
} from '../utils/offerLaborCostShare';
import {
  LEGAL_ATTACHMENT_KINDS,
  isLegalAttachmentApplicable,
  legalAttachmentFilename,
  legalAttachmentSettingsFromUnknown,
  parseExcludedStandardAttachments,
  shouldIncludeLegalAttachment,
  type LegalAttachmentKind,
} from '../utils/offerLegalAttachments';
import {
  activePositionEditKeysFromCounts,
  collectPositionRowIds,
  decrementPositionFieldEditCount,
  incrementPositionFieldEditCount,
  isPositionRowId,
  mergePositionsKeepingInFlightEdits,
  positionRowIdKey,
  positionRowIdsEqual,
} from '../utils/positionRowId';
import {
  applySendPdfEmailBodyTemplate,
  getDefaultSendPdfEmailBodyTemplate,
} from '../utils/sendPdfEmailBodyTemplate';
import { formatDateToDDMMYYYY, formatDDMMYYYYInput, parseDDMMYYYYToYMD, todayYMD } from '../../../utils/date';
import {
  DEFAULT_SKONTO_DAYS,
  formatPaymentTermsSentence,
  isReplaceableDefaultPaymentTermsSentence,
  normalizePaymentTermsDays,
  paymentTermsContextFromDocumentType,
  resolveInvoiceDueYmd,
} from '../../../utils/paymentTerms';
import {
  formatGermanNumber,
  isIncompleteGermanNumberInput,
  parseGermanNumber,
  parseGermanNumberOnBlur,
  parseGermanNumberOrNull,
} from '../../../utils/parseGermanNumber';
import {
  buildDocumentExportReferenceId,
  buildYearlyInvoiceReferenceId,
  currentDocumentYearSuffix,
  documentTypeLabelToExportCode,
  isInvoiceFamilyCode,
  isInvoiceFamilyDocumentType,
} from '../../../utils/documentExportReference';
import { OfferUnitPicker } from './OfferUnitPicker';
import { CustomerBlockNotice } from './CustomerPartySummary';
import {
  resolveDocumentBilling,
  resolveInvoiceDebtor,
} from '../utils/invoiceDebtor';
import {
  formatCompanyAddressLines,
  formatCompanyWebsiteUrl,
  formatDecimal,
  formatDirectorFooterLines,
  formatEuro,
  formatLeadPartyName,
  formatLeistungsort,
  formatRegistryLine,
  isPrivateCustomerLead,
  OFFER_TITLE_TOTALS_AMOUNT_PCT,
  OFFER_TOTALS_COLUMN_WIDTH_PT,
  resolveCustomerNumberLabel,
} from '../utils/offerDocumentUtils';
import {
  DEFAULT_OFFER_VAT_MODE,
  getOfferVatOption,
  isZeroVatSpecialMode,
} from '../utils/offerVat';
import {
  computeFinalInvoiceTotals,
  type PaidPartialInvoice,
} from '../utils/finalInvoiceTotals';
import {
  derivePaidPartialInvoices,
  excludeCurrentDocumentDeductions,
  isSelectableDeductionRow,
  mapInvoiceRowToDeductionCandidate,
  resolveCurrentDeductionDocument,
  resolveDeductionSelectionIds,
  type BoundIssuedInvoice,
  type CurrentDeductionDocument,
  type DeductionInvoiceRow,
} from '../utils/finalInvoiceDeduction';
import { FinalInvoiceDeductionPickerModal } from './FinalInvoiceDeductionPickerModal';
import { PositionSurchargePickerModal } from './PositionSurchargePickerModal';
import {
  assignPositionNumberLabels,
  buildSurchargeDescriptionForConfig,
  computeSurchargeBaseSum,
  computeSurchargeLinePrice,
  listEligibleSurchargeTargets,
  reconcileSurchargeDraftRows,
  surchargeConfigTargetsOrPercentChanged,
  type PositionSurchargeTableRow,
} from '../utils/positionSurcharge';
import {
  applyCustomerFacingLaborPricing,
  applyHourLaborPatchIfNeeded,
  countNonVirtualRowsBefore,
  DEFAULT_TITLE_LABOR_ROW_LABEL,
  effectiveHourlyRate,
  effectiveLaborMinutes,
  formatTitleLaborRowName,
  hourlyRateOverrideFromEpInput,
  injectTitleLaborRows,
  isHourLaborPricedRow,
  isVirtualTitleLaborRow,
  laborSharePerUnit,
  parseHourlyRateOverride,
  normalizeTitleLaborRowLabel,
  normalizeTitleLaborRowNames,
  patchForUnitChange,
  resolveTitleLaborRowName,
  storeTitleLaborRowName,
  virtualTitleLaborParentId,
} from '../utils/offerLaborPricing';
import {
  negateLedgerSnapshot,
  isLedgerSnapshotV1,
  ledgerSnapshotDraftRowsFromEditor,
  negateOfferDraftRowForStorno,
  forceStornoNegativeUnitPriceIfPositive,
  type InvoiceLedgerSnapshotV1,
} from '../utils/invoiceLedgerSnapshot';
import {
  ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS,
  ABSCHLAG_PERCENT_PRESETS,
  computePartialInvoiceBaseNet,
  isCumulativePartialInvoice,
  formatPartialInvoiceDescriptionFallback,
  formatPartialInvoiceLineName,
  grossFromNetAmount,
  netFromGrossAmount,
  isAbschlagPresetPercent,
  remainingPartialInvoiceNet,
  roundAbschlagPercent,
} from '../utils/partialInvoiceTotals';
import {
  computeInvoicePayableTotals,
  DEFAULT_SITE_LEVY_LABEL,
  normalizeRetention,
  normalizeSiteLevy,
  type InvoiceRetention,
  type InvoiceSiteLevy,
} from '../utils/invoicePayableTotals';
import {
  documentLayoutAssetSrc,
  fetchDefaultDocumentLayoutTemplate,
  resolveLayoutAssetDataUrls,
} from '../../settings/utils/documentLayoutApi';
import {
  billingRowKeyFromDraftRow,
  buildBilledDraftRowsFromBilling,
  buildPositionBillingRows,
  buildPositionInvoiceQuantitiesMap,
  clampInvoiceQty,
  isBillablePositionRowKind,
  type PartialInvoiceMode,
  type PositionBillingRowState,
} from '../utils/positionBillingLedger';
import { ComposeEmailModal } from '../../inbox/components/ComposeEmailModal';
import { AppModal } from '../../../components/AppModal';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { EInvoiceTaxIdDialog, type EInvoiceTaxIds } from './EInvoiceTaxIdDialog';
import {
  extractZugferdBrCodes,
  getLikelyEInvoiceRecipientHint,
  getLikelyEInvoiceSellerHint,
  getLikelyEInvoiceVatHint,
  getLikelyEInvoiceVatSellerHint,
  getMissingEInvoiceTaxIdentifiers,
  getZeroPricePositionsHint,
  isZugferdSerializableLineItem,
  joinZugferdWarningMessages,
  userMessageForZugferdPdfError,
} from '@/server/utils/zugferdUserMessages';
import { shouldEmbedZugferd } from '@/server/utils/zugferdEmbedEligible';
import { EINVOICE_MANDATORY_BLOCK_MESSAGE, isEinvoiceMandatory } from '../../invoices/utils/einvoiceMandate';
import { mailDispatchPurposeForDocumentLabel } from '../../../types/mailDispatch';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { fetchDispatchAccount } from '../../../utils/mailDispatchApi';
import {
  DUPLICATE_DOCUMENT_WARNING_QUESTION,
  formatDuplicateDocumentWarning,
  invoicesListTabForDocumentType,
  overviewDocumentTypeFromGermanLabel,
  shouldWarnNewDocumentNumber,
  uniqueDocumentNumbers,
} from '../utils/duplicateDocumentNumberWarning';

type OfferPdfSendNotice =
  | null
  | { type: 'need_mail' }
  | { type: 'error'; message: string; title?: string }
  | { type: 'partial'; message: string }
  | { type: 'success'; message: string };

const DOCUMENT_ZOOM_MIN = 0.5;
const DOCUMENT_ZOOM_MAX = 3;
/** Button / shortcut steps; Ctrl + wheel zooms continuously in between. */
const DOCUMENT_ZOOM_STEPS = [0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];
const DOCUMENT_ZOOM_STORAGE_KEY = 'offerDocumentEditor.zoom';

function clampDocumentZoom(value: number): number {
  return Math.min(DOCUMENT_ZOOM_MAX, Math.max(DOCUMENT_ZOOM_MIN, Math.round(value * 100) / 100));
}

function stepDocumentZoom(current: number, direction: 1 | -1): number {
  const next =
    direction > 0
      ? DOCUMENT_ZOOM_STEPS.find((s) => s > current + 0.001)
      : [...DOCUMENT_ZOOM_STEPS].reverse().find((s) => s < current - 0.001);
  return next ?? current;
}

function readStoredDocumentZoom(): number {
  try {
    const raw = Number(window.localStorage.getItem(DOCUMENT_ZOOM_STORAGE_KEY));
    return Number.isFinite(raw) && raw > 0 ? clampDocumentZoom(raw) : 1;
  } catch {
    return 1;
  }
}

const ZOOM_CONTROL_BTN =
  'inline-flex h-full w-9 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text disabled:pointer-events-none disabled:opacity-35';

/** Floating zoom pill in the bottom-right corner of the document pane. */
function DocumentZoomControls({
  zoom,
  onZoomOut,
  onZoomIn,
  onReset,
  onFitWidth,
}: {
  zoom: number;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onReset: () => void;
  onFitWidth: () => void;
}) {
  const percent = Math.round(zoom * 100);
  return (
    <div
      className="pointer-events-auto absolute bottom-4 right-6 z-20 flex h-9 items-center overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-lg"
      title="Strg + Mausrad zum Zoomen"
    >
      <button
        type="button"
        className={ZOOM_CONTROL_BTN}
        onClick={onZoomOut}
        disabled={zoom <= DOCUMENT_ZOOM_MIN}
        title="Verkleinern (Strg + −)"
        aria-label="Zoom verkleinern"
      >
        <Minus className="h-4 w-4" />
      </button>
      <button
        type="button"
        className="h-full min-w-[3.5rem] border-x border-app-border px-2 text-xs font-medium tabular-nums text-app-text-muted transition-colors hover:bg-app-hover hover:text-app-text"
        onClick={onReset}
        title="Auf 100 % zurücksetzen (Strg + 0)"
        aria-label={`Zoom ${percent} Prozent, Klick setzt auf 100 Prozent`}
      >
        {percent}%
      </button>
      <button
        type="button"
        className={ZOOM_CONTROL_BTN}
        onClick={onZoomIn}
        disabled={zoom >= DOCUMENT_ZOOM_MAX}
        title="Vergrößern (Strg + +)"
        aria-label="Zoom vergrößern"
      >
        <Plus className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={`${ZOOM_CONTROL_BTN} border-l border-app-border`}
        onClick={onFitWidth}
        title="An Seitenbreite anpassen"
        aria-label="An Seitenbreite anpassen"
      >
        <MoveHorizontal className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Helvetica-metric stack — matches PDF / HtmlLayoutBands (inline style preferred over Tailwind arbitrary). */
const OFFER_DOCUMENT_FONT_STYLE: React.CSSProperties = {
  ...OFFER_PDF_FONT_STYLE,
};

function newId(): string {
  return `b_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Numeric text input with German formatting (1.234,56).
 * Accepts comma or dot as decimal separator and dot as thousands separator.
 * Keeps intermediate values like "12," while typing (no sync from parent while focused).
 */
const DecimalInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  onFocus?: () => void;
  onBlur?: (committed: number) => void;
  className?: string;
  min?: number;
  max?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Empty the field on focus when the committed value is 0 (e.g. default price). */
  clearOnFocusWhenZero?: boolean;
}> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  className,
  min,
  max,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  clearOnFocusWhenZero = false,
}) => {
  const formatDisplay = React.useCallback(
    (n: number) => formatGermanNumber(n, { minimumFractionDigits, maximumFractionDigits }),
    [minimumFractionDigits, maximumFractionDigits],
  );

  const [display, setDisplay] = React.useState(() => formatDisplay(value));
  const committedRef = React.useRef(value);
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (focusedRef.current) return;
    committedRef.current = value;
    setDisplay(formatDisplay(value));
  }, [value, formatDisplay]);

  const issued = useOfferDocumentIssued();
  const tryCommit = React.useCallback(
    (raw: string): number | null => {
      if (isIncompleteGermanNumberInput(raw)) return null;
      const num = parseGermanNumberOrNull(raw);
      if (num == null) return null;
      if (min !== undefined && num < min) return null;
      if (max !== undefined && num > max) return null;
      return num;
    },
    [min, max],
  );

  if (issued) {
    const alignRight = /\btext-right\b/.test(className ?? '');
    return (
      <span className={`block w-full min-w-0 font-inherit tabular-nums py-1 text-[9.5pt]${alignRight ? ' text-right' : ''}`}>
        {display}
      </span>
    );
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={(e) => {
        const raw = e.target.value;
        setDisplay(raw);
        const num = tryCommit(raw);
        if (num != null) {
          committedRef.current = num;
          onChange(num);
        }
      }}
      onFocus={() => {
        focusedRef.current = true;
        if (clearOnFocusWhenZero && committedRef.current === 0) {
          setDisplay('');
        }
        onFocus?.();
      }}
      onBlur={() => {
        focusedRef.current = false;
        const trimmed = display.trim();
        const parsed = parseGermanNumberOnBlur(trimmed);
        let num = parsed ?? committedRef.current;
        if (!Number.isFinite(num)) num = 0;
        const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, num));
        committedRef.current = clamped;
        setDisplay(formatDisplay(clamped));
        onChange(clamped);
        onBlur?.(clamped);
      }}
      className={className}
    />
  );
};

/** @deprecated Nutze OfferDocumentEditablePara */
export type EditableTextPara = OfferDocumentEditablePara;

type EditableParagraphListProps = {
  paragraphs: OfferDocumentEditablePara[];
  minParagraphs?: number;
  variant?: 'default' | 'muted';
  onCommitParagraph: (index: number, text: string) => void | Promise<void>;
  onRemoveParagraph: (index: number) => void | Promise<void>;
  onAddParagraph: () => void | Promise<void>;
};

type CombinedRow = {
  id: string;
  kind: OfferDraftRow['kind'];
  leadPositionId: PositionRowId | null;
  sourceType: LeadPosition['source_type'] | null;
  quantity: number;
  unit: string;
  item_name: string;
  description: string;
  internal_note: string;
  unit_price_vk: number;
  unit_cost_ek: number;
  surcharge_percent: number;
  labor_minutes: number;
  hourly_rate: number | null;
  image: OfferDraftRow['image'] | null;
  positionSurcharge?: OfferDraftRow['positionSurcharge'];
  isApi: boolean;
  article_number: string | null;
  isEventual: boolean;
  numberLabel?: string;
  costKind?: OfferDraftRow['costKind'];
  components?: OfferingPositionComponent[];
  metal_kind?: LeadPosition['metal_kind'];
  metal_surcharge?: number | null;
  metal_priced_ek?: number | null;
  titleLevel?: number | null;
  rowDepth?: number;
};

/** Minimum a row needs for the Papierkorb (Dokument- und Kalkulationsansicht). */
type DeletableTableRow = {
  id: string;
  kind: OfferDraftRow['kind'];
  item_name: string;
  isApi: boolean;
  leadPositionId: PositionRowId | null;
};

function titleDeleteDescription(members: Array<Pick<DeletableTableRow, 'kind'>>): string {
  const positions = members.filter((m) => m.kind !== 'title' && m.kind !== 'text' && m.kind !== 'image').length;
  const subtitles = members.filter((m) => m.kind === 'title').length;
  const parts: string[] = [];
  if (positions > 0) parts.push(positions === 1 ? '1 Position' : `${positions} Positionen`);
  if (subtitles > 0) parts.push(subtitles === 1 ? '1 Untertitel' : `${subtitles} Untertitel`);
  if (parts.length === 0) parts.push(`${members.length} Zeilen`);
  return `Dieser Titel enthält ${parts.join(' und ')}.`;
}

function uglComponentRoleLabel(
  role: OfferingPositionComponent['ugl_role'],
  surchargeType: string | null,
): string | null {
  if (role === 'labor') return 'Lohn';
  if (role === 'surcharge') return surchargeType?.trim() || 'Zuschlag';
  return null;
}

const POSITION_SURCHARGE_DEFAULT_TITLE = 'Zuschlag auf vorherige Positionen';

function makeDraftRow(kind: OfferDraftRow['kind'], extras?: Partial<OfferDraftRow>): OfferDraftRow {
  return {
    id: newId(),
    kind,
    leadPositionId: null,
    sourceType:
      kind === 'title'
        ? 'custom_title'
        : kind === 'text'
          ? 'custom_text'
          : kind === 'image'
            ? 'custom_image'
            : kind === 'positionSurcharge'
              ? 'custom_surcharge'
              : 'custom_free',
    quantity:
      kind === 'title' || kind === 'text' || kind === 'image'
        ? 0
        : 1,
    unit:
      kind === 'title'
        ? 'Titel'
        : kind === 'freePosition'
          ? 'Stk'
          : kind === 'positionSurcharge'
            ? 'psch'
            : '',
    item_name: kind === 'positionSurcharge' ? POSITION_SURCHARGE_DEFAULT_TITLE : '',
    description: '',
    internal_note: '',
    unit_price_vk: 0,
    unit_cost_ek: 0,
    surcharge_percent: 0,
    labor_minutes: 0,
    hourly_rate: null,
    image: kind === 'image' ? { src: '', alt: '' } : null,
    positionSurcharge: kind === 'positionSurcharge' ? { percent: 0, targetRowIds: [] } : null,
    isEventual: false,
    ...(kind === 'title' ? { titleLevel: extras?.titleLevel ?? MIN_TITLE_LEVEL } : {}),
    ...extras,
  };
}

function EditableParagraphList({
  paragraphs,
  minParagraphs = 1,
  variant = 'default',
  onCommitParagraph,
  onRemoveParagraph,
  onAddParagraph,
}: EditableParagraphListProps) {
  const issued = useOfferDocumentIssued();
  const textClass = variant === 'muted' ? 'text-neutral-800' : 'text-neutral-900';

  return (
    <div className="space-y-3">
      {paragraphs.map((p, i) => (
        <div key={p.id} className="group relative pl-0">
          <OfferInlineEdit
            value={p.text}
            onSave={(next) => onCommitParagraph(i, next)}
            multiline
            viewTextClassName={`text-[9.5pt] leading-[1.45] ${textClass}`}
            emptyHint="Leerer Absatz – klicken zum Bearbeiten"
            aria-label={`Absatz ${i + 1}`}
          />
          {!issued && paragraphs.length > minParagraphs ? (
            <button
              type="button"
              className="mt-1 text-[9pt] text-red-600 hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => void onRemoveParagraph(i)}
              aria-label="Absatz entfernen"
            >
              Absatz entfernen
            </button>
          ) : null}
        </div>
      ))}
      {issued ? null : (
        <button
          type="button"
          onClick={() => void onAddParagraph()}
          className="inline-flex items-center gap-1 text-[9pt] text-sky-500 hover:text-sky-600 font-medium mt-1 mb-1"
        >
          <Plus className="w-3.5 h-3.5 shrink-0" />
          Absatz hinzufügen
        </button>
      )}
    </div>
  );
}

export type OfferDocumentEditorProps = {
  lead: Lead;
  leadRouteId: string;
  /** Aktives Angebot (offerings.id); ohne Angabe speichert der Server das primäre Angebot. */
  offeringId?: string | null;
  /** Dokumenttyp des aktuell erstellten Dokuments (z. B. „Angebot“, „Rechnung“). */
  documentTypeLabel: string;
  onClose: () => void;
  /** Nach erfolgreichem PATCH des Angebotsentwurfs (z. B. Lead neu laden). */
  onDraftSaved?: () => void | Promise<void>;
  /** Nach PDF-Download oder -Versand (Lead-Dokumente / Projekt-Feed aktualisieren). */
  onProjectFeedChanged?: () => void | Promise<void>;
};

function lineTotal(qty: number, unit: number): number {
  return Math.round(qty * unit * 100) / 100;
}

type BlockInsertKind = 'text' | 'title' | 'freePosition' | 'image';

const BLOCK_INSERT_KINDS = new Set<BlockInsertKind>(['text', 'title', 'freePosition', 'image']);

/** Fallback when dnd-kit payload is missing — ids must stay in sync with BlockPaletteItem. */
const BLOCK_DRAG_KIND_BY_ID: Record<string, BlockInsertKind | 'positionSurcharge'> = {
  'block-text': 'text',
  'block-title': 'title',
  'block-free': 'freePosition',
  'block-image': 'image',
  'block-surcharge': 'positionSurcharge',
};

const BLOCK_PALETTE_LABEL_BY_ID: Record<string, string> = {
  'block-text': 'Text',
  'block-title': 'Titel',
  'block-free': 'Freie Position',
  'block-image': 'Bild',
  'block-surcharge': 'Zuschlag auf vorherige Positionen',
};

/** Matches Bausteine column width: aside 320px minus p-3 padding on each side. */
const BLOCK_PALETTE_DRAG_WIDTH_PX = 296;

const BLOCK_PALETTE_ITEM_CLASS =
  'rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text-muted cursor-grab active:cursor-grabbing flex items-center gap-2 w-full';

type OfferDragPreview = { label: string; variant: 'palette' | 'compact' };

function resolveBlockPaletteKind(active: DragEndEvent['active']): BlockInsertKind | 'positionSurcharge' | null {
  const dataKind = String(active.data.current?.kind ?? '');
  if (dataKind === 'positionSurcharge') return 'positionSurcharge';
  if (BLOCK_INSERT_KINDS.has(dataKind as BlockInsertKind)) return dataKind as BlockInsertKind;
  return BLOCK_DRAG_KIND_BY_ID[String(active.id)] ?? null;
}

function resolveTableDropIndex(
  event: DragEndEvent,
  numberedRows: Array<{ id: string }>,
): number | null {
  if (!event.over) return null;
  const overId = String(event.over.id);
  if (overId.startsWith('drop-')) {
    const parsed = Number(overId.replace('drop-', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (overId.startsWith('row-')) {
    const overRowId = overId.replace('row-', '');
    const rowIndex = numberedRows.findIndex((r) => r.id === overRowId);
    return rowIndex >= 0 ? rowIndex : null;
  }
  return null;
}

function isPointInRect(point: { x: number; y: number }, rect: DOMRect): boolean {
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

function hitTestTableInsertIndex(
  tableEl: HTMLElement,
  pointer: { x: number; y: number },
): number | null {
  const dropBands = tableEl.querySelectorAll<HTMLElement>('[data-offer-drop-index]');
  for (const band of dropBands) {
    const rect = band.getBoundingClientRect();
    if (pointer.y >= rect.top && pointer.y <= rect.bottom) {
      const idx = Number(band.dataset.offerDropIndex);
      if (Number.isFinite(idx)) return idx;
    }
  }

  const rowEls = tableEl.querySelectorAll<HTMLElement>('[data-offer-row-index]');
  for (const rowEl of rowEls) {
    const rect = rowEl.getBoundingClientRect();
    if (pointer.y >= rect.top && pointer.y <= rect.bottom) {
      const idx = Number(rowEl.dataset.offerRowIndex);
      if (!Number.isFinite(idx)) continue;
      const mid = rect.top + rect.height / 2;
      return pointer.y < mid ? idx : idx + 1;
    }
  }

  return null;
}

function resolveTableInsertIndexFromPointer(
  tableEl: HTMLElement,
  pointer: { x: number; y: number },
  rowCount: number,
  layout?: OfferTableRowLayout | null,
  scrollEl?: HTMLElement | null,
): number {
  const hit = hitTestTableInsertIndex(tableEl, pointer);
  if (hit != null) return hit;
  if (layout && scrollEl && layout.count === rowCount) {
    return insertIndexFromPointerLayout(pointer, scrollEl, layout);
  }
  return rowCount;
}

function resolveTableDropIndexFromPointer(
  tableEl: HTMLElement,
  pointer: { x: number; y: number },
  numberedRows: Array<{ id: string }>,
  layout?: OfferTableRowLayout | null,
  scrollEl?: HTMLElement | null,
): number | null {
  const tableRect = tableEl.getBoundingClientRect();
  if (!isPointInRect(pointer, tableRect)) return null;
  return resolveTableInsertIndexFromPointer(tableEl, pointer, numberedRows.length, layout, scrollEl);
}

function resolveTableDropIndexForPaletteDrop(
  event: DragEndEvent,
  numberedRows: Array<{ id: string }>,
  tableEl: HTMLElement | null,
  pointer: { x: number; y: number } | null,
  layout?: OfferTableRowLayout | null,
  scrollEl?: HTMLElement | null,
): number | null {
  const fromOver = resolveTableDropIndex(event, numberedRows);
  if (fromOver != null) return fromOver;
  if (!tableEl || !pointer) return null;
  return resolveTableDropIndexFromPointer(tableEl, pointer, numberedRows, layout, scrollEl);
}

/** Palette/picker drops in the document preview: in-table bands, above → 0, below → append. */
function resolvePaletteDropIndex(
  event: DragEndEvent,
  numberedRows: Array<{ id: string }>,
  tableEl: HTMLElement | null,
  pointer: { x: number; y: number } | null,
  layout?: OfferTableRowLayout | null,
  scrollEl?: HTMLElement | null,
): number | null {
  if (tableEl && pointer) {
    const tableRect = tableEl.getBoundingClientRect();
    if (isPointInRect(pointer, tableRect)) {
      return resolveTableDropIndexForPaletteDrop(event, numberedRows, tableEl, pointer, layout, scrollEl);
    }
    if (pointer.y > tableRect.bottom) return numberedRows.length;
    return 0;
  }
  return resolveTableDropIndex(event, numberedRows);
}

function isPaletteDragSource(source: unknown): boolean {
  return source === 'block' || source === 'picker';
}

/** Palette drags: document preview → top/bottom slot; over table → row bands; sidebar → none. */
function resolvePaletteDragCollisions(
  args: Parameters<CollisionDetection>[0],
  pointer: { x: number; y: number } | null,
  documentPreviewEl: HTMLElement | null,
  tableEl: HTMLElement | null,
  rowCount: number,
  layout?: OfferTableRowLayout | null,
): ReturnType<CollisionDetection> {
  if (!pointer || !documentPreviewEl) return [];
  if (!isPointInRect(pointer, documentPreviewEl.getBoundingClientRect())) return [];

  if (tableEl && isPointInRect(pointer, tableEl.getBoundingClientRect())) {
    const dropIndex = resolveTableInsertIndexFromPointer(
      tableEl,
      pointer,
      rowCount,
      layout,
      documentPreviewEl,
    );
    const dropId = `drop-${dropIndex}`;
    const dropTarget = args.droppableContainers.find((c) => String(c.id) === dropId);
    if (dropTarget) return [{ id: dropTarget.id, data: { droppableContainer: dropTarget } }];
    if (layout) return [];
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCenter(args);
  }

  const tableRect = tableEl?.getBoundingClientRect();
  const dropId =
    tableRect && pointer.y > tableRect.bottom ? `drop-${rowCount}` : 'drop-0';
  const dropTarget = args.droppableContainers.find((c) => String(c.id) === dropId);
  return dropTarget ? [{ id: dropTarget.id, data: { droppableContainer: dropTarget } }] : [];
}

function reconcileDraftSurchargesForTable(
  draftRows: OfferDraftRow[],
  positions: LeadPosition[],
  options?: { omitUncoveredApiPositions?: boolean },
  laborOpts?: { hourlyRate: number; listLaborSeparately?: boolean },
): OfferDraftRow[] {
  const combined = buildOfferMergedTableRows(positions, draftRows, options) as PositionSurchargeTableRow[];
  const priced = laborOpts
    ? applyCustomerFacingLaborPricing(combined, {
        hourlyRate: laborOpts.hourlyRate,
        listLaborSeparately: false,
      })
    : combined;
  return reconcileSurchargeDraftRows(draftRows, priced) as OfferDraftRow[];
}

const CREDIT_OR_STORNO_MERGE_OPTIONS = { omitUncoveredApiPositions: true } as const;

function defaultPaymentTermsFromLead(
  company: CompanySettings | null | undefined,
  org: Lead['organization'],
): OfferDocumentPaymentTerms {
  const isActiveContract =
    org?.has_framework_contract === true &&
    (!org.framework_valid_until || new Date(org.framework_valid_until) >= new Date());
  if (isActiveContract && org?.payment_terms_days) {
    return paymentTermsFromSettings({
      mode: 'net_days',
      days: org.payment_terms_days,
      skontoPercent: org.skonto_percent,
      skontoDays: org.skonto_days,
    });
  }
  return paymentTermsFromSettings({
    mode: company?.payment_terms_mode,
    days: company?.payment_terms_days ?? 7,
    skontoPercent: company?.skonto_percent,
    skontoDays: company?.skonto_days,
  });
}

function defaultAfterTotalsParas(opts?: {
  mode?: unknown;
  days?: unknown;
  context?: 'offer' | 'invoice';
  skontoPercent?: unknown;
  skontoDays?: unknown;
}): OfferDocumentEditablePara[] {
  return [
    {
      id: newId(),
      text: formatPaymentTermsSentence({
        mode: opts?.mode,
        days: opts?.days ?? 7,
        context: opts?.context ?? 'offer',
        skontoPercent: opts?.skontoPercent,
        skontoDays: opts?.skontoDays,
      }),
    },
    {
      id: newId(),
      text: 'Falls Ihnen das Angebot zusagt, senden Sie es bitte unterschrieben zurück.',
    },
  ];
}

function normalizePositions(data: unknown): LeadPosition[] {
  if (!Array.isArray(data)) return [];
  return data as LeadPosition[];
}

/** Stock intros that were injected before empty Textvorlagen were allowed. */
const STOCK_INTRO_LETTER_TEXTS: readonly (readonly string[])[] = [
  ['Sehr geehrte Damen und Herren,', 'Anbei schicken wir Ihnen das angeforderte Angebot. Dieses lautet wie folgt:'],
  ['Sehr geehrte Damen und Herren,', 'anbei erhalten Sie unsere Rechnung.'],
  [
    'Sehr geehrte Damen und Herren,',
    'anbei erhalten Sie unsere Rechnung über die nachfolgend aufgeführten Leistungen.',
  ],
];

function introMatchesStockDefault(paras: OfferDocumentEditablePara[]): boolean {
  const texts = paras.map((p) => p.text.trim());
  return STOCK_INTRO_LETTER_TEXTS.some(
    (stock) => stock.length === texts.length && stock.every((line, i) => line === texts[i]),
  );
}

function resolveIntroLetterParas(input: {
  isWorkReport: boolean;
  draftIntro: OfferDocumentEditablePara[] | undefined;
  documentTexts: { introLetterParas: string[] } | null;
}): OfferDocumentEditablePara[] {
  if (input.isWorkReport) {
    return input.draftIntro ?? defaultWorkReportIntroLetterParas();
  }
  if (input.documentTexts) {
    const fromTemplate = input.documentTexts.introLetterParas.map((text) => ({ id: newId(), text }));
    if (fromTemplate.length === 0) {
      if (input.draftIntro && input.draftIntro.length > 0 && !introMatchesStockDefault(input.draftIntro)) {
        return input.draftIntro;
      }
      return [];
    }
    return input.draftIntro ?? fromTemplate;
  }
  return input.draftIntro ?? [];
}

function defaultWorkReportIntroLetterParas(): OfferDocumentEditablePara[] {
  return [
    { id: newId(), text: 'Sehr geehrte Damen und Herren,' },
    {
      id: newId(),
      text: 'Anbei schicken wir Ihnen den angeforderten Arbeitsbericht. Dieser lautet wie folgt:',
    },
  ];
}

function defaultWorkReportAfterTotalsParas(): OfferDocumentEditablePara[] {
  return [{ id: newId(), text: 'Hiermit wird der aufgeführte Arbeitsbericht bestätigt.' }];
}

function VirtualLaborNameInput({
  value,
  onCommit,
  className,
}: {
  value: string;
  onCommit: (value: string) => void;
  className?: string;
}) {
  const [text, setText] = React.useState(value);
  React.useEffect(() => setText(value), [value]);
  return (
    <input
      value={text}
      maxLength={200}
      aria-label="Bezeichnung der Arbeitszeitzeile"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(text)}
      className={className}
    />
  );
}

const WORK_REPORT_OFFER_INTRO_SENTENCE =
  'Anbei schicken wir Ihnen das angeforderte Angebot. Dieses lautet wie folgt:';
const WORK_REPORT_INTRO_SENTENCE =
  'Anbei schicken wir Ihnen den angeforderten Arbeitsbericht. Dieser lautet wie folgt:';

const SIGNATURE_REQUEST_SENTENCE = 'Falls Ihnen das Angebot zusagt, senden Sie es bitte unterschrieben zurück.';
const OLD_PARTIAL_INVOICE_INTRO_SENTENCE = 'anbei erhalten Sie die Abschlagsrechnung für die bisher erbrachten Leistungen.';
const NEW_PARTIAL_INVOICE_INTRO_SENTENCE = 'anbei erhalten Sie die Abschlagsrechnung.';
const OLD_FOLLOWUP_SENTENCE = 'Für Rückfragen zu diesem Angebot stehen wir Ihnen gerne zur Verfügung.';

const DOCS_WITHOUT_SIGNATURE = new Set(['Rechnung', 'Abschlagsrechnung', 'Schlussrechnung', 'Stornorechnung', 'Gutschrift']);

/** Kopfzeilen-Aktionen (Anhänge, GAEB, IDS): randlos wie die übrigen Header-Buttons der App. */
const EDITOR_HEADER_ACTION_CLASS =
  'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text transition-colors';

/** Umschalter zwischen Kundendokument und Kalkulationsansicht. */
function EditorViewToggle({
  value,
  onChange,
  calculationHintCount = 0,
}: {
  value: 'document' | 'calculation';
  onChange: (next: 'document' | 'calculation') => void;
  /** Offene Margen-Hinweise; nur Anzeige, blockiert nichts. */
  calculationHintCount?: number;
}) {
  return (
    <div className="flex h-9 shrink-0 items-center rounded-md border border-app-border bg-app-surface-muted p-0.5">
      {(['document', 'calculation'] as const).map((view) => (
        <button
          key={view}
          type="button"
          onClick={() => onChange(view)}
          aria-label={
            view === 'calculation' && calculationHintCount > 0
              ? `Kalkulation, ${calculationHintCount} ${calculationHintCount === 1 ? 'Hinweis' : 'Hinweise'}`
              : undefined
          }
          className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors ${
            value === view
              ? 'bg-app-surface text-app-text shadow-sm'
              : 'text-app-text-muted hover:text-app-text'
          }`}
        >
          {view === 'document' ? 'Dokument' : 'Kalkulation'}
          {view === 'calculation' && calculationHintCount > 0 ? (
            <span
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold tabular-nums text-amber-800"
              title={`${calculationHintCount} ${calculationHintCount === 1 ? 'Hinweis' : 'Hinweise'} zur Kalkulation`}
            >
              {calculationHintCount}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** Status als stiller Punkt plus Text statt farbiger Pille: der Titel bleibt das Wichtigste in der Kopfzeile. */
const EDITOR_STATUS_BASE = 'inline-flex items-center gap-1.5 text-xs font-medium text-app-text-muted';
const EDITOR_STATUS_DOT = 'inline-block h-1.5 w-1.5 shrink-0 rounded-full';
const EDITOR_STATUS_BADGE_DONE = `${EDITOR_STATUS_BASE} [&>i]:bg-emerald-500`;
const EDITOR_STATUS_BADGE_DRAFT = `${EDITOR_STATUS_BASE} [&>i]:bg-amber-400`;

/** Document types that appear in Rechnungen / Angebote overview (matches server isMatchingOverviewDocumentType). */
const LEDGER_OVERVIEW_DOCUMENT_LABELS = new Set([
  'Angebot',
  'Rechnung',
  'Abschlagsrechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
]);

function stripSignatureRequestSentence(
  paras: OfferDocumentEditablePara[],
  shouldStrip: boolean,
): OfferDocumentEditablePara[] {
  if (!shouldStrip) return paras;
  return paras.filter((p) => p.text.trim() !== SIGNATURE_REQUEST_SENTENCE);
}

function normalizeDefaultSentences(
  paras: OfferDocumentEditablePara[],
  documentTypeLabel: string,
): OfferDocumentEditablePara[] {
  return paras.flatMap((p) => {
    const t = p.text.trim();
    if (documentTypeLabel.trim() === 'Abschlagsrechnung' && t === OLD_PARTIAL_INVOICE_INTRO_SENTENCE) {
      return [{ ...p, text: NEW_PARTIAL_INVOICE_INTRO_SENTENCE }];
    }
    if (t === OLD_FOLLOWUP_SENTENCE) {
      return [{ ...p, text: FOLLOWUP_QUESTIONS_SENTENCE }];
    }
    if (documentTypeLabel.trim() === 'Arbeitsbericht') {
      if (t === WORK_REPORT_OFFER_INTRO_SENTENCE) {
        return [{ ...p, text: WORK_REPORT_INTRO_SENTENCE }];
      }
      if (t.startsWith('Die Zahlungsfrist nach Rechnungsstellung')) {
        return [{ ...p, text: 'Hiermit wird der aufgeführte Arbeitsbericht bestätigt.' }];
      }
      if (t === FOLLOWUP_QUESTIONS_SENTENCE) {
        return [];
      }
      if (t === SIGNATURE_REQUEST_SENTENCE || t.startsWith('Falls Ihnen das Angebot zusagt')) {
        return [];
      }
    }
    return [p];
  });
}

function blocksToPersist(blocks: OfferIntroBlock[]) {
  return blocks.map((b) =>
    b.type === 'image'
      ? ({ id: b.id, type: 'image' as const, alt: b.alt })
      : b.type === 'title'
        ? { id: b.id, type: 'title' as const, content: b.content }
        : { id: b.id, type: 'text' as const, content: b.content }
  );
}

/**
 * Durchschnittliche Zeichenbreite in `ch` für Spaltenbreiten der Positionstabelle:
 * `1ch` ist eine Ziffer, Punkte/Kommas/„€“/Buchstaben sind im Schnitt schmaler.
 */
const POSITION_TABLE_CH_PER_CHAR = 0.8;

/** Textarea that auto-resizes to fit its content on every value change, including initial render. */
function AutoResizeTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fitted = el.offsetHeight;
    const content = el.scrollHeight;
    if (content > fitted + 1) {
      el.style.height = `${content}px`;
      return;
    }
    if (content >= fitted - 1) return;
    // Shrink only: `height: auto` is required, but Chrome may re-dispatch `input` if we
    // flip auto→px on every keystroke (Sentry MEISTERPLATFORM-FRONTEND-A).
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  return <textarea ref={ref} {...props} />;
}

export const OfferDocumentEditor: React.FC<OfferDocumentEditorProps> = ({
  lead,
  leadRouteId,
  offeringId,
  documentTypeLabel,
  onClose,
  onDraftSaved,
  onProjectFeedChanged,
}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Quer-Funktionen anderer Module: vor dem Laden des Modulstands gelten sie als aus.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const rechnungenEnabled = modulesLoaded && isEnabled('rechnungen');
  const idsEnabled = modulesLoaded && isEnabled('ids');
  const quoteOffersEnabled = modulesLoaded && isEnabled('ausschreibungen');
  const canViewStammdaten = can('web.stammdaten.view');
  const cancelledStornoLedgerIdRef = React.useRef<string | null>(null);
  const creditedInvoiceLedgerIdRef = React.useRef<string | null>(null);
  /** Original invoice of a Storno/Gutschrift for the e-invoice reference (BG-3). */
  const cancelledInvoiceReferenceRef = React.useRef<{ id: string; issueDate: string | null } | null>(null);
  const creditedInvoiceReferenceRef = React.useRef<{ id: string; issueDate: string | null } | null>(null);
  const stornoDraftsAppliedForRef = React.useRef<string | null>(null);
  const gutschriftDraftsAppliedForRef = React.useRef<string | null>(null);
  /** Rechnung aus Arbeitsberichten: tatsächlich übernommene Berichte (für invoice-document-sent). */
  const workReportInvoiceIdsRef = React.useRef<string[] | null>(null);
  const workReportDraftsAppliedForRef = React.useRef<string | null>(null);
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [location.pathname]);
  const urlInstanceId = React.useMemo(
    () => instanceIdFromPathname(location.pathname),
    [location.pathname],
  );
  const positionsApiBase = React.useMemo(
    () =>
      offeringId
        ? offeringPositionsApiBase(offeringId)
        : `/api/leads/${encodeURIComponent(leadRouteId)}/positions`,
    [offeringId, leadRouteId],
  );
  const [company, setCompany] = React.useState<CompanySettings | null>(null);
  const [holidayDates, setHolidayDates] = React.useState<Set<string>>(() => new Set());
  /** Last template used for PDF generation (fail-open; null = legacy layout). */
  const activeLayoutTemplateRef = React.useRef<DocumentLayoutTemplateRow | null>(null);
  /** Live default layout for HTML WYSIWYG (null = legacy HTML header/footer). */
  const [documentLayout, setDocumentLayout] = React.useState<DocumentLayoutV1 | null>(null);
  /** False until the default-template request finishes — avoids painting legacy then switching. */
  const [layoutPreviewReady, setLayoutPreviewReady] = React.useState(false);
  const [layoutHtmlAssetUrls, setLayoutHtmlAssetUrls] = React.useState<Record<string, string>>({});
  const [isMdViewport, setIsMdViewport] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true,
  );
  /** Auto scale so the A4 sheet fits the pane width (≤ 1). */
  const [sheetFitScale, setSheetFitScale] = React.useState(1);
  /** Pane width ÷ A4 width — used by „Seitenbreite“. */
  const [sheetPaneWidthRatio, setSheetPaneWidthRatio] = React.useState(1);
  /** User zoom on top of the fit scale (1 = fit / 100 %). */
  const [documentZoom, setDocumentZoom] = React.useState(readStoredDocumentZoom);
  const sheetScale = sheetFitScale * documentZoom;
  /** Unscaled sheet height in CSS px (layout size, transforms ignored). */
  const [sheetLayoutHeightPx, setSheetLayoutHeightPx] = React.useState<number | null>(null);
  /** Sheet point the next zoom step should keep in place (cursor, else viewport centre). */
  const zoomAnchorRef = React.useRef<{
    sheetX: number;
    sheetY: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  const sheetScaleRef = React.useRef(sheetScale);
  sheetScaleRef.current = sheetScale;
  const appliedSheetScaleRef = React.useRef(sheetScale);
  const sheetOuterRef = React.useRef<HTMLDivElement | null>(null);
  const sheetInnerRef = React.useRef<HTMLDivElement | null>(null);
  const [positions, setPositions] = React.useState<LeadPosition[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [logoBust, setLogoBust] = React.useState(0);
  const [introContent, setIntroContent] = React.useState<OfferDocumentContentBlock[]>([]);
  const [closingContent, setClosingContent] = React.useState<OfferDocumentContentBlock[]>([]);
  const [draftRows, setDraftRows] = React.useState<OfferDraftRow[]>([]);
  const [attachments, setAttachments] = React.useState<OfferAttachment[]>([]);
  const [excludedStandardAttachments, setExcludedStandardAttachments] = React.useState<LegalAttachmentKind[]>([]);
  const [discount, setDiscount] = React.useState<OfferDiscount | null>(null);
  const [discountEditing, setDiscountEditing] = React.useState(false);
  const [siteLevy, setSiteLevy] = React.useState<InvoiceSiteLevy | null>(null);
  const [retention, setRetention] = React.useState<InvoiceRetention | null>(null);
  const [constructionDeductionsCustomized, setConstructionDeductionsCustomized] = React.useState(false);
  const [constructionDeductionsEditing, setConstructionDeductionsEditing] = React.useState(false);
  const [vatModeEditing, setVatModeEditing] = React.useState(false);
  const [vatMode, setVatMode] = React.useState<OfferVatMode>(DEFAULT_OFFER_VAT_MODE);
  /** Netto-/USt-/Brutto-Summen und Rabatt-Zwischensummen im Editor und PDF ausblenden. */
  const [hideTotals, setHideTotals] = React.useState(false);
  const [laborCostShare, setLaborCostShare] = React.useState<LaborCostShareMode>('auto');
  /** Arbeitsbericht: Spalte „Artikel-Nr.“ im Editor und PDF ausblenden. */
  const [hideArticleNumber, setHideArticleNumber] = React.useState(false);
  /** Positionstabelle: Langtext/Beschreibung unter dem Kurztext im Editor und PDF ausblenden. */
  const [hidePositionDescriptions, setHidePositionDescriptions] = React.useState(false);
  /** Kundenausgabe: vollständige Positionen oder Titelzusammenstellung. */
  const [positionDetailLevel, setPositionDetailLevel] = React.useState<'full' | 'titles'>('full');
  const [calculationHourlyRate, setCalculationHourlyRate] = React.useState(0);
  const [calculationHourlyRateFromCustomer, setCalculationHourlyRateFromCustomer] = React.useState(false);
  const [calculationLaborSeparately, setCalculationLaborSeparately] = React.useState(false);
  const [calculationLaborRowLabel, setCalculationLaborRowLabel] = React.useState(
    DEFAULT_TITLE_LABOR_ROW_LABEL,
  );
  const [calculationLaborRowNames, setCalculationLaborRowNames] = React.useState<
    Record<string, string>
  >({});
  const [calculationRealLaborCostRate, setCalculationRealLaborCostRate] = React.useState(0);
  /** Ausgeblendete Margen-Hinweise dieses Entwurfs (IDs aus offerMarginHints). */
  const [calculationDismissedHints, setCalculationDismissedHints] = React.useState<string[]>([]);
  const [calculationCopperQuoteEurPer100kg, setCalculationCopperQuoteEurPer100kg] =
    React.useState<number | null>(null);
  const [calculationAluminumQuoteEurPer100kg, setCalculationAluminumQuoteEurPer100kg] =
    React.useState<number | null>(null);
  const [calculationMetalQuoteSource, setCalculationMetalQuoteSource] = React.useState<
    'manual' | 'westmetall' | 'sonepar'
  >('manual');
  const [calculationMetalQuoteDate, setCalculationMetalQuoteDate] = React.useState<string | null>(
    null,
  );
  const [
    calculationMetalProcurementCostPercent,
    setCalculationMetalProcurementCostPercent,
  ] = React.useState(1);
  const [instanceMetalQuotes, setInstanceMetalQuotes] = React.useState<{
    loaded: boolean;
    copper: number | null;
    aluminum: number | null;
    source: 'manual' | 'westmetall' | 'sonepar';
    quoteDate: string | null;
    procurementCostPercent: number;
  }>({
    loaded: false,
    copper: null,
    aluminum: null,
    source: 'manual',
    quoteDate: null,
    procurementCostPercent: 1,
  });
  const [metalQuotesSaving, setMetalQuotesSaving] = React.useState(false);
  const vatPickerWrapRef = React.useRef<HTMLDivElement | null>(null);
  const previewMenuWrapRef = React.useRef<HTMLDivElement | null>(null);
  const [previewMenuOpen, setPreviewMenuOpen] = React.useState(false);
  const [deletingPositionId, setDeletingPositionId] = React.useState<PositionRowId | null>(null);
  const [positionMutateError, setPositionMutateError] = React.useState<string | null>(null);
  /** Rückfrage beim Löschen eines Titels, der noch Positionen enthält. */
  const [titleDeletePrompt, setTitleDeletePrompt] = React.useState<{
    title: DeletableTableRow;
    members: DeletableTableRow[];
  } | null>(null);
  const [titleDeleteBusy, setTitleDeleteBusy] = React.useState(false);
  const [showMobilePositionPicker, setShowMobilePositionPicker] = React.useState(false);
  const [activeDragPreview, setActiveDragPreview] = React.useState<OfferDragPreview | null>(null);
  const [expandedComponentRowIds, setExpandedComponentRowIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  /** Leere interne Notizen bleiben ausgeblendet, bis sie über „+ Interne Notiz“ geöffnet werden. */
  const [openInternalNoteRowIds, setOpenInternalNoteRowIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const setInternalNoteOpen = React.useCallback((rowId: string, open: boolean) => {
    setOpenInternalNoteRowIds((prev) => {
      if (prev.has(rowId) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);
  const [surchargeModalState, setSurchargeModalState] = React.useState<
    | { mode: 'insert'; insertIndex: number }
    | { mode: 'edit'; rowId: string }
    | null
  >(null);
  const surchargeVkSyncRef = React.useRef<Record<string, number>>({});
  const documentPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const positionTableRef = React.useRef<HTMLDivElement | null>(null);
  const documentRowLayoutRef = React.useRef<OfferTableRowLayout | null>(null);
  const [documentScrollEl, setDocumentScrollEl] = React.useState<HTMLDivElement | null>(null);
  const [documentTableEl, setDocumentTableEl] = React.useState<HTMLDivElement | null>(null);
  const lastPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const numberedRowCountRef = React.useRef(0);

  const offerDndCollisionDetection = React.useCallback<CollisionDetection>(
    (args) => {
      if (isPaletteDragSource(args.active.data.current?.source)) {
        return resolvePaletteDragCollisions(
          args,
          lastPointerRef.current,
          documentPreviewRef.current,
          positionTableRef.current,
          numberedRowCountRef.current,
          documentRowLayoutRef.current,
        );
      }
      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) return pointerCollisions;
      return closestCenter(args);
    },
    [],
  );

  // Track the live pointer position so we can tell on drop whether a palette/picker
  // item was released over the positions table (dnd-kit's closestCenter always
  // reports a nearest droppable, even when released far outside the table).
  React.useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onPointer, true);
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('pointerup', onPointer, true);
    return () => {
      window.removeEventListener('pointermove', onPointer, true);
      window.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('pointerup', onPointer, true);
    };
  }, []);

  React.useEffect(() => {
    if (!capabilitiesLoaded) return;
    if (!canViewStammdaten) {
      // Ohne Stammdaten-Recht: dieselben Vorgaben wie bei einem fehlgeschlagenen Laden.
      setInstanceMetalQuotes({
        loaded: true,
        copper: null,
        aluminum: null,
        source: 'manual',
        quoteDate: null,
        procurementCostPercent: 1,
      });
      return;
    }
    let cancelled = false;
    fetch('/api/instance-settings')
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Metallnotierungen konnten nicht geladen werden.');
        return data?.settings as
          | {
              copper_quote_eur_per_100kg?: number | null;
              aluminum_quote_eur_per_100kg?: number | null;
              metal_quote_source?: 'manual' | 'westmetall' | 'sonepar';
              metal_quote_date?: string | null;
              metal_procurement_cost_percent?: number;
            }
          | undefined;
      })
      .then((settings) => {
        if (cancelled) return;
        setInstanceMetalQuotes({
          loaded: true,
          copper: settings?.copper_quote_eur_per_100kg ?? null,
          aluminum: settings?.aluminum_quote_eur_per_100kg ?? null,
          source: settings?.metal_quote_source ?? 'manual',
          quoteDate: settings?.metal_quote_date ?? null,
          procurementCostPercent: Math.max(
            0,
            settings?.metal_procurement_cost_percent ?? 1,
          ),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setInstanceMetalQuotes({
            loaded: true,
            copper: null,
            aluminum: null,
            source: 'manual',
            quoteDate: null,
            procurementCostPercent: 1,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [capabilitiesLoaded, canViewStammdaten]);

  const [offerTitleRest, setOfferTitleRest] = React.useState(
    () => (lead.lead_title ?? '').trim() || '—'
  );
  const [issueDateEdit, setIssueDateEdit] = React.useState(() => formatDateToDDMMYYYY(todayYMD()));
  const [recipientNameLinesEdit, setRecipientNameLinesEdit] = React.useState<string[]>([]);
  const [recipientStreetLineEdit, setRecipientStreetLineEdit] = React.useState('');
  const [recipientPlzOrtLineEdit, setRecipientPlzOrtLineEdit] = React.useState('');
  const [leistungsortEdit, setLeistungsortEdit] = React.useState(() => formatLeistungsort(lead));
  const [orderNumberEdit, setOrderNumberEdit] = React.useState(
    () => (lead.baustelle?.bestellnummer ?? '').trim(),
  );
  const [leistungszeitraumEdit, setLeistungszeitraumEdit] = React.useState<OfferLeistungszeitraum | null>(null);
  const [lieferdatumEdit, setLieferdatumEdit] = React.useState<OfferLeistungszeitraum | null>(null);
  const [hideDocumentTypeInTitle, setHideDocumentTypeInTitle] = React.useState(false);
  const [hideLeistungszeitraum, setHideLeistungszeitraum] = React.useState(false);
  const [hideLieferdatum, setHideLieferdatum] = React.useState(false);
  const introLetterParas = contentToParas(introContent);
  const afterTotalsParas = contentToParas(closingContent);
  const introBlocks = introContent.flatMap((block) =>
    block.type === 'title' ? [{ id: block.id, type: 'title' as const, content: block.content }] : [],
  );
  const closingBlocks = closingContent.flatMap((block) =>
    block.type === 'title' ? [{ id: block.id, type: 'title' as const, content: block.content }] : [],
  );
  const [paymentTermsCustomized, setPaymentTermsCustomized] = React.useState(false);
  const paymentTermsCustomizedRef = React.useRef(false);
  const paymentTermsCustomizedSessionRef = React.useRef<string | null>(null);
  const paymentTermsTextIdRef = React.useRef<string | null>(null);
  const [closingGreeting, setClosingGreeting] = React.useState('Mit freundlichen Grüßen');
  const [signatureHint, setSignatureHint] = React.useState('Ort, Datum, Unterschrift Auftraggeber');
  const [abschlagPercent, setAbschlagPercent] = React.useState(50);
  const [partialInvoiceAmount, setPartialInvoiceAmount] = React.useState(0);
  const [partialInvoiceDescription, setPartialInvoiceDescription] = React.useState('');
  const [partialInvoiceLineName, setPartialInvoiceLineName] = React.useState('');
  const [partialInvoiceAmountIsGross, setPartialInvoiceAmountIsGross] = React.useState(false);
  const [partialInvoiceMode, setPartialInvoiceMode] = React.useState<PartialInvoiceMode>('percent');
  /** null = Default je Modus (Prozent kumuliert, Betrag nicht). */
  const [partialInvoiceCumulative, setPartialInvoiceCumulative] = React.useState<boolean | null>(null);
  /** Abschlag: Auftragssumme netto fürs PDF; null = Angebotsnetto. */
  const [orderTotalNet, setOrderTotalNet] = React.useState<number | null>(null);
  const [documentPaymentTerms, setDocumentPaymentTerms] = React.useState<OfferDocumentPaymentTerms>(() =>
    paymentTermsFromSettings({ mode: 'net_days', days: 7 }),
  );
  const [positionInvoiceQuantities, setPositionInvoiceQuantities] = React.useState<Record<string, number>>({});
  const [aggregatedInvoicedQuantities, setAggregatedInvoicedQuantities] = React.useState<Record<string, number>>({});
  const [billingLedgerError, setBillingLedgerError] = React.useState<string | null>(null);
  const [positionPartialInvoiceIds, setPositionPartialInvoiceIds] = React.useState<Set<string>>(new Set());
  /** Offene Einbehalte aus gestellten Vor-Rechnungen (Rechnung/Abschlag) — für Schluss fällig. */
  const [heldPriorRetentions, setHeldPriorRetentions] = React.useState<
    Array<{ id: string; retentionGross: number; referenceId: string }>
  >([]);
  const [billingLedgerLoadedKey, setBillingLedgerLoadedKey] = React.useState<string | null>(null);
  const billingLedgerInitRef = React.useRef<string | null>(null);
  const isPartialInvoice = documentTypeLabel.trim() === 'Abschlagsrechnung';
  const isFinalInvoice = documentTypeLabel.trim() === 'Schlussrechnung';
  const isPercentPartialInvoice = isPartialInvoice && partialInvoiceMode === 'percent';
  const isAmountPartialInvoice = isPartialInvoice && partialInvoiceMode === 'amount';
  const isPositionPartialInvoice = isPartialInvoice && partialInvoiceMode === 'position';
  const isLumpSumPartialInvoice = isPercentPartialInvoice || isAmountPartialInvoice;
  const isCumulativeLumpSumPartialInvoice =
    isLumpSumPartialInvoice && isCumulativePartialInvoice(partialInvoiceMode, partialInvoiceCumulative);
  const showInvoicePaymentTermsControls = isPartialInvoice || isFinalInvoice;
  const showPositionBillingColumns = isPositionPartialInvoice || isFinalInvoice;
  const isWorkReport = documentTypeLabel.trim() === 'Arbeitsbericht';
  const canToggleDocumentTotals =
    documentTypeLabel.trim() === 'Angebot' || documentTypeLabel.trim() === 'Kostenvoranschlag';
  const totalsHidden = isWorkReport || (canToggleDocumentTotals && hideTotals);
  const isCreditOrStornoDocument =
    documentTypeLabel.trim() === 'Gutschrift' || documentTypeLabel.trim() === 'Stornorechnung';
  /**
   * „Rechnung aus Arbeitsberichten“ (`?workReports=id,id`): Zeilen kommen aus den Berichten.
   * Vorgangspositionen und Vorgangs-Entwurf bleiben unverändert (Belegkette statt Überschreiben).
   */
  const workReportInvoiceParam =
    documentTypeLabel.trim() === 'Rechnung' ? (searchParams.get('workReports') ?? '').trim() : '';
  const workReportInvoiceIds = React.useMemo(
    () => workReportInvoiceParam.split(',').map((id) => id.trim()).filter(Boolean),
    [workReportInvoiceParam],
  );
  const isWorkReportInvoice = workReportInvoiceIds.length > 0;
  /** Für Callbacks (persistFull, syncOrder): im Arbeitsbericht-Modus nichts in den Vorgang schreiben. */
  const workReportInvoiceRef = React.useRef(false);
  workReportInvoiceRef.current = isWorkReportInvoice;
  /** Beleg aus Snapshot: weder Katalogpositionen noch Vorgangs-Entwurf schreiben. */
  const keepsOfferingUntouched = isCreditOrStornoDocument || isWorkReportInvoice;
  const creditOrStornoMergeOptions = keepsOfferingUntouched
    ? CREDIT_OR_STORNO_MERGE_OPTIONS
    : undefined;
  const treatEventualAsNormal = documentTreatsEventualAsNormal(documentTypeLabel, {
    partialInvoiceMode: isPartialInvoice ? partialInvoiceMode : undefined,
  });
  const hidePositionPrices = isWorkReport;
  const showArticleNumberColumn = isWorkReport && !hideArticleNumber;
  const positionTableColSpan =
    (hidePositionPrices ? (showArticleNumberColumn ? 6 : 5) : 7) +
    (showPositionBillingColumns ? 1 : 0);
  /** Negative Einzelpreise (z. B. Stornorechnung, manuelle Gutschriftenzeilen). */
  const allowNegativePositionNet = React.useMemo(() => {
    const t = documentTypeLabel.trim();
    return t === 'Stornorechnung' || t === 'Gutschrift' || t === 'Rechnung' || t === 'Abschlagsrechnung' || t === 'Schlussrechnung';
  }, [documentTypeLabel]);
  const [selectedDeductionInvoiceIds, setSelectedDeductionInvoiceIds] = React.useState<string[]>([]);
  const [deductionCandidates, setDeductionCandidates] = React.useState<PaidPartialInvoice[]>([]);
  const deductionCandidatesRef = React.useRef<PaidPartialInvoice[]>([]);
  React.useEffect(() => {
    deductionCandidatesRef.current = deductionCandidates;
  }, [deductionCandidates]);
  const [paidPartialsError, setPaidPartialsError] = React.useState<string | null>(null);
  const [deductionPickerOpen, setDeductionPickerOpen] = React.useState(false);
  const deductionInitKeyRef = React.useRef<string | null>(null);
  const deductionLoadGenRef = React.useRef(0);
  const deductionInitCompleteRef = React.useRef(false);
  const deductionRowsRef = React.useRef<DeductionInvoiceRow[]>([]);
  /** Synced immediately on selection changes so persist never writes a stale empty list. */
  const selectedDeductionInvoiceIdsRef = React.useRef<string[]>([]);
  const currentDeductionDocumentRef = React.useRef<CurrentDeductionDocument>({ ids: [], referenceIds: [] });
  const liveDeductionReferenceRef = React.useRef<string | null>(null);
  const boundIssuedInvoiceRef = React.useRef<BoundIssuedInvoice | null>(null);
  const [currentDeductionDocumentTick, setCurrentDeductionDocumentTick] = React.useState(0);

  const applyCurrentDeductionDocument = React.useCallback(() => {
    currentDeductionDocumentRef.current = resolveCurrentDeductionDocument({
      boundIssued: boundIssuedInvoiceRef.current,
      liveReferenceId: liveDeductionReferenceRef.current,
    });
    setCurrentDeductionDocumentTick((n) => n + 1);
  }, []);

  const rememberCurrentDeductionDocument = React.useCallback((patch: CurrentDeductionDocument) => {
    const refs = Array.from(patch.referenceIds ?? [])
      .map((ref) => String(ref ?? '').trim())
      .filter(Boolean);
    const ids = Array.from(patch.ids ?? [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean);
    if (refs.length > 0) {
      liveDeductionReferenceRef.current = refs[refs.length - 1] ?? null;
    }
    if (ids.length > 0) {
      boundIssuedInvoiceRef.current = {
        id: ids[ids.length - 1],
        referenceId:
          refs[refs.length - 1] ??
          liveDeductionReferenceRef.current ??
          boundIssuedInvoiceRef.current?.referenceId,
      };
    }
    applyCurrentDeductionDocument();
  }, [applyCurrentDeductionDocument]);

  const setSelectedDeductionInvoiceIdsSynced = React.useCallback((next: string[] | ((prev: string[]) => string[])) => {
    setSelectedDeductionInvoiceIds((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      selectedDeductionInvoiceIdsRef.current = resolved;
      return resolved;
    });
  }, []);

  const shouldShowSignature = !DOCS_WITHOUT_SIGNATURE.has(documentTypeLabel.trim());

  const [documentTexts, setDocumentTexts] = React.useState<{
    introLetterParas: string[];
    closingGreeting: string;
    closingBlocks: string[];
    sendPdfEmailBodyTemplate: string;
  } | null>(null);
  const [documentTextsError, setDocumentTextsError] = React.useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = React.useState('');
  const initialRepairDoneRef = React.useRef<string | null>(null);
  const positionsLoadedSessionRef = React.useRef<string | null>(null);
  const hydratedDraftRowsSessionRef = React.useRef<string | null>(null);
  /** Ausstellungsdatum pro Editor-Sitzung nur einmal auf heute setzen — nicht aus Draft-Cache. */
  const issueDateHydratedSessionRef = React.useRef<string | null>(null);
  const activePositionFieldEditCountsRef = React.useRef(new Map<string, number>());

  const editorSessionKey = React.useMemo(
    () => `${lead.id}:${offeringId ?? ''}:${documentTypeLabel}`,
    [lead.id, offeringId, documentTypeLabel],
  );

  React.useEffect(() => {
    liveDeductionReferenceRef.current = null;
    const draftRaw = offeringId
      ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
        lead.offer_document_draft)
      : lead.offer_document_draft;
    boundIssuedInvoiceRef.current =
      parseOfferDocumentDraft(draftRaw)?.currentIssuedInvoiceByDocType?.[documentTypeLabel] ?? null;
    applyCurrentDeductionDocument();
  }, [editorSessionKey, applyCurrentDeductionDocument, documentTypeLabel, offeringId]);

  const billingLedgerKey = React.useMemo(
    () => (offeringId ? `${editorSessionKey}:${offeringId}` : null),
    [editorSessionKey, offeringId],
  );

  const billingLedgerReady = Boolean(
    billingLedgerKey && billingLedgerLoadedKey === billingLedgerKey && !billingLedgerError,
  );

  const usePositionBillingTotals =
    isPositionPartialInvoice || (isFinalInvoice && Boolean(offeringId) && billingLedgerReady);

  const currentDeductionDocument = React.useMemo(
    () => currentDeductionDocumentRef.current,
    [currentDeductionDocumentTick],
  );

  const priorDeductionCandidates = React.useMemo(
    () =>
      excludeCurrentDocumentDeductions(
        deductionCandidates,
        currentDeductionDocument,
        selectedDeductionInvoiceIds,
      ),
    [deductionCandidates, currentDeductionDocument, selectedDeductionInvoiceIds],
  );

  const paidPartialDeductionCandidates = React.useMemo(() => {
    if (isPartialInvoice) return priorDeductionCandidates;
    if (!isFinalInvoice) return [];
    // Schlussrechnung: without offering ledger we cannot distinguish billing modes; keep legacy lump-sum.
    if (!showPositionBillingColumns || !offeringId) return priorDeductionCandidates;
    // While loading or after ledger failure, fall back to legacy lump-sum deductions.
    if (!billingLedgerReady) return priorDeductionCandidates;
    // Position-based partial amounts are already in remaining row quantities; exclude from lump-sum.
    if (positionPartialInvoiceIds.size === 0) return priorDeductionCandidates;
    return priorDeductionCandidates.filter((c) => !positionPartialInvoiceIds.has(c.id));
  }, [
    isPartialInvoice,
    isFinalInvoice,
    showPositionBillingColumns,
    offeringId,
    billingLedgerReady,
    priorDeductionCandidates,
    positionPartialInvoiceIds,
  ]);

  /** Selected prior Abschläge for the summary table (Abschlag + Schlussrechnung). */
  const previousPartialInvoicesSummary = React.useMemo(
    () => derivePaidPartialInvoices(priorDeductionCandidates, selectedDeductionInvoiceIds),
    [priorDeductionCandidates, selectedDeductionInvoiceIds],
  );

  /** Selected prior Abschläge applied as monetary deductions. Empty selection stays empty. */
  const paidPartialInvoices = React.useMemo(
    () => derivePaidPartialInvoices(paidPartialDeductionCandidates, selectedDeductionInvoiceIds),
    [paidPartialDeductionCandidates, selectedDeductionInvoiceIds],
  );

  const previousPartialsSummaryTotals = React.useMemo(
    () =>
      computeFinalInvoiceTotals({
        baseNetAmount: 0,
        baseVatAmount: 0,
        paidPartials: previousPartialInvoicesSummary,
      }),
    [previousPartialInvoicesSummary],
  );

  const showPaidPartialsSummary =
    (isFinalInvoice || isPartialInvoice) && previousPartialInvoicesSummary.length > 0;

  const draftSignature = React.useMemo(
    () => JSON.stringify(lead.offer_document_draft ?? null),
    [lead.offer_document_draft]
  );

  const deductionDraftSignature = React.useMemo(() => {
    const raw = offeringId
      ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
        lead.offer_document_draft)
      : lead.offer_document_draft;
    return JSON.stringify(raw ?? null);
  }, [offeringId, lead.offerings, lead.offer_document_draft]);

  React.useEffect(() => {
    if (!isFinalInvoice && !isPartialInvoice) return;
    const raw = offeringId
      ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
        lead.offer_document_draft)
      : lead.offer_document_draft;
    const bound = parseOfferDocumentDraft(raw)?.currentIssuedInvoiceByDocType?.[documentTypeLabel] ?? null;
    if (!bound) return;
    boundIssuedInvoiceRef.current = bound;
    applyCurrentDeductionDocument();
  }, [
    isFinalInvoice,
    isPartialInvoice,
    deductionDraftSignature,
    documentTypeLabel,
    offeringId,
    lead.offerings,
    lead.offer_document_draft,
    applyCurrentDeductionDocument,
  ]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/settings/document-texts?type=${encodeURIComponent(documentTypeLabel)}`
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          introLetterParas: string[];
          closingGreeting: string;
          closingBlocks: string[];
          sendPdfEmailBodyTemplate?: string;
        };
        if (cancelled) return;
        setDocumentTexts({
          introLetterParas: Array.isArray(data.introLetterParas) ? data.introLetterParas : [],
          closingGreeting: typeof data.closingGreeting === 'string' ? data.closingGreeting : '',
          closingBlocks: Array.isArray(data.closingBlocks) ? data.closingBlocks : [],
          sendPdfEmailBodyTemplate:
            typeof data.sendPdfEmailBodyTemplate === 'string' && data.sendPdfEmailBodyTemplate.trim().length > 0
              ? data.sendPdfEmailBodyTemplate.trim()
              : getDefaultSendPdfEmailBodyTemplate(documentTypeLabel),
        });
      } catch (e) {
        if (cancelled) return;
        setDocumentTextsError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentTypeLabel]);

  React.useEffect(() => {
    if (documentTypeLabel.trim() !== 'Stornorechnung') {
      cancelledStornoLedgerIdRef.current = null;
      cancelledInvoiceReferenceRef.current = null;
      stornoDraftsAppliedForRef.current = null;
      setStornoBootstrapError(null);
      return;
    }
    const cancelId = searchParams.get('cancelInvoice')?.trim();
    if (!cancelId) {
      cancelledStornoLedgerIdRef.current = null;
      cancelledInvoiceReferenceRef.current = null;
      stornoDraftsAppliedForRef.current = null;
      setStornoBootstrapError(null);
      return;
    }
    let cancelled = false;
    setStornoBootstrapError(null);
    (async () => {
      try {
        const res = await fetch(`/api/invoices/${encodeURIComponent(cancelId)}/storno-bootstrap`, {
          credentials: 'include',
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string; ledgerSnapshot?: unknown; cancelledInvoiceDisplayId?: string | null; cancelledIssueDate?: string | null };
        if (!res.ok) throw new Error(data.error ?? 'Storno-Vorlage konnte nicht geladen werden.');
        const rawSnap = data.ledgerSnapshot;
        if (!isLedgerSnapshotV1(rawSnap)) throw new Error('Ungültige Storno-Vorlage.');
        const neg = negateLedgerSnapshot(rawSnap);
        if (cancelled) return;
        cancelledStornoLedgerIdRef.current = cancelId;
        if (stornoDraftsAppliedForRef.current !== cancelId) {
          const mergedFallback = ledgerSnapshotDraftRowsFromEditor(positions, []).map(negateOfferDraftRowForStorno);
          let nextDraftRows =
            neg.draftRows.length > 0 ? neg.draftRows : mergedFallback.length > 0 ? mergedFallback : neg.draftRows;
          const canApplyDrafts = nextDraftRows.length > 0 || positions.length > 0;
          if (canApplyDrafts) {
            nextDraftRows = nextDraftRows.map(forceStornoNegativeUnitPriceIfPositive);
            setDraftRows(nextDraftRows);
            setDiscount(neg.discount && neg.discount.value !== 0 ? neg.discount : null);
            setVatMode(neg.vat_mode);
            setAbschlagPercent(neg.abschlagPercent);
            setPartialInvoiceAmount(neg.partialInvoiceAmount ?? 0);
            setPartialInvoiceDescription(neg.partialInvoiceDescription ?? '');
            setPartialInvoiceMode(
              neg.partialInvoiceMode === 'position'
                ? 'position'
                : neg.partialInvoiceMode === 'amount'
                  ? 'amount'
                  : 'percent',
            );
            setPartialInvoiceCumulative(
              typeof neg.partialInvoiceCumulative === 'boolean' ? neg.partialInvoiceCumulative : null,
            );
            setPositionInvoiceQuantities(
              neg.positionInvoiceQuantities && typeof neg.positionInvoiceQuantities === 'object'
                ? { ...neg.positionInvoiceQuantities }
                : {},
            );
            if (neg.isFinalInvoice) {
              setDeductionCandidates(neg.paidPartialInvoices);
              setSelectedDeductionInvoiceIdsSynced(neg.paidPartialInvoices.map((p) => p.id));
              deductionInitCompleteRef.current = true;
              deductionRowsRef.current = [];
            } else {
              setDeductionCandidates([]);
              setSelectedDeductionInvoiceIdsSynced([]);
              deductionInitCompleteRef.current = false;
              deductionRowsRef.current = [];
            }
            stornoDraftsAppliedForRef.current = cancelId;
          }
        }
        const nr = String(data.cancelledInvoiceDisplayId ?? '').trim() || '—';
        const issueIso = data.cancelledIssueDate ?? null;
        cancelledInvoiceReferenceRef.current =
          nr !== '—' ? { id: nr, issueDate: issueIso ? String(issueIso).slice(0, 10) : null } : null;
        const issueDe = issueIso ? formatDateToDDMMYYYY(String(issueIso).slice(0, 10)) : '—';
        const greeting =
          (documentTexts?.introLetterParas?.[0] ?? '').trim() || 'Sehr geehrte Damen und Herren,';
        const stornoIntro = `hiermit erhalten Sie die Stornorechnung der Rechnung Nr. ${nr} vom ${issueDe}.`;
        setIntroContent(parasToTextBlocks([
          { id: newId(), text: greeting },
          { id: newId(), text: stornoIntro },
        ]));
        setClosingContent([]);
      } catch (e) {
        if (!cancelled) {
          setStornoBootstrapError(e instanceof Error ? e.message : 'Storno-Vorlage konnte nicht geladen werden.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentTypeLabel, searchParams, documentTexts?.introLetterParas, positions]);

  React.useEffect(() => {
    if (documentTypeLabel.trim() !== 'Gutschrift') {
      creditedInvoiceLedgerIdRef.current = null;
      creditedInvoiceReferenceRef.current = null;
      gutschriftDraftsAppliedForRef.current = null;
      return;
    }
    const creditId = searchParams.get('creditInvoice')?.trim();
    if (!creditId) {
      creditedInvoiceLedgerIdRef.current = null;
      creditedInvoiceReferenceRef.current = null;
      gutschriftDraftsAppliedForRef.current = null;
      return;
    }
    let cancelled = false;
    setStornoBootstrapError(null);
    (async () => {
      try {
        const res = await fetch(`/api/invoices/${encodeURIComponent(creditId)}/credit-bootstrap`, {
          credentials: 'include',
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          ledgerSnapshot?: unknown;
          creditedInvoiceDisplayId?: string | null;
          creditedIssueDate?: string | null;
        };
        if (!res.ok) throw new Error(data.error ?? 'Gutschrift-Vorlage konnte nicht geladen werden.');
        if (cancelled) return;
        const rawSnap = data.ledgerSnapshot;
        if (!isLedgerSnapshotV1(rawSnap)) throw new Error('Ungültige Gutschrift-Vorlage.');
        creditedInvoiceLedgerIdRef.current = creditId;
        if (gutschriftDraftsAppliedForRef.current !== creditId) {
          const neg = negateLedgerSnapshot(rawSnap);
          const mergedFallback = ledgerSnapshotDraftRowsFromEditor(positions, []).map(negateOfferDraftRowForStorno);
          let nextDraftRows =
            neg.draftRows.length > 0 ? neg.draftRows : mergedFallback.length > 0 ? mergedFallback : neg.draftRows;
          const canApplyDrafts = nextDraftRows.length > 0 || positions.length > 0;
          if (canApplyDrafts) {
            nextDraftRows = nextDraftRows.map(forceStornoNegativeUnitPriceIfPositive);
            setDraftRows(nextDraftRows);
            setDiscount(neg.discount && neg.discount.value !== 0 ? neg.discount : null);
            setVatMode(neg.vat_mode);
            setAbschlagPercent(neg.abschlagPercent);
            setPartialInvoiceAmount(neg.partialInvoiceAmount ?? 0);
            setPartialInvoiceDescription(neg.partialInvoiceDescription ?? '');
            setPartialInvoiceMode(
              neg.partialInvoiceMode === 'position'
                ? 'position'
                : neg.partialInvoiceMode === 'amount'
                  ? 'amount'
                  : 'percent',
            );
            setPartialInvoiceCumulative(
              typeof neg.partialInvoiceCumulative === 'boolean' ? neg.partialInvoiceCumulative : null,
            );
            setPositionInvoiceQuantities(
              neg.positionInvoiceQuantities && typeof neg.positionInvoiceQuantities === 'object'
                ? { ...neg.positionInvoiceQuantities }
                : {},
            );
            gutschriftDraftsAppliedForRef.current = creditId;
          }
        }
        const nr = String(data.creditedInvoiceDisplayId ?? '').trim() || '—';
        const issueIso = data.creditedIssueDate ?? null;
        creditedInvoiceReferenceRef.current =
          nr !== '—' ? { id: nr, issueDate: issueIso ? String(issueIso).slice(0, 10) : null } : null;
        const issueDe = issueIso ? formatDateToDDMMYYYY(String(issueIso).slice(0, 10)) : '—';
        const greeting =
          (documentTexts?.introLetterParas?.[0] ?? '').trim() || 'Sehr geehrte Damen und Herren,';
        const creditIntro = `hiermit erhalten Sie die Gutschrift zu Rechnung Nr. ${nr} vom ${issueDe}.`;
        setIntroContent(parasToTextBlocks([
          { id: newId(), text: greeting },
          { id: newId(), text: creditIntro },
        ]));
        setClosingContent([]);
      } catch (e) {
        if (!cancelled) {
          setStornoBootstrapError(e instanceof Error ? e.message : 'Gutschrift-Vorlage konnte nicht geladen werden.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentTypeLabel, searchParams, documentTexts?.introLetterParas, positions]);

  React.useEffect(() => {
    if (!isWorkReportInvoice || !offeringId) {
      workReportInvoiceIdsRef.current = null;
      workReportDraftsAppliedForRef.current = null;
      return;
    }
    const key = `${offeringId}:${workReportInvoiceIds.join(',')}`;
    if (workReportDraftsAppliedForRef.current === key) return;
    let cancelled = false;
    setStornoBootstrapError(null);
    (async () => {
      try {
        const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/work-reports/billable`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportIds: workReportInvoiceIds }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          reportIds?: string[];
          lines?: Array<{
            offering_position_id: string | null;
            material_id: string | null;
            leistung_id: string | null;
            item_name: string;
            description: string;
            unit: string;
            quantity: number;
            unit_price_vk: number;
            unit_cost_ek: number;
            labor_minutes: number;
          }>;
        };
        if (!res.ok) throw new Error(data.error ?? 'Arbeitsberichte konnten nicht geladen werden.');
        if (cancelled) return;
        const rows: OfferDraftRow[] = (data.lines ?? []).map((line) => ({
          id: newId(),
          kind: 'freePosition',
          leadPositionId: (line.offering_position_id as PositionRowId | null) ?? null,
          sourceType: line.material_id ? 'material' : line.leistung_id ? 'leistung' : 'custom',
          quantity: line.quantity,
          unit: line.unit,
          item_name: line.item_name,
          description: line.description,
          unit_price_vk: line.unit_price_vk,
          unit_cost_ek: line.unit_cost_ek,
          labor_minutes: line.labor_minutes,
        }));
        if (!rows.length) {
          throw new Error('Die ausgewählten Arbeitsberichte enthalten keine noch offenen Mengen.');
        }
        setDraftRows(rows);
        setPositionInvoiceQuantities({});
        workReportInvoiceIdsRef.current = data.reportIds ?? workReportInvoiceIds;
        workReportDraftsAppliedForRef.current = key;
      } catch (e) {
        if (!cancelled) {
          setStornoBootstrapError(e instanceof Error ? e.message : 'Arbeitsberichte konnten nicht geladen werden.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isWorkReportInvoice, offeringId, workReportInvoiceIds]);

  React.useEffect(() => {
    if ((!isFinalInvoice && !isPartialInvoice) || !rechnungenEnabled) {
      setSelectedDeductionInvoiceIdsSynced([]);
      setDeductionCandidates([]);
      setPaidPartialsError(null);
      deductionInitKeyRef.current = null;
      deductionInitCompleteRef.current = false;
      deductionRowsRef.current = [];
      liveDeductionReferenceRef.current = null;
      boundIssuedInvoiceRef.current = null;
      currentDeductionDocumentRef.current = { ids: [], referenceIds: [] };
      return;
    }

    const initKey = `${editorSessionKey}:${deductionDraftSignature}`;
    if (deductionInitKeyRef.current === initKey) return;

    const loadGen = ++deductionLoadGenRef.current;
    let cancelled = false;
    (async () => {
      try {
        setPaidPartialsError(null);
        const fallbackVatRatePercent = getOfferVatOption(vatMode).ratePercent;
        const draftRaw = offeringId
          ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
            lead.offer_document_draft)
          : lead.offer_document_draft;
        const parsed = parseOfferDocumentDraft(draftRaw);
        // Honor explicit draft selection, including an empty list ("Keine").
        const rawExplicit = parsed?.selectedDeductionInvoiceIds;
        const hasExplicitSelection =
          (isFinalInvoice || isPartialInvoice) && Array.isArray(rawExplicit);

        // Prefer offering-/lead-scoped Abschläge — org-wide lists are paginated and miss recent ones.
        const orgId = lead.organization?.id;
        const contactId = lead.contact_id;
        const params = new URLSearchParams({
          type: 'outgoing',
          status: 'all',
          document_type: 'partial_invoice',
          per_page: '100',
          sort: 'issue_date',
          dir: 'asc',
        });
        if (offeringId) {
          params.set('offering_id', offeringId);
        } else if (lead.id) {
          params.set('lead_id', String(lead.id));
        } else if (orgId) {
          params.set('organization_id', String(orgId));
        } else if (contactId != null) {
          params.set('contact_id', String(contactId));
        }

        const res = await fetch(`/api/invoices?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? 'Abschlagsrechnungen konnten nicht geladen werden.');
        }
        const json = (await res.json()) as { items?: DeductionInvoiceRow[] };
        if (cancelled || loadGen !== deductionLoadGenRef.current) return;

        const rows = Array.isArray(json.items) ? json.items : [];
        if (rows.length === 0) {
          // Keep any candidates already seeded from the offering billing ledger.
          deductionInitKeyRef.current = initKey;
          deductionInitCompleteRef.current = true;
          return;
        }
        const candidates = rows
          .filter(isSelectableDeductionRow)
          .map((row) => mapInvoiceRowToDeductionCandidate(row, fallbackVatRatePercent));
        // Die Auswahl im Picker darf Abschläge anderer Angebote desselben Kunden enthalten; die
        // Liste hier ist aber auf das aktuelle Angebot eingegrenzt. Explizit gewählte IDs bleiben
        // deshalb erhalten, solange sie als Kandidat bekannt sind — sonst springen sie nach dem
        // Speichern des Entwurfs sofort wieder aus der Tabelle.
        const nextSelectedIds = hasExplicitSelection
          ? (() => {
              const known = new Set<string>([
                ...candidates.map((c) => c.id),
                ...deductionCandidatesRef.current.map((c) => c.id),
              ]);
              return (parsed?.selectedDeductionInvoiceIds ?? []).filter((id) => known.has(String(id)));
            })()
          : resolveDeductionSelectionIds(
              rows,
              undefined,
              false,
              offeringId,
              currentDeductionDocumentRef.current,
            );

        if (cancelled || loadGen !== deductionLoadGenRef.current) return;

        deductionRowsRef.current = rows;
        setDeductionCandidates((prev) => {
          const byId = new Map(prev.map((c) => [c.id, c]));
          for (const c of candidates) byId.set(c.id, c);
          return Array.from(byId.values());
        });
        setSelectedDeductionInvoiceIdsSynced((prev) => {
          if (hasExplicitSelection) return nextSelectedIds;
          if (nextSelectedIds.length === 0) return prev;
          const merged = new Set([...prev, ...nextSelectedIds]);
          return Array.from(merged);
        });
        deductionInitKeyRef.current = initKey;
        deductionInitCompleteRef.current = true;
      } catch (e) {
        if (cancelled || loadGen !== deductionLoadGenRef.current) return;
        // Do not clear ledger-seeded candidates on fetch errors.
        setPaidPartialsError(e instanceof Error ? e.message : 'Abschlagsrechnungen konnten nicht geladen werden.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isFinalInvoice,
    isPartialInvoice,
    editorSessionKey,
    deductionDraftSignature,
    lead.id,
    lead.contact_id,
    lead.organization?.id,
    lead.offerings,
    lead.offer_document_draft,
    offeringId,
    vatMode,
    rechnungenEnabled,
  ]);

  React.useEffect(() => {
    if ((!isFinalInvoice && !isPartialInvoice) || deductionRowsRef.current.length === 0) return;
    const fallbackVatRatePercent = getOfferVatOption(vatMode).ratePercent;
    const fromRows = deductionRowsRef.current
      .filter(isSelectableDeductionRow)
      .map((row) => mapInvoiceRowToDeductionCandidate(row, fallbackVatRatePercent));
    if (fromRows.length === 0) return;
    setDeductionCandidates((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      for (const c of fromRows) byId.set(c.id, c);
      return Array.from(byId.values());
    });
  }, [isFinalInvoice, isPartialInvoice, vatMode]);

  React.useEffect(() => {
    if (!showPositionBillingColumns || !offeringId || !rechnungenEnabled) {
      setAggregatedInvoicedQuantities({});
      setPositionPartialInvoiceIds(new Set());
      setHeldPriorRetentions([]);
      setBillingLedgerError(null);
      setBillingLedgerLoadedKey(null);
      billingLedgerInitRef.current = null;
      return;
    }
    const initKey = `${editorSessionKey}:${offeringId}`;
    let cancelled = false;
    setBillingLedgerLoadedKey(null);
    (async () => {
      try {
        setBillingLedgerError(null);
        const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/position-billing-ledger`, {
          credentials: 'include',
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? 'Abrechnungsstand konnte nicht geladen werden.');
        }
        const json = (await res.json()) as {
          invoicedQuantities?: Record<string, number>;
          invoices?: Array<{
            id: string;
            billingMode?: string;
            document_type?: string;
            status?: string | null;
            reference_id?: string | null;
            invoice_number?: string | null;
            issue_date?: string | null;
            net_amount?: number | null;
            tax_amount?: number | null;
            gross_amount?: number | null;
            retention_gross?: number | null;
            retention_status?: string | null;
          }>;
        };
        if (cancelled) return;
        setAggregatedInvoicedQuantities(json.invoicedQuantities ?? {});
        const ledgerInvoices = Array.isArray(json.invoices) ? json.invoices : [];
        const positionIds = new Set(
          ledgerInvoices
            .filter((inv) => inv.document_type === 'partial_invoice' && inv.billingMode === 'position')
            .map((inv) => String(inv.id)),
        );
        setPositionPartialInvoiceIds(positionIds);

        const HELD_RETENTION_DOC_TYPES = new Set(['invoice', 'partial_invoice']);
        const HELD_RETENTION_STATUSES = new Set(['open', 'paid', 'overdue']);
        setHeldPriorRetentions(
          ledgerInvoices
            .filter((inv) => {
              const docType = String(inv.document_type ?? '').trim();
              const status = String(inv.status ?? '').trim().toLowerCase();
              const retentionStatus = String(inv.retention_status ?? '').trim().toLowerCase();
              const retentionGross = Math.max(0, Number(inv.retention_gross) || 0);
              return (
                HELD_RETENTION_DOC_TYPES.has(docType) &&
                HELD_RETENTION_STATUSES.has(status) &&
                retentionStatus === 'held' &&
                retentionGross > 0
              );
            })
            .map((inv) => ({
              id: String(inv.id),
              retentionGross:
                Math.round(Math.max(0, Number(inv.retention_gross) || 0) * 100) / 100,
              referenceId: String(inv.invoice_number || inv.reference_id || inv.id),
            })),
        );

        // Seed Abschlag-Summary from the same offering ledger that powers "Bereits abg."
        if (isPartialInvoice || isFinalInvoice) {
          const fallbackVatRatePercent = getOfferVatOption(vatMode).ratePercent;
          const ledgerRows: DeductionInvoiceRow[] = ledgerInvoices.map((inv) => ({
            id: String(inv.id),
            document_type: String(inv.document_type ?? ''),
            status: inv.status ?? null,
            reference_id: inv.reference_id ?? null,
            invoice_number: inv.invoice_number ?? null,
            issue_date: inv.issue_date ?? null,
            net_amount: inv.net_amount ?? null,
            tax_amount: inv.tax_amount ?? null,
            gross_amount: inv.gross_amount ?? null,
            retention_gross: inv.retention_gross ?? null,
            retention_status: inv.retention_status ?? null,
            offering_id: offeringId,
          }));
          const ledgerCandidates = ledgerRows
            .filter(isSelectableDeductionRow)
            .map((row) => mapInvoiceRowToDeductionCandidate(row, fallbackVatRatePercent));
          if (ledgerCandidates.length > 0) {
            setDeductionCandidates((prev) => {
              const byId = new Map(prev.map((c) => [c.id, c]));
              for (const c of ledgerCandidates) byId.set(c.id, c);
              return Array.from(byId.values());
            });
            setSelectedDeductionInvoiceIdsSynced((prev) => {
              const parsedSelection = parseOfferDocumentDraft(
                offeringId
                  ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
                    lead.offer_document_draft)
                  : lead.offer_document_draft,
              )?.selectedDeductionInvoiceIds;
              if (Array.isArray(parsedSelection)) return prev;
              if (prev.length > 0) return prev;
              return excludeCurrentDocumentDeductions(
                ledgerCandidates,
                currentDeductionDocumentRef.current,
              ).map((c) => c.id);
            });
            deductionInitCompleteRef.current = true;
          }
        }

        billingLedgerInitRef.current = initKey;
        setBillingLedgerLoadedKey(initKey);
      } catch (e) {
        if (cancelled) return;
        setBillingLedgerLoadedKey(null);
        setBillingLedgerError(e instanceof Error ? e.message : 'Abrechnungsstand konnte nicht geladen werden.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPositionBillingColumns, offeringId, editorSessionKey, isPartialInvoice, isFinalInvoice, vatMode, rechnungenEnabled]);

  React.useEffect(() => {
    const draftRaw = offeringId
      ? (lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
        lead.offer_document_draft)
      : lead.offer_document_draft;
    const parsed = parseOfferDocumentDraft(draftRaw);
    const billing = resolveDocumentBilling(lead, documentTypeLabel, parsed);
    const recipientDefaults = billing.layout;
    const draftDocumentTypeRaw = parsed?.documentType;
    const draftDocumentType = (typeof draftDocumentTypeRaw === 'string' && draftDocumentTypeRaw.trim().length > 0)
      ? draftDocumentTypeRaw
      : 'Angebot';
    const draftMatchesSelected = draftDocumentType === documentTypeLabel;
    /** Overview-Storno: Zeilen/Intro kommen aus Bootstrap — nicht aus dem gespeicherten Angebots-Entwurf überschreiben. */
    const stornoSnapshotFromOverview =
      documentTypeLabel.trim() === 'Stornorechnung' && Boolean(searchParams.get('cancelInvoice')?.trim());
    const gutschriftSnapshotFromOverview =
      documentTypeLabel.trim() === 'Gutschrift' && Boolean(searchParams.get('creditInvoice')?.trim());
    const workReportInvoiceFromOverview =
      documentTypeLabel.trim() === 'Rechnung' && Boolean(searchParams.get('workReports')?.trim());
    const skipDraftOverwrite =
      stornoSnapshotFromOverview || gutschriftSnapshotFromOverview || workReportInvoiceFromOverview;
    /** Manuelles Storno: Positionspreise aus Angebots-/Rechnungs-Entwurf sollen Storno-typisch negativ sein. */
    const stornoManualDocument =
      (documentTypeLabel.trim() === 'Stornorechnung' && !searchParams.get('cancelInvoice')?.trim()) ||
      (documentTypeLabel.trim() === 'Gutschrift' && !searchParams.get('creditInvoice')?.trim());

    setOfferTitleRest(parsed?.offerTitleRest ?? ((lead.lead_title ?? '').trim() || '—'));
    const todayLabel = formatDateToDDMMYYYY(todayYMD());
    if (issueDateHydratedSessionRef.current !== editorSessionKey) {
      issueDateHydratedSessionRef.current = editorSessionKey;
      setIssueDateEdit(todayLabel);
    }
    setRecipientNameLinesEdit(recipientDefaults.nameLines);
    setRecipientStreetLineEdit(recipientDefaults.streetLine);
    setRecipientPlzOrtLineEdit(recipientDefaults.plzOrtLine);
    setLeistungsortEdit(parsed?.leistungsort ?? formatLeistungsort(lead));
    {
      const storedLz = parseOfferLeistungszeitraum(parsed?.leistungszeitraum);
      if (storedLz) {
        setLeistungszeitraumEdit(storedLz);
      } else if (documentShowsLeistungszeitraum(documentTypeLabel)) {
        setLeistungszeitraumEdit(
          resolveInvoiceLeistungszeitraum({ issueDate: parsed?.issueDate ?? todayLabel }),
        );
      } else {
        setLeistungszeitraumEdit(null);
      }
    }
    setLieferdatumEdit(parsed?.lieferdatum ?? null);
    setHideDocumentTypeInTitle(parsed?.hideDocumentTypeInTitle === true);
    setHideLeistungszeitraum(
      documentLocksLeistungszeitraum(documentTypeLabel) ? false : parsed?.hideLeistungszeitraum === true,
    );
    setHideLieferdatum(parsed?.hideLieferdatum === true);
    if (!skipDraftOverwrite) {
      const introParasBase = resolveIntroLetterParas({
        isWorkReport,
        draftIntro:
          draftMatchesSelected && parsed?.introLetterParas !== undefined
            ? parsed.introLetterParas
            : undefined,
        documentTexts,
      });
      const introParasNormalized = normalizeDefaultSentences(introParasBase, documentTypeLabel);
      const baseAfterTotals =
        documentTypeLabel.trim() === 'Stornorechnung' || documentTypeLabel.trim() === 'Gutschrift'
          ? draftMatchesSelected && parsed?.afterTotalsParas?.length
            ? parsed.afterTotalsParas
            : []
          : isWorkReport
            ? draftMatchesSelected && parsed?.afterTotalsParas?.length
              ? parsed.afterTotalsParas
              : defaultWorkReportAfterTotalsParas()
            : parsed?.afterTotalsParas?.length
              ? parsed.afterTotalsParas
              : defaultAfterTotalsParas({
                  mode: company?.payment_terms_mode,
                  days: company?.payment_terms_days ?? 7,
                  context: paymentTermsContextFromDocumentType(documentTypeLabel),
                  skontoPercent: company?.skonto_percent,
                  skontoDays: company?.skonto_days,
                });
      const sanitizedAfterTotals = stripStornoFixedAfterTotals(
        stripSignatureRequestSentence(baseAfterTotals, !shouldShowSignature),
      );

      if (paymentTermsCustomizedSessionRef.current !== editorSessionKey) {
        paymentTermsCustomizedSessionRef.current = editorSessionKey;
        paymentTermsCustomizedRef.current = parsed?.paymentTermsCustomized === true;
      } else if (parsed?.paymentTermsCustomized === true) {
        paymentTermsCustomizedRef.current = true;
      }
      setPaymentTermsCustomized(paymentTermsCustomizedRef.current);

      // Firmen-/Org-Standard nur auf Platzhalter oder die aktuelle Firmenzeile anwenden.
      // Nutzer-Edits (7 → 14, 14 → 7, 7 → 30, …) bleiben stehen; Rahmenvertrag darf die Firmenzeile ersetzen.
      const orgForPayment = lead.organization;
      const isOrgContractActive =
        orgForPayment?.has_framework_contract === true &&
        (!orgForPayment.framework_valid_until ||
          new Date(orgForPayment.framework_valid_until) >= new Date());
      let afterTotalsToSet = sanitizedAfterTotals;
      const paymentContext = paymentTermsContextFromDocumentType(documentTypeLabel);
      const companyLine = company
        ? formatPaymentTermsSentence({
            mode: company.payment_terms_mode,
            days: company.payment_terms_days,
            context: paymentContext,
            skontoPercent: company.skonto_percent,
            skontoDays: company.skonto_days,
          })
        : '';
      const canApplyDefaultPaymentTerms =
        !paymentTermsCustomizedRef.current &&
        documentTypeLabel.trim() !== 'Stornorechnung' &&
        documentTypeLabel.trim() !== 'Gutschrift' &&
        !isWorkReport &&
        isReplaceableDefaultPaymentTermsSentence(
          afterTotalsToSet[0]?.text ?? '',
          paymentContext,
          companyLine,
        );
      if (
        canApplyDefaultPaymentTerms &&
        isOrgContractActive &&
        orgForPayment?.payment_terms_days
      ) {
        const orgPaymentLine = formatPaymentTermsSentence({
          mode: 'net_days',
          days: normalizePaymentTermsDays(orgForPayment.payment_terms_days),
          context: paymentContext,
          skontoPercent: orgForPayment.skonto_percent,
          skontoDays: orgForPayment.skonto_days,
        });
        afterTotalsToSet =
          afterTotalsToSet.length > 0
            ? [{ ...afterTotalsToSet[0], text: orgPaymentLine }, ...afterTotalsToSet.slice(1)]
            : afterTotalsToSet;
      } else if (canApplyDefaultPaymentTerms && companyLine && afterTotalsToSet[0]) {
        afterTotalsToSet = [
          { ...afterTotalsToSet[0], text: companyLine },
          ...afterTotalsToSet.slice(1),
        ];
      }
      const closingParasNormalized = normalizeDefaultSentences(afterTotalsToSet, documentTypeLabel);
      const closingLegacyBlocks: OfferIntroBlock[] =
        draftMatchesSelected && parsed?.closingBlocks !== undefined
          ? hydrateIntroBlocksFromDraft(parsed.closingBlocks)
          : documentTexts?.closingBlocks?.length
            ? withoutDuplicateClosingTexts(
                documentTexts.closingBlocks,
                closingParasNormalized.map((p) => p.text),
              ).map((content) => ({
                id: newId(),
                type: 'text' as const,
                content,
              }))
            : [];
      const normalizeContentSentences = (blocks: OfferDocumentContentBlock[]): OfferDocumentContentBlock[] => {
        const out: OfferDocumentContentBlock[] = [];
        for (const block of blocks) {
          if (block.type !== 'text') {
            out.push(block);
            continue;
          }
          for (const para of normalizeDefaultSentences([{ id: block.id, text: block.content }], documentTypeLabel)) {
            out.push({ id: para.id, type: 'text', content: para.text });
          }
        }
        return out;
      };
      const hydratedContent = clampInlineImagesAcrossZones(
        normalizeContentSentences(
          normalizeOfferDocumentContent({
            content: draftMatchesSelected ? parsed?.introContent : undefined,
            paras: introParasNormalized,
            blocks: parsed?.introBlocks,
          }),
        ),
        normalizeContentSentences(
          normalizeOfferDocumentContent({
            content: draftMatchesSelected ? parsed?.closingContent : undefined,
            paras: closingParasNormalized,
            blocks: closingLegacyBlocks,
          }),
        ),
      );
      setIntroContent(hydratedContent.intro);
      setClosingContent(hydratedContent.closing);
      paymentTermsTextIdRef.current =
        hydratedContent.closing.find((block) => block.type === 'text')?.id ?? null;
    }
    setClosingGreeting(
      isWorkReport
        ? ''
        : draftMatchesSelected && parsed?.closingGreeting !== undefined
          ? parsed.closingGreeting
          : documentTexts?.closingGreeting?.trim().length
            ? documentTexts.closingGreeting
            : 'Mit freundlichen Grüßen',
    );
    setSignatureHint(parsed?.signatureHint ?? 'Ort, Datum, Unterschrift Auftraggeber');
    if (!skipDraftOverwrite) {
      let incomingDraftRows = Array.isArray(parsed?.draftRows)
        ? parsed.draftRows.filter((r) => r.kind !== 'apiPosition')
        : [];
      if (stornoManualDocument && incomingDraftRows.length > 0) {
        const economic = incomingDraftRows.filter(
          (r) => r.kind !== 'title' && r.kind !== 'text' && r.kind !== 'image',
        );
        const hasNegative = economic.some((r) => r.unit_price_vk < 0);
        if (!hasNegative) {
          incomingDraftRows = incomingDraftRows.map((r) => negateOfferDraftRowForStorno(r));
        }
      }
      if (hydratedDraftRowsSessionRef.current !== editorSessionKey) {
        hydratedDraftRowsSessionRef.current = editorSessionKey;
        setDraftRows((prev) =>
          JSON.stringify(prev) === JSON.stringify(incomingDraftRows) ? prev : incomingDraftRows,
        );
      }
    }
    const incomingAttachments = Array.isArray(parsed?.attachments) ? parsed.attachments : [];
    setAttachments((prev) =>
      JSON.stringify(prev) === JSON.stringify(incomingAttachments) ? prev : incomingAttachments
    );
    const incomingExcluded = parseExcludedStandardAttachments(parsed?.excluded_standard_attachments);
    setExcludedStandardAttachments((prev) =>
      JSON.stringify(prev) === JSON.stringify(incomingExcluded) ? prev : incomingExcluded
    );
    if (!skipDraftOverwrite) {
      const parsedDiscount = parsed?.discount;
      const org = lead.organization;
      const isActiveContract =
        org?.has_framework_contract === true &&
        (!org.framework_valid_until || new Date(org.framework_valid_until) >= new Date());
      const orgDiscount =
        isActiveContract && org?.discount_percent && org.discount_percent > 0
          ? { type: 'percent' as const, value: org.discount_percent }
          : null;
      setDiscount(
        parsedDiscount && parsedDiscount.value > 0
          ? parsedDiscount
          : orgDiscount
      );
      const showConstructionDeductions =
        documentTypeLabel.trim() === 'Abschlagsrechnung' ||
        documentTypeLabel.trim() === 'Schlussrechnung' ||
        documentTypeLabel.trim() === 'Rechnung';
      if (showConstructionDeductions) {
        const parsedLevy = normalizeSiteLevy(parsed?.siteLevy);
        const parsedRet = normalizeRetention(parsed?.retention);
        const customized = parsed?.constructionDeductionsCustomized === true;
        setConstructionDeductionsCustomized(customized);
        const orgLevyPct = Number(org?.site_levy_percent);
        // Schluss: kein stiller Org-Einbehalt — offene Vor-Einbehalte werden addiert, nicht erneut abgezogen.
        const orgRetPct =
          documentTypeLabel.trim() === 'Abschlagsrechnung'
            ? Number(org?.retention_percent_partial)
            : NaN;
        if (customized) {
          setSiteLevy(parsedLevy);
          setRetention(parsedRet);
        } else {
          setSiteLevy(
            parsedLevy ??
              (Number.isFinite(orgLevyPct) && orgLevyPct > 0
                ? { kind: 'site_levy', label: DEFAULT_SITE_LEVY_LABEL, percent: orgLevyPct, amountNet: null }
                : null),
          );
          setRetention(
            documentTypeLabel.trim() === 'Schlussrechnung'
              ? null
              : parsedRet ??
                  (Number.isFinite(orgRetPct) && orgRetPct > 0
                    ? { kind: 'performance', percent: orgRetPct, amountGross: null }
                    : null),
          );
        }
      } else {
        setSiteLevy(null);
        setRetention(null);
        setConstructionDeductionsCustomized(false);
      }
      setAbschlagPercent(roundAbschlagPercent(Number(parsed?.abschlagPercent ?? 50) || 0));
      setPartialInvoiceAmount(
        Math.max(0, Number(parsed?.partialInvoiceAmount ?? parsed?.amountAlreadyInvoiced ?? 0) || 0),
      );
      setPartialInvoiceDescription(typeof parsed?.partialInvoiceDescription === 'string' ? parsed.partialInvoiceDescription : '');
      setPartialInvoiceLineName(typeof parsed?.partialInvoiceLineName === 'string' ? parsed.partialInvoiceLineName : '');
      setPartialInvoiceAmountIsGross(parsed?.partialInvoiceAmountIsGross === true);
      setOrderTotalNet(typeof parsed?.orderTotalNet === 'number' ? parsed.orderTotalNet : null);
      setPartialInvoiceMode(
        parsed?.partialInvoiceMode === 'position'
          ? 'position'
          : parsed?.partialInvoiceMode === 'amount'
            ? 'amount'
            : 'percent',
      );
      setPartialInvoiceCumulative(
        typeof parsed?.partialInvoiceCumulative === 'boolean' ? parsed.partialInvoiceCumulative : null,
      );
      setDocumentPaymentTerms(
        parsed?.paymentTerms ?? defaultPaymentTermsFromLead(company, lead.organization),
      );
      setPositionInvoiceQuantities(
        parsed?.positionInvoiceQuantities && typeof parsed.positionInvoiceQuantities === 'object'
          ? { ...parsed.positionInvoiceQuantities }
          : {},
      );
      setVatMode(billing.vatMode);
      setHideTotals(
        isWorkReport ? true : draftMatchesSelected ? (parsed?.hideTotals ?? false) : false,
      );
      setLaborCostShare(draftMatchesSelected ? parseLaborCostShareMode(parsed?.laborCostShare) : 'auto');
      setHideArticleNumber(
        isWorkReport && draftMatchesSelected ? (parsed?.hideArticleNumber ?? false) : false,
      );
      setHidePositionDescriptions(
        draftMatchesSelected ? (parsed?.hidePositionDescriptions ?? false) : false,
      );
      setPositionDetailLevel(
        draftMatchesSelected && parsed?.positionDetailLevel === 'titles' ? 'titles' : 'full',
      );
      const fromCustomer = parsed?.calculationHourlyRateFromCustomer === true;
      const orgRate = Math.max(0, lead.organization?.hourly_rate ?? 0);
      setCalculationHourlyRateFromCustomer(fromCustomer);
      setCalculationLaborSeparately(parsed?.calculationLaborSeparately === true);
      setCalculationLaborRowLabel(normalizeTitleLaborRowLabel(parsed?.calculationLaborRowLabel));
      setCalculationLaborRowNames(normalizeTitleLaborRowNames(parsed?.calculationLaborRowNames));
      setCalculationDismissedHints(parsed?.calculationDismissedHints ?? []);
      setCalculationHourlyRate(
        fromCustomer && orgRate > 0 ? orgRate : Math.max(0, parsed?.calculationHourlyRate ?? 0),
      );
      setCalculationRealLaborCostRate(
        draftMatchesSelected ? Math.max(0, parsed?.calculationRealLaborCostRate ?? 0) : 0,
      );
      const selectedOffering = offeringId
        ? lead.offerings?.find((item) => item.id === offeringId)
        : undefined;
      if (
        selectedOffering?.metal_quote_source === 'manual' ||
        selectedOffering?.metal_quote_source === 'westmetall' ||
        selectedOffering?.metal_quote_source === 'sonepar'
      ) {
        setCalculationCopperQuoteEurPer100kg(selectedOffering.copper_quote_eur_per_100kg ?? null);
        setCalculationAluminumQuoteEurPer100kg(selectedOffering.aluminum_quote_eur_per_100kg ?? null);
        setCalculationMetalQuoteSource(selectedOffering.metal_quote_source);
        setCalculationMetalQuoteDate(selectedOffering.metal_quote_date ?? null);
        setCalculationMetalProcurementCostPercent(
          selectedOffering.metal_procurement_cost_percent ??
            instanceMetalQuotes.procurementCostPercent,
        );
      } else if (instanceMetalQuotes.loaded) {
        setCalculationCopperQuoteEurPer100kg(
          parsed?.calculationCopperQuoteEurPer100kg !== undefined
            ? parsed.calculationCopperQuoteEurPer100kg
            : instanceMetalQuotes.copper,
        );
        setCalculationAluminumQuoteEurPer100kg(
          parsed?.calculationAluminumQuoteEurPer100kg !== undefined
            ? parsed.calculationAluminumQuoteEurPer100kg
            : instanceMetalQuotes.aluminum,
        );
        setCalculationMetalQuoteSource(
          parsed?.calculationMetalQuoteSource ?? instanceMetalQuotes.source,
        );
        setCalculationMetalQuoteDate(
          parsed?.calculationMetalQuoteDate !== undefined
            ? parsed.calculationMetalQuoteDate
            : instanceMetalQuotes.quoteDate,
        );
        setCalculationMetalProcurementCostPercent(
          parsed?.calculationMetalProcurementCostPercent ??
            instanceMetalQuotes.procurementCostPercent,
        );
      }
    }
  }, [
    lead.id,
    lead.offerings,
    offeringId,
    draftSignature,
    documentTexts,
    documentTypeLabel,
    isWorkReport,
    searchParams,
    shouldShowSignature,
    editorSessionKey,
    instanceMetalQuotes,
    lead.organization?.invoice_recipient_organization_id,
    lead.organization?.invoice_recipient_contact_id,
    lead.organization?.tax_treatment,
    lead.contact?.invoice_recipient_organization_id,
    lead.contact?.invoice_recipient_contact_id,
    lead.contact?.tax_treatment,
  ]);

  React.useEffect(() => {
    setOrderNumberEdit((lead.baustelle?.bestellnummer ?? '').trim());
  }, [lead.baustelle?.bestellnummer]);

  React.useEffect(() => {
    if (documentTypeLabel.trim() !== 'Stornorechnung' && documentTypeLabel.trim() !== 'Gutschrift') return;
    if (searchParams.get('cancelInvoice')?.trim() || searchParams.get('creditInvoice')?.trim()) return;
    if (positions.length === 0) return;
    setDraftRows((prev) => {
      if (prev.length > 0) return prev;
      return ledgerSnapshotDraftRowsFromEditor(positions, []).map((r) => negateOfferDraftRowForStorno(r));
    });
  }, [documentTypeLabel, searchParams, positions]);

  type DraftSnap = {
    offerTitleRest: string;
    hideDocumentTypeInTitle: boolean;
    issueDate: string;
    recipientNameLines: string[];
    recipientStreetLine: string;
    recipientPlzOrtLine: string;
    leistungsort: string;
    leistungszeitraum: OfferLeistungszeitraum | null;
    lieferdatum: OfferLeistungszeitraum | null;
    hideLeistungszeitraum: boolean;
    hideLieferdatum: boolean;
    introLetterParas: OfferDocumentEditablePara[];
    afterTotalsParas: OfferDocumentEditablePara[];
    paymentTermsCustomized: boolean;
    closingGreeting: string;
    signatureHint: string;
    introBlocks: OfferIntroBlock[];
    closingBlocks: OfferIntroBlock[];
    introContent: OfferDocumentContentBlock[];
    closingContent: OfferDocumentContentBlock[];
    draftRows: OfferDraftRow[];
    attachments: OfferAttachment[];
    excludedStandardAttachments: LegalAttachmentKind[];
    discount: OfferDiscount | null;
    siteLevy: InvoiceSiteLevy | null;
    retention: InvoiceRetention | null;
    constructionDeductionsCustomized: boolean;
    abschlagPercent: number;
    partialInvoiceAmount: number;
    partialInvoiceDescription: string;
    partialInvoiceLineName: string;
    partialInvoiceAmountIsGross: boolean;
    partialInvoiceMode: PartialInvoiceMode;
    partialInvoiceCumulative: boolean | null;
    orderTotalNet: number | null;
    documentPaymentTerms: OfferDocumentPaymentTerms;
    positionInvoiceQuantities: Record<string, number>;
    vatMode: OfferVatMode;
    hideTotals: boolean;
    laborCostShare: LaborCostShareMode;
    hideArticleNumber: boolean;
    hidePositionDescriptions: boolean;
    positionDetailLevel: 'full' | 'titles';
    calculationHourlyRate: number;
    calculationHourlyRateFromCustomer: boolean;
    calculationLaborSeparately: boolean;
    calculationLaborRowLabel: string;
    calculationLaborRowNames: Record<string, string>;
    calculationRealLaborCostRate: number;
    calculationDismissedHints: string[];
    calculationCopperQuoteEurPer100kg: number | null;
    calculationAluminumQuoteEurPer100kg: number | null;
    calculationMetalQuoteSource: 'manual' | 'westmetall' | 'sonepar';
    calculationMetalQuoteDate: string | null;
    calculationMetalProcurementCostPercent: number;
    /** Vor dem Laden der Instanz-Notierungen dürfen die Felder nicht als „bewusst leer“ gespeichert werden. */
    metalQuotesHydrated: boolean;
    selectedDeductionInvoiceIds: string[];
  };

  const snapRef = React.useRef<DraftSnap>({
    offerTitleRest: '',
    hideDocumentTypeInTitle: false,
    issueDate: '',
    recipientNameLines: [],
    recipientStreetLine: '',
    recipientPlzOrtLine: '',
    leistungsort: '',
    leistungszeitraum: null,
    lieferdatum: null,
    hideLeistungszeitraum: false,
    hideLieferdatum: false,
    introLetterParas: [],
    afterTotalsParas: [],
    paymentTermsCustomized: false,
    closingGreeting: '',
    signatureHint: '',
    introBlocks: [],
    closingBlocks: [],
    introContent: [],
    closingContent: [],
    draftRows: [],
    attachments: [],
    excludedStandardAttachments: [],
    discount: null,
    siteLevy: null,
    retention: null,
    constructionDeductionsCustomized: false,
    abschlagPercent: 50,
    partialInvoiceAmount: 0,
    partialInvoiceDescription: '',
    partialInvoiceLineName: '',
    partialInvoiceAmountIsGross: false,
    partialInvoiceMode: 'percent',
    partialInvoiceCumulative: null,
    orderTotalNet: null,
    documentPaymentTerms: paymentTermsFromSettings({ mode: 'net_days', days: 7 }),
    positionInvoiceQuantities: {},
    vatMode: DEFAULT_OFFER_VAT_MODE,
    hideTotals: false,
    laborCostShare: 'auto',
    hideArticleNumber: false,
    hidePositionDescriptions: false,
    positionDetailLevel: 'full',
    calculationHourlyRate: 0,
    calculationHourlyRateFromCustomer: false,
    calculationLaborSeparately: false,
    calculationLaborRowLabel: DEFAULT_TITLE_LABOR_ROW_LABEL,
    calculationLaborRowNames: {},
    calculationRealLaborCostRate: 0,
    calculationDismissedHints: [],
    calculationCopperQuoteEurPer100kg: null,
    calculationAluminumQuoteEurPer100kg: null,
    calculationMetalQuoteSource: 'manual',
    calculationMetalQuoteDate: null,
    calculationMetalProcurementCostPercent: 1,
    metalQuotesHydrated: false,
    selectedDeductionInvoiceIds: [],
  });

  /** Seriennummern aus PDF-Export (allocate), bis der Lead neu geladen ist — damit persistFull sie nicht überschreibt. */
  const latestExportSerialRef = React.useRef<Record<string, number>>({});
  /** Zuletzt vergebene globale Dokumentennummern (allocate), bis der Lead neu geladen ist. */
  const latestExportReferenceRef = React.useRef<Record<string, string>>({});

  const getCurrentDraftRaw = React.useCallback(() => {
    if (offeringId) {
      return (
        lead.offerings?.find((x) => x.id === offeringId)?.offer_document_draft ??
        lead.offer_document_draft
      );
    }
    return lead.offer_document_draft;
  }, [offeringId, lead.offerings, lead.offer_document_draft]);

  React.useEffect(() => {
    const p = parseOfferDocumentDraft(getCurrentDraftRaw());
    const m = p?.export_serial_by_doc_type;
    if (m) {
      for (const k of Object.keys(latestExportSerialRef.current)) {
        if ((m[k] ?? 0) >= (latestExportSerialRef.current[k] ?? 0)) {
          delete latestExportSerialRef.current[k];
        }
      }
    }
    const refMap = p?.export_reference_by_doc_type;
    if (refMap) {
      for (const k of Object.keys(latestExportReferenceRef.current)) {
        if (refMap[k] === latestExportReferenceRef.current[k]) {
          delete latestExportReferenceRef.current[k];
        }
      }
    }
  }, [getCurrentDraftRaw]);

  snapRef.current = {
    offerTitleRest,
    hideDocumentTypeInTitle,
    issueDate: issueDateEdit,
    recipientNameLines: recipientNameLinesEdit,
    recipientStreetLine: recipientStreetLineEdit,
    recipientPlzOrtLine: recipientPlzOrtLineEdit,
    leistungsort: leistungsortEdit,
    leistungszeitraum: leistungszeitraumEdit,
    lieferdatum: lieferdatumEdit,
    hideLeistungszeitraum,
    hideLieferdatum,
    introLetterParas,
    afterTotalsParas,
    paymentTermsCustomized,
    closingGreeting,
    signatureHint,
    introBlocks,
    closingBlocks,
    introContent,
    closingContent,
    draftRows,
    attachments,
    excludedStandardAttachments,
    discount,
    siteLevy,
    retention,
    constructionDeductionsCustomized,
    abschlagPercent,
    partialInvoiceAmount,
    partialInvoiceDescription,
    partialInvoiceLineName,
    partialInvoiceAmountIsGross,
    partialInvoiceMode,
    partialInvoiceCumulative,
    orderTotalNet,
    documentPaymentTerms,
    positionInvoiceQuantities,
    vatMode,
    hideTotals,
    laborCostShare,
    hideArticleNumber,
    hidePositionDescriptions,
    positionDetailLevel,
    calculationHourlyRate,
    calculationHourlyRateFromCustomer,
    calculationLaborSeparately,
    calculationLaborRowLabel,
    calculationLaborRowNames,
    calculationRealLaborCostRate,
    calculationDismissedHints,
    calculationCopperQuoteEurPer100kg,
    calculationAluminumQuoteEurPer100kg,
    calculationMetalQuoteSource,
    calculationMetalQuoteDate,
    calculationMetalProcurementCostPercent,
    metalQuotesHydrated: instanceMetalQuotes.loaded,
    selectedDeductionInvoiceIds,
  };

  type PersistOverrides = Partial<DraftSnap> & {
    currentIssuedInvoiceByDocType?: Record<string, BoundIssuedInvoice>;
  };

  const draftSaveContextRef = React.useRef({ leadRouteId, offeringId, onDraftSaved });
  draftSaveContextRef.current = { leadRouteId, offeringId, onDraftSaved };
  const draftErrorTokenRef = React.useRef(0);
  const draftPersistQueueRef = React.useRef<ReturnType<
    typeof createLatestDraftPersistQueue<OfferDocumentDraft>
  > | null>(null);
  if (!draftPersistQueueRef.current) {
    let errorTokenAtSaveStart = 0;
    draftPersistQueueRef.current = createLatestDraftPersistQueue({
      save: async (sanitized) => {
        errorTokenAtSaveStart = draftErrorTokenRef.current;
        const { leadRouteId: routeId, offeringId: currentOfferingId } = draftSaveContextRef.current;
        const res = await fetch(`/api/leads/${encodeURIComponent(routeId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            offer_document_draft: sanitized,
            ...(currentOfferingId ? { offering_id: currentOfferingId } : {}),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? 'Angebotsentwurf konnte nicht gespeichert werden.');
        }
      },
      onIdle: async () => {
        const token = errorTokenAtSaveStart;
        await draftSaveContextRef.current.onDraftSaved?.();
        if (draftErrorTokenRef.current === token) setPositionMutateError(null);
      },
    });
  }

  const persistFull = React.useCallback(
    async (overrides: PersistOverrides = {}) => {
      try {
      if (pdfPreviewRef.current?.documentNumberCommitted) return;
      // Rechnung aus Arbeitsberichten: Vorgangs-Entwurf bleibt der Arbeitsentwurf des Angebots.
      if (workReportInvoiceRef.current) return;
      const s = snapRef.current;
      const existingDraft = parseOfferDocumentDraft(getCurrentDraftRaw());
      const serialMap: Record<string, number> = { ...(existingDraft?.export_serial_by_doc_type ?? {}) };
      for (const [k, v] of Object.entries(latestExportSerialRef.current)) {
        serialMap[k] = Math.max(serialMap[k] ?? 0, v);
      }
      const referenceMap: Record<string, string> = { ...(existingDraft?.export_reference_by_doc_type ?? {}) };
      for (const [k, v] of Object.entries(latestExportReferenceRef.current)) {
        if (v) referenceMap[k] = v;
      }
      const merged: OfferDocumentDraft = {
        offerTitleRest: overrides.offerTitleRest ?? s.offerTitleRest,
        hideDocumentTypeInTitle:
          overrides.hideDocumentTypeInTitle !== undefined
            ? overrides.hideDocumentTypeInTitle
            : s.hideDocumentTypeInTitle,
        recipientNameLines: overrides.recipientNameLines ?? s.recipientNameLines,
        recipientStreetLine: overrides.recipientStreetLine ?? s.recipientStreetLine,
        recipientPlzOrtLine: overrides.recipientPlzOrtLine ?? s.recipientPlzOrtLine,
        leistungsort: overrides.leistungsort ?? s.leistungsort,
        leistungszeitraum: (() => {
          const raw =
            overrides.leistungszeitraum !== undefined ? overrides.leistungszeitraum : s.leistungszeitraum;
          if (!documentShowsLeistungszeitraum(documentTypeLabel)) return raw;
          return resolveInvoiceLeistungszeitraum({
            leistungszeitraum: raw,
            issueDate: overrides.issueDate ?? s.issueDate,
          });
        })(),
        lieferdatum: overrides.lieferdatum !== undefined ? overrides.lieferdatum : s.lieferdatum,
        hideLeistungszeitraum: documentLocksLeistungszeitraum(documentTypeLabel)
          ? false
          : overrides.hideLeistungszeitraum !== undefined
            ? overrides.hideLeistungszeitraum
            : s.hideLeistungszeitraum,
        hideLieferdatum:
          overrides.hideLieferdatum !== undefined ? overrides.hideLieferdatum : s.hideLieferdatum,
        introLetterParas: contentToParas(overrides.introContent ?? s.introContent),
        afterTotalsParas: contentToParas(overrides.closingContent ?? s.closingContent),
        introContent: overrides.introContent ?? s.introContent,
        closingContent: overrides.closingContent ?? s.closingContent,
        ...(overrides.paymentTermsCustomized === true ||
        s.paymentTermsCustomized ||
        existingDraft?.paymentTermsCustomized === true
          ? { paymentTermsCustomized: true as const }
          : {}),
        closingGreeting: overrides.closingGreeting ?? s.closingGreeting,
        signatureHint: overrides.signatureHint ?? s.signatureHint,
        documentType: documentTypeLabel,
        introBlocks: contentToLegacyBlocks(overrides.introContent ?? s.introContent),
        closingBlocks: contentToLegacyBlocks(overrides.closingContent ?? s.closingContent),
        draftRows: overrides.draftRows ?? s.draftRows,
        attachments: overrides.attachments ?? s.attachments,
        excluded_standard_attachments:
          overrides.excludedStandardAttachments ?? s.excludedStandardAttachments,
        discount:
          !isPartialInvoice
            ? (overrides.discount !== undefined ? overrides.discount : s.discount) ?? undefined
            : undefined,
        siteLevy: (() => {
          const next = overrides.siteLevy !== undefined ? overrides.siteLevy : s.siteLevy;
          return next ?? undefined;
        })(),
        retention: (() => {
          const next = overrides.retention !== undefined ? overrides.retention : s.retention;
          return next ?? undefined;
        })(),
        ...(overrides.constructionDeductionsCustomized === true ||
        s.constructionDeductionsCustomized
          ? { constructionDeductionsCustomized: true as const }
          : {}),
        abschlagPercent: roundAbschlagPercent(
          Number(overrides.abschlagPercent !== undefined ? overrides.abschlagPercent : s.abschlagPercent) || 0,
        ),
        partialInvoiceAmount: Math.max(
          0,
          Number(
            overrides.partialInvoiceAmount !== undefined ? overrides.partialInvoiceAmount : s.partialInvoiceAmount,
          ) || 0,
        ),
        partialInvoiceDescription:
          typeof (overrides.partialInvoiceDescription !== undefined
            ? overrides.partialInvoiceDescription
            : s.partialInvoiceDescription) === 'string'
            ? String(
                overrides.partialInvoiceDescription !== undefined
                  ? overrides.partialInvoiceDescription
                  : s.partialInvoiceDescription,
              )
            : '',
        partialInvoiceLineName:
          typeof (overrides.partialInvoiceLineName !== undefined
            ? overrides.partialInvoiceLineName
            : s.partialInvoiceLineName) === 'string'
            ? String(
                overrides.partialInvoiceLineName !== undefined
                  ? overrides.partialInvoiceLineName
                  : s.partialInvoiceLineName,
              )
            : '',
        ...((overrides.partialInvoiceAmountIsGross !== undefined
          ? overrides.partialInvoiceAmountIsGross
          : s.partialInvoiceAmountIsGross)
          ? { partialInvoiceAmountIsGross: true as const }
          : {}),
        ...(isPartialInvoice
          ? {
              partialInvoiceMode:
                overrides.partialInvoiceMode !== undefined
                  ? overrides.partialInvoiceMode
                  : s.partialInvoiceMode,
            }
          : {}),
        ...(isPartialInvoice
          ? (() => {
              const next =
                overrides.partialInvoiceCumulative !== undefined
                  ? overrides.partialInvoiceCumulative
                  : s.partialInvoiceCumulative;
              return next != null ? { partialInvoiceCumulative: next } : {};
            })()
          : {}),
        ...(isPartialInvoice
          ? (() => {
              const next = overrides.orderTotalNet !== undefined ? overrides.orderTotalNet : s.orderTotalNet;
              return next != null ? { orderTotalNet: next } : {};
            })()
          : {}),
        paymentTerms: overrides.documentPaymentTerms ?? s.documentPaymentTerms,
        ...(((isPartialInvoice && partialInvoiceMode === 'position') || isFinalInvoice)
          ? {
              positionInvoiceQuantities:
                overrides.positionInvoiceQuantities !== undefined
                  ? overrides.positionInvoiceQuantities
                  : s.positionInvoiceQuantities,
            }
          : {}),
        vat_mode: (overrides.vatMode ?? s.vatMode) ?? DEFAULT_OFFER_VAT_MODE,
        hideTotals: overrides.hideTotals !== undefined ? overrides.hideTotals : s.hideTotals,
        laborCostShare: overrides.laborCostShare !== undefined ? overrides.laborCostShare : s.laborCostShare,
        hidePositionDescriptions:
          overrides.hidePositionDescriptions !== undefined
            ? overrides.hidePositionDescriptions
            : s.hidePositionDescriptions,
        positionDetailLevel:
          overrides.positionDetailLevel !== undefined
            ? overrides.positionDetailLevel
            : s.positionDetailLevel,
        calculationHourlyRate: Math.max(
          0,
          overrides.calculationHourlyRate !== undefined
            ? overrides.calculationHourlyRate
            : s.calculationHourlyRate,
        ),
        calculationHourlyRateFromCustomer:
          overrides.calculationHourlyRateFromCustomer !== undefined
            ? overrides.calculationHourlyRateFromCustomer
            : s.calculationHourlyRateFromCustomer,
        calculationLaborSeparately:
          overrides.calculationLaborSeparately !== undefined
            ? overrides.calculationLaborSeparately
            : s.calculationLaborSeparately,
        calculationLaborRowLabel: normalizeTitleLaborRowLabel(
          overrides.calculationLaborRowLabel !== undefined
            ? overrides.calculationLaborRowLabel
            : s.calculationLaborRowLabel,
        ),
        calculationLaborRowNames: normalizeTitleLaborRowNames(
          overrides.calculationLaborRowNames !== undefined
            ? overrides.calculationLaborRowNames
            : s.calculationLaborRowNames,
        ),
        calculationRealLaborCostRate: Math.max(
          0,
          overrides.calculationRealLaborCostRate !== undefined
            ? overrides.calculationRealLaborCostRate
            : s.calculationRealLaborCostRate,
        ),
        ...(() => {
          const dismissed = overrides.calculationDismissedHints ?? s.calculationDismissedHints;
          return dismissed.length > 0 ? { calculationDismissedHints: dismissed } : {};
        })(),
        ...(isWorkReport
          ? {
              hideArticleNumber:
                overrides.hideArticleNumber !== undefined
                  ? overrides.hideArticleNumber
                  : s.hideArticleNumber,
            }
          : existingDraft?.hideArticleNumber !== undefined
            ? { hideArticleNumber: existingDraft.hideArticleNumber }
            : {}),
        ...( (isFinalInvoice || isPartialInvoice) && deductionInitCompleteRef.current
          ? (() => {
              const idsToPersist =
                overrides.selectedDeductionInvoiceIds ?? selectedDeductionInvoiceIdsRef.current;
              // Never persist an accidental empty selection from a stale snap during init races.
              if (
                overrides.selectedDeductionInvoiceIds === undefined &&
                idsToPersist.length === 0
              ) {
                return {};
              }
              return { selectedDeductionInvoiceIds: idsToPersist };
            })()
          : {}),
        ...(Object.keys(serialMap).length > 0 ? { export_serial_by_doc_type: serialMap } : {}),
        ...(Object.keys(referenceMap).length > 0 ? { export_reference_by_doc_type: referenceMap } : {}),
        ...(() => {
          const issuedMap = {
            ...(existingDraft?.currentIssuedInvoiceByDocType ?? {}),
            ...(overrides.currentIssuedInvoiceByDocType ?? {}),
          };
          return Object.keys(issuedMap).length > 0 ? { currentIssuedInvoiceByDocType: issuedMap } : {};
        })(),
      };
      const sanitized = sanitizeOfferDocumentDraftForSave(merged);
      assertDraftJsonSize(sanitized);
      await draftPersistQueueRef.current!.enqueue(sanitized);
      } catch (err) {
        draftErrorTokenRef.current += 1;
        setPositionMutateError(offerDraftSaveErrorMessage(err));
      }
    },
    [getCurrentDraftRaw, documentTypeLabel, isPartialInvoice, isFinalInvoice, isWorkReport, partialInvoiceMode]
  );

  /**
   * Betreff = abgeleitetes Typ-Präfix + gespeicherter Titel. Bearbeitet wird die ganze
   * Zeile, damit das Präfix manuell gelöscht werden kann; fehlt es beim Speichern,
   * merkt sich der Draft das als `hideDocumentTypeInTitle`.
   *
   * Nennt die Belegzeile den Typ bereits, wird gar kein Präfix angeboten — das Fehlen
   * ist dann keine Nutzerentscheidung und darf den Draft nicht umstellen.
   */
  const typePrefixOfferedInTitle = !documentTitleShowsType(documentLayout, documentTypeLabel);
  const hideTitleTypePrefix = hideDocumentTypeInTitle || !typePrefixOfferedInTitle;
  const offerTitleParts = React.useMemo(
    () =>
      offerTitleLineParts(documentTypeLabel, offerTitleRest, {
        hidePrefix: hideTitleTypePrefix,
      }),
    [documentTypeLabel, offerTitleRest, hideTitleTypePrefix],
  );
  const offerTitleEditValue =
    offerTitleParts.title === EMPTY_OFFER_TITLE_SENTINEL
      ? ''
      : `${offerTitleParts.prefix}${offerTitleParts.title}`;
  const saveOfferTitleLine = React.useCallback(
    async (next: string) => {
      const { title, hidePrefix } = parseEditedOfferTitleLine(documentTypeLabel, next);
      const nextHidePrefix = typePrefixOfferedInTitle ? hidePrefix : hideDocumentTypeInTitle;
      setOfferTitleRest(title);
      setHideDocumentTypeInTitle(nextHidePrefix);
      await persistFull({ offerTitleRest: title, hideDocumentTypeInTitle: nextHidePrefix });
    },
    [documentTypeLabel, persistFull, typePrefixOfferedInTitle, hideDocumentTypeInTitle],
  );

  const persistBestellnummer = React.useCallback(
    async (next: string) => {
      if (pdfPreviewRef.current?.documentNumberCommitted) return;
      const res = await fetch(`/api/leads/${encodeURIComponent(leadRouteId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ baustelle: { bestellnummer: next.trim() || null } }),
      });
      if (!res.ok) {
        throw new Error('Bestellnummer konnte nicht gespeichert werden.');
      }
      await onDraftSaved?.();
    },
    [leadRouteId, onDraftSaved],
  );

  const handleDeductionPickerConfirm = React.useCallback(
    (ids: string[], candidates: PaidPartialInvoice[]) => {
      deductionLoadGenRef.current += 1;
      const prior = excludeCurrentDocumentDeductions(candidates, currentDeductionDocumentRef.current, ids);
      const allowed = new Set(prior.map((c) => c.id));
      const nextIds = ids.filter((id) => allowed.has(id));
      setDeductionCandidates(prior);
      selectedDeductionInvoiceIdsRef.current = nextIds;
      setSelectedDeductionInvoiceIdsSynced(nextIds);
      deductionInitCompleteRef.current = true;
      deductionInitKeyRef.current = `${editorSessionKey}:${deductionDraftSignature}`;
      void persistFull({ selectedDeductionInvoiceIds: nextIds });
    },
    [persistFull, editorSessionKey, deductionDraftSignature],
  );

  const persistIntroContent = React.useCallback(
    async (next: OfferDocumentContentBlock[]) => {
      setIntroContent(next);
      await persistFull({ introContent: next });
    },
    [persistFull],
  );

  const persistClosingContent = React.useCallback(
    async (next: OfferDocumentContentBlock[], extra?: { paymentTermsCustomized?: boolean }) => {
      setClosingContent(next);
      await persistFull({
        closingContent: next,
        ...(extra?.paymentTermsCustomized ? { paymentTermsCustomized: true } : {}),
      });
    },
    [persistFull],
  );

  const commitAttachments = React.useCallback(
    async (next: OfferAttachment[]) => {
      setAttachments(next);
      await persistFull({ attachments: next });
    },
    [persistFull]
  );

  const removeOfferAttachment = React.useCallback(
    async (attachment: OfferAttachment) => {
      await deleteOfferAttachmentStorage(leadRouteId, attachment);
      await commitAttachments(attachments.filter((a) => a.id !== attachment.id));
    },
    [attachments, commitAttachments, leadRouteId],
  );

  const replaceOfferAttachment = React.useCallback(
    async (attachment: OfferAttachment, file: File) => {
      const uploaded = await uploadOfferAttachment(leadRouteId, file);
      await commitAttachments(attachments.map((a) => (a.id === attachment.id ? uploaded : a)));
      try {
        await deleteOfferAttachmentStorage(leadRouteId, attachment);
      } catch {
        /* replacement already persisted; leftover storage is non-blocking */
      }
    },
    [attachments, commitAttachments, leadRouteId],
  );

  const commitExcludedStandardAttachments = React.useCallback(
    async (next: LegalAttachmentKind[]) => {
      setExcludedStandardAttachments(next);
      await persistFull({ excludedStandardAttachments: next });
    },
    [persistFull],
  );

  const excludeStandardAttachment = React.useCallback(
    async (kind: LegalAttachmentKind) => {
      if (excludedStandardAttachments.includes(kind)) return;
      await commitExcludedStandardAttachments([...excludedStandardAttachments, kind]);
    },
    [commitExcludedStandardAttachments, excludedStandardAttachments],
  );

  const restoreStandardAttachment = React.useCallback(
    async (kind: LegalAttachmentKind) => {
      if (!excludedStandardAttachments.includes(kind)) return;
      await commitExcludedStandardAttachments(excludedStandardAttachments.filter((k) => k !== kind));
    },
    [commitExcludedStandardAttachments, excludedStandardAttachments],
  );

  const legalSettingsForEditor = React.useMemo(
    () => legalAttachmentSettingsFromUnknown(company),
    [company],
  );
  const standardAttachmentsForEditor = React.useMemo(
    () =>
      LEGAL_ATTACHMENT_KINDS.filter((kind) =>
        isLegalAttachmentApplicable({
          kind,
          documentTypeLabel,
          isPrivateCustomer: isPrivateCustomerLead(lead),
          settings: legalSettingsForEditor,
        }),
      ).map((kind) => ({
        kind,
        filename: legalAttachmentFilename(kind, legalSettingsForEditor),
        excluded: excludedStandardAttachments.includes(kind),
      })),
    [documentTypeLabel, excludedStandardAttachments, lead, legalSettingsForEditor],
  );

  const remainingImageSlots = remainingInlineImageSlots(introContent, closingContent);
  const [imagePicker, setImagePicker] = React.useState<
    | { zone: 'intro' | 'closing'; index: number }
    | { zone: 'intro' | 'closing'; editBlockId: string }
    | null
  >(null);
  const inlineImageObjectPaths = React.useMemo(() => {
    const paths = new Set<string>();
    for (const block of [...introContent, ...closingContent]) {
      if (block.type === 'imageGrid') {
        for (const image of block.images) paths.add(image.object_path);
      }
    }
    return paths;
  }, [introContent, closingContent]);

  const markPaymentTermsCustomized = React.useCallback(() => {
    paymentTermsCustomizedRef.current = true;
    setPaymentTermsCustomized(true);
  }, []);

  const persistDocumentPaymentTerms = React.useCallback(
    async (next: OfferDocumentPaymentTerms, wording?: string) => {
      setDocumentPaymentTerms(next);
      markPaymentTermsCustomized();
      const sentence =
        (wording ?? '').trim() ||
        formatPaymentTermsSentence({
          mode: next.mode,
          days: next.days,
          context: paymentTermsContextFromDocumentType(documentTypeLabel),
          skontoPercent: next.skontoPercent,
          skontoDays: next.skontoDays,
        });
      const prev = snapRef.current.closingContent;
      const firstText = paymentTermsTextIdRef.current
        ? prev.find((block) => block.id === paymentTermsTextIdRef.current)
        : prev.find((block) => block.type === 'text');
      const nextClosing =
        firstText && firstText.type === 'text'
          ? updateContentText(prev, firstText.id, sentence)
          : [{ id: newDocumentContentId(), type: 'text' as const, content: sentence }, ...prev];
      if (!paymentTermsTextIdRef.current) {
        const saved = nextClosing.find((block) => block.type === 'text');
        paymentTermsTextIdRef.current = saved?.id ?? null;
      }
      setClosingContent(nextClosing);
      await persistFull({
        documentPaymentTerms: next,
        paymentTermsCustomized: true,
        closingContent: nextClosing,
      });
    },
    [documentTypeLabel, markPaymentTermsCustomized, persistFull],
  );

  const paymentTermsBlockId = React.useMemo(() => {
    if (isWorkReport || isCreditOrStornoDocument) return null;
    if (paymentTermsTextIdRef.current) {
      const found = closingContent.find((block) => block.id === paymentTermsTextIdRef.current);
      if (found?.type === 'text') return found.id;
    }
    return closingContent.find((block) => block.type === 'text')?.id ?? null;
  }, [closingContent, isCreditOrStornoDocument, isWorkReport]);

  const paymentTermsWording = React.useMemo(() => {
    const block = paymentTermsBlockId
      ? closingContent.find((item) => item.id === paymentTermsBlockId)
      : closingContent.find((item) => item.type === 'text');
    return block?.type === 'text' ? block.content : '';
  }, [closingContent, paymentTermsBlockId]);

  const isFirstClosingText = React.useCallback((id: string) => {
    if (paymentTermsTextIdRef.current) return paymentTermsTextIdRef.current === id;
    const first = snapRef.current.closingContent.find((block) => block.type === 'text');
    return first?.id === id;
  }, []);

  React.useEffect(() => {
    if (!company) return;
    if (paymentTermsCustomizedRef.current) return;
    const org = lead.organization;
    const isActiveContract =
      org?.has_framework_contract === true &&
      (!org.framework_valid_until || new Date(org.framework_valid_until) >= new Date());

    const paymentContext = paymentTermsContextFromDocumentType(documentTypeLabel);
    const companyLine = formatPaymentTermsSentence({
      mode: company.payment_terms_mode,
      days: company.payment_terms_days,
      context: paymentContext,
      skontoPercent: company.skonto_percent,
      skontoDays: company.skonto_days,
    });

    const applyIfReplaceable = (nextLine: string, allowCompanyLine: boolean) => {
      setClosingContent((prev) => {
        if (paymentTermsCustomizedRef.current) return prev;
        const firstText =
          (paymentTermsTextIdRef.current
            ? prev.find((block) => block.id === paymentTermsTextIdRef.current)
            : null) ?? prev.find((block) => block.type === 'text');
        const current = firstText?.type === 'text' ? firstText.content : '';
        if (!firstText || firstText.type !== 'text') return prev;
        if (current.trim() === nextLine) return prev;
        if (
          !isReplaceableDefaultPaymentTermsSentence(
            current,
            paymentContext,
            allowCompanyLine ? companyLine : null,
          )
        ) {
          return prev;
        }
        return updateContentText(prev, firstText.id, nextLine);
      });
    };

    // Firmen-Standard: nur Platzhalter nachziehen, geänderte Tage (z. B. 14) nicht anfassen
    if (!isActiveContract) {
      applyIfReplaceable(companyLine, false);
      setDocumentPaymentTerms(
        paymentTermsFromSettings({
          mode: company.payment_terms_mode,
          days: company.payment_terms_days,
          skontoPercent: company.skonto_percent,
          skontoDays: company.skonto_days,
        }),
      );
      return;
    }

    // Aktiver Rahmenvertrag: Org-Werte auf Platzhalter oder Firmenzeile setzen
    const days = org?.payment_terms_days ?? company.payment_terms_days;
    const orgMode = org?.payment_terms_days ? 'net_days' : company.payment_terms_mode;
    const orgLine = formatPaymentTermsSentence({
      mode: orgMode,
      days,
      context: paymentContext,
      // Sofort is "ohne Abzug" — never mix org Skonto into that sentence.
      skontoPercent: orgMode === 'immediate' ? null : org?.skonto_percent,
      skontoDays: orgMode === 'immediate' ? null : org?.skonto_days,
    });
    applyIfReplaceable(orgLine, true);
    setDocumentPaymentTerms(
      paymentTermsFromSettings({
        mode: orgMode,
        days,
        skontoPercent: orgMode === 'immediate' ? null : org?.skonto_percent,
        skontoDays: orgMode === 'immediate' ? null : org?.skonto_days,
      }),
    );
  }, [company, lead.organization, documentTypeLabel]);

  React.useEffect(() => {
    let cancelled = false;
    positionsLoadedSessionRef.current = null;
    (async () => {
      setLoadError(null);
      try {
        const [coRes, posRes, meRes] = await Promise.all([
          fetch('/api/settings/company'),
          fetch(positionsApiBase),
          fetch('/api/me', { credentials: 'include' }),
        ]);
        if (!coRes.ok) throw new Error('Firmendaten konnten nicht geladen werden.');
        const co = (await coRes.json()) as CompanySettings;
        let meName = '';
        if (meRes.ok) {
          const me = (await meRes.json()) as { user?: { username?: string; email?: string } };
          meName = String(me.user?.username ?? '').trim();
        }
        if (cancelled) return;
        setCompany(co);
        setCurrentUserName(meName);
        setLogoBust((b) => b + 1);
        if (!posRes.ok) {
          positionsLoadedSessionRef.current = null;
          setLoadError('Positionen konnten nicht geladen werden.');
        } else {
          const pos = await posRes.json();
          positionsLoadedSessionRef.current = editorSessionKey;
          setPositions(normalizePositions(pos));
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [positionsApiBase, urlInstanceId, editorSessionKey]);

  React.useEffect(() => {
    const immediate =
      (showInvoicePaymentTermsControls ? documentPaymentTerms.mode : company?.payment_terms_mode) ===
      'immediate';
    if (!immediate) {
      setHolidayDates(new Set());
      return;
    }
    let cancelled = false;
    const issueYmd = parseDDMMYYYYToYMD(issueDateEdit) ?? todayYMD();
    const issueYear = Number(issueYmd.slice(0, 4));
    const years = Number.isFinite(issueYear)
      ? [issueYear, issueYear + 1]
      : [new Date().getFullYear(), new Date().getFullYear() + 1];
    void (async () => {
      const dates = new Set<string>();
      try {
        const results = await Promise.all(
          years.map((y) => fetch(`/api/holidays?year=${y}`, { credentials: 'include' })),
        );
        for (const res of results) {
          if (!res.ok) continue;
          const json = (await res.json()) as { items?: Array<{ holiday_date?: string }> };
          for (const item of json.items ?? []) {
            const d = String(item.holiday_date ?? '').slice(0, 10);
            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) dates.add(d);
          }
        }
      } catch {
        /* fall back to Mon–Sat without holidays */
      }
      if (!cancelled) setHolidayDates(dates);
    })();
    return () => {
      cancelled = true;
    };
  }, [company?.payment_terms_mode, documentPaymentTerms.mode, showInvoicePaymentTermsControls, issueDateEdit]);

  // Fail-open: HTML WYSIWYG uses the default template when present; legacy markup otherwise.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const tpl = await fetchDefaultDocumentLayoutTemplate();
      if (cancelled) return;
      activeLayoutTemplateRef.current = tpl;
      const layout = tpl?.layout ?? null;
      setDocumentLayout(layout);
      if (!layout) {
        setLayoutHtmlAssetUrls({});
        setLayoutPreviewReady(true);
        return;
      }
      const urls: Record<string, string> = {};
      for (const el of [...layout.header.elements, ...layout.footer.elements]) {
        const path = (el.imageObjectPath ?? '').trim();
        if (path && (el.key === 'headerBanner' || el.key === 'signatureImage')) {
          urls[el.id] = documentLayoutAssetSrc(path);
        }
      }
      setLayoutHtmlAssetUrls(urls);
      setLayoutPreviewReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsMdViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Lieferantenangebote (Ausschreibung): Angebote je Position für Pill und Popover im Kalkulationseditor.
  type QuoteOffersPayload = {
    ausschreibung: { id: string; display_id: string | null } | null;
    byPositionId: Record<string, PositionQuoteOffers>;
    appliedByPositionId: Record<string, string>;
  };
  const [quoteOffers, setQuoteOffers] = React.useState<QuoteOffersPayload | null>(null);
  const [quoteOffersRow, setQuoteOffersRow] = React.useState<OfferCalculationRow | null>(null);
  const [quoteApplying, setQuoteApplying] = React.useState(false);
  const [quoteApplyError, setQuoteApplyError] = React.useState<string | null>(null);
  const reloadQuoteOffers = React.useCallback(async () => {
    if (!offeringId || !quoteOffersEnabled) {
      setQuoteOffers(null);
      return;
    }
    try {
      const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/quote-offers`, { credentials: 'include' });
      if (!res.ok) return;
      const body = (await res.json()) as QuoteOffersPayload;
      setQuoteOffers(body?.ausschreibung ? body : null);
    } catch {
      // Ohne Angebote bleibt der Editor unverändert.
    }
  }, [offeringId, quoteOffersEnabled]);
  React.useEffect(() => {
    void reloadQuoteOffers();
  }, [reloadQuoteOffers]);
  const quoteOfferSummaries = React.useMemo<Record<string, QuoteOffersSummary> | undefined>(() => {
    if (!quoteOffers) return undefined;
    const out: Record<string, QuoteOffersSummary> = {};
    for (const [positionId, entry] of Object.entries(quoteOffers.byPositionId)) {
      const cheapest = entry.offers.find((o) => o.quoteId === entry.cheapestQuoteId) ?? null;
      const appliedItemId = quoteOffers.appliedByPositionId[positionId];
      const applied = appliedItemId ? entry.offers.find((o) => o.lines.some((l) => l.itemId === appliedItemId)) ?? null : null;
      out[positionId] = {
        count: entry.offers.length,
        cheapestEk: cheapest?.unitEkPerLvUnit ?? null,
        cheapestSupplier: cheapest?.supplierName ?? null,
        appliedSupplier: applied?.supplierName ?? null,
      };
    }
    return out;
  }, [quoteOffers]);
  const quoteOffersContext = React.useMemo<QuoteOffersPositionContext | null>(() => {
    if (!quoteOffersRow || quoteOffersRow.leadPositionId == null) return null;
    const positionId = String(quoteOffersRow.leadPositionId);
    return {
      positionId,
      label: `${quoteOffersRow.numberLabel ? `${quoteOffersRow.numberLabel} ` : ''}${quoteOffersRow.item_name}`.trim(),
      quantity: quoteOffersRow.quantity,
      unit: quoteOffersRow.unit,
      currentEk: quoteOffersRow.unit_cost_ek,
      appliedQuoteItemId: quoteOffers?.appliedByPositionId[positionId] ?? null,
    };
  }, [quoteOffersRow, quoteOffers]);

  const reloadPositions = React.useCallback(async () => {
    try {
      const posRes = await fetch(`${positionsApiBase}`);
      if (posRes.ok) {
        const pos = await posRes.json();
        const incoming = normalizePositions(pos);
        setPositions((prev) =>
          mergePositionsKeepingInFlightEdits(
            prev,
            incoming,
            activePositionEditKeysFromCounts(activePositionFieldEditCountsRef.current),
          ),
        );
      }
    } catch {
      // ignore reload errors (UI bleibt konservativ)
    }
  }, [positionsApiBase]);

  const registerPositionFieldEdit = React.useCallback((id: PositionRowId) => {
    incrementPositionFieldEditCount(activePositionFieldEditCountsRef.current, id);
  }, []);

  const releasePositionFieldEdit = React.useCallback((id: PositionRowId) => {
    // Defer so a blur→focus tab within the same row does not drop the lock first.
    queueMicrotask(() => {
      decrementPositionFieldEditCount(activePositionFieldEditCountsRef.current, id);
    });
  }, []);

  const displayIdForExport = React.useMemo(() => {
    if (offeringId) {
      const o = lead.offerings?.find((x) => x.id === offeringId);
      const oid = String(o?.display_id ?? '').trim();
      if (oid) return oid;
    }
    return String((lead.display_id ?? '').trim() || lead.id);
  }, [lead.offerings, lead.display_id, lead.id, offeringId]);
  const activeOfferingDraft = React.useMemo(() => {
    if (offeringId) {
      const o = lead.offerings?.find((x) => x.id === offeringId);
      return parseOfferDocumentDraft(o?.offer_document_draft ?? null);
    }
    return parseOfferDocumentDraft(lead.offer_document_draft);
  }, [lead.offerings, lead.offer_document_draft, offeringId]);
  const recipientLayoutDefault = resolveDocumentBilling(lead, documentTypeLabel, activeOfferingDraft).layout;
  const recipientLayout = {
    nameLines: recipientNameLinesEdit.length > 0 ? recipientNameLinesEdit : recipientLayoutDefault.nameLines,
    streetLine: recipientStreetLineEdit,
    plzOrtLine: recipientPlzOrtLineEdit,
  };
  const documentTypeCode = React.useMemo(
    () => documentTypeLabelToExportCode(documentTypeLabel),
    [documentTypeLabel],
  );
  const lastExportSerial = Math.max(
    activeOfferingDraft?.export_serial_by_doc_type?.[documentTypeLabel] ?? 0,
    latestExportSerialRef.current[documentTypeLabel] ?? 0,
  );
  const sharedInvoiceFamilySerial = React.useMemo(() => {
    const invoiceLabels = ['Rechnung', 'Abschlagsrechnung', 'Schlussrechnung', 'Stornorechnung', 'Gutschrift'];
    const serialFromRefs = (source: Record<string, string> | undefined): number[] => {
      if (!source) return [];
      return invoiceLabels
        .map((label) => {
          const ref = String(source[label] ?? '').trim();
          const match = ref.match(/^[A-Z]{2}-\d{2}-(\d+)(?:-\d+)?$/);
          return match ? Number(match[1]) : 0;
        })
        .filter((value) => Number.isFinite(value) && value > 0);
    };
    const draftRefMap = activeOfferingDraft?.export_reference_by_doc_type;
    const latestRefMap = latestExportReferenceRef.current;
    const serialFromLegacyMap = invoiceLabels.map((label) => activeOfferingDraft?.export_serial_by_doc_type?.[label] ?? 0);
    const values = [...serialFromRefs(draftRefMap), ...serialFromRefs(latestRefMap), ...serialFromLegacyMap];
    return values.length > 0 ? Math.max(...values) : 0;
  }, [activeOfferingDraft]);
  /**
   * Read-only Server-Vorschau der nächsten Dokumentnummer (zählt nichts hoch).
   * Wichtig für die Rechnungsfamilie, deren Nummer aus der globalen Jahressequenz pro Instanz stammt
   * und daher nicht aus dem lokalen Vorgangs-Draft ableitbar ist.
   */
  const forceNewDocument = searchParams.get('neu') === '1';
  const [sageReads, setSageReads] = React.useState<SageImportReadDocument[] | null>(
    forceNewDocument ? [] : null,
  );
  React.useEffect(() => {
    if (forceNewDocument || !offeringId) {
      setSageReads([]);
      return;
    }
    // Sage-Importe liegen bei den Rechnungen; Modulstand abwarten, ohne Modul gibt es keine.
    if (!modulesLoaded) return;
    if (!rechnungenEnabled) {
      setSageReads([]);
      return;
    }
    let cancelled = false;
    setSageReads(null);
    const params = new URLSearchParams({ documentTypeLabel });
    fetch(`/api/offerings/${encodeURIComponent(offeringId)}/sage-import-documents?${params.toString()}`, {
      credentials: 'include',
    })
      .then(async (response) => (response.ok ? response.json() : []))
      .then((rows: unknown) => {
        if (!cancelled) setSageReads(Array.isArray(rows) ? rows as SageImportReadDocument[] : []);
      })
      .catch(() => {
        if (!cancelled) setSageReads([]);
      });
    return () => {
      cancelled = true;
    };
  }, [forceNewDocument, offeringId, documentTypeLabel, modulesLoaded, rechnungenEnabled]);

  const [serverDocPreviewRef, setServerDocPreviewRef] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (sageReads === null || sageReads.length > 0) {
      setServerDocPreviewRef(null);
      return;
    }
    let cancelled = false;
    setServerDocPreviewRef(null);
    const params = new URLSearchParams({ documentTypeLabel });
    if (offeringId) params.set('offering_id', offeringId);
    fetch(`/api/leads/${encodeURIComponent(leadRouteId)}/document-number-preview?${params.toString()}`, {
      credentials: 'include',
    })
      .then(async (r) => (r.ok ? ((await r.json()) as { referenceId?: string }) : null))
      .then((j) => {
        if (!cancelled && j?.referenceId) setServerDocPreviewRef(j.referenceId);
      })
      .catch(() => {
        if (!cancelled) setServerDocPreviewRef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadRouteId, offeringId, documentTypeLabel, sageReads]);

  /**
   * Vorschau der Dokumenten-ID für den Editor-Header.
   * Die endgültige Nummer wird erst bei Download oder Versand vergeben (allocate).
   */
  const documentExportRefPreview = React.useMemo(() => {
    const sessionRef = latestExportReferenceRef.current[documentTypeLabel];
    if (sessionRef) return sessionRef;
    if (serverDocPreviewRef) return serverDocPreviewRef;
    if (isInvoiceFamilyCode(documentTypeCode)) {
      // Rechnungsnummern stammen aus der globalen Jahressequenz – lokalen (ggf. veralteten) Draft ignorieren.
      return buildYearlyInvoiceReferenceId(documentTypeLabel, currentDocumentYearSuffix(), sharedInvoiceFamilySerial + 1);
    }
    return buildDocumentExportReferenceId(
      displayIdForExport,
      documentTypeLabel,
      lastExportSerial + 1,
    );
  }, [
    activeOfferingDraft,
    displayIdForExport,
    documentTypeCode,
    documentTypeLabel,
    lastExportSerial,
    sharedInvoiceFamilySerial,
    serverDocPreviewRef,
  ]);
  const angebotReferenceId = React.useMemo(() => {
    const storedRef = activeOfferingDraft?.export_reference_by_doc_type?.Angebot;
    if (storedRef) return storedRef;
    // Legacy-Angebote ohne globale Nummer: aus alter Laufnummer + display_id rekonstruieren.
    const offerSerial = activeOfferingDraft?.export_serial_by_doc_type?.Angebot ?? 0;
    if (!offerSerial) return '';
    return buildDocumentExportReferenceId(displayIdForExport, 'Angebot', offerSerial);
  }, [activeOfferingDraft, displayIdForExport]);
  /** Abschlag nach Betrag: nur mit Angebot auf „Angebot − bisherige Abschläge“ begrenzen. */
  const hasPartialOfferBasis = Boolean(angebotReferenceId);
  const partialInvoiceDescriptionFallback = React.useMemo(
    () => formatPartialInvoiceDescriptionFallback(angebotReferenceId, leistungsortEdit),
    [angebotReferenceId, leistungsortEdit],
  );
  const effectivePartialDescription = partialInvoiceDescription.trim() || partialInvoiceDescriptionFallback;

  const combinedRows = React.useMemo<CombinedRow[]>(
    () => buildOfferMergedTableRows(positions, draftRows, creditOrStornoMergeOptions) as CombinedRow[],
    [positions, draftRows, creditOrStornoMergeOptions]
  );

  const customerLaborOpts = React.useMemo(
    () => ({
      hourlyRate: calculationHourlyRate,
      listLaborSeparately: calculationLaborSeparately,
      laborRowLabel: calculationLaborRowLabel,
      laborRowNames: calculationLaborRowNames,
    }),
    [calculationHourlyRate, calculationLaborSeparately, calculationLaborRowLabel, calculationLaborRowNames],
  );

  const commitLaborRowName = React.useCallback(
    (rowId: string, edited: string, hours: number) => {
      const parentId = virtualTitleLaborParentId(rowId);
      if (!parentId) return;
      const stored = storeTitleLaborRowName(edited, hours, calculationHourlyRate);
      const autoName = formatTitleLaborRowName(hours, calculationHourlyRate, calculationLaborRowLabel);
      const resolved = resolveTitleLaborRowName(
        hours,
        calculationHourlyRate,
        stored,
        calculationLaborRowLabel,
      );
      setCalculationLaborRowNames((prev) => {
        const next = { ...prev };
        if (!stored || resolved === autoName) delete next[parentId];
        else next[parentId] = stored;
        void persistFull({ calculationLaborRowNames: next });
        return next;
      });
    },
    [calculationHourlyRate, calculationLaborRowLabel, persistFull],
  );

  const storedRowsForPricing = React.useMemo(
    () =>
      combinedRows.map((r) => ({
        ...r,
        isEventual: treatEventualAsNormal ? false : r.isEventual,
      })),
    [combinedRows, treatEventualAsNormal],
  );

  const numberedRows = React.useMemo(
    () =>
      assignPositionNumberLabels(
        applyCustomerFacingLaborPricing(storedRowsForPricing, customerLaborOpts),
      ),
    [storedRowsForPricing, customerLaborOpts],
  );

  /** Zeilen der Kalkulationsliste; die Margen-Hinweise rechnen auf genau diesen Indizes. */
  const calculationTableRows = React.useMemo(
    () =>
      assignPositionNumberLabels(
        calculationLaborSeparately
          ? injectTitleLaborRows(storedRowsForPricing, calculationHourlyRate, {
              laborRowLabel: calculationLaborRowLabel,
              laborRowNames: calculationLaborRowNames,
            })
          : storedRowsForPricing,
      ),
    [
      calculationHourlyRate,
      calculationLaborRowLabel,
      calculationLaborRowNames,
      calculationLaborSeparately,
      storedRowsForPricing,
    ],
  );

  /** Nicht blockierende Hinweise (Ziel-Marge, Verlustpositionen, …); Gutschrift/Storno haben keine. */
  const calculationMarginHints = React.useMemo(() => {
    if (isCreditOrStornoDocument) return [];
    const dismissed = new Set(calculationDismissedHints);
    return computeOfferMarginHints({
      rows: calculationTableRows,
      hourlyRate: calculationHourlyRate,
      realLaborCostRate: calculationRealLaborCostRate,
      targetMarginPercent: company?.offer_target_margin_percent ?? null,
      quoteOffersByPositionId: quoteOfferSummaries,
    }).filter((hint) => !dismissed.has(hint.id));
  }, [
    calculationDismissedHints,
    calculationHourlyRate,
    calculationRealLaborCostRate,
    calculationTableRows,
    company?.offer_target_margin_percent,
    isCreditOrStornoDocument,
    quoteOfferSummaries,
  ]);
  const calculationHintCount = React.useMemo(
    () => groupOfferMarginHints(calculationMarginHints).length,
    [calculationMarginHints],
  );

  const dismissCalculationHints = React.useCallback(
    (ids: string[]) => {
      const current = snapRef.current.calculationDismissedHints;
      const next = Array.from(new Set([...current, ...ids]));
      if (next.length === current.length) return;
      setCalculationDismissedHints(next);
      void persistFull({ calculationDismissedHints: next });
    },
    [persistFull],
  );

  numberedRowCountRef.current = numberedRows.length;

  const positionBillingStates = React.useMemo(
    () =>
      showPositionBillingColumns
        ? buildPositionBillingRows(
            numberedRows.map((r) => ({
              id: r.id,
              kind: r.kind,
              leadPositionId: r.leadPositionId ?? null,
              quantity: r.quantity,
            })),
            aggregatedInvoicedQuantities,
            positionInvoiceQuantities,
            {
              autoFillRemaining:
                isFinalInvoice &&
                Boolean(offeringId) &&
                billingLedgerReady &&
                Object.keys(positionInvoiceQuantities).length === 0,
            },
          )
        : [],
    [
      showPositionBillingColumns,
      numberedRows,
      aggregatedInvoicedQuantities,
      positionInvoiceQuantities,
      isFinalInvoice,
      offeringId,
      billingLedgerReady,
    ],
  );

  const positionBillingStateByRowId = React.useMemo(
    () => new Map(positionBillingStates.map((s) => [s.rowId, s])),
    [positionBillingStates],
  );

  const rowsForPositionBillingTotals = React.useMemo(() => {
    if (!showPositionBillingColumns) return numberedRows;
    const qtyMap = new Map(positionBillingStates.map((s) => [s.rowId, s.invoiceQty]));
    const adjusted = storedRowsForPricing.map((r) => {
      const invoiceQty = qtyMap.get(r.id);
      if (invoiceQty === undefined) return r;
      if (r.kind === 'title' || r.kind === 'text' || r.kind === 'image') return r;
      return { ...r, quantity: invoiceQty };
    });
    return assignPositionNumberLabels(
      applyCustomerFacingLaborPricing(adjusted, customerLaborOpts),
    );
  }, [
    showPositionBillingColumns,
    numberedRows,
    positionBillingStates,
    storedRowsForPricing,
    customerLaborOpts,
  ]);

  const positionBillingNetTotal = React.useMemo(
    () =>
      rowsForPositionBillingTotals.reduce((s, r) => {
        if (r.kind === 'title' || r.kind === 'text' || r.kind === 'image') return s;
        return s + lineTotal(r.quantity, r.unit_price_vk);
      }, 0),
    [rowsForPositionBillingTotals],
  );

  const positionBillingPdfRows = React.useMemo(
    () =>
      rowsForPositionBillingTotals
        .filter(
          (r) =>
            r.kind !== 'title' &&
            r.kind !== 'text' &&
            r.kind !== 'image' &&
            r.quantity > 0,
        )
        .map((r) => ({
          id: r.id,
          kind: r.kind,
          quantity: r.quantity,
          unit: r.unit,
          item_name: r.item_name,
          description: r.description,
          unit_price_vk: r.unit_price_vk,
          numberLabel: r.numberLabel ?? '',
          isEventual: r.isEventual === true,
        })),
    [rowsForPositionBillingTotals],
  );

  const schlussrechnungAutoFillRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isFinalInvoice || !offeringId) return;
    const initKey = `${editorSessionKey}:${offeringId}`;
    if (billingLedgerInitRef.current !== initKey) return;
    if (schlussrechnungAutoFillRef.current === initKey) return;
    if (Object.keys(positionInvoiceQuantities).length > 0) {
      schlussrechnungAutoFillRef.current = initKey;
      return;
    }
    const states = buildPositionBillingRows(
      numberedRows.map((r) => ({
        id: r.id,
        kind: r.kind,
        leadPositionId: r.leadPositionId ?? null,
        quantity: r.quantity,
      })),
      aggregatedInvoicedQuantities,
      undefined,
      { autoFillRemaining: true },
    );
    const map = buildPositionInvoiceQuantitiesMap(states);
    if (Object.keys(map).length === 0) {
      schlussrechnungAutoFillRef.current = initKey;
      return;
    }
    schlussrechnungAutoFillRef.current = initKey;
    setPositionInvoiceQuantities(map);
    void persistFull({ positionInvoiceQuantities: map });
  }, [
    isFinalInvoice,
    offeringId,
    editorSessionKey,
    numberedRows,
    aggregatedInvoicedQuantities,
    positionInvoiceQuantities,
    persistFull,
  ]);

  const setPositionInvoiceQty = React.useCallback(
    (rowKey: string, qty: number, remaining: number) => {
      const clamped = clampInvoiceQty(qty, remaining);
      setPositionInvoiceQuantities((prev) => {
        const next = { ...prev };
        if (clamped <= 0) delete next[rowKey];
        else next[rowKey] = clamped;
        void persistFull({ positionInvoiceQuantities: next });
        return next;
      });
    },
    [persistFull],
  );

  const rawNetTotal = React.useMemo(
    () =>
      numberedRows.reduce((s, r) => {
        if (!contributesToOfferNetTotal(r)) return s;
        return s + lineTotal(r.quantity, r.unit_price_vk);
      }, 0),
    [numberedRows]
  );
  const discountAmount = React.useMemo(() => {
    if (!discount || discount.value <= 0) return 0;
    return discount.type === 'percent'
      ? Math.round(rawNetTotal * (discount.value / 100) * 100) / 100
      : Math.min(discount.value, rawNetTotal);
  }, [rawNetTotal, discount]);
  const discountedOfferNetTotal = rawNetTotal - discountAmount;
  /** Abschlag: Auftragssumme netto auf dem PDF — Override oder Angebotsnetto. */
  const effectiveOrderTotalNet =
    orderTotalNet ?? Math.round(discountedOfferNetTotal * 100) / 100;
  const positionBillingDiscountAmount = React.useMemo(() => {
    if (!discount || discount.value <= 0) return 0;
    return discount.type === 'percent'
      ? Math.round(positionBillingNetTotal * (discount.value / 100) * 100) / 100
      : Math.min(discount.value, positionBillingNetTotal);
  }, [positionBillingNetTotal, discount]);
  const partialInvoiceNetTotal = React.useMemo(() => {
    if (!isPartialInvoice) return rawNetTotal;
    if (isPositionPartialInvoice) {
      return Math.round((positionBillingNetTotal - positionBillingDiscountAmount) * 100) / 100;
    }
    return computePartialInvoiceBaseNet({
      mode: isAmountPartialInvoice ? 'amount' : 'percent',
      offerNet: discountedOfferNetTotal,
      priorPartialNet: previousPartialsSummaryTotals.paidPartialNetTotal,
      abschlagPercent,
      partialInvoiceAmount,
      positionNet: 0,
      capAtRemaining: false,
    }).baseNet;
  }, [
    isPartialInvoice,
    isPositionPartialInvoice,
    isAmountPartialInvoice,
    rawNetTotal,
    discountedOfferNetTotal,
    abschlagPercent,
    partialInvoiceAmount,
    previousPartialsSummaryTotals.paidPartialNetTotal,
    positionBillingNetTotal,
    positionBillingDiscountAmount,
  ]);
  const baseNetTotal = React.useMemo(() => {
    if (isPositionPartialInvoice) return partialInvoiceNetTotal;
    if (isFinalInvoice && usePositionBillingTotals) {
      return Math.round((positionBillingNetTotal - positionBillingDiscountAmount) * 100) / 100;
    }
    if (isPartialInvoice) return partialInvoiceNetTotal;
    return discountedOfferNetTotal;
  }, [
    isPositionPartialInvoice,
    isFinalInvoice,
    usePositionBillingTotals,
    partialInvoiceNetTotal,
    positionBillingNetTotal,
    positionBillingDiscountAmount,
    isPartialInvoice,
    discountedOfferNetTotal,
  ]);
  const partialInvoiceLineNameFallback = React.useMemo(
    () =>
      formatPartialInvoiceLineName({
        mode: isAmountPartialInvoice ? 'amount' : 'percent',
        percent: abschlagPercent,
        netAmount: baseNetTotal,
        offerReferenceId: angebotReferenceId,
      }),
    [abschlagPercent, angebotReferenceId, baseNetTotal, isAmountPartialInvoice],
  );
  const effectivePartialInvoiceLineName =
    partialInvoiceLineName.trim() || partialInvoiceLineNameFallback;
  const vatOption = React.useMemo(() => getOfferVatOption(vatMode), [vatMode]);
  const vatRatePercent = vatOption.ratePercent;
  const vatRate = vatRatePercent / 100;
  const baseVatAmount = Math.round(baseNetTotal * vatRate * 100) / 100;
  const baseGrossTotal = Math.round((baseNetTotal + baseVatAmount) * 100) / 100;
  const finalInvoiceTotals = React.useMemo(
    () => computeFinalInvoiceTotals({ baseNetAmount: baseNetTotal, baseVatAmount, paidPartials: paidPartialInvoices }),
    [baseNetTotal, baseVatAmount, paidPartialInvoices],
  );
  const applyPartialDeductions = isFinalInvoice || isCumulativeLumpSumPartialInvoice;
  const netTotalBeforeLevy = applyPartialDeductions ? finalInvoiceTotals.finalNetAmount : baseNetTotal;
  const vatAmountBeforeLevy = applyPartialDeductions ? finalInvoiceTotals.finalVatAmount : baseVatAmount;
  const priorRetentionsDueGross = React.useMemo(() => {
    if (!isFinalInvoice) return 0;
    // Offene Einbehalte aus Vor-Rechnungen (Rechnung + Abschlag), unabhängig von Abschlag-Auswahl.
    if (heldPriorRetentions.length > 0) {
      return (
        Math.round(
          heldPriorRetentions.reduce((sum, row) => sum + Math.max(0, row.retentionGross), 0) * 100,
        ) / 100
      );
    }
    // Fallback ohne Ledger: Einbehalte der ausgewählten Abschläge.
    return (
      Math.round(
        paidPartialInvoices.reduce((sum, inv) => sum + Math.max(0, Number(inv.retentionGross) || 0), 0) *
          100,
      ) / 100
    );
  }, [isFinalInvoice, heldPriorRetentions, paidPartialInvoices]);
  const skontoPercentForPayable = React.useMemo(() => {
    if (documentPaymentTerms.mode === 'immediate') return null;
    const n = Number(documentPaymentTerms.skontoPercent);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [documentPaymentTerms.mode, documentPaymentTerms.skontoPercent]);
  const showConstructionPayableControls = isPartialInvoice || isFinalInvoice || documentTypeLabel.trim() === 'Rechnung';
  const payableTotals = React.useMemo(
    () =>
      computeInvoicePayableTotals({
        faceNetBeforeLevy: netTotalBeforeLevy,
        faceVatBeforeLevy: vatAmountBeforeLevy,
        vatRatePercent,
        siteLevy: showConstructionPayableControls ? siteLevy : null,
        retention: showConstructionPayableControls ? retention : null,
        priorRetentionsDueGross,
        skontoPercent: showConstructionPayableControls ? skontoPercentForPayable : null,
      }),
    [
      netTotalBeforeLevy,
      vatAmountBeforeLevy,
      vatRatePercent,
      showConstructionPayableControls,
      siteLevy,
      retention,
      priorRetentionsDueGross,
      skontoPercentForPayable,
    ],
  );
  const netTotal = payableTotals.faceNet;
  const vatAmount = payableTotals.faceVat;
  const grossTotal = payableTotals.faceGross;
  const showDeductionTotals = applyPartialDeductions && paidPartialInvoices.length > 0;
  const showPayableExtras =
    showConstructionPayableControls &&
    (payableTotals.siteLevyNet > 0 ||
      payableTotals.retentionGross > 0 ||
      payableTotals.priorRetentionsDueGross > 0 ||
      payableTotals.skontoAmount > 0);
  const showConstructionDeductionsUi =
    showConstructionPayableControls &&
    (company?.construction_deductions_enabled === true ||
      siteLevy != null ||
      retention != null ||
      payableTotals.priorRetentionsDueGross > 0);
  const vatLegalNote = vatOption.legalNote ?? '';
  const hideVatAndGross = isZeroVatSpecialMode(vatMode);
  const laborCostShareNote = React.useMemo(() => {
    const useBilledRows = isPositionPartialInvoice || (isFinalInvoice && usePositionBillingTotals);
    return resolveLaborCostShareFootnote({
      rows: useBilledRows ? rowsForPositionBillingTotals : numberedRows,
      documentTypeLabel,
      netTotal,
      rawNetTotal: useBilledRows ? positionBillingNetTotal : rawNetTotal,
      vatRatePercent,
      hideTotals: totalsHidden,
      settings: laborCostShareSettingsFromCompany(company),
      mode: laborCostShare,
      isPrivateCustomer: isPrivateCustomerLead(lead),
      hourlyRate: calculationHourlyRate,
      listLaborSeparately: calculationLaborSeparately,
      partialInvoiceMode: isPartialInvoice ? partialInvoiceMode : undefined,
    });
  }, [
    isPositionPartialInvoice,
    isFinalInvoice,
    usePositionBillingTotals,
    rowsForPositionBillingTotals,
    numberedRows,
    documentTypeLabel,
    netTotal,
    positionBillingNetTotal,
    rawNetTotal,
    vatRatePercent,
    totalsHidden,
    company,
    laborCostShare,
    lead,
    calculationHourlyRate,
    calculationLaborSeparately,
    isPartialInvoice,
    partialInvoiceMode,
  ]);
  const laborCostShareToggleOn = laborCostShare === 'on' || Boolean(laborCostShareNote);

  React.useEffect(() => {
    if (!vatModeEditing) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = vatPickerWrapRef.current;
      if (el && !el.contains(e.target as Node)) setVatModeEditing(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [vatModeEditing]);
  useEscapeClose(() => setVatModeEditing(false), vatModeEditing);
  useEscapeClose(() => setShowMobilePositionPicker(false), showMobilePositionPicker);

  React.useEffect(() => {
    if (!previewMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = previewMenuWrapRef.current;
      if (el && !el.contains(e.target as Node)) setPreviewMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [previewMenuOpen]);

  const persistZoneContent = React.useCallback(
    async (zone: 'intro' | 'closing', next: OfferDocumentContentBlock[]) => {
      if (zone === 'intro') await persistIntroContent(next);
      else await persistClosingContent(next);
    },
    [persistClosingContent, persistIntroContent],
  );

  const syncOrder = React.useCallback(
    async (rows: CombinedRow[]) => {
      if (workReportInvoiceRef.current) return;
      const orderIds = collectPositionRowIds(rows.map((r) => r.leadPositionId));
      if (!orderIds.length) return;
      const res = await fetch(`${positionsApiBase}/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: orderIds }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setPositionMutateError((j as { error?: string }).error || 'Sortierung speichern fehlgeschlagen');
        return;
      }
      setPositionMutateError(null);
      const data = await res.json();
      const incoming = normalizePositions(data);
      setPositions((prev) =>
        mergePositionsKeepingInFlightEdits(
          prev,
          incoming,
          activePositionEditKeysFromCounts(activePositionFieldEditCountsRef.current),
        ),
      );
    },
    [positionsApiBase]
  );

  // Only updates local state – no API call on every keystroke (prevents flickering).
  const updateDraftRow = (id: string, patch: Partial<OfferDraftRow>) => {
    setDraftRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // Keep API-backed rows editable in the UI before blur-save.
  const updateApiRowLocal = React.useCallback(
    (leadPositionId: PositionRowId, patch: Partial<LeadPosition>) => {
      setPositions((prev) => {
        let changed = false;
        const nextRows = prev.map((p) => {
          if (!positionRowIdsEqual(p.id, leadPositionId)) return p;
          const patchKeys = Object.keys(patch) as Array<keyof LeadPosition>;
          if (patchKeys.length > 0 && patchKeys.every((key) => Object.is(p[key], patch[key]))) {
            return p;
          }
          const next = { ...p, ...patch };
          const qty = Number(next.quantity ?? 0);
          const unitVk = Number(next.unit_price_vk ?? 0);
          const line_total = lineTotal(qty, unitVk);
          changed = true;
          return { ...next, line_total };
        });
        return changed ? nextRows : prev;
      });
    },
    []
  );

  // Persist edits for API/KI rows (same UX as manual rows).
  type PositionRowSyncPatch = Partial<
    Pick<
      LeadPosition,
      | 'quantity'
      | 'unit'
      | 'item_name'
      | 'description'
      | 'internal_note'
      | 'unit_price_vk'
      | 'unit_cost_ek'
      | 'surcharge_percent'
      | 'labor_minutes'
      | 'hourly_rate'
      | 'is_eventual'
      | 'title_level'
    >
  >;

  const syncApiRowNow = React.useCallback(
    async (leadPositionId: PositionRowId, patch?: PositionRowSyncPatch) => {
      const row = positions.find((p) => positionRowIdsEqual(p.id, leadPositionId));
      if (!row) return;
      const merged = { ...row, ...patch };
      setPositionMutateError(null);
      try {
        const res = await fetch(
          `${positionsApiBase}/${leadPositionId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              quantity: merged.quantity,
              unit: merged.unit,
              item_name: merged.item_name,
              description: merged.description,
              internal_note: merged.internal_note ?? '',
              unit_cost_ek: merged.unit_cost_ek,
              surcharge_percent: merged.surcharge_percent,
              labor_minutes: merged.labor_minutes,
              unit_price_vk: merged.unit_price_vk,
              is_eventual: merged.is_eventual === true,
              ...(merged.title_level != null ? { title_level: merged.title_level } : {}),
              ...(patch && Object.prototype.hasOwnProperty.call(patch, 'hourly_rate')
                ? { hourly_rate: parseHourlyRateOverride(patch.hourly_rate) }
                : {}),
            }),
          }
        );
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error || 'Position konnte nicht gespeichert werden.');
        }
        const updated = (await res.json()) as LeadPosition;
        setPositions((prev) => {
          const withServer = prev.map((p) =>
            positionRowIdsEqual(p.id, leadPositionId) ? { ...updated, item_name: updated.item_name ?? '' } : p,
          );
          return mergePositionsKeepingInFlightEdits(
            prev,
            withServer,
            activePositionEditKeysFromCounts(activePositionFieldEditCountsRef.current),
          );
        });
      } catch (e) {
        setPositionMutateError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
        await reloadPositions();
      }
    },
    [positions, positionsApiBase, reloadPositions]
  );

  /** Notierung gilt nur für dieses Angebot: Spalten speichern, Katalogzeilen neu bepreisen. */
  const commitMetalQuotes = React.useCallback(
    async (next: {
      copper: number | null;
      aluminum: number | null;
      source: 'manual' | 'westmetall' | 'sonepar';
      quoteDate: string | null;
      procurementCostPercent: number;
    }) => {
      setCalculationCopperQuoteEurPer100kg(next.copper);
      setCalculationAluminumQuoteEurPer100kg(next.aluminum);
      setCalculationMetalQuoteSource(next.source);
      setCalculationMetalQuoteDate(next.quoteDate);
      setCalculationMetalProcurementCostPercent(next.procurementCostPercent);
      setMetalQuotesSaving(true);
      setPositionMutateError(null);
      try {
        const res = await fetch(`${positionsApiBase}/recalculate-metal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            copper_quote_eur_per_100kg: next.copper,
            aluminum_quote_eur_per_100kg: next.aluminum,
            metal_quote_source: next.source,
            metal_quote_date: next.quoteDate,
            metal_procurement_cost_percent: next.procurementCostPercent,
          }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(
            (j as { error?: string }).error || 'Metallzuschlag konnte nicht neu berechnet werden.',
          );
        }
        const incoming = normalizePositions(await res.json());
        setPositions((prev) =>
          mergePositionsKeepingInFlightEdits(
            prev,
            incoming,
            activePositionEditKeysFromCounts(activePositionFieldEditCountsRef.current),
          ),
        );
        // Draft-Zeilen überlagern die API-Preise in Tabelle und PDF und müssen mitziehen.
        const byId = new Map(incoming.map((p) => [positionRowIdKey(p.id), p]));
        let draftChanged = false;
        const nextDraftRows = draftRows.map((row) => {
          if (!isPositionRowId(row.leadPositionId)) return row;
          const updated = byId.get(positionRowIdKey(row.leadPositionId));
          if (
            !updated ||
            (row.unit_cost_ek === updated.unit_cost_ek &&
              row.unit_price_vk === updated.unit_price_vk)
          ) {
            return row;
          }
          draftChanged = true;
          return {
            ...row,
            unit_cost_ek: updated.unit_cost_ek,
            unit_price_vk: updated.unit_price_vk,
          };
        });
        if (draftChanged) {
          setDraftRows(nextDraftRows);
          await persistFull({ draftRows: nextDraftRows });
        } else {
          await onDraftSaved?.();
        }
      } catch (e) {
        setPositionMutateError(
          e instanceof Error ? e.message : 'Metallzuschlag konnte nicht neu berechnet werden.',
        );
      } finally {
        setMetalQuotesSaving(false);
      }
    },
    [draftRows, onDraftSaved, persistFull, positionsApiBase],
  );

  const removeDraftRow = (id: string) => {
    const removedIndex = draftRows.findIndex((x) => x.id === id);
    const removed = removedIndex >= 0 ? draftRows[removedIndex] : undefined;
    if (!removed) return;
    const mutateCatalog = !keepsOfferingUntouched && isPositionRowId(removed.leadPositionId);
    const removedPosition = mutateCatalog
      ? positions.find((p) => positionRowIdsEqual(p.id, removed.leadPositionId))
      : undefined;
    const nextPositions = mutateCatalog
      ? positions.filter((p) => !positionRowIdsEqual(p.id, removed.leadPositionId))
      : positions;
    const next = reconcileDraftSurchargesForTable(
      draftRows.filter((x) => x.id !== id),
      nextPositions,
      creditOrStornoMergeOptions,
      customerLaborOpts,
    );
    setDraftRows(next);
    if (!mutateCatalog) {
      void persistFull({ draftRows: next });
      return;
    }
    setPositions(nextPositions);
    void (async () => {
      try {
        await draftPersistQueueRef.current!.settle();
        const res = await fetch(`${positionsApiBase}/${removed.leadPositionId}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error || 'Position konnte nicht gelöscht werden.');
        }
        for (const row of next) {
          if (row.kind !== 'positionSurcharge' || row.leadPositionId == null) continue;
          await syncDraftRowNow(row.id, {
            positionSurcharge: row.positionSurcharge ?? undefined,
            unit_price_vk: row.unit_price_vk,
            description: row.description,
          });
        }
      } catch (err) {
        setDraftRows((prev) => {
          if (prev.some((row) => row.id === removed.id)) return prev;
          const copy = [...prev];
          copy.splice(Math.min(removedIndex, copy.length), 0, removed);
          return copy;
        });
        if (removedPosition) {
          setPositions((prev) =>
            prev.some((p) => positionRowIdsEqual(p.id, removedPosition.id)) ? prev : [...prev, removedPosition],
          );
        }
        draftErrorTokenRef.current += 1;
        setPositionMutateError(offerDraftSaveErrorMessage(err));
      }
    })();
  };

  /**
   * Mehrere Zeilen auf einmal entfernen (Titel samt Abschnitt): ein Draft-Update, danach die
   * Katalogpositionen nacheinander per API löschen — wie `removeDraftRow`/`deleteApiPosition`.
   */
  const deleteTableRows = async (rows: DeletableTableRow[]) => {
    const rowIds = new Set(rows.map((r) => r.id));
    const apiIds = keepsOfferingUntouched
      ? []
      : rows.map((r) => r.leadPositionId).filter((id): id is PositionRowId => isPositionRowId(id));
    const matchesApi = (id: PositionRowId | null) => apiIds.some((apiId) => positionRowIdsEqual(apiId, id));
    const nextPositions = positions.filter((p) => !matchesApi(p.id));
    const next = reconcileDraftSurchargesForTable(
      draftRows.filter((d) => !rowIds.has(d.id) && !matchesApi(d.leadPositionId)),
      nextPositions,
      creditOrStornoMergeOptions,
      customerLaborOpts,
    );
    setPositionMutateError(null);
    setDraftRows(next);
    setPositions(nextPositions);
    try {
      await persistFull({ draftRows: next });
      if (apiIds.length === 0) return;
      await draftPersistQueueRef.current!.settle();
      for (const id of apiIds) {
        const res = await fetch(`${positionsApiBase}/${id}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error || 'Position konnte nicht gelöscht werden.');
        }
      }
      for (const row of next) {
        if (row.kind !== 'positionSurcharge' || row.leadPositionId == null) continue;
        await syncDraftRowNow(row.id, {
          positionSurcharge: row.positionSurcharge ?? undefined,
          unit_price_vk: row.unit_price_vk,
          description: row.description,
        });
      }
      await reloadPositions();
    } catch (err) {
      setPositionMutateError(offerDraftSaveErrorMessage(err));
      await reloadPositions();
    }
  };

  /** Papierkorb einer Zeile: Titel mit Inhalt fragt nach, alles andere löscht sofort. */
  const requestRowDelete = (row: DeletableTableRow) => {
    if (row.kind === 'title') {
      const members = titleSectionRows(numberedRows, row.id);
      if (members.length > 0) {
        setTitleDeletePrompt({ title: row, members });
        return;
      }
    }
    if (row.isApi && row.leadPositionId != null) {
      void deleteApiPosition(row.leadPositionId);
    } else {
      removeDraftRow(row.id);
    }
  };

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [panelMode, setPanelMode] = React.useState<'blocks' | 'search'>('blocks');
  /** Dokument- vs. Kalkulationsansicht steht in der URL, damit der Link teilbar bleibt. */
  const editorView: 'document' | 'calculation' =
    searchParams.get('view') === 'calculation' ? 'calculation' : 'document';
  const setEditorView = React.useCallback(
    (next: 'document' | 'calculation') => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'calculation') params.set('view', 'calculation');
          else params.delete('view');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  /** `&ids=open` (Link aus dem KI-Chat, get_ids_cart_preview) öffnet den IDS-Shop-Dialog einmal. */
  const idsAutoOpen = searchParams.get('ids') === 'open';
  const clearIdsAutoOpen = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('ids');
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const positionInsertPendingRef = React.useRef(0);
  const [positionInsertBusy, setPositionInsertBusy] = React.useState(false);
  const wrapPositionInsert = React.useCallback(async <T,>(run: () => Promise<T>): Promise<T> => {
    positionInsertPendingRef.current += 1;
    if (positionInsertPendingRef.current === 1) setPositionInsertBusy(true);
    try {
      return await run();
    } finally {
      positionInsertPendingRef.current -= 1;
      if (positionInsertPendingRef.current <= 0) {
        positionInsertPendingRef.current = 0;
        setPositionInsertBusy(false);
      }
    }
  }, []);

  const createOrUpdateDraftBackendRow = React.useCallback(
    async (row: OfferDraftRow, sortOrder?: number) => {
      if (keepsOfferingUntouched) {
        return row.leadPositionId as PositionRowId;
      }
      const sourceType: LeadPosition['source_type'] =
        row.kind === 'title'
          ? 'custom_title'
          : row.kind === 'text'
            ? 'custom_text'
            : row.kind === 'image'
              ? 'custom_image'
              : row.kind === 'positionSurcharge'
                ? 'custom_surcharge'
                : 'custom_free';
      const body: Record<string, unknown> = {
        source_type: sourceType,
        quantity:
          row.kind === 'freePosition' || row.kind === 'positionSurcharge' ? row.quantity : 0,
        unit:
          row.kind === 'title'
            ? 'Titel'
            : row.kind === 'freePosition' || row.kind === 'positionSurcharge'
              ? row.unit
              : '',
        item_name: row.item_name,
        description:
          row.kind === 'image'
            ? `[Bild] ${(row.image?.alt || row.description || '').trim()}`.trim()
            : row.description,
        internal_note: row.internal_note ?? '',
        unit_cost_ek: row.unit_cost_ek ?? 0,
        surcharge_percent: row.surcharge_percent ?? 0,
        labor_minutes: row.labor_minutes ?? 0,
        unit_price_vk:
          row.kind === 'freePosition' || row.kind === 'positionSurcharge' ? row.unit_price_vk : 0,
      };
      if (row.hourly_rate !== undefined) body.hourly_rate = parseHourlyRateOverride(row.hourly_rate);
      if (typeof row.isEventual === 'boolean') body.is_eventual = row.isEventual;
      if (row.kind === 'title') body.title_level = row.titleLevel ?? MIN_TITLE_LEVEL;
      if (typeof sortOrder === 'number') body.sort_order = sortOrder;
      if (row.leadPositionId != null) {
        const res = await fetch(
          `${positionsApiBase}/${row.leadPositionId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        );
        if (!res.ok) throw new Error('Row update failed');
        return row.leadPositionId;
      }
      const res = await fetch(`${positionsApiBase}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error || 'Row create failed');
      }
      const created = (await res.json()) as LeadPosition;
      return created.id;
    },
    [leadRouteId, keepsOfferingUntouched]
  );

  // Called on blur – persists the row to the backend exactly once.
  const syncDraftRowNow = React.useCallback(
    async (rowId: string, patch?: Partial<OfferDraftRow>) => {
      let mergedRow: OfferDraftRow | null = null;
      setDraftRows((prev) => {
        const row = prev.find((r) => r.id === rowId);
        if (!row) return prev;
        mergedRow = { ...row, ...patch };
        const next = prev.map((r) => (r.id === rowId ? mergedRow! : r));
        void persistFull({ draftRows: next });
        return next;
      });
      if (!mergedRow) return;
      const sortOrder = positions.length + draftRows.findIndex((r) => r.id === rowId);
      try {
        const leadPositionId = await createOrUpdateDraftBackendRow(mergedRow, sortOrder);
        if (mergedRow.leadPositionId !== leadPositionId) {
          setDraftRows((prev) => {
            const next = prev.map((r) => (r.id === rowId ? { ...r, leadPositionId } : r));
            void persistFull({ draftRows: next });
            return next;
          });
        }
      } catch {
        // Sync errors are non-fatal; the draft is already persisted above.
      }
    },
    [createOrUpdateDraftBackendRow, draftRows, persistFull, positions.length]
  );

  const deleteApiPosition = React.useCallback(
    async (id: PositionRowId) => {
      setPositionMutateError(null);
      setDeletingPositionId(id);
      if (keepsOfferingUntouched) {
        const nextDraft = reconcileDraftSurchargesForTable(
          draftRows.filter((d) => !positionRowIdsEqual(d.leadPositionId, id)),
          positions,
          creditOrStornoMergeOptions,
          customerLaborOpts,
        );
        setDraftRows(nextDraft);
        void persistFull({ draftRows: nextDraft });
        setDeletingPositionId(null);
        return;
      }
      try {
        const res = await fetch(`${positionsApiBase}/${id}`, {
          method: 'DELETE',
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error || 'Position konnte nicht gelöscht werden.');
        }
        const nextPositions = positions.filter((p) => !positionRowIdsEqual(p.id, id));
        const nextDraft = reconcileDraftSurchargesForTable(
          draftRows.filter((d) => !positionRowIdsEqual(d.leadPositionId, id)),
          nextPositions,
          undefined,
          customerLaborOpts,
        );
        setPositions(nextPositions);
        setDraftRows(nextDraft);
        await persistFull({ draftRows: nextDraft });
        for (const row of nextDraft) {
          if (row.kind !== 'positionSurcharge' || row.leadPositionId == null) continue;
          await syncDraftRowNow(row.id, {
            positionSurcharge: row.positionSurcharge ?? undefined,
            unit_price_vk: row.unit_price_vk,
            description: row.description,
          });
        }
        await reloadPositions();
      } catch (e) {
        setPositionMutateError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
        await reloadPositions();
      } finally {
        setDeletingPositionId(null);
      }
    },
    [
      positions,
      draftRows,
      positionsApiBase,
      reloadPositions,
      persistFull,
      syncDraftRowNow,
      keepsOfferingUntouched,
      creditOrStornoMergeOptions,
      customerLaborOpts,
    ],
  );

  const handleApiPositionFieldFocus = React.useCallback((leadPositionId: PositionRowId | null | undefined) => {
    if (isPositionRowId(leadPositionId)) registerPositionFieldEdit(leadPositionId);
  }, [registerPositionFieldEdit]);

  const handleApiPositionFieldBlur = React.useCallback(
    (leadPositionId: PositionRowId | null | undefined, patch?: PositionRowSyncPatch) => {
      if (!isPositionRowId(leadPositionId)) return;
      void syncApiRowNow(leadPositionId, patch).finally(() => releasePositionFieldEdit(leadPositionId));
    },
    [syncApiRowNow, releasePositionFieldEdit],
  );

  const handlePositionFieldBlur = React.useCallback(
    (row: Pick<CombinedRow, 'id' | 'isApi' | 'leadPositionId'>, patch?: PositionRowSyncPatch) => {
      if (row.isApi) {
        handleApiPositionFieldBlur(row.leadPositionId, patch);
        return;
      }
      void syncDraftRowNow(row.id, patch);
    },
    [handleApiPositionFieldBlur, syncDraftRowNow],
  );

  const applyHourLaborEp = React.useCallback(
    (
      row: Pick<CombinedRow, 'id' | 'isApi' | 'leadPositionId' | 'hourly_rate'>,
      committed: number,
      persist: boolean,
    ) => {
      const current = parseHourlyRateOverride(row.hourly_rate);
      const toStore = hourlyRateOverrideFromEpInput(committed, current, calculationHourlyRate);
      const changed = toStore !== current;
      if (changed) {
        if (row.isApi && row.leadPositionId != null) {
          updateApiRowLocal(row.leadPositionId, { hourly_rate: toStore });
        } else {
          updateDraftRow(row.id, { hourly_rate: toStore });
        }
      }
      if (!persist) return;
      handlePositionFieldBlur(row, changed ? { hourly_rate: toStore } : undefined);
    },
    [calculationHourlyRate, handlePositionFieldBlur, updateApiRowLocal],
  );

  const handleCalculationRowCommit = React.useCallback(
    (row: OfferCalculationRow, patch: OfferCalculationPatch) => {
      if (isVirtualTitleLaborRow(row)) {
        if (typeof patch.item_name === 'string') {
          commitLaborRowName(row.id, patch.item_name, row.quantity);
        }
        return;
      }
      const nextPatch = applyHourLaborPatchIfNeeded(row, patch);
      if (row.isApi && row.leadPositionId != null) {
        updateApiRowLocal(row.leadPositionId, nextPatch);
        void syncApiRowNow(row.leadPositionId, nextPatch);
        return;
      }
      updateDraftRow(row.id, nextPatch);
      void syncDraftRowNow(row.id, nextPatch);
    },
    [commitLaborRowName, syncApiRowNow, syncDraftRowNow, updateApiRowLocal],
  );

  const toggleRowEventual = React.useCallback(
    (row: Pick<CombinedRow, 'id' | 'isApi' | 'leadPositionId' | 'isEventual'>) => {
      const next = !row.isEventual;
      if (row.isApi && row.leadPositionId != null) {
        updateApiRowLocal(row.leadPositionId, { is_eventual: next });
        void syncApiRowNow(row.leadPositionId, { is_eventual: next });
        return;
      }
      void syncDraftRowNow(row.id, { isEventual: next });
    },
    [syncApiRowNow, syncDraftRowNow, updateApiRowLocal],
  );

  const applyTitleLevel = React.useCallback(
    (row: CombinedRow, nextLevel: number) => {
      if (row.kind !== 'title') return;
      const index = numberedRows.findIndex((r) => r.id === row.id);
      const max = index >= 0 ? maxAllowedTitleLevelAt(numberedRows, index) : MIN_TITLE_LEVEL;
      const clamped = Math.max(MIN_TITLE_LEVEL, Math.min(max, Math.floor(nextLevel)));
      if (clamped === (row.titleLevel ?? MIN_TITLE_LEVEL)) return;
      if (row.isApi && row.leadPositionId != null) {
        updateApiRowLocal(row.leadPositionId, { title_level: clamped });
        void syncApiRowNow(row.leadPositionId, { title_level: clamped });
        return;
      }
      updateDraftRow(row.id, { titleLevel: clamped });
      void syncDraftRowNow(row.id, { titleLevel: clamped });
    },
    [numberedRows, syncApiRowNow, syncDraftRowNow, updateApiRowLocal],
  );

  const syncDraftRowsOrderAndPersist = React.useCallback(
    async (nextDraftRows: OfferDraftRow[]) => {
      let mutable = [...nextDraftRows];
      for (let i = 0; i < mutable.length; i += 1) {
        const r = mutable[i];
        try {
          const leadPositionId = await createOrUpdateDraftBackendRow(r, positions.length + i);
          if (r.leadPositionId !== leadPositionId) {
            mutable[i] = { ...r, leadPositionId };
          }
        } catch {
          // Continue syncing remaining rows even if one fails.
        }
      }
      setDraftRows(mutable);
      await persistFull({ draftRows: mutable });
      const merged = buildOfferMergedTableRows(
        positions,
        mutable,
        creditOrStornoMergeOptions,
      ) as CombinedRow[];
      await syncOrder(merged);
    },
    [createOrUpdateDraftBackendRow, creditOrStornoMergeOptions, persistFull, positions, syncOrder]
  );

  // On initial load: create backend rows for drafts that never got a leadPositionId, and
  // add a draft row for every position that does not have one yet (GAEB import and similar).
  React.useEffect(() => {
    if (!company || positionsLoadedSessionRef.current !== editorSessionKey) return;
    if (initialRepairDoneRef.current === editorSessionKey) return;
    initialRepairDoneRef.current = editorSessionKey;
    // Zeilen aus Arbeitsberichten sind ein Snapshot — keine Katalogpositionen anlegen/ergänzen.
    if (workReportInvoiceRef.current) return;
    const current = snapRef.current.draftRows;
    const { rows, changed } = ensureDraftRowsCoverPositions(
      positions,
      current,
      creditOrStornoMergeOptions,
    );
    const base = changed ? rows : current;
    if (changed) setDraftRows(base);
    const unlinked = base.filter((r) => r.leadPositionId == null);
    if (unlinked.length > 0) {
      void syncDraftRowsOrderAndPersist(base).catch(() => undefined);
      return;
    }
    if (changed) void persistFull({ draftRows: base });
  }, [
    company,
    positions,
    editorSessionKey,
    creditOrStornoMergeOptions,
    persistFull,
    syncDraftRowsOrderAndPersist,
  ]);

  const insertPositionSurchargeAt = React.useCallback(
    async (insertIndex: number, config: OfferPositionSurchargeConfig) =>
      wrapPositionInsert(async () => {
        const labeledRows = assignPositionNumberLabels(
          combinedRows.map((r) => ({
            id: r.id,
            kind: r.kind,
            quantity: r.quantity,
            unit_price_vk: r.unit_price_vk,
            item_name: r.item_name,
            positionSurcharge: r.positionSurcharge,
            isEventual: r.isEventual,
          })),
        );
        // Order labels by their position in the document, not by selection order.
        const description = buildSurchargeDescriptionForConfig(labeledRows, config);
        const baseSum = computeSurchargeBaseSum(labeledRows, config, insertIndex);
        const unit_price_vk = computeSurchargeLinePrice(baseSum, config);

        const draft: OfferDraftRow = {
          ...makeDraftRow('positionSurcharge'),
          item_name: POSITION_SURCHARGE_DEFAULT_TITLE,
          description,
          positionSurcharge: config,
          unit_price_vk,
          quantity: 1,
          unit: 'psch',
        };

        const targetIndex = Math.max(0, Math.min(combinedRows.length, insertIndex));
        const draftInsertIdx = combinedRows.slice(0, targetIndex).filter((r) => !r.isApi).length;

        const createBody = {
          source_type: 'custom_surcharge',
          quantity: 1,
          unit: 'psch',
          item_name: draft.item_name,
          description: draft.description,
          unit_cost_ek: 0,
          surcharge_percent: 0,
          unit_price_vk,
          sort_order: targetIndex,
        } as Record<string, unknown>;

        const res = await fetch(`${positionsApiBase}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createBody),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error || 'Einfügen fehlgeschlagen');
        }
        const created = (await res.json()) as LeadPosition;

        const linkedDraft: OfferDraftRow = { ...draft, leadPositionId: created.id };
        const di = Math.max(0, Math.min(draftRows.length, draftInsertIdx));
        const nextDraftRows = [...draftRows.slice(0, di), linkedDraft, ...draftRows.slice(di)];

        setPositions((prev) => {
          const sorted = [...prev].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          const insertAt = Math.max(0, Math.min(sorted.length, targetIndex));
          const next = [...sorted.slice(0, insertAt), created, ...sorted.slice(insertAt)];
          return next.map((p, idx) => ({ ...p, sort_order: idx, pos: idx + 1 }));
        });
        setDraftRows(nextDraftRows);
        void persistFull({ draftRows: nextDraftRows });
      }),
    [wrapPositionInsert, combinedRows, draftRows, positionsApiBase, persistFull],
  );

  const insertBlockRowAt = React.useCallback(
    async (kind: 'text' | 'title' | 'freePosition' | 'image', insertIndex: number) =>
      wrapPositionInsert(async () => {
        const targetIndex = Math.max(0, Math.min(combinedRows.length, insertIndex));
        const prevTitle = [...combinedRows.slice(0, targetIndex)].reverse().find((r) => r.kind === 'title');
        const draft = makeDraftRow(
          kind,
          kind === 'title'
            ? { titleLevel: prevTitle ? (prevTitle.titleLevel ?? MIN_TITLE_LEVEL) : MIN_TITLE_LEVEL }
            : undefined,
        );
        const draftInsertIdx = combinedRows.slice(0, targetIndex).filter((r) => !r.isApi).length;

        const sourceType: LeadPosition['source_type'] =
          kind === 'title'
            ? 'custom_title'
            : kind === 'text'
              ? 'custom_text'
              : kind === 'image'
                ? 'custom_image'
                : 'custom_free';

        const optimisticDescription =
          kind === 'image'
            ? `[Bild] ${(draft.image?.alt || draft.description || '').trim()}`.trim()
            : draft.description;
        const optimisticUnit = kind === 'title' ? 'Titel' : kind === 'freePosition' ? draft.unit : '';
        const optimisticQuantity = kind === 'freePosition' ? draft.quantity : 0;
        const optimisticUnitPrice = kind === 'freePosition' ? draft.unit_price_vk : 0;

        const createBody = {
          source_type: sourceType,
          quantity: optimisticQuantity,
          unit: optimisticUnit,
          item_name: draft.item_name,
          description: optimisticDescription,
          unit_cost_ek: draft.unit_cost_ek ?? 0,
          surcharge_percent: draft.surcharge_percent ?? 0,
          unit_price_vk: optimisticUnitPrice,
          sort_order: targetIndex,
          ...(kind === 'title' ? { title_level: draft.titleLevel ?? MIN_TITLE_LEVEL } : {}),
        } as Record<string, unknown>;

        const res = await fetch(`${positionsApiBase}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createBody),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error || 'Einfügen fehlgeschlagen');
        }
        const created = (await res.json()) as LeadPosition;

        const linkedDraft: OfferDraftRow = { ...draft, leadPositionId: created.id };
        const di = Math.max(0, Math.min(draftRows.length, draftInsertIdx));
        const nextDraftRows = [...draftRows.slice(0, di), linkedDraft, ...draftRows.slice(di)];

        setPositions((prev) => {
          const sorted = [...prev].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          const insertAt = Math.max(0, Math.min(sorted.length, targetIndex));
          const next = [...sorted.slice(0, insertAt), created, ...sorted.slice(insertAt)];
          return next.map((p, idx) => ({ ...p, sort_order: idx, pos: idx + 1 }));
        });
        setDraftRows(nextDraftRows);
        void persistFull({ draftRows: nextDraftRows });
      }),
    [wrapPositionInsert, combinedRows, draftRows, positionsApiBase, persistFull],
  );

  const insertPickerItemAt = React.useCallback(
    async (
      kind: 'material' | 'leistung',
      id: string,
      insertIndex?: number,
      _seed?: { name?: string; unit?: string | null; gross_price?: number | null; description?: string | null }
    ) =>
      wrapPositionInsert(async () => {
        const payload: Record<string, unknown> = kind === 'material' ? { material_id: id } : { leistung_id: id };
        if (typeof insertIndex === 'number') payload.sort_order = Math.max(0, insertIndex);
        const res = await fetch(`${positionsApiBase}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error || 'Einfügen fehlgeschlagen');
        }
        await reloadPositions();
      }),
    [wrapPositionInsert, leadRouteId, reloadPositions]
  );

  const partialInvoicePreviewRow = React.useMemo(
    () => ({
      id: 'partial-invoice-preview-row',
      kind: 'freePosition' as const,
      leadPositionId: null,
      sourceType: 'custom_free' as const,
      quantity: 1,
      unit: 'psch',
      item_name: effectivePartialInvoiceLineName,
      description: effectivePartialDescription,
      internal_note: '',
      unit_price_vk: baseNetTotal,
      unit_cost_ek: 0,
      surcharge_percent: 0,
      image: null,
      isApi: false,
      article_number: null,
      isEventual: false,
      numberLabel: '1',
    }),
    [baseNetTotal, effectivePartialDescription, effectivePartialInvoiceLineName],
  );
  const tableRows = isLumpSumPartialInvoice ? [partialInvoicePreviewRow] : numberedRows;
  // Pos.-Spalte wächst mit der längsten Nummer (z. B. „2.10“, „01.02.0010“), sonst überdeckt die Menge sie.
  // Reine Länge statt %-Mix: Chrome behandelt calc()/max() mit % bei Tabellenspalten als auto.
  const positionColumnStyle = React.useMemo<React.CSSProperties>(() => {
    const longest = tableRows.reduce((max, r) => Math.max(max, (r.numberLabel ?? '').length), 0);
    // Mindestens „Pos.“; Griff (14px + 4px Padding + 4px Abstand) + Zellpadding links 8pt / rechts 8px.
    return { width: `calc(${(Math.max(longest, 4) * POSITION_TABLE_CH_PER_CHAR + 0.5).toFixed(2)}ch + 22px + 8pt + 8px)` };
  }, [tableRows]);
  // Einheit ebenso nach Inhalt: Picker-Padding/Rahmen 18px + Chevron 18px + Zellpadding 16px.
  const unitColumnStyle = React.useMemo<React.CSSProperties>(() => {
    const longest = tableRows.reduce((max, r) => Math.max(max, (r.unit ?? '').length), 0);
    return { width: `calc(${(Math.min(Math.max(longest, 4), 8) * POSITION_TABLE_CH_PER_CHAR + 0.5).toFixed(2)}ch + 52px)` };
  }, [tableRows]);
  // EP/GP nach längstem Betrag statt fester %, damit die Beschreibung den Platz bekommt.
  // Zellpadding 8px + EP-Eingabefeld (Padding/Rahmen 18px).
  const amountColumnStyle = React.useMemo<React.CSSProperties>(() => {
    const longest = tableRows.reduce(
      (max, r) =>
        Math.max(
          max,
          formatEuro(r.unit_price_vk ?? 0).length,
          formatEuro((r.unit_price_vk ?? 0) * (r.quantity ?? 0)).length,
        ),
      0,
    );
    return { width: `calc(${(Math.max(longest, 8) * POSITION_TABLE_CH_PER_CHAR + 1).toFixed(2)}ch + 26px)` };
  }, [tableRows]);
  const titleSubtotalByAfterRowId = React.useMemo(() => {
    if (isLumpSumPartialInvoice || hidePositionPrices) return new Map<string, ReturnType<typeof computeTitleSubtotals>>();
    const source = showPositionBillingColumns ? rowsForPositionBillingTotals : numberedRows;
    return groupTitleSubtotalsByAfterRowId(computeTitleSubtotals(source));
  }, [
    isLumpSumPartialInvoice,
    hidePositionPrices,
    showPositionBillingColumns,
    rowsForPositionBillingTotals,
    numberedRows,
  ]);

  const virtualizeDocumentTable =
    editorView === 'document' &&
    !isLumpSumPartialInvoice &&
    tableRows.length >= DOCUMENT_VIRTUALIZE_MIN_ROWS;
  const documentTableRowIds = React.useMemo(() => tableRows.map((row) => row.id), [tableRows]);
  const estimateDocumentTableRowHeight = React.useCallback(
    (index: number) => {
      const row = tableRows[index];
      if (!row) return 48;
      return estimateDocumentRowPx(row, {
        hideDescriptions: hidePositionDescriptions,
        expanded: expandedComponentRowIds.has(row.id),
        hasTitleSubtotal: (titleSubtotalByAfterRowId.get(row.id)?.length ?? 0) > 0,
        titleSubtotalCount: titleSubtotalByAfterRowId.get(row.id)?.length ?? 0,
      });
    },
    [expandedComponentRowIds, hidePositionDescriptions, tableRows, titleSubtotalByAfterRowId],
  );
  const documentRowWindow = useOfferTableRowWindow({
    enabled: virtualizeDocumentTable,
    rowIds: documentTableRowIds,
    estimateHeight: estimateDocumentTableRowHeight,
    scrollEl: documentScrollEl,
    tableEl: documentTableEl,
    scale: sheetScale,
  });
  documentRowLayoutRef.current = documentRowWindow.layoutRef.current;

  const titleTotalsSummaryList = React.useMemo(() => {
    if (
      !layoutShowsTitleTotalsSummary(documentLayout) ||
      totalsHidden ||
      isLumpSumPartialInvoice ||
      hidePositionPrices
    ) {
      return [];
    }
    const source = showPositionBillingColumns ? rowsForPositionBillingTotals : numberedRows;
    return computeTitleSubtotals(source);
  }, [
    documentLayout,
    totalsHidden,
    isLumpSumPartialInvoice,
    hidePositionPrices,
    showPositionBillingColumns,
    rowsForPositionBillingTotals,
    numberedRows,
  ]);

  const onRowImageUpload = React.useCallback((rowId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      if (!src) return;
      setDraftRows((prev) => {
        const next = prev.map((r) =>
          r.id === rowId ? { ...r, image: { src, alt: file.name, filename: file.name } } : r
        );
        void persistFull({ draftRows: next });
        return next;
      });
    };
    reader.readAsDataURL(file);
  }, [persistFull]);

  const onTableDragEnd = React.useCallback(
    async (event: DragEndEvent) => {
      const { active } = event;
      const source = active.data.current?.source;
      const tableEl = positionTableRef.current;
      const documentPreviewEl = documentPreviewRef.current;
      const pointer = lastPointerRef.current;
      const pointerOverDocument =
        Boolean(documentPreviewEl && pointer && isPointInRect(pointer, documentPreviewEl.getBoundingClientRect()));

      if (isPaletteDragSource(source) && !pointerOverDocument) {
        return;
      }

      const dropIndex =
        source === 'block' || source === 'picker'
          ? resolvePaletteDropIndex(
              event,
              numberedRows,
              tableEl,
              pointer,
              documentRowLayoutRef.current,
              documentPreviewEl,
            )
          : resolveTableDropIndex(event, numberedRows);
      if (dropIndex == null) return;
      const storedDropIndex = countNonVirtualRowsBefore(numberedRows, dropIndex);
      setPositionMutateError(null);
      try {
        if (source === 'block') {
          const kind = resolveBlockPaletteKind(active);
          if (!kind) return;
          if (kind === 'positionSurcharge') {
            setSurchargeModalState({ mode: 'insert', insertIndex: storedDropIndex });
            return;
          }
          await insertBlockRowAt(kind, storedDropIndex);
          return;
        }
        if (source === 'picker') {
          await insertPickerItemAt(
            active.data.current?.kind as 'material' | 'leistung',
            String(active.data.current?.id ?? ''),
            storedDropIndex,
            {
              name: String(active.data.current?.name ?? ''),
              unit: (active.data.current?.unit as string | null | undefined) ?? null,
              gross_price:
                typeof active.data.current?.gross_price === 'number' ? (active.data.current?.gross_price as number) : null,
              description: (active.data.current?.description as string | null | undefined) ?? null,
            }
          );
          return;
        }
        if (source === 'row') {
          const rowId = String(active.data.current?.rowId ?? '');
          const from = numberedRows.findIndex((r) => r.id === rowId);
          if (from < 0) return;
          const next = [...numberedRows];
          const [moved] = next.splice(from, 1);
          const insertAt = dropIndex > from ? dropIndex - 1 : dropIndex;
          next.splice(Math.max(0, Math.min(next.length, insertAt)), 0, moved);
          let nextDraft = next
            .filter((r) => !isVirtualTitleLaborRow(r))
            .map((r) => {
              const existing =
                draftRows.find((d) => d.id === r.id) ??
                (isPositionRowId(r.leadPositionId)
                  ? draftRows.find(
                      (d) =>
                        isPositionRowId(d.leadPositionId) &&
                        positionRowIdsEqual(d.leadPositionId, r.leadPositionId as PositionRowId),
                    )
                  : undefined);
              if (existing) {
                return {
                  ...existing,
                  leadPositionId: r.leadPositionId ?? existing.leadPositionId,
                  unit_price_vk: r.unit_price_vk,
                };
              }
              if (r.isApi) return draftRowFromMergedTableRow(r);
              return {
                id: r.id,
                kind: r.kind,
                leadPositionId: r.leadPositionId,
                sourceType: r.sourceType,
                quantity: r.quantity,
                unit: r.unit,
                item_name: r.item_name,
                description: r.description,
                internal_note: r.internal_note,
                unit_price_vk: r.unit_price_vk,
                unit_cost_ek: r.unit_cost_ek,
                surcharge_percent: r.surcharge_percent,
                image: r.image,
                positionSurcharge: r.positionSurcharge ?? null,
              } as OfferDraftRow;
            });
          const reconciledById = new Map(
            reconcileSurchargeDraftRows(draftRows, next).map((d) => [d.id, d]),
          );
          nextDraft = nextDraft.map((d) => {
            const reconciled = reconciledById.get(d.id);
            if (!reconciled) return d;
            return {
              ...d,
              positionSurcharge: reconciled.positionSurcharge ?? null,
              unit_price_vk: reconciled.unit_price_vk,
              description: reconciled.description,
            };
          });
          setDraftRows(nextDraft);
          // Keep `positions.sort_order` in sync with the table (combinedRows order follows `draftRows`).
          setPositions((prev) => {
            const byId = new Map(prev.map((p) => [positionRowIdKey(p.id), p]));
            const seen = new Set<string>();
            const reordered: LeadPosition[] = [];
            for (const r of next) {
              const lid = r.leadPositionId;
              if (!isPositionRowId(lid)) continue;
              const key = positionRowIdKey(lid);
              const base = byId.get(key);
              if (!base) continue;
              seen.add(key);
              reordered.push({ ...base, sort_order: reordered.length, pos: reordered.length + 1 });
            }
            const rest = prev
              .filter((p) => !seen.has(positionRowIdKey(p.id)))
              .map((p, i) => ({
                ...p,
                sort_order: reordered.length + i,
                pos: reordered.length + i + 1,
              }));
            return [...reordered, ...rest];
          });
          const hadMissingLeadPositionIds = nextDraft.some((r) => r.leadPositionId == null);
          await persistFull({ draftRows: nextDraft });
          for (let i = 0; i < nextDraft.length; i += 1) {
            const r = nextDraft[i];
            if (r.leadPositionId == null) {
              const leadPositionId = await createOrUpdateDraftBackendRow(r, i);
              nextDraft[i] = { ...r, leadPositionId };
            }
          }
          setDraftRows(nextDraft);
          await persistFull({ draftRows: nextDraft });
          const orderedLeadIds = collectPositionRowIds(
            next.map((r) => {
              if (r.isApi) return r.leadPositionId;
              const match = nextDraft.find((d) => d.id === r.id);
              return match?.leadPositionId ?? null;
            }),
          );
          const orderRes = await fetch(`${positionsApiBase}/order`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order: orderedLeadIds }),
          });
          if (!orderRes.ok) {
            const j = await orderRes.json().catch(() => ({}));
            throw new Error((j as { error?: string }).error || 'Sortierung speichern fehlgeschlagen');
          }
          if (hadMissingLeadPositionIds) await reloadPositions();
        }
      } catch (e) {
        setPositionMutateError(e instanceof Error ? e.message : 'Drag & Drop fehlgeschlagen');
        await reloadPositions();
      }
    },
    [
      createOrUpdateDraftBackendRow,
      insertBlockRowAt,
      insertPickerItemAt,
      leadRouteId,
      numberedRows,
      persistFull,
      reloadPositions,
      draftRows,
    ]
  );

  const onTableDragStart = React.useCallback((event: DragStartEvent) => {
    const source = event.active.data.current?.source;
    if (source === 'block') {
      setActiveDragPreview({
        label: BLOCK_PALETTE_LABEL_BY_ID[String(event.active.id)] ?? 'Baustein',
        variant: 'palette',
      });
      return;
    }
    if (source === 'picker') {
      const name = String(event.active.data.current?.name ?? '').trim();
      setActiveDragPreview({
        label: name || 'Material/Leistung',
        variant: 'palette',
      });
      return;
    }
    if (source === 'row') {
      setActiveDragPreview({ label: 'Zeile verschieben', variant: 'compact' });
      return;
    }
    setActiveDragPreview(null);
  }, []);

  const onTableDragCancel = React.useCallback(() => {
    setActiveDragPreview(null);
  }, []);

  const surchargeModalCandidates = React.useMemo(() => {
    if (!surchargeModalState) return [];
    const beforeIndex =
      surchargeModalState.mode === 'insert'
        ? surchargeModalState.insertIndex
        : numberedRows.findIndex((r) => r.id === surchargeModalState.rowId);
    if (beforeIndex < 0) return [];
    return listEligibleSurchargeTargets(numberedRows, beforeIndex);
  }, [surchargeModalState, numberedRows]);

  const surchargeModalInitial = React.useMemo(() => {
    if (!surchargeModalState || surchargeModalState.mode === 'insert') {
      return { percent: 20, amount: null as number | null, surchargeMode: 'percent' as const, targetRowIds: [] as string[] };
    }
    const row = draftRows.find((r) => r.id === surchargeModalState.rowId);
    const config = row?.positionSurcharge;
    return {
      percent: config?.percent ?? 20,
      amount: config?.amount ?? null,
      surchargeMode: config?.mode === 'amount' ? ('amount' as const) : ('percent' as const),
      targetRowIds: config?.targetRowIds ?? [],
    };
  }, [surchargeModalState, draftRows]);

  const handleSurchargeModalConfirm = React.useCallback(
    async (config: OfferPositionSurchargeConfig) => {
      if (!surchargeModalState) return;
      setPositionMutateError(null);
      try {
        if (surchargeModalState.mode === 'insert') {
          await insertPositionSurchargeAt(surchargeModalState.insertIndex, config);
          return;
        }
        const rowId = surchargeModalState.rowId;
        const rowIndex = numberedRows.findIndex((r) => r.id === rowId);
        const labeled = assignPositionNumberLabels(
          numberedRows.map((r) => ({
            id: r.id,
            kind: r.kind,
            quantity: r.quantity,
            unit_price_vk: r.unit_price_vk,
            item_name: r.item_name,
            positionSurcharge: r.id === rowId ? config : r.positionSurcharge,
            isEventual: r.isEventual,
          })),
        );
        const baseSum = computeSurchargeBaseSum(labeled, config, rowIndex);
        const unit_price_vk = computeSurchargeLinePrice(baseSum, config);
        const prevConfig = draftRows.find((r) => r.id === rowId)?.positionSurcharge;
        const patch: Partial<OfferDraftRow> = { positionSurcharge: config, unit_price_vk };
        if (surchargeConfigTargetsOrPercentChanged(prevConfig, config)) {
          patch.description = buildSurchargeDescriptionForConfig(labeled, config);
        }
        updateDraftRow(rowId, patch);
        await syncDraftRowNow(rowId, patch);
      } catch (e) {
        setPositionMutateError(e instanceof Error ? e.message : 'Zuschlag speichern fehlgeschlagen');
        throw e;
      }
    },
    [surchargeModalState, insertPositionSurchargeAt, numberedRows, draftRows, syncDraftRowNow],
  );

  React.useEffect(() => {
    if (isLumpSumPartialInvoice) return;
    for (const row of numberedRows) {
      if (row.kind !== 'positionSurcharge' || row.isApi) continue;
      const prev = surchargeVkSyncRef.current[row.id];
      if (prev === row.unit_price_vk) continue;
      surchargeVkSyncRef.current[row.id] = row.unit_price_vk;
      void syncDraftRowNow(row.id, { unit_price_vk: row.unit_price_vk });
    }
  }, [numberedRows, isLumpSumPartialInvoice, syncDraftRowNow]);

  const companyLines = company ? formatCompanyAddressLines(company) : [];
  const websiteUrl = company ? formatCompanyWebsiteUrl(company.website) : '';
  const websiteHref = websiteUrl || null;
  const websiteDisplay = company?.website
    ? company.website.replace(/^https?:\/\//i, '') || company.website
    : '';
  const secondaryMetaKind = React.useMemo(
    () => documentSecondaryMetaKind(documentTypeLabel),
    [documentTypeLabel],
  );
  const invoiceLzRequired = documentShowsLeistungszeitraum(documentTypeLabel);
  /** Rechnung/Schluss/Storno/Gutschrift: Pflicht. Abschlagsrechnung: per Default sichtbar, ausblendbar. */
  const lzHideLocked = documentLocksLeistungszeitraum(documentTypeLabel);
  const lzHidden = secondaryMetaKind === 'leistungszeitraum' && !lzHideLocked && hideLeistungszeitraum;
  const invoiceLzResetValue = React.useMemo(
    () => (invoiceLzRequired ? resolveInvoiceLeistungszeitraum({ issueDate: issueDateEdit }) : null),
    [invoiceLzRequired, issueDateEdit],
  );
  const effectiveLeistungszeitraum = React.useMemo(
    () =>
      invoiceLzRequired
        ? resolveInvoiceLeistungszeitraum({
            leistungszeitraum: leistungszeitraumEdit,
            issueDate: issueDateEdit,
          })
        : parseOfferLeistungszeitraum(leistungszeitraumEdit),
    [invoiceLzRequired, leistungszeitraumEdit, issueDateEdit],
  );
  const canHideSecondaryMeta =
    secondaryMetaKind === 'lieferdatum' ||
    (secondaryMetaKind === 'leistungszeitraum' && !lzHideLocked);
  const lzEditInvoiceProps = {
    ...(secondaryMetaKind === 'leistungszeitraum' && invoiceLzRequired
      ? { clearMode: 'reset' as const, resetValue: invoiceLzResetValue }
      : {}),
    // Ausblenden direkt im Popup — auch für Vorlagen, die den Zeitraum in einer Textzeile zeigen.
    ...(secondaryMetaKind === 'leistungszeitraum' && !lzHideLocked
      ? {
          onHide: async () => {
            setHideLeistungszeitraum(true);
            await persistFull({ hideLeistungszeitraum: true });
          },
        }
      : {}),
  };
  /** Editor-Hinweis, solange der Zeitraum ausgeblendet ist (erscheint nicht im PDF). */
  const lzRestoreLink = lzHidden ? (
    <button
      type="button"
      className="text-sky-600 hover:underline"
      onClick={() => {
        setHideLeistungszeitraum(false);
        void persistFull({ hideLeistungszeitraum: false });
      }}
    >
      Leistungszeitraum einblenden
    </button>
  ) : null;
  const showSecondaryMeta = React.useMemo(() => {
    if (secondaryMetaKind === 'leistungszeitraum') {
      if (lzHideLocked) return true;
      return !hideLeistungszeitraum;
    }
    if (secondaryMetaKind === 'lieferdatum') return !hideLieferdatum;
    return false;
  }, [secondaryMetaKind, lzHideLocked, hideLeistungszeitraum, hideLieferdatum]);
  const secondaryMetaLabel = secondaryMetaKind ? secondaryMetaFieldLabel(secondaryMetaKind) : '';
  const secondaryMetaValue =
    secondaryMetaKind === 'lieferdatum' ? lieferdatumEdit : effectiveLeistungszeitraum;
  const secondaryMetaDisplay =
    secondaryMetaKind === 'lieferdatum'
      ? formatLeistungszeitraumDisplay(lieferdatumEdit)
      : formatLeistungszeitraumDisplay(effectiveLeistungszeitraum);
  const hasSecondaryMetaValue = parseOfferLeistungszeitraum(secondaryMetaValue) != null;
  /** Empty string omits optional freeText clauses (e.g. Lieferdatum) in bands. Invoices always show. */
  const bandLeistungszeitraumDisplay =
    secondaryMetaKind != null &&
    hasSecondaryMetaValue &&
    !lzHidden &&
    !(secondaryMetaKind === 'lieferdatum' && hideLieferdatum)
      ? secondaryMetaDisplay
      : '';

  const metaLabelClass =
    'block text-[9.5pt] font-normal text-app-text-muted leading-tight mb-0';
  const metaHideBtnClass =
    'text-[8.5pt] font-medium text-sky-500 hover:text-sky-600 underline-offset-2 hover:underline shrink-0';
  const directorLines = company ? formatDirectorFooterLines(company) : [];
  const registryLine = company ? formatRegistryLine(company) : null;

  const ansprechNames = currentUserName;
  const ansprechRole = '';
  const showAnsprechBlock = Boolean(ansprechNames);
  const ansprechMail = (company?.email ?? '').trim();
  const ansprechLine =
    ansprechRole && ansprechNames
      ? `${ansprechRole}: ${ansprechNames}`
      : ansprechNames || ansprechRole;

  const documentHeadingLine1Preview = `${documentTypeLabel} ${documentExportRefPreview}`;
  const layoutPreviewPending = isMdViewport && !layoutPreviewReady;
  const showLayoutBands = layoutPreviewReady && documentLayout != null && isMdViewport;
  const bodyFontSizePt = documentLayout?.body.fontSizePt || 9.5;
  const pageMargins = resolveDocumentPageMargins(documentLayout);
  const tableHeaderBg = documentLayout?.body.tableHeaderBg || '#eceef1';

  const [paymentQrDataUrl, setPaymentQrDataUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!documentLayout || !layoutShowsPaymentQr(documentLayout)) {
      setPaymentQrDataUrl(null);
      return;
    }
    const remittance =
      documentHeadingLine1Preview.replace(new RegExp(`^${documentTypeLabel}\\s+`), '').trim() ||
      documentHeadingLine1Preview.trim();
    let cancelled = false;
    void resolvePaymentQrDataUrl(
      paymentQrResolveInput({
        company,
        documentTypeLabel,
        grossTotal:
          payableTotals.payableAmount > 0 &&
          (payableTotals.retentionGross > 0 || payableTotals.priorRetentionsDueGross > 0)
            ? payableTotals.payableAmount
            : grossTotal,
        remittanceText: remittance,
        layout: documentLayout,
      }),
    ).then((url) => {
      if (!cancelled) setPaymentQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [
    documentLayout,
    company,
    documentTypeLabel,
    documentHeadingLine1Preview,
    grossTotal,
    payableTotals.payableAmount,
    payableTotals.retentionGross,
    payableTotals.priorRetentionsDueGross,
  ]);

  const paymentQrAfterGreeting =
    Boolean(paymentQrDataUrl) && layoutPaymentQrPlacement(documentLayout) === 'afterGreeting';
  const paymentQrBesideTotals = Boolean(paymentQrDataUrl) && !paymentQrAfterGreeting;
  const paymentQrAlign = layoutPaymentQrAlign(documentLayout);
  const paymentQrPreview = paymentQrDataUrl ? (
    <div
      className="flex shrink-0 flex-col items-center"
      style={{ width: `${PAYMENT_QR_SIZE_PT + 8}pt` }}
    >
      <img
        src={paymentQrDataUrl}
        alt="GiroCode"
        width={PAYMENT_QR_SIZE_PT}
        height={PAYMENT_QR_SIZE_PT}
        style={{ width: `${PAYMENT_QR_SIZE_PT}pt`, height: `${PAYMENT_QR_SIZE_PT}pt` }}
        draggable={false}
      />
      <p
        className="mt-1 text-center text-neutral-500 whitespace-pre-line"
        style={{ fontSize: '6.5pt', lineHeight: 1.25 }}
      >
        {PAYMENT_QR_CAPTION}
      </p>
    </div>
  ) : null;

  const offerBandData = React.useMemo<PdfLayoutBandData | null>(() => {
    if (!documentLayout) return null;
    return {
      company,
      logoUrl: company?.has_logo ? companyLogoSrc(urlInstanceId, logoBust) : null,
      assetUrls: layoutHtmlAssetUrls,
      recipientNameLines: recipientLayout.nameLines,
      recipientStreetLine: recipientLayout.streetLine,
      recipientPlzOrtLine: recipientLayout.plzOrtLine,
      companyLines,
      websiteDisplay,
      websiteHref,
      directorLines,
      registryLine,
      ansprechLine,
      showAnsprechBlock,
      ansprechMail,
      documentHeadingLine1: documentHeadingLine1Preview,
      offerTitleRest,
      hideDocumentTypeInTitle: hideTitleTypePrefix,
      documentTypeLabel,
      leistungsort: leistungsortEdit,
      leistungszeitraumDisplay: bandLeistungszeitraumDisplay,
      todayLabel: issueDateEdit,
      customerNumberLabel: resolveCustomerNumberLabel(lead),
      projectNumberLabel: (lead.display_id ?? '').trim() || null,
      orderNumberLabel: resolveOrderNumberLabel(documentTypeLabel, orderNumberEdit),
      orderTotalLabel: isPartialInvoice ? formatEuro(effectiveOrderTotalNet) : null,
      // Match PDF: number only (type is the field label), not the full heading.
      invoiceNumberLabel:
        documentHeadingLine1Preview
          .replace(new RegExp(`^${documentTypeLabel}\\s+`), '')
          .trim() || null,
    };
  }, [
    documentLayout,
    company,
    urlInstanceId,
    logoBust,
    layoutHtmlAssetUrls,
    recipientLayout.nameLines,
    recipientLayout.streetLine,
    recipientLayout.plzOrtLine,
    companyLines,
    websiteDisplay,
    websiteHref,
    directorLines,
    registryLine,
    ansprechLine,
    showAnsprechBlock,
    ansprechMail,
    documentHeadingLine1Preview,
    offerTitleRest,
    hideTitleTypePrefix,
    documentTypeLabel,
    leistungsortEdit,
    bandLeistungszeitraumDisplay,
    issueDateEdit,
    orderNumberEdit,
    isPartialInvoice,
    effectiveOrderTotalNet,
    lead,
  ]);

  React.useEffect(() => {
    if (!showLayoutBands || layoutPreviewPending) {
      setSheetFitScale(1);
      setSheetPaneWidthRatio(1);
      setSheetLayoutHeightPx(null);
      return;
    }
    const pane = documentPreviewRef.current;
    const inner = sheetInnerRef.current;
    if (!pane || !inner) return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const a4CssPx = PDF_A4_WIDTH_PT * (96 / 72);
        // The sheet box grows with the user zoom, so measure the pane's content box instead.
        const cs = window.getComputedStyle(pane);
        const w =
          pane.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
        const ratio = w > 0 ? w / a4CssPx : 1;
        const nextScale = ratio < 1 ? ratio : 1;
        const nextHeight = Math.max(inner.scrollHeight, inner.offsetHeight);
        setSheetFitScale((prev) => (prev === nextScale ? prev : nextScale));
        setSheetPaneWidthRatio((prev) => (Math.abs(prev - ratio) < 0.001 ? prev : ratio));
        setSheetLayoutHeightPx((prev) =>
          prev != null && Math.abs(prev - nextHeight) < 0.5 ? prev : nextHeight,
        );
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(pane);
    ro.observe(inner);
    const imgs = [...inner.querySelectorAll('img')];
    for (const img of imgs) img.addEventListener('load', update);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      for (const img of imgs) img.removeEventListener('load', update);
    };
  }, [
    showLayoutBands,
    layoutPreviewPending,
    documentLayout,
    afterTotalsParas,
    closingBlocks,
    introBlocks,
    introLetterParas,
    introContent,
    closingContent,
    draftRows.length,
    totalsHidden,
  ]);

  const documentZoomEnabled = editorView === 'document' && showLayoutBands;

  React.useEffect(() => {
    try {
      window.localStorage.setItem(DOCUMENT_ZOOM_STORAGE_KEY, String(documentZoom));
    } catch {
      /* storage unavailable — zoom just isn't remembered */
    }
  }, [documentZoom]);

  /** Remembers the sheet point in the middle of the pane, so button zoom keeps that spot. */
  const recordZoomAnchor = React.useCallback((clientX?: number, clientY?: number) => {
    const pane = documentPreviewRef.current;
    const sheet = sheetOuterRef.current;
    if (!pane || !sheet) return;
    const view = pane.getBoundingClientRect();
    const page = sheet.getBoundingClientRect();
    const cx = clientX ?? view.left + view.width / 2;
    const cy = clientY ?? view.top + view.height / 2;
    zoomAnchorRef.current = {
      sheetX: (cx - page.left) / sheetScaleRef.current,
      sheetY: (cy - page.top) / sheetScaleRef.current,
      clientX: cx,
      clientY: cy,
    };
  }, []);

  const applyDocumentZoom = React.useCallback(
    (next: number | ((z: number) => number)) => {
      recordZoomAnchor();
      setDocumentZoom((z) => clampDocumentZoom(typeof next === 'function' ? next(z) : next));
    },
    [recordZoomAnchor],
  );

  const zoomDocumentToPageWidth = React.useCallback(() => {
    // 100 % already fits the pane on narrow screens; wider panes let the sheet fill them.
    applyDocumentZoom(sheetPaneWidthRatio / sheetFitScale);
  }, [applyDocumentZoom, sheetFitScale, sheetPaneWidthRatio]);

  // Keep the anchored sheet point under the cursor / pane centre after the scale changed.
  React.useLayoutEffect(() => {
    if (appliedSheetScaleRef.current === sheetScale) return;
    appliedSheetScaleRef.current = sheetScale;
    const anchor = zoomAnchorRef.current;
    zoomAnchorRef.current = null;
    const pane = documentPreviewRef.current;
    const sheet = sheetOuterRef.current;
    if (!anchor || !pane || !sheet) return;
    const page = sheet.getBoundingClientRect();
    pane.scrollLeft += page.left + anchor.sheetX * sheetScale - anchor.clientX;
    pane.scrollTop += page.top + anchor.sheetY * sheetScale - anchor.clientY;
  }, [sheetScale]);

  // Ctrl/Cmd + wheel (and trackpad pinch) zooms the sheet instead of the browser page.
  React.useEffect(() => {
    const pane = documentScrollEl;
    if (!pane || !documentZoomEnabled) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      recordZoomAnchor(e.clientX, e.clientY);
      const magnitude = Math.min(3, Math.max(0.2, Math.abs(e.deltaY) / 50));
      const delta = (e.deltaY < 0 ? 1 : -1) * 0.05 * magnitude;
      setDocumentZoom((z) => clampDocumentZoom(z * (1 + delta)));
    };
    pane.addEventListener('wheel', onWheel, { passive: false });
    return () => pane.removeEventListener('wheel', onWheel);
  }, [documentScrollEl, documentZoomEnabled, recordZoomAnchor]);

  // Ctrl/Cmd + „+“ / „-“ / „0“ — only outside text fields so editing shortcuts stay untouched.
  React.useEffect(() => {
    if (!documentZoomEnabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        applyDocumentZoom((z) => stepDocumentZoom(z, 1));
      } else if (e.key === '-') {
        e.preventDefault();
        applyDocumentZoom((z) => stepDocumentZoom(z, -1));
      } else if (e.key === '0') {
        e.preventDefault();
        applyDocumentZoom(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applyDocumentZoom, documentZoomEnabled]);

  const renderBandOverride = React.useCallback(
    (el: LayoutElement): React.ReactNode | undefined => {
      const fs = el.fontSizePt ?? 9.5;
      const color = el.color || '#334155';
      const baseText: React.CSSProperties = {
        fontSize: `${fs}pt`,
        ...OFFER_PDF_FONT_STYLE,
        color,
        fontWeight: el.bold ? 700 : 400,
        fontStyle: el.italic ? 'italic' : 'normal',
        textDecoration: el.underline ? 'underline' : 'none',
        textAlign: resolveLayoutTextAlign(el.align),
        lineHeight: 1.35,
        width: '100%',
      };
      const alignClass = layoutAlignTextClass(el.align);

      if (el.key === 'recipient') {
        return (
          <div style={baseText}>
            <OfferInlineEdit
              value={recipientLayout.nameLines.join('\n')}
              onSave={async (v) => {
                const next = v
                  .split(/\r?\n/)
                  .map((x) => x.trim())
                  .filter(Boolean);
                setRecipientNameLinesEdit(next);
                await persistFull({ recipientNameLines: next });
              }}
              multiline
              viewTextClassName="leading-[1.35]"
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              aria-label="Empfänger Name/Firma"
            />
            <OfferInlineEdit
              value={recipientLayout.streetLine ?? ''}
              onSave={async (v) => {
                setRecipientStreetLineEdit(v);
                await persistFull({ recipientStreetLine: v });
              }}
              multiline={false}
              viewTextClassName="leading-[1.35]"
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              emptyHint="Straße und Hausnummer"
              aria-label="Empfänger Straße"
            />
            <OfferInlineEdit
              value={recipientLayout.plzOrtLine ?? ''}
              onSave={async (v) => {
                setRecipientPlzOrtLineEdit(v);
                await persistFull({ recipientPlzOrtLine: v });
              }}
              multiline={false}
              viewTextClassName="leading-[1.35]"
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              emptyHint="PLZ Ort"
              aria-label="Empfänger PLZ Ort"
            />
          </div>
        );
      }

      if (el.key === 'offerTitle') {
        return (
          <div style={baseText}>
            <OfferInlineEdit
              value={offerTitleEditValue}
              onSave={saveOfferTitleLine}
              multiline={false}
              viewContent={
                <span
                  className="flex w-full flex-wrap items-baseline"
                  style={{ justifyContent: layoutAlignToFlexJustify(el.align) }}
                >
                  {offerTitleParts.prefix ? (
                    <span style={{ whiteSpace: 'pre' }}>{offerTitleParts.prefix}</span>
                  ) : null}
                  <span className="min-w-0">{offerTitleParts.title}</span>
                </span>
              }
              viewTextClassName="leading-[1.35]"
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              editInputClassName="font-inherit"
              aria-label="Angebotstitel im Dokument"
            />
          </div>
        );
      }

      // Template often merges Ort/Zeitraum into one freeText line with tokens.
      if (el.key === 'freeText') {
        const raw = el.text ?? '';
        const hasOrt = raw.includes('{{leistungsort}}');
        const hasZeit = raw.includes('{{leistungszeitraum}}');
        if (!hasOrt && !hasZeit) return undefined;

        const zeitLabel = secondaryMetaLabel || 'Leistungszeitraum';
        const zeitHiddenByFlag =
          lzHidden ||
          (secondaryMetaKind === 'lieferdatum' && hideLieferdatum);
        // Invoice family, Arbeitsbericht, and Lieferschein get a secondary meta row.
        const showZeit = hasZeit && secondaryMetaKind != null && !zeitHiddenByFlag;
        const saveZeitraum = async (v: typeof secondaryMetaValue) => {
          if (secondaryMetaKind === 'lieferdatum') {
            setHideLieferdatum(false);
            setLieferdatumEdit(v);
            await persistFull({ lieferdatum: v, hideLieferdatum: false });
          } else {
            setHideLeistungszeitraum(false);
            setLeistungszeitraumEdit(v);
            await persistFull({ leistungszeitraum: v, hideLeistungszeitraum: false });
          }
        };

        return (
          <div
            style={{
              ...baseText,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              justifyContent: layoutAlignToFlexJustify(el.align),
              columnGap: '0.35em',
              rowGap: 2,
            }}
          >
            {hasOrt ? (
              <>
                <span>Leistungsort:</span>
                <OfferInlineEdit
                  value={leistungsortEdit}
                  onSave={async (v) => {
                    setLeistungsortEdit(v);
                    await persistFull({ leistungsort: v });
                  }}
                  multiline
                  viewTextClassName="leading-[1.35]"
                  viewWrapperClassName="!w-auto inline-block min-w-[3rem] max-w-full align-baseline"
                  emptyHint="Ort eingeben…"
                  aria-label="Leistungsort"
                />
              </>
            ) : null}
            {hasOrt && showZeit && hasSecondaryMetaValue ? (
              <span aria-hidden>•</span>
            ) : null}
            {showZeit && hasSecondaryMetaValue ? (
              <>
                <span>{zeitLabel}:</span>
                <OfferLeistungszeitraumEdit
                  value={secondaryMetaValue}
                  onSave={saveZeitraum}
                  viewTextClassName="leading-[1.35]"
                  viewWrapperClassName="!w-auto inline-block align-baseline"
                  aria-label={zeitLabel}
                  {...lzEditInvoiceProps}
                />
              </>
            ) : showZeit ? (
              <>
                {hasOrt ? (
                  <span aria-hidden className="text-neutral-300">
                    •
                  </span>
                ) : null}
                <OfferLeistungszeitraumEdit
                  value={secondaryMetaValue}
                  onSave={saveZeitraum}
                  emptyHint={`${zeitLabel} setzen`}
                  viewTextClassName="leading-[1.35] text-sky-600"
                  viewWrapperClassName="!w-auto inline-block align-baseline"
                  aria-label={zeitLabel}
                  {...lzEditInvoiceProps}
                />
              </>
            ) : null}
            {hasZeit && lzHidden ? lzRestoreLink : null}
          </div>
        );
      }

      if (el.key === 'leistungsort' || el.key === 'issueDate' || el.key === 'leistungszeitraum' || el.key === 'orderNumber') {
        if (el.key === 'orderNumber') {
          // Angebot / KVA / AB / Lieferschein: template may include the field — hide at runtime.
          if (!isInvoiceFamilyDocumentType(documentTypeLabel)) return null;
        }
        if (el.key === 'leistungszeitraum') {
          // Angebot / KVA / AB: template may include the field — hide at runtime.
          if (!secondaryMetaKind) return null;
          // Match PDF bands: honor per-draft hide flags when a value exists.
          const zeitHiddenByFlag =
            lzHidden ||
            (secondaryMetaKind === 'lieferdatum' && hideLieferdatum);
          if (zeitHiddenByFlag) return lzHidden ? <div style={baseText}>{lzRestoreLink}</div> : null;
          if (!hasSecondaryMetaValue) {
            // Keep a clickable empty control so the Zeitraum can still be set.
            const zeitLabel = secondaryMetaLabel || 'Leistungszeitraum';
            return (
              <div style={baseText}>
                <OfferLeistungszeitraumEdit
                  value={secondaryMetaValue}
                  onSave={async (v) => {
                    if (secondaryMetaKind === 'lieferdatum') {
                      setHideLieferdatum(false);
                      setLieferdatumEdit(v);
                      await persistFull({ lieferdatum: v, hideLieferdatum: false });
                    } else {
                      setHideLeistungszeitraum(false);
                      setLeistungszeitraumEdit(v);
                      await persistFull({ leistungszeitraum: v, hideLeistungszeitraum: false });
                    }
                  }}
                  emptyHint={`${zeitLabel} setzen`}
                  viewTextClassName="leading-[1.35] text-sky-600"
                  viewWrapperClassName={`!w-full ${alignClass}`.trim()}
                  aria-label={zeitLabel}
                  {...lzEditInvoiceProps}
                />
              </div>
            );
          }
        }
        const value =
          el.key === 'leistungsort'
            ? leistungsortEdit
            : el.key === 'issueDate'
              ? issueDateEdit
              : el.key === 'orderNumber'
                ? orderNumberEdit
                : secondaryMetaDisplay;
        const fieldDefaultLabel =
          el.key === 'leistungszeitraum' && secondaryMetaLabel
            ? secondaryMetaLabel
            : el.key === 'issueDate'
              ? issueDateFieldLabel(documentTypeLabel)
              : undefined;
        const pres = labeledFieldPresentation(
          el,
          value.trim() || '—',
          fieldDefaultLabel ? { defaultLabel: fieldDefaultLabel } : undefined,
        );
        const labelStyle: React.CSSProperties = {
          fontSize: `${pres.labelFontSizePt}pt`,
          ...OFFER_PDF_FONT_STYLE,
          fontWeight: pres.labelBold ? 700 : 400,
          color: pres.labelColor,
          fontStyle: pres.italic ? 'italic' : 'normal',
          textDecoration: pres.underline ? 'underline' : 'none',
          lineHeight: 1.35,
        };
        const valueClass = 'leading-[1.35]';
        const editor =
          el.key === 'leistungszeitraum' ? (
            <OfferLeistungszeitraumEdit
              value={secondaryMetaValue}
              onSave={async (v) => {
                if (secondaryMetaKind === 'lieferdatum') {
                  setLieferdatumEdit(v);
                  await persistFull({ lieferdatum: v });
                } else {
                  setLeistungszeitraumEdit(v);
                  await persistFull({ leistungszeitraum: v });
                }
              }}
              viewTextClassName={valueClass}
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              aria-label={secondaryMetaLabel || 'Leistungszeitraum'}
              {...lzEditInvoiceProps}
            />
          ) : el.key === 'issueDate' ? (
            <OfferInlineEdit
              value={issueDateEdit}
              onSave={async (v) => {
                setIssueDateEdit(formatDDMMYYYYInput(v));
              }}
              multiline={false}
              viewTextClassName={`${valueClass} tabular-nums`}
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              aria-label="Ausstellungsdatum"
            />
          ) : el.key === 'orderNumber' ? (
            <OfferInlineEdit
              value={orderNumberEdit}
              onSave={async (v) => {
                const next = v.trim();
                await persistBestellnummer(next);
                setOrderNumberEdit(next);
              }}
              multiline={false}
              viewTextClassName={valueClass}
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              emptyHint="Bestellnummer setzen"
              emptyClassName="text-sky-600"
              aria-label="Bestellnummer"
            />
          ) : (
            <OfferInlineEdit
              value={leistungsortEdit}
              onSave={async (v) => {
                setLeistungsortEdit(v);
                await persistFull({ leistungsort: v });
              }}
              multiline
              viewTextClassName={valueClass}
              viewWrapperClassName={`!w-full ${alignClass}`.trim()}
              aria-label="Leistungsort"
            />
          );
        if (pres.placement === 'inline') {
          const sep = inlineLabelValueSeparator(el.key);
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                justifyContent: layoutAlignToFlexJustify(el.align),
                width: '100%',
                textAlign: resolveLayoutTextAlign(el.align),
                ...OFFER_PDF_FONT_STYLE,
                fontSize: `${pres.valueFontSizePt}pt`,
                color: pres.valueColor,
                fontWeight: pres.valueBold ? 700 : 400,
                lineHeight: 1.35,
              }}
            >
              {pres.label ? (
                <span style={labelStyle}>
                  {pres.label}
                  {sep}
                </span>
              ) : null}
              <span
                style={{
                  fontWeight: pres.valueBold ? 700 : 400,
                  minWidth: 0,
                  flex: resolveLayoutTextAlign(el.align) === 'left' ? 1 : undefined,
                }}
              >
                {editor}
              </span>
            </div>
          );
        }
        return (
          <div
            style={{
              width: '100%',
              textAlign: resolveLayoutTextAlign(el.align),
              ...OFFER_PDF_FONT_STYLE,
              fontSize: `${pres.valueFontSizePt}pt`,
              color: pres.valueColor,
              fontWeight: pres.valueBold ? 700 : 400,
              lineHeight: 1.35,
            }}
          >
            {pres.label ? <div style={{ ...labelStyle, marginBottom: 2 }}>{pres.label}</div> : null}
            {editor}
          </div>
        );
      }

      return undefined;
    },
    [
      recipientLayout.nameLines,
      recipientLayout.streetLine,
      recipientLayout.plzOrtLine,
      offerTitleParts,
      offerTitleEditValue,
      saveOfferTitleLine,
      leistungsortEdit,
      issueDateEdit,
      orderNumberEdit,
      secondaryMetaDisplay,
      secondaryMetaValue,
      secondaryMetaKind,
      secondaryMetaLabel,
      hasSecondaryMetaValue,
      hideLeistungszeitraum,
      hideLieferdatum,
      invoiceLzRequired,
      lzEditInvoiceProps,
      persistFull,
      persistBestellnummer,
      documentTypeLabel,
    ],
  );

  const pdfUrlRevokeRef = React.useRef<string | null>(null);
  const [pdfPreview, setPdfPreview] = React.useState<{
    url: string;
    filename: string;
    referenceId: string;
    /** True after the first successful download/send reserved this referenceId. */
    documentNumberCommitted?: boolean;
    /** Human-readable E-Rechnung / ZUGFeRD hint when embedding failed or payload was rejected. */
    zugferdWarning?: string;
    /** E-Rechnungspflicht: Grund, warum dieses PDF ohne XML nicht ausgestellt werden darf. */
    einvoiceBlocked?: string;
    /** Frozen when the PDF is built — must match the PDF (see onDraftSaved after preview). */
    netTotal: number;
    vatAmount: number;
    grossTotal: number;
  } | null>(null);
  const [pdfOverlayOpen, setPdfOverlayOpen] = React.useState(false);
  const pdfPreviewRef = React.useRef(pdfPreview);
  pdfPreviewRef.current = pdfPreview;
  const [pdfLoading, setPdfLoading] = React.useState(false);
  /** Aktueller Schritt der PDF-Erzeugung für den Platzhalter in der Vorschau. */
  const [pdfPhase, setPdfPhase] = React.useState<PdfPreviewPhase | null>(null);
  /** Vorschau nach dem Bauen öffnen — nicht, wenn der Nutzer sie währenddessen geschlossen hat. */
  const pdfOpenWhenReadyRef = React.useRef(true);
  /** Im E-Rechnungs-Popup gespeicherte Kunden-Kennungen; `lead.organization` lädt erst später neu. */
  const [orgTaxIdOverride, setOrgTaxIdOverride] = React.useState<(EInvoiceTaxIds & { orgId: string }) | null>(null);
  const [einvoiceTaxIdPrompt, setEinvoiceTaxIdPrompt] = React.useState<{
    positionDetailLevel?: 'full' | 'titles';
  } | null>(null);
  /** „Ohne E-Rechnung weiter“ gilt bis zum Schließen des Editors. */
  const einvoiceTaxIdSkippedRef = React.useRef(false);
  const invoiceDebtor = React.useMemo(() => resolveInvoiceDebtor(lead), [lead]);
  const taxOrgId =
    isInvoiceFamilyDocumentType(documentTypeLabel) && invoiceDebtor.source === 'recipient_organization'
      ? invoiceDebtor.recipientOrganizationId
      : lead.organization?.id ?? null;
  const recipientOrgTaxIds: EInvoiceTaxIds = React.useMemo(() => {
    if (taxOrgId && orgTaxIdOverride?.orgId === taxOrgId) return orgTaxIdOverride;
    if (
      isInvoiceFamilyDocumentType(documentTypeLabel) &&
      invoiceDebtor.source === 'recipient_organization'
    ) {
      return {
        vat_id: invoiceDebtor.vatId,
        commercial_register_number: invoiceDebtor.commercialRegisterNumber,
      };
    }
    const org = lead.organization;
    return {
      vat_id: (org?.vat_id ?? '').trim(),
      commercial_register_number: (org?.commercial_register_number ?? '').trim(),
    };
  }, [documentTypeLabel, invoiceDebtor, lead.organization, orgTaxIdOverride, taxOrgId]);
  const missingEInvoiceTaxIds = getMissingEInvoiceTaxIdentifiers({
    vatMode,
    recipientVatId: recipientOrgTaxIds.vat_id,
    recipientLegalRegistrationId: recipientOrgTaxIds.commercial_register_number,
    sellerVatId: company?.vat_id,
    sellerTaxNumber: company?.tax_number,
  });
  const [offerMailComposeOpen, setOfferMailComposeOpen] = React.useState(false);
  const [offerMailComposeAccountId, setOfferMailComposeAccountId] = React.useState<string | null>(null);
  const [offerMailComposeResetKey, setOfferMailComposeResetKey] = React.useState(0);
  const [offerMailComposeBusy, setOfferMailComposeBusy] = React.useState(false);
  /**
   * Die PDF, die dem Mail-Dialog angehängt wurde. Nach dem Versand wird genau sie im Projekt
   * abgelegt; die Blob-URL der Vorschau war zu dem Zeitpunkt teils schon ungültig (27.10.2026).
   */
  const offerMailPdfFileRef = React.useRef<File | null>(null);
  const [offerMailComposeInitial, setOfferMailComposeInitial] = React.useState<{
    to: string;
    cc: string;
    subject: string;
    body: string;
    attachments: File[];
    signatureHtml?: string | null;
    fromEmail?: string | null;
  } | null>(null);
  const [offerPdfSendNotice, setOfferPdfSendNotice] = React.useState<OfferPdfSendNotice>(null);
  const [duplicateExportPrompt, setDuplicateExportPrompt] = React.useState<{
    kind: 'download' | 'email';
    existingNumbers: string[];
    nextNumber: string;
  } | null>(null);
  const [duplicateExportBusy, setDuplicateExportBusy] = React.useState(false);
  /** Storno-Schnellpfad (?cancelInvoice=): API-Fehler inline, kein window.alert. */
  const [stornoBootstrapError, setStornoBootstrapError] = React.useState<string | null>(null);

  React.useEffect(
    () => () => {
      if (pdfUrlRevokeRef.current) {
        URL.revokeObjectURL(pdfUrlRevokeRef.current);
        pdfUrlRevokeRef.current = null;
      }
    },
    []
  );

  const closePdfPreview = React.useCallback(() => {
    // Während des Bauens geschlossen: fertiges PDF nicht ungefragt wieder aufklappen.
    pdfOpenWhenReadyRef.current = false;
    if (pdfPreviewRef.current?.documentNumberCommitted) {
      setPdfOverlayOpen(false);
      return;
    }
    if (pdfUrlRevokeRef.current) {
      URL.revokeObjectURL(pdfUrlRevokeRef.current);
      pdfUrlRevokeRef.current = null;
    }
    setPdfPreview(null);
    setPdfOverlayOpen(false);
  }, []);

  /**
   * Wird die gerade gestellte Rechnung als Abschlag ausgewählt, entsteht die nächste.
   * Die gestellte PDF darf dann nicht erneut mit ihrer Nummer versendet werden.
   */
  React.useEffect(() => {
    if (!pdfPreview?.documentNumberCommitted) return;
    const committedRef = pdfPreview.referenceId.trim().toLowerCase();
    const deductsCommitted = previousPartialInvoicesSummary.some(
      (invoice) => invoice.referenceId.trim().toLowerCase() === committedRef,
    );
    if (!deductsCommitted) return;
    if (pdfUrlRevokeRef.current) {
      URL.revokeObjectURL(pdfUrlRevokeRef.current);
      pdfUrlRevokeRef.current = null;
    }
    setPdfPreview(null);
    setPdfOverlayOpen(false);
  }, [pdfPreview, previousPartialInvoicesSummary]);

  /** Mit `pdfFile` wird genau diese Datei abgelegt (der Mail-Anhang), sonst die PDF der Vorschau. */
  const uploadCurrentPdfToLeadDocuments = React.useCallback(async (pdfFile?: File | null) => {
    let file: File;
    let filename: string;
    if (pdfFile) {
      file = pdfFile;
      filename = pdfFile.name;
    } else {
      if (!pdfPreview) return null;
      const pdfRes = await fetch(pdfPreview.url);
      if (!pdfRes.ok) throw new Error('PDF konnte nicht für die Dokumentenablage geladen werden.');
      const blob = await pdfRes.blob();
      file =
        blob instanceof File
          ? blob
          : new File([blob], pdfPreview.filename, { type: blob.type || 'application/pdf' });
      filename = pdfPreview.filename;
    }
    const formData = new FormData();
    formData.append('file', file, filename);
    const uploadRes = await fetch(`/api/leads/${encodeURIComponent(leadRouteId)}/documents`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const uploadJson = await uploadRes.json().catch(() => ({}));
    if (!uploadRes.ok) {
      throw new Error((uploadJson as { error?: string }).error ?? 'PDF konnte nicht bei den Lead-Dokumenten abgelegt werden.');
    }
    return uploadJson;
  }, [pdfPreview, leadRouteId]);

  /** Der Upload kann serverseitig durchlaufen, während die Antwort verloren geht. */
  const findUploadedLeadDocument = React.useCallback(async (filenameOverride?: string): Promise<{ id?: string } | null> => {
    const filename = (filenameOverride ?? pdfPreview?.filename)?.trim();
    if (!filename) return null;
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(leadRouteId)}/documents`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const docs = (await res.json().catch(() => [])) as Array<{
        id?: string;
        filename?: string;
        created_at?: string;
      }>;
      if (!Array.isArray(docs)) return null;
      const match = docs
        .filter((doc) => (doc.filename ?? '').trim() === filename && doc.id)
        .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))[0];
      return match ? { id: match.id } : null;
    } catch {
      return null;
    }
  }, [pdfPreview, leadRouteId]);

  /** Read-only: nächste voraussichtliche Nummer (Zähler wird nicht erhöht). */
  const fetchDocumentExportReferencePreview = React.useCallback(async () => {
    const params = new URLSearchParams({ documentTypeLabel });
    if (offeringId) params.set('offering_id', offeringId);
    const res = await fetch(
      `/api/leads/${encodeURIComponent(leadRouteId)}/document-number-preview?${params.toString()}`,
      { credentials: 'include' },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error ?? 'Dokumentennummer konnte nicht ermittelt werden.');
    }
    return (await res.json()) as { serial: number; referenceId: string };
  }, [leadRouteId, offeringId, documentTypeLabel]);

  /** Vergibt die nächste Nummer endgültig (Zähler wird erhöht) — nur bei Download/Versand. */
  const allocateDocumentExportReference = React.useCallback(async () => {
    const res = await fetch(
      `/api/leads/${encodeURIComponent(leadRouteId)}/allocate-document-export-serial`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          documentTypeLabel,
          ...(offeringId ? { offering_id: offeringId } : {}),
        }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error ?? 'Dokumentennummer konnte nicht vergeben werden.');
    }
    const allocJson = (await res.json()) as { serial: number; referenceId: string };
    latestExportSerialRef.current[documentTypeLabel] = Math.max(
      latestExportSerialRef.current[documentTypeLabel] ?? 0,
      allocJson.serial,
    );
    latestExportReferenceRef.current[documentTypeLabel] = allocJson.referenceId;
    rememberCurrentDeductionDocument({ referenceIds: [allocJson.referenceId] });
    return allocJson;
  }, [leadRouteId, offeringId, documentTypeLabel, rememberCurrentDeductionDocument]);

  const persistOfferDocumentToInvoiceOverview = React.useCallback(
    async (options?: {
      /** Nummer endgültig vergeben (Download/Versand), nicht bei PDF-Vorschau. */
      commitDocumentNumber?: boolean;
      inboxAccountId?: string | null;
      /** Beim Versand: die angehängte PDF, damit genau sie im Projekt landet. */
      pdfFile?: File | null;
    }): Promise<{
      referenceId: string;
      errors: string[];
      warnings: string[];
      ledgerSaved: boolean;
      /** PDF liegt nicht in den Projektdateien (Eintrag in der Übersicht kann trotzdem bestehen). */
      documentStoreError?: string;
    }> => {
      const errors: string[] = [];
      const warnings: string[] = [];
      const docLabel = documentTypeLabel.trim();
      const persistToOverview = LEDGER_OVERVIEW_DOCUMENT_LABELS.has(docLabel);

      const snapNet = pdfPreview?.netTotal ?? netTotal;
      const snapVat = pdfPreview?.vatAmount ?? vatAmount;
      const snapGross = pdfPreview?.grossTotal ?? grossTotal;

      let referenceId = '';
      const previewReferenceId = pdfPreview?.referenceId?.trim() ?? '';
      if (persistToOverview && options?.commitDocumentNumber) {
        try {
          if (pdfPreview?.documentNumberCommitted && previewReferenceId) {
            referenceId = previewReferenceId;
          } else {
            const nextPreview = await fetchDocumentExportReferencePreview();
            if (previewReferenceId && previewReferenceId !== nextPreview.referenceId) {
              errors.push(
                `Die PDF-Vorschau ist veraltet (${previewReferenceId}); die nächste freie Nummer ist ${nextPreview.referenceId}. Bitte PDF neu erzeugen.`,
              );
              return { referenceId: previewReferenceId, errors, warnings, ledgerSaved: false };
            }
            const allocJson = await allocateDocumentExportReference();
            referenceId = allocJson.referenceId;
            if (previewReferenceId && previewReferenceId !== referenceId) {
              errors.push(
                `Die PDF-Vorschau enthält ${previewReferenceId}, vergeben wurde ${referenceId}. Bitte PDF neu erzeugen und erneut herunterladen oder versenden.`,
              );
              return { referenceId, errors, warnings, ledgerSaved: false };
            }
          }
        } catch (e) {
          errors.push(e instanceof Error ? e.message : 'Dokumentennummer konnte nicht vergeben werden.');
          return { referenceId: '', errors, warnings, ledgerSaved: false };
        }
      } else if (persistToOverview) {
        referenceId = previewReferenceId;
      }
      if (persistToOverview && options?.commitDocumentNumber && !referenceId) {
        errors.push('Dokumentennummer fehlt — bitte PDF erneut erzeugen.');
        return { referenceId: '', errors, warnings, ledgerSaved: false };
      }

      const invoiceDueTypes = new Set(['Rechnung', 'Abschlagsrechnung', 'Schlussrechnung']);
      const isInvoiceLike = invoiceDueTypes.has(docLabel);
      const issueYmdForDue = parseDDMMYYYYToYMD(issueDateEdit) ?? todayYMD();
      const dueYmd = resolveInvoiceDueYmd({
        issueYmd: issueYmdForDue,
        mode: showInvoicePaymentTermsControls ? documentPaymentTerms.mode : company?.payment_terms_mode,
        days: showInvoicePaymentTermsControls
          ? documentPaymentTerms.days
          : company?.payment_terms_days,
        holidayDates,
      });

      const persistSnap =
        persistToOverview &&
        (docLabel === 'Rechnung' || docLabel === 'Abschlagsrechnung' || docLabel === 'Schlussrechnung');
      const persistStornoSnap = persistToOverview && (docLabel === 'Stornorechnung' || docLabel === 'Gutschrift');
      const mergedLedgerDraft = ledgerSnapshotDraftRowsFromEditor(
        positions,
        draftRows,
        persistStornoSnap || workReportInvoiceRef.current ? { omitUncoveredApiPositions: true } : undefined,
        customerLaborOpts,
      );
      const positionQtyMap = buildPositionInvoiceQuantitiesMap(positionBillingStates);
      const hasPositionBillingQty = Object.keys(positionQtyMap).length > 0;
      let ledgerSnapshot: Record<string, unknown> | undefined;
      if (persistSnap) {
        let finalLine: OfferDraftRow | null = null;
        let draftRowsForSnap: OfferDraftRow[];
        let snapPartialMode: PartialInvoiceMode | undefined;
        let snapPositionQuantities: Record<string, number> | undefined;

        if (isLumpSumPartialInvoice) {
          draftRowsForSnap = [];
          snapPartialMode = isAmountPartialInvoice ? 'amount' : 'percent';
        } else if (isPositionPartialInvoice) {
          const sourceForBilling =
            mergedLedgerDraft.length > 0
              ? mergedLedgerDraft
              : ledgerSnapshotDraftRowsFromEditor(positions, draftRows, undefined, customerLaborOpts);
          draftRowsForSnap = buildBilledDraftRowsFromBilling(sourceForBilling, positionBillingStates);
          snapPartialMode = 'position';
          snapPositionQuantities = positionQtyMap;
        } else if (docLabel === 'Rechnung') {
          draftRowsForSnap =
            mergedLedgerDraft.length > 0
              ? mergedLedgerDraft
              : (JSON.parse(JSON.stringify(draftRows)) as OfferDraftRow[]);
          snapPositionQuantities = Object.fromEntries(
            buildPositionBillingRows(
              numberedRows.map((r) => ({
                id: r.id,
                kind: r.kind,
                leadPositionId: r.leadPositionId ?? null,
                quantity: r.quantity,
              })),
              {},
              undefined,
              { autoFillRemaining: true },
            )
              .filter((s) => s.offerQty > 0)
              .map((s) => [s.rowKey, s.offerQty]),
          );
        } else if (isFinalInvoice && hasPositionBillingQty) {
          const sourceForBilling =
            mergedLedgerDraft.length > 0
              ? mergedLedgerDraft
              : ledgerSnapshotDraftRowsFromEditor(positions, draftRows, undefined, customerLaborOpts);
          draftRowsForSnap = buildBilledDraftRowsFromBilling(sourceForBilling, positionBillingStates);
          snapPositionQuantities = positionQtyMap;
          if (paidPartialInvoices.length > 0) {
            finalLine = {
              id: 'ledger-final-line',
              kind: 'freePosition',
              quantity: 1,
              unit: 'psch',
              item_name: 'Schlussrechnung (nach Abzug bisheriger Abschlagsrechnungen)',
              description: `Berücksichtigte Abschlagsrechnungen: ${paidPartialInvoices.map((invoice) => invoice.referenceId).join(', ')}`,
              unit_price_vk: allowNegativePositionNet ? snapNet : Math.max(0, snapNet),
            };
          }
        } else if (isFinalInvoice) {
          draftRowsForSnap = JSON.parse(JSON.stringify(draftRows)) as OfferDraftRow[];
          finalLine = {
            id: 'ledger-final-line',
            kind: 'freePosition',
            quantity: 1,
            unit: 'psch',
            item_name: 'Schlussrechnung (nach Abzug bisheriger Abschlagsrechnungen)',
            description:
              paidPartialInvoices.length > 0
                ? `Berücksichtigte Abschlagsrechnungen: ${paidPartialInvoices.map((invoice) => invoice.referenceId).join(', ')}`
                : 'Schlussrechnungsbetrag',
            unit_price_vk: allowNegativePositionNet ? snapNet : Math.max(0, snapNet),
          };
        } else {
          draftRowsForSnap =
            mergedLedgerDraft.length > 0
              ? mergedLedgerDraft
              : (JSON.parse(JSON.stringify(draftRows)) as OfferDraftRow[]);
        }

        const snap: InvoiceLedgerSnapshotV1 = {
          v: 1,
          sourceDocumentTypeLabel: docLabel,
          draftRows: draftRowsForSnap,
          discount: discount ? { ...discount } : null,
          vat_mode: vatMode,
          abschlagPercent,
          ...(isAmountPartialInvoice ? { partialInvoiceAmount } : {}),
          partialInvoiceDescription,
          ...(partialInvoiceLineName.trim() ? { partialInvoiceLineName: partialInvoiceLineName.trim() } : {}),
          paidPartialInvoices: isFinalInvoice || isPartialInvoice ? paidPartialInvoices.map((p) => ({ ...p })) : [],
          isPartialInvoice,
          isFinalInvoice,
          finalSnapshotLine: finalLine,
          ...(snapPartialMode ? { partialInvoiceMode: snapPartialMode } : {}),
          ...(isLumpSumPartialInvoice ? { partialInvoiceCumulative: isCumulativeLumpSumPartialInvoice } : {}),
          ...(snapPositionQuantities && Object.keys(snapPositionQuantities).length > 0
            ? { positionInvoiceQuantities: snapPositionQuantities }
            : {}),
          ...(activeLayoutTemplateRef.current
            ? {
                layoutTemplateId: activeLayoutTemplateRef.current.id,
                layoutTemplateVersion: activeLayoutTemplateRef.current.version,
              }
            : {}),
          siteLevy: siteLevy ? { ...siteLevy } : null,
          retention: retention ? { ...retention } : null,
          siteLevyNet: payableTotals.siteLevyNet,
          retentionGross: payableTotals.retentionGross,
          priorRetentionsDueGross: payableTotals.priorRetentionsDueGross,
          payableAmount: payableTotals.payableAmount,
          skontoAmount: payableTotals.skontoAmount,
        };
        ledgerSnapshot = snap as unknown as Record<string, unknown>;
      } else if (persistStornoSnap) {
        const snapDraftRows =
          mergedLedgerDraft.length > 0
            ? mergedLedgerDraft
            : (JSON.parse(JSON.stringify(draftRows)) as OfferDraftRow[]);
        const gutschriftPositionQuantities: Record<string, number> = {};
        if (docLabel === 'Gutschrift') {
          for (const row of snapDraftRows) {
            if (!isBillablePositionRowKind(row.kind)) continue;
            const key = billingRowKeyFromDraftRow(row);
            if (!key || key.startsWith('draft:')) continue;
            const qty = Math.abs(Number(row.quantity));
            if (!Number.isFinite(qty) || qty === 0) continue;
            gutschriftPositionQuantities[key] = (gutschriftPositionQuantities[key] ?? 0) + qty;
          }
        }
        const snap: InvoiceLedgerSnapshotV1 = {
          v: 1,
          sourceDocumentTypeLabel: docLabel,
          draftRows: snapDraftRows,
          discount: discount ? { ...discount } : null,
          vat_mode: vatMode,
          abschlagPercent,
          partialInvoiceDescription,
          paidPartialInvoices: [],
          isPartialInvoice: false,
          isFinalInvoice: false,
          finalSnapshotLine: null,
          ...(Object.keys(gutschriftPositionQuantities).length > 0
            ? { positionInvoiceQuantities: gutschriftPositionQuantities }
            : {}),
          ...(activeLayoutTemplateRef.current
            ? {
                layoutTemplateId: activeLayoutTemplateRef.current.id,
                layoutTemplateVersion: activeLayoutTemplateRef.current.version,
              }
            : {}),
        };
        ledgerSnapshot = snap as unknown as Record<string, unknown>;
      }

      const pdfFile = options?.pdfFile ?? null;
      let uploadedLeadDocument: { id?: string } | null = null;
      let documentStoreError: string | undefined;
      try {
        uploadedLeadDocument = await uploadCurrentPdfToLeadDocuments(pdfFile);
      } catch {
        uploadedLeadDocument = await findUploadedLeadDocument(pdfFile?.name);
        if (!uploadedLeadDocument?.id) {
          await new Promise((r) => setTimeout(r, 280));
          try {
            uploadedLeadDocument = await uploadCurrentPdfToLeadDocuments(pdfFile);
          } catch (e2) {
            uploadedLeadDocument = await findUploadedLeadDocument(pdfFile?.name);
            if (!uploadedLeadDocument?.id) {
              documentStoreError =
                e2 instanceof Error ? e2.message : 'PDF konnte nicht bei den Lead-Dokumenten abgelegt werden.';
            }
          }
        }
      }
      // Ohne Datei und ohne Vorschau gibt es nichts hochzuladen; das darf nicht als abgelegt durchgehen.
      if (!uploadedLeadDocument?.id && !documentStoreError) {
        uploadedLeadDocument = await findUploadedLeadDocument(pdfFile?.name);
        if (!uploadedLeadDocument?.id) {
          documentStoreError = 'PDF konnte nicht bei den Lead-Dokumenten abgelegt werden.';
        }
      }

      if (!persistToOverview) {
        return { referenceId, errors, warnings, ledgerSaved: false, documentStoreError };
      }

      let ledgerSaved = false;
      try {
        const dueDate = isInvoiceLike ? dueYmd : undefined;
        const invoiceSentUrl = offeringId
          ? `/api/offerings/${encodeURIComponent(offeringId)}/invoice-document-sent`
          : `/api/leads/${encodeURIComponent(leadRouteId)}/invoice-document-sent`;
        const res = await fetch(invoiceSentUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            leadDocumentId: uploadedLeadDocument?.id ?? null,
            documentTypeLabel,
            referenceId,
            filename: pdfPreview?.filename ?? null,
            partyName:
              (isInvoiceFamilyDocumentType(documentTypeLabel) && invoiceDebtor.source !== 'project_customer'
                ? recipientNameLinesEdit[0] || invoiceDebtor.partyName
                : formatLeadPartyName(lead)) || null,
            leadDisplayId: lead.display_id ?? lead.id,
            leadTitle: offerTitleRest.trim() || lead.lead_title || null,
            issueDate: issueDateEdit,
            dueDate,
            currency: 'EUR',
            netAmount: snapNet,
            grossAmount: snapGross,
            taxAmount: snapVat,
            payableAmount: payableTotals.payableAmount,
            retentionGross: payableTotals.retentionGross,
            siteLevyNet: payableTotals.siteLevyNet,
            skontoAmount: payableTotals.skontoAmount,
            priorRetentionInvoiceIds: isFinalInvoice
              ? (heldPriorRetentions.length > 0
                  ? heldPriorRetentions.map((row) => row.id)
                  : paidPartialInvoices
                      .filter((p) => (p.retentionGross ?? 0) > 0)
                      .map((p) => p.id))
              : [],
            ledgerSnapshot: ledgerSnapshot ?? {},
            cancelledInvoiceId: cancelledStornoLedgerIdRef.current,
            creditedInvoiceId: creditedInvoiceLedgerIdRef.current,
            ...(workReportInvoiceRef.current && workReportInvoiceIdsRef.current?.length
              ? { workReportIds: workReportInvoiceIdsRef.current }
              : {}),
            ...(offeringId ? { offering_id: offeringId } : {}),
            ...(options?.inboxAccountId != null
              ? { inbox_account_id: String(options.inboxAccountId) }
              : {}),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? 'Rechnungseintrag konnte nicht erstellt werden.');
        }
        ledgerSaved = true;
        const sentPayload = (await res.json().catch(() => ({}))) as {
          id?: string;
          invoice_number?: string | null;
          reference_id?: string | null;
          forwarding?: { ok?: boolean; error?: string };
        };
        const issuedId = String(sentPayload.id ?? '').trim();
        const issuedRef = String(sentPayload.invoice_number || sentPayload.reference_id || referenceId || '').trim();
        if (issuedId || issuedRef) {
          rememberCurrentDeductionDocument({
            ids: issuedId ? [issuedId] : [],
            referenceIds: issuedRef ? [issuedRef] : [],
          });
          await persistFull({
            currentIssuedInvoiceByDocType: {
              [documentTypeLabel]: {
                ...(issuedId ? { id: issuedId } : {}),
                ...(issuedRef ? { referenceId: issuedRef } : {}),
              },
            },
          });
        }
        const fwd = sentPayload.forwarding;
        if (fwd && fwd.ok === false && fwd.error) {
          warnings.push(`Weiterleitung an Buchhaltung fehlgeschlagen: ${fwd.error}`);
        }
      } catch (e) {
        errors.push(e instanceof Error ? e.message : 'Rechnungseintrag konnte nicht erstellt werden.');
      }

      if (
        options?.commitDocumentNumber &&
        persistToOverview &&
        referenceId &&
        ledgerSaved
      ) {
        setPdfPreview((prev) =>
          prev ? { ...prev, documentNumberCommitted: true, referenceId } : null,
        );
        rememberCurrentDeductionDocument({ referenceIds: [referenceId] });
      }

      return { referenceId, errors, warnings, ledgerSaved, documentStoreError };
    },
    [
      pdfPreview,
      documentTypeLabel,
      netTotal,
      vatAmount,
      grossTotal,
      leadRouteId,
      offeringId,
      company?.payment_terms_days,
      company?.payment_terms_mode,
      holidayDates,
      positions,
      draftRows,
      discount,
      vatMode,
      abschlagPercent,
      partialInvoiceDescription,
      paidPartialInvoices,
      isCumulativeLumpSumPartialInvoice,
      heldPriorRetentions,
      payableTotals,
      isFinalInvoice,
      isPartialInvoice,
      allowNegativePositionNet,
      uploadCurrentPdfToLeadDocuments,
      findUploadedLeadDocument,
      allocateDocumentExportReference,
      fetchDocumentExportReferencePreview,
      rememberCurrentDeductionDocument,
      persistFull,
      lead,
      invoiceDebtor,
      recipientNameLinesEdit,
      offerTitleRest,
      issueDateEdit,
      customerLaborOpts,
      rechnungenEnabled,
    ],
  );

  const downloadCurrentPdf = React.useCallback(async () => {
    if (!pdfPreview) return;
    const { errors, warnings, documentStoreError } = await persistOfferDocumentToInvoiceOverview({
      commitDocumentNumber: true,
    });
    const allIssues = [...errors, ...warnings, ...(documentStoreError ? [documentStoreError] : [])];
    if (allIssues.length > 0) {
      setOfferPdfSendNotice({
        type: 'error',
        title: 'Herunterladen nicht möglich',
        message: allIssues.join('\n'),
      });
      return;
    }
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    await onProjectFeedChanged?.();
  }, [pdfPreview, persistOfferDocumentToInvoiceOverview, onProjectFeedChanged]);

  const openOfferPdfEmailCompose = React.useCallback(async () => {
    if (!pdfPreview) return;
    setOfferMailComposeBusy(true);
    try {
      const dispatchPurpose = mailDispatchPurposeForDocumentLabel(documentTypeLabel);
      const dispatch = await fetchDispatchAccount(dispatchPurpose);
      if (!dispatch.account_id && !dispatch.can_choose) {
        setOfferPdfSendNotice({ type: 'need_mail' });
        return;
      }
      const [pdfRes, leadRes] = await Promise.all([
        fetch(pdfPreview.url),
        fetch(`/api/leads/${encodeURIComponent(String(lead.id))}`, { credentials: 'include' }),
      ]);
      if (!pdfRes.ok) throw new Error('PDF konnte nicht geladen werden.');
      const blob = await pdfRes.blob();
      const file = new File([blob], pdfPreview.filename, { type: 'application/pdf' });
      offerMailPdfFileRef.current = file;
      const titleRest = offerTitleRest.trim();
      const subject = `${documentTypeLabel} ${pdfPreview.referenceId}${titleRest ? ` ${titleRest}` : ''}`.trim();
      let to = leadOutboundEmail(lead);
      let cc = '';
      let leadForGreeting: Lead = lead;
      if (leadRes.ok) {
        try {
          const fresh = (await leadRes.json()) as Lead;
          leadForGreeting = fresh;
          const freshBilling = resolveDocumentBilling(fresh, documentTypeLabel, null);
          if (freshBilling.email !== undefined) {
            to = freshBilling.email;
            cc = (freshBilling.cc ?? []).join(', ');
          } else {
            const billingEmail = fresh.organization?.billing_contact?.email?.trim() ?? '';
            to = billingEmail || leadOutboundEmail(fresh) || to;
            const ccList = Array.isArray(fresh.organization?.billing_email_cc)
              ? (fresh.organization.billing_email_cc as string[]).filter(Boolean)
              : [];
            cc = ccList.join(', ');
          }
        } catch {
          /* keep in-memory lead */
        }
      }
      const recipientName = contactDisplayName(leadForGreeting.contact ?? { name: leadForGreeting.name }).trim();
      const greeting = recipientName
        ? `Sehr geehrter ${recipientName},`
        : 'Sehr geehrte Damen und Herren,';
      const issueYmd = parseDDMMYYYYToYMD(issueDateEdit) ?? todayYMD();
      const issueDateForEmail = formatDateToDDMMYYYY(issueYmd);
      const orgDays = leadForGreeting.organization?.payment_terms_days;
      const dueYmdForEmail = showInvoicePaymentTermsControls
        ? resolveInvoiceDueYmd({
            issueYmd,
            mode: documentPaymentTerms.mode,
            days: documentPaymentTerms.days,
            holidayDates,
          })
        : orgDays
          ? resolveInvoiceDueYmd({ issueYmd, mode: 'net_days', days: orgDays })
          : resolveInvoiceDueYmd({
              issueYmd,
              mode: company?.payment_terms_mode,
              days: company?.payment_terms_days,
              holidayDates,
            });
      const dueDate = formatDateToDDMMYYYY(dueYmdForEmail);
      const body = applySendPdfEmailBodyTemplate(documentTexts?.sendPdfEmailBodyTemplate, greeting, documentTypeLabel, {
        invoiceNumber: pdfPreview.referenceId,
        date: issueDateForEmail,
        amount: formatEuro(pdfPreview.grossTotal),
        dueDate,
      });
      setOfferMailComposeInitial({
        to,
        cc,
        subject,
        body,
        attachments: [file],
        signatureHtml: dispatch.signature_html ?? null,
        fromEmail: dispatch.email,
      });
      setOfferMailComposeAccountId(dispatch.account_id);
      setOfferMailComposeResetKey((k) => k + 1);
      setOfferMailComposeOpen(true);
    } catch (e) {
      setOfferPdfSendNotice({
        type: 'error',
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setOfferMailComposeBusy(false);
    }
  }, [
    pdfPreview,
    offerTitleRest,
    documentTypeLabel,
    lead,
    company?.payment_terms_days,
    company?.payment_terms_mode,
    holidayDates,
    documentTexts?.sendPdfEmailBodyTemplate,
    issueDateEdit,
  ]);

  const loadExistingOverviewDocumentNumbers = React.useCallback(async (): Promise<string[]> => {
    const documentType = overviewDocumentTypeFromGermanLabel(documentTypeLabel);
    if (!documentType) return [];
    // Angebote prüft das Modul Angebote über den Vorgang, damit die Warnung auch ohne Rechnungen
    // kommt (Kostenvoranschläge landen nicht in der Belegübersicht, documentType ist dort null).
    if (documentType === 'offer' && offeringId) {
      if (!modulesLoaded || !isEnabled('angebote')) return [];
      const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/offer-documents`, {
        credentials: 'include',
      });
      if (!res.ok) return [];
      const json = (await res.json().catch(() => ({}))) as {
        offeringId?: string;
        documents?: Array<{ offering_id?: string | null; invoice_number?: string | null; reference_id?: string | null }>;
      };
      const ownOfferingId = typeof json.offeringId === 'string' ? json.offeringId : null;
      if (!ownOfferingId || !Array.isArray(json.documents)) return [];
      // Die Antwort enthält alle Angebote des Projekts; gewarnt wird wie bisher nur für diesen Vorgang.
      return uniqueDocumentNumbers(json.documents.filter((doc) => doc.offering_id === ownOfferingId));
    }
    if (!rechnungenEnabled) return [];
    const params = new URLSearchParams({
      type: invoicesListTabForDocumentType(documentType),
      status: 'all',
      document_type: documentType,
      per_page: '100',
    });
    if (offeringId) params.set('offering_id', offeringId);
    else if (lead.id) params.set('lead_id', String(lead.id));
    else return [];
    const res = await fetch(`/api/invoices?${params.toString()}`, { credentials: 'include' });
    if (!res.ok) return [];
    const json = (await res.json().catch(() => ({}))) as {
      items?: Array<{ invoice_number?: string | null; reference_id?: string | null }>;
    };
    return uniqueDocumentNumbers(Array.isArray(json.items) ? json.items : []);
  }, [documentTypeLabel, offeringId, lead.id, rechnungenEnabled, modulesLoaded, isEnabled]);

  const confirmOrRunDocumentExport = React.useCallback(async (kind: 'download' | 'email') => {
    const preview = pdfPreview;
    if (!preview || duplicateExportBusy) return;
    if (preview.einvoiceBlocked && !preview.documentNumberCommitted) {
      setOfferPdfSendNotice({ type: 'error', title: 'E-Rechnung erforderlich', message: preview.einvoiceBlocked });
      return;
    }
    const previewStillOpen = () => pdfPreviewRef.current?.url === preview.url;
    const run = () => {
      if (!previewStillOpen()) return;
      if (kind === 'download') void downloadCurrentPdf();
      else void openOfferPdfEmailCompose();
    };
    if (preview.documentNumberCommitted) {
      run();
      return;
    }
    setDuplicateExportBusy(true);
    try {
      const existingNumbers = await loadExistingOverviewDocumentNumbers();
      if (!previewStillOpen()) return;
      const { warn, existingNumbers: others } = shouldWarnNewDocumentNumber({
        documentNumberCommitted: false,
        previewReferenceId: preview.referenceId,
        existingNumbers,
      });
      if (!warn) {
        run();
        return;
      }
      setDuplicateExportPrompt({
        kind,
        existingNumbers: others,
        nextNumber: preview.referenceId.trim(),
      });
    } catch {
      run();
    } finally {
      setDuplicateExportBusy(false);
    }
  }, [
    pdfPreview,
    duplicateExportBusy,
    loadExistingOverviewDocumentNumbers,
    downloadCurrentPdf,
    openOfferPdfEmailCompose,
  ]);

  const handleOfferDocumentSent = React.useCallback(async () => {
    const { referenceId, errors, warnings, documentStoreError } = await persistOfferDocumentToInvoiceOverview({
      commitDocumentNumber: true,
      inboxAccountId: offerMailComposeAccountId,
      pdfFile: offerMailPdfFileRef.current,
    });
    const warningBlock =
      warnings.length > 0 ? `\n\nHinweis:\n${warnings.join('\n')}` : '';
    const documentStoreBlock = documentStoreError
      ? `\n\nDas PDF wurde nicht in den Projektdateien abgelegt: ${documentStoreError}`
      : '';
    if (errors.length > 0) {
      setOfferPdfSendNotice({
        type: 'partial',
        message:
          `Die E-Mail an den Kunden wurde versendet${referenceId ? ` (${referenceId})` : ''}.\n\n` +
          'Bitte senden Sie die Rechnung nicht erneut an den Kunden. ' +
          'Prüfen Sie stattdessen in der Rechnungsübersicht, ob der Eintrag vorhanden ist, ' +
          'oder wenden Sie sich an den Support.\n\n' +
          `Interner Fehler:\n${errors.join('\n')}` +
          documentStoreBlock +
          warningBlock,
      });
      await onProjectFeedChanged?.();
      return;
    }
    if (documentStoreError) {
      setOfferPdfSendNotice({
        type: 'partial',
        message:
          `${documentTypeLabel} ${referenceId} wurde per E-Mail an den Kunden versendet, ` +
          'aber nicht in den Projektdateien abgelegt.\n\n' +
          'Bitte nicht erneut senden. Das PDF hängt an der gesendeten E-Mail im Posteingang ' +
          'und kann von dort im Projekt unter „Dateien“ hochgeladen werden.\n\n' +
          `Fehler: ${documentStoreError}` +
          warningBlock,
      });
      await onProjectFeedChanged?.();
      return;
    }
    if (warnings.length > 0) {
      setOfferPdfSendNotice({
        type: 'success',
        message:
          `${documentTypeLabel} ${referenceId} wurde erfolgreich per E-Mail an den Kunden versendet.` +
          warningBlock,
      });
      await onProjectFeedChanged?.();
      return;
    }
    setOfferPdfSendNotice({
      type: 'success',
      message: `${documentTypeLabel} ${referenceId} wurde erfolgreich per E-Mail versendet und bei den Dokumenten des Projekts abgelegt.`,
    });
    await onProjectFeedChanged?.();
  }, [persistOfferDocumentToInvoiceOverview, documentTypeLabel, offerMailComposeAccountId, onProjectFeedChanged]);

  const generatePdfPreview = React.useCallback(async (options?: {
    positionDetailLevel?: 'full' | 'titles';
    skipTaxIdCheck?: boolean;
    recipientTaxIds?: EInvoiceTaxIds;
  }) => {
    const pdfPositionDetailLevel = options?.positionDetailLevel ?? positionDetailLevel;
    if (pdfPreviewRef.current?.documentNumberCommitted) {
      setPdfOverlayOpen(true);
      return;
    }
    if (
      !options?.skipTaxIdCheck &&
      !einvoiceTaxIdSkippedRef.current &&
      shouldEmbedZugferd(documentTypeLabel) &&
      missingEInvoiceTaxIds.buyer
    ) {
      setEinvoiceTaxIdPrompt({ positionDetailLevel: options?.positionDetailLevel });
      return;
    }
    // Frisch im Popup gespeichert: der State ist in diesem Callback noch nicht aktualisiert.
    const recipientTaxIds = options?.recipientTaxIds ?? recipientOrgTaxIds;
    setShowMobilePositionPicker(false);
    setPdfLoading(true);
    setPdfPhase(null);
    // Vorschau sofort öffnen: Platzhalter mit Fortschritt statt eines stummen Buttons.
    pdfOpenWhenReadyRef.current = true;
    setPdfOverlayOpen(true);
    // Phase setzen und einen Frame abwarten, sonst blockiert der nächste Render-Durchlauf den
    // Haupt-Thread, bevor React den neuen Text gezeichnet hat.
    const onPdfPhase = async (phase: PdfPreviewPhase) => {
      setPdfPhase(phase);
      await new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    };
    try {
      if (invoiceLzRequired && !lzHidden && !effectiveLeistungszeitraum) {
        window.alert(
          'Bitte ein gültiges Rechnungsdatum setzen, damit der Leistungszeitraum übernommen werden kann.',
        );
        return;
      }
      const previewJson = await fetchDocumentExportReferencePreview();
      const documentHeadingLine1 = `${documentTypeLabel} ${previewJson.referenceId}`;

      const [{ pdf }, pdfModule, { loadPdfFonts }] = await Promise.all([
        import('@react-pdf/renderer'),
        isWorkReport ? import('./WorkReportPdfDocument') : import('./OfferPdfDocument'),
        import('../../../utils/pdfFonts'),
      ]);
      await loadPdfFonts();
      const safeFileStem = `${documentTypeLabel}_${previewJson.referenceId}`
        .replace(/[/\\?%*:|"<>]/g, '-')
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9._-]+/gi, '_');
      const filename = `${safeFileStem || 'dokument'}.pdf`;
      let logoUrl: string | null = null;
      if (company?.has_logo === true) {
        try {
          logoUrl = await fetchCompanyLogoDataUrl(urlInstanceId, logoBust);
        } catch {
          logoUrl = null;
        }
      }
      // Reuse the preview-resolved template so PDF matches HTML; fetch only if still pending.
      let layoutTemplate = activeLayoutTemplateRef.current;
      if (!layoutPreviewReady) {
        layoutTemplate = await fetchDefaultDocumentLayoutTemplate();
        activeLayoutTemplateRef.current = layoutTemplate;
      }
      const documentLayoutForPdf = layoutTemplate?.layout ?? documentLayout;
      const layoutAssetUrls = await resolveLayoutAssetDataUrls(documentLayoutForPdf);
      const inlineAttachments = [
        ...introContent.flatMap((block) => (block.type === 'imageGrid' ? block.images : [])),
        ...closingContent.flatMap((block) => (block.type === 'imageGrid' ? block.images : [])),
      ];
      const resolvedInline = await resolveOfferImageDataUrls(leadRouteId, inlineAttachments);
      const srcById = new Map(resolvedInline.map((img) => [img.id, img.src]));
      const resolvedIntro = mapResolvedContent(introContent, srcById);
      const resolvedClosing = mapResolvedContent(closingContent, srcById);
      const unresolvedInline = [...resolvedIntro.unresolved, ...resolvedClosing.unresolved];
      const imageAttachments = isWorkReport
        ? []
        : attachments.filter(
            (attachment) => attachment.mime_type === 'image/png' || attachment.mime_type === 'image/jpeg',
          );
      const appendixImages = await resolveOfferImageDataUrls(leadRouteId, imageAttachments);
      const appendixImageIds = new Set(appendixImages.map((image) => image.id));

      let blob: Blob;
      if (isWorkReport) {
        const { WorkReportPdfDocument } = pdfModule as typeof import('./WorkReportPdfDocument');
        const workPositions = combinedRows
          .filter((r) => r.kind !== 'title' && r.kind !== 'text' && r.kind !== 'image')
          .map((r, idx) => ({
            pos: idx + 1,
            quantity: r.quantity,
            unit: r.unit || 'Stk',
            item_name: r.item_name || 'Position',
            description: r.description || null,
            article_number: (r.article_number ?? '').trim() || null,
          }));
        blob = await pdf(
          <WorkReportPdfDocument
            company={company}
            logoUrl={logoUrl}
            documentHeadingLine1={documentHeadingLine1}
            offerTitleRest={offerTitleRest}
            recipientNameLines={recipientLayout.nameLines}
            recipientStreetLine={recipientLayout.streetLine}
            recipientPlzOrtLine={recipientLayout.plzOrtLine}
            companyLines={companyLines}
            leistungsort={leistungsortEdit}
            leistungszeitraum={leistungszeitraumEdit}
            issueDate={issueDateEdit}
            introLetterParas={introLetterParas}
            introBlocks={introBlocks}
            afterTotalsParas={afterTotalsParas}
            introContent={resolvedIntro.content}
            closingContent={resolvedClosing.content}
            ansprechLine={ansprechLine}
            showAnsprechBlock={showAnsprechBlock}
            ansprechMail={ansprechMail}
            directorLines={directorLines}
            registryLine={registryLine}
            websiteDisplay={websiteDisplay}
            websiteHref={websiteHref}
            positions={workPositions}
            signatureHint={signatureHint}
            hideArticleNumber={hideArticleNumber}
            hidePositionDescriptions={hidePositionDescriptions}
            layout={documentLayoutForPdf}
            layoutAssetUrls={layoutAssetUrls}
            customerNumberLabel={resolveCustomerNumberLabel(lead)}
            projectNumberLabel={(lead.display_id ?? '').trim() || null}
          />,
        ).toBlob();
      } else {
        const { OfferPdfDocument, renderOfferPdfWithTablePlacement } =
          pdfModule as typeof import('./OfferPdfDocument');
        const invoiceNumberForQr =
          documentHeadingLine1.replace(new RegExp(`^${documentTypeLabel}\\s+`), '').trim() ||
          documentHeadingLine1.trim();
        const pdfPaymentQrDataUrl =
          paymentQrDataUrl ??
          (await resolvePaymentQrDataUrl(
            paymentQrResolveInput({
              company,
              documentTypeLabel,
              grossTotal:
                payableTotals.payableAmount > 0 &&
                (payableTotals.retentionGross > 0 || payableTotals.priorRetentionsDueGross > 0)
                  ? payableTotals.payableAmount
                  : grossTotal,
              remittanceText: invoiceNumberForQr,
              layout: documentLayoutForPdf,
            }),
          ));
        const offerPdfElement = (
        <OfferPdfDocument
          company={company}
          documentTypeLabel={documentTypeLabel}
          recipientNameLines={recipientLayout.nameLines}
          recipientStreetLine={recipientLayout.streetLine}
          recipientPlzOrtLine={recipientLayout.plzOrtLine}
          companyLines={companyLines}
          websiteDisplay={websiteDisplay}
          websiteHref={websiteHref}
          directorLines={directorLines}
          registryLine={registryLine}
          ansprechLine={ansprechLine}
          showAnsprechBlock={showAnsprechBlock}
          ansprechMail={ansprechMail}
          logoUrl={logoUrl}
          documentHeadingLine1={documentHeadingLine1}
          offerTitleRest={offerTitleRest}
          hideDocumentTypeInTitle={hideDocumentTypeInTitle}
          leistungsort={leistungsortEdit}
          leistungszeitraum={effectiveLeistungszeitraum}
          lieferdatum={lieferdatumEdit}
          hideLeistungszeitraum={lzHideLocked ? false : hideLeistungszeitraum}
          hideLieferdatum={hideLieferdatum}
          todayLabel={issueDateEdit}
          introLetterParas={introLetterParas}
          introBlocks={introBlocks}
          afterTotalsParas={afterTotalsParas}
          closingBlocks={closingBlocks}
          introContent={resolvedIntro.content}
          closingContent={resolvedClosing.content}
          positions={positions}
          draftRows={draftRows}
          netTotal={netTotal}
          rawNetTotal={rawNetTotal}
          discount={discount}
          discountAmount={discountAmount}
          vatMode={vatMode}
          vatRatePercent={vatRatePercent}
          vatLegalNote={vatLegalNote}
          hideVatAndGross={hideVatAndGross}
          hideTotals={totalsHidden}
          hidePositionDescriptions={hidePositionDescriptions}
          positionDetailLevel={pdfPositionDetailLevel}
          laborCostShareNote={laborCostShareNote}
          vatAmount={vatAmount}
          grossTotal={grossTotal}
          // Pauschal ohne „kumuliert“ wird nichts abgezogen — sonst druckt das PDF eine Abzugszeile.
          paidPartialInvoices={
            isLumpSumPartialInvoice && !isCumulativeLumpSumPartialInvoice ? [] : paidPartialInvoices
          }
          previousPartialInvoicesSummary={showPaidPartialsSummary ? previousPartialInvoicesSummary : []}
          baseNetTotal={showDeductionTotals || isFinalInvoice ? baseNetTotal : null}
          baseVatAmount={
            showDeductionTotals || isFinalInvoice
              ? payableTotals.siteLevyNet > 0
                ? null
                : baseVatAmount
              : null
          }
          siteLevyNet={payableTotals.siteLevyNet}
          siteLevyLabel={siteLevy?.label || DEFAULT_SITE_LEVY_LABEL}
          siteLevyPercent={siteLevy?.percent ?? null}
          netTotalBeforeLevy={netTotalBeforeLevy}
          retentionGross={payableTotals.retentionGross}
          retentionPercent={retention?.percent ?? null}
          priorRetentionsDueGross={payableTotals.priorRetentionsDueGross}
          payableAmount={payableTotals.payableAmount}
          skontoAmount={payableTotals.skontoAmount}
          skontoPercent={skontoPercentForPayable}
          payableWithSkonto={payableTotals.payableWithSkonto}
          closingGreeting={closingGreeting}
          signatureHint={signatureHint}
          partialInvoice={
            isLumpSumPartialInvoice
              ? {
                  percent: isAmountPartialInvoice ? 0 : abschlagPercent,
                  offerReferenceId: angebotReferenceId,
                  netAmount: baseNetTotal,
                  description: effectivePartialDescription,
                  lineName: effectivePartialInvoiceLineName,
                  mode: isAmountPartialInvoice ? 'amount' : 'percent',
                }
              : null
          }
          positionBillingRows={
            isPositionPartialInvoice || (isFinalInvoice && positionBillingPdfRows.length > 0)
              ? positionBillingPdfRows
              : null
          }
          layout={documentLayoutForPdf}
          layoutAssetUrls={layoutAssetUrls}
          paymentQrDataUrl={pdfPaymentQrDataUrl}
          appendixImages={appendixImages}
          customerNumberLabel={resolveCustomerNumberLabel(lead)}
          projectNumberLabel={(lead.display_id ?? '').trim() || null}
          orderNumberLabel={resolveOrderNumberLabel(documentTypeLabel, orderNumberEdit)}
          orderTotalNet={isPartialInvoice ? effectiveOrderTotalNet : null}
          calculationHourlyRate={calculationHourlyRate}
          calculationLaborSeparately={calculationLaborSeparately}
          calculationLaborRowLabel={calculationLaborRowLabel}
          calculationLaborRowNames={calculationLaborRowNames}
        />
      );
        blob = await renderOfferPdfWithTablePlacement(
          offerPdfElement.props as OfferPdfDocumentProps,
          (doc) => pdf(doc).toBlob(),
          onPdfPhase,
        );
      }

      // --- Anhänge anhängen (vor ZUGFeRD): PDFs seitenweise, Bilder als A4-Seite ---
      let mergedBlob = blob;
      const legalSettings = legalAttachmentSettingsFromUnknown(company);
      const includeAgb = shouldIncludeLegalAttachment({
        kind: 'agb',
        documentTypeLabel,
        isPrivateCustomer: isPrivateCustomerLead(lead),
        settings: legalSettings,
        excluded: excludedStandardAttachments,
      });
      const includeWiderruf = shouldIncludeLegalAttachment({
        kind: 'widerrufsformular',
        documentTypeLabel,
        isPrivateCustomer: isPrivateCustomerLead(lead),
        settings: legalSettings,
        excluded: excludedStandardAttachments,
      });
      const mergeAttachments = [
        ...attachments.filter((attachment) => !appendixImageIds.has(attachment.id)),
        ...unresolvedInline,
      ];
      if (mergeAttachments.length > 0 || includeAgb || includeWiderruf) {
        await onPdfPhase('attachments');
        try {
          const mergeForm = new FormData();
          mergeForm.append('pdf', blob, filename);
          mergeForm.append(
            'attachments',
            JSON.stringify(
              mergeAttachments.map((a) => ({ object_path: a.object_path, mime_type: a.mime_type })),
            ),
          );
          mergeForm.append('include_agb', includeAgb ? '1' : '0');
          mergeForm.append('include_widerrufsformular', includeWiderruf ? '1' : '0');
          const mergeRes = await fetch(
            `/api/leads/${encodeURIComponent(leadRouteId)}/merge-attachments`,
            { method: 'POST', body: mergeForm, credentials: 'include' },
          );
          if (mergeRes.ok) {
            mergedBlob = await mergeRes.blob();
          } else {
            const errText = await mergeRes.text().catch(() => '');
            console.warn('[merge-attachments] Server-Fehler, plain PDF wird verwendet:', errText);
          }
        } catch (mergeErr) {
          console.warn('[merge-attachments] Fehler beim Anhängen, plain PDF wird verwendet:', mergeErr);
        }
      }

      // --- ZUGFeRD/Factur-X: only for invoice-like document types (not Arbeitsbericht etc.) ---
      let finalBlob = mergedBlob;
      let zugferdWarning: string | undefined;
      let zugferdEmbedded = false;
      if (shouldEmbedZugferd(documentTypeLabel)) await onPdfPhase('zugferd');
      if (!shouldEmbedZugferd(documentTypeLabel)) {
        const url = URL.createObjectURL(finalBlob);
        if (pdfUrlRevokeRef.current) URL.revokeObjectURL(pdfUrlRevokeRef.current);
        pdfUrlRevokeRef.current = url;
        setPdfPreview({
          url,
          filename,
          referenceId: previewJson.referenceId,
          documentNumberCommitted: false,
          zugferdWarning: undefined,
          netTotal: 0,
          vatAmount: 0,
          grossTotal: 0,
        });
        rememberCurrentDeductionDocument({ referenceIds: [previewJson.referenceId] });
        if (pdfOpenWhenReadyRef.current) setPdfOverlayOpen(true);
        return;
      }
      /** Positionen ohne Preis fallen aus dem XML — siehe isZugferdSerializableLineItem. */
      let zeroPriceHint: string | null = null;
      const collectZugferdHints = () =>
        joinZugferdWarningMessages([
          zeroPriceHint,
          getLikelyEInvoiceRecipientHint({
            nameLine: recipientLayout.nameLines[0] ?? '',
            street: recipientLayout.streetLine,
            plzOrt: recipientLayout.plzOrtLine,
          }),
          getLikelyEInvoiceVatHint({
            vatMode,
            recipientVatId: recipientTaxIds.vat_id,
            recipientTaxNumber: (lead.organization?.tax_number ?? '').trim(),
            recipientLegalRegistrationId: recipientTaxIds.commercial_register_number,
          }),
          getLikelyEInvoiceVatSellerHint({ vatMode, vatId: company.vat_id, taxNumber: company.tax_number }),
          getLikelyEInvoiceSellerHint({
            vatId: company.vat_id,
            taxNumber: company.tax_number,
            commercialRegisterNumber: company.commercial_register_number,
          }),
        ]);
      try {
        const usePositionZugferd =
          isPositionPartialInvoice || (isFinalInvoice && positionBillingPdfRows.length > 0);
        const zugferdLineItems = isLumpSumPartialInvoice
          ? [
              {
                lineId: '1',
                name: effectivePartialInvoiceLineName,
                description: effectivePartialDescription || undefined,
                quantity: 1,
                unit: 'psch',
                unitPriceNet: baseNetTotal,
              },
            ]
          : usePositionZugferd
            ? positionBillingPdfRows.map((r, idx) => ({
                lineId: String(idx + 1),
                name: r.item_name || 'Position',
                description: r.description || undefined,
                quantity: r.quantity,
                unit: r.unit,
                unitPriceNet: r.unit_price_vk,
              }))
            : numberedRows
                .filter((r) => r.kind !== 'title' && r.kind !== 'text' && r.kind !== 'image' && r.quantity > 0)
                .map((r, idx) => ({
                  lineId: String(idx + 1),
                  name: r.item_name || 'Position',
                  description: r.description || undefined,
                  quantity: r.quantity,
                  unit: r.unit,
                  unitPriceNet: r.unit_price_vk,
                }));
        let zugferdLineItemsFinal =
          isFinalInvoice && !usePositionZugferd
            ? [
                {
                  lineId: '1',
                  name: 'Schlussrechnung (nach Abzug bisheriger Abschlagsrechnungen)',
                  description:
                    paidPartialInvoices.length > 0
                      ? `Berücksichtigte Abschlagsrechnungen: ${paidPartialInvoices.map((invoice) => invoice.referenceId).join(', ')}`
                      : 'Schlussrechnungsbetrag',
                  quantity: 1,
                  unit: 'psch',
                  unitPriceNet: allowNegativePositionNet ? netTotal : Math.max(0, netTotal),
                },
              ]
            : zugferdLineItems;
        if (usePositionZugferd) {
          let zugferdLineIdx = zugferdLineItemsFinal.length;
          const zugferdAdjustments: typeof zugferdLineItemsFinal = [];
          if (positionBillingDiscountAmount > 0) {
            zugferdLineIdx += 1;
            zugferdAdjustments.push({
              lineId: String(zugferdLineIdx),
              name: 'Rabatt',
              description: undefined,
              quantity: 1,
              unit: 'psch',
              unitPriceNet: -positionBillingDiscountAmount,
            });
          }
          if (isFinalInvoice || isPartialInvoice) {
            for (const invoice of paidPartialInvoices) {
              zugferdLineIdx += 1;
              zugferdAdjustments.push({
                lineId: String(zugferdLineIdx),
                name: `Abzug Abschlagsrechnung ${invoice.referenceId}`,
                description: undefined,
                quantity: 1,
                unit: 'psch',
                unitPriceNet: -invoice.netAmount,
              });
            }
          }
          if (zugferdAdjustments.length > 0) {
            zugferdLineItemsFinal = [...zugferdLineItemsFinal, ...zugferdAdjustments];
          }
        } else if (isCumulativeLumpSumPartialInvoice && paidPartialInvoices.length > 0) {
          let zugferdLineIdx = zugferdLineItemsFinal.length;
          const zugferdAdjustments: typeof zugferdLineItemsFinal = [];
          for (const invoice of paidPartialInvoices) {
            zugferdLineIdx += 1;
            zugferdAdjustments.push({
              lineId: String(zugferdLineIdx),
              name: `Abzug Abschlagsrechnung ${invoice.referenceId}`,
              description: undefined,
              quantity: 1,
              unit: 'psch',
              unitPriceNet: -invoice.netAmount,
            });
          }
          zugferdLineItemsFinal = [...zugferdLineItemsFinal, ...zugferdAdjustments];
        }

        if (payableTotals.siteLevyNet > 0) {
          const finalLumpAlreadyFaceNet = isFinalInvoice && !usePositionZugferd;
          if (!finalLumpAlreadyFaceNet) {
            zugferdLineItemsFinal = [
              ...zugferdLineItemsFinal,
              {
                lineId: String(zugferdLineItemsFinal.length + 1),
                name: siteLevy?.label || DEFAULT_SITE_LEVY_LABEL,
                description:
                  siteLevy?.percent != null
                    ? `${siteLevy.percent.toLocaleString('de-DE')} % vom Netto`
                    : undefined,
                quantity: 1,
                unit: 'psch',
                unitPriceNet: -payableTotals.siteLevyNet,
              },
            ];
          }
        }

        zeroPriceHint = getZeroPricePositionsHint(
          zugferdLineItemsFinal.filter((item) => !isZugferdSerializableLineItem(item)).length,
        );

        const deliverySource = lzHidden
          ? null
          : invoiceLzRequired
            ? effectiveLeistungszeitraum
            : leistungszeitraumEdit;
        const deliveryParsed = leistungszeitraumToDateRange(deliverySource);

        const invoiceData = {
          referenceId: previewJson.referenceId,
          documentTypeLabel,
          issueDateEdit,
          company,
          recipientName: recipientLayout.nameLines[0] ?? '',
          recipientStreet: recipientLayout.streetLine ?? null,
          recipientPlzOrtLine: recipientLayout.plzOrtLine ?? null,
          recipientVatId: recipientTaxIds.vat_id || undefined,
          recipientTaxNumber: (lead.organization?.tax_number ?? '').trim() || undefined,
          recipientLegalRegistrationId: recipientTaxIds.commercial_register_number || undefined,
          lineItems: zugferdLineItemsFinal,
          netTotal,
          vatRatePercent,
          vatAmount,
          grossTotal,
          vatMode,
          documentNotes: (() => {
            const notes: Array<{ content: string }> = [];
            if (laborCostShareNote) notes.push({ content: laborCostShareNote });
            if (payableTotals.retentionGross > 0 || payableTotals.priorRetentionsDueGross > 0) {
              notes.push({
                content: `Zahlbetrag: ${payableTotals.payableAmount.toFixed(2)} EUR${
                  payableTotals.skontoAmount > 0
                    ? ` · bei Skonto: ${payableTotals.payableWithSkonto.toFixed(2)} EUR`
                    : ''
                }`,
              });
            }
            return notes.length > 0 ? notes : undefined;
          })(),
          deliveryPeriod: deliveryParsed ?? undefined,
          precedingInvoices: (() => {
            const label = documentTypeLabel.trim();
            const ref =
              label === 'Stornorechnung'
                ? cancelledInvoiceReferenceRef.current
                : label === 'Gutschrift'
                  ? creditedInvoiceReferenceRef.current
                  : null;
            return ref ? [ref] : undefined;
          })(),
          paymentTerms: showInvoicePaymentTermsControls ? documentPaymentTerms : undefined,
        };

        const formData = new FormData();
        formData.append('pdf', mergedBlob, filename);
        formData.append('invoiceData', JSON.stringify(invoiceData));

        const zugferdRes = await fetch(
          `/api/leads/${encodeURIComponent(leadRouteId)}/zugferd-pdf`,
          { method: 'POST', body: formData, credentials: 'include' },
        );
        if (zugferdRes.ok) {
          finalBlob = await zugferdRes.blob();
          zugferdEmbedded = true;
          zugferdWarning = zeroPriceHint ?? undefined;
        } else {
          const errText = await zugferdRes.text().catch(() => '');
          let parsed: { error?: string; userMessage?: string } = {};
          try {
            parsed = JSON.parse(errText) as { error?: string; userMessage?: string };
          } catch {
            parsed = { error: errText.slice(0, 500) };
          }
          const serverMsg = (
            parsed.userMessage?.trim() ||
            userMessageForZugferdPdfError(parsed.error ?? `HTTP ${zugferdRes.status}`)
          ).trim();
          const codes = extractZugferdBrCodes(parsed.error ?? '');
          zugferdWarning =
            joinZugferdWarningMessages([
              collectZugferdHints(),
              serverMsg ||
                'Die E-Rechnungs-Daten konnten nicht eingebettet werden. Das PDF ist lesbar, ohne ZUGFeRD/XML.',
              codes.length > 0 ? `Regeln: ${codes.join(', ')}` : null,
            ]) || undefined;
          console.warn('[zugferd] Server-Fehler, plain PDF wird verwendet:', errText);
        }
      } catch (zugferdErr) {
        const networkMsg =
          'Beim Einbetten der E-Rechnungs-Daten ist ein Verbindungs- oder Anwendungsfehler aufgetreten. Das PDF ist vollständig lesbar, ohne gültiges ZUGFeRD/XML.';
        zugferdWarning = joinZugferdWarningMessages([collectZugferdHints(), networkMsg]) || undefined;
        console.warn('[zugferd] Fehler beim Einbetten der ZUGFeRD-XML, plain PDF wird verwendet:', zugferdErr);
      }
      // ---------------------------------------------------------------------------------

      const einvoiceBlocked =
        !zugferdEmbedded
        && isEinvoiceMandatory({
          mandatoryFrom: company?.einvoice_mandatory_from,
          issueDate: parseDDMMYYYYToYMD(issueDateEdit),
          documentTypeLabel,
          isBusinessCustomer: !isPrivateCustomerLead(lead),
          grossAmount: grossTotal,
          recipientVatId: recipientTaxIds.vat_id,
        })
          ? EINVOICE_MANDATORY_BLOCK_MESSAGE
          : undefined;

      const url = URL.createObjectURL(finalBlob);
      if (pdfUrlRevokeRef.current) URL.revokeObjectURL(pdfUrlRevokeRef.current);
      pdfUrlRevokeRef.current = url;
      setPdfPreview({
        url,
        filename,
        referenceId: previewJson.referenceId,
        documentNumberCommitted: false,
        zugferdWarning,
        einvoiceBlocked,
        netTotal,
        vatAmount,
        grossTotal,
      });
      rememberCurrentDeductionDocument({ referenceIds: [previewJson.referenceId] });
      if (pdfOpenWhenReadyRef.current) setPdfOverlayOpen(true);
    } catch (e) {
      if (import.meta.env.DEV) console.error('[offer pdf]', e);
      const detail = e instanceof Error ? e.message : String(e);
      setOfferPdfSendNotice({
        type: 'error',
        title: 'PDF konnte nicht erzeugt werden',
        message: `${detail}\n\nTipp: In der Entwicklerkonsole (F12) stehen ggf. weitere Hinweise.`,
      });
    } finally {
      setPdfLoading(false);
      setPdfPhase(null);
    }
  }, [
    company,
    leadRouteId,
    fetchDocumentExportReferencePreview,
    logoBust,
    urlInstanceId,
    websiteUrl,
    websiteDisplay,
    websiteHref,
    ansprechLine,
    ansprechRole,
    ansprechNames,
    showAnsprechBlock,
    ansprechMail,
    recipientLayout.nameLines,
    recipientLayout.streetLine,
    recipientLayout.plzOrtLine,
    companyLines,
    directorLines,
    registryLine,
    offerTitleRest,
    hideDocumentTypeInTitle,
    documentTypeLabel,
    leistungsortEdit,
    leistungszeitraumEdit,
    effectiveLeistungszeitraum,
    invoiceLzRequired,
    lieferdatumEdit,
    hideLeistungszeitraum,
    hideLieferdatum,
    issueDateEdit,
    orderNumberEdit,
    introLetterParas,
    introBlocks,
    afterTotalsParas,
    closingBlocks,
    introContent,
    closingContent,
    positions,
    draftRows,
    combinedRows,
    numberedRows,
    netTotal,
    rawNetTotal,
    discount,
    discountAmount,
    vatMode,
    vatRatePercent,
    vatLegalNote,
    hideVatAndGross,
    totalsHidden,
    vatAmount,
    grossTotal,
    isFinalInvoice,
    paidPartialInvoices,
    isCumulativeLumpSumPartialInvoice,
    previousPartialInvoicesSummary,
    baseNetTotal,
    baseVatAmount,
    effectivePartialDescription,
    effectivePartialInvoiceLineName,
    closingGreeting,
    signatureHint,
    afterTotalsParas,
    attachments,
    excludedStandardAttachments,
    company,
    isPartialInvoice,
    allowNegativePositionNet,
    isWorkReport,
    hideArticleNumber,
    hidePositionDescriptions,
    laborCostShareNote,
    currentUserName,
    lead,
    recipientOrgTaxIds,
    missingEInvoiceTaxIds.buyer,
    layoutPreviewReady,
    documentLayout,
    calculationHourlyRate,
    calculationLaborSeparately,
    calculationLaborRowLabel,
    calculationLaborRowNames,
    rememberCurrentDeductionDocument,
  ]);

  const pdfPreviewCloseLocked = duplicateExportBusy || duplicateExportPrompt != null;
  // E-Mail-Dialog über der Vorschau schließt per Escape selbst; die Vorschau bleibt dann offen.
  useEscapeClose(
    closePdfPreview,
    Boolean(pdfPreview && pdfOverlayOpen) && !pdfPreviewCloseLocked && !offerMailComposeOpen,
  );
  const documentIssued = Boolean(pdfPreview?.documentNumberCommitted);
  const tableStartsOnPage2 = layoutStartsTableOnPage2(documentLayout);
  const positionTableToolbar = documentIssued ? null : (
    <div className="mb-2 flex justify-end gap-2">
      {isWorkReport ? (
        <button
          type="button"
          onClick={() => {
            const next = !hideArticleNumber;
            setHideArticleNumber(next);
            void persistFull({ hideArticleNumber: next });
          }}
          className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
          title={
            hideArticleNumber
              ? 'Spalte „Artikel-Nr.“ wieder anzeigen'
              : 'Spalte „Artikel-Nr.“ ausblenden'
          }
        >
          {hideArticleNumber ? 'Artikel-Nr. einblenden' : 'Artikel-Nr. ausblenden'}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => {
          const next = !hidePositionDescriptions;
          setHidePositionDescriptions(next);
          void persistFull({ hidePositionDescriptions: next });
        }}
        className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
        title={
          hidePositionDescriptions
            ? 'Beschreibungen der Positionen wieder anzeigen'
            : 'Beschreibungen der Positionen ausblenden'
        }
      >
        {hidePositionDescriptions ? 'Beschreibungen einblenden' : 'Beschreibungen ausblenden'}
      </button>
    </div>
  );
  /** Ausgabeumfang (alle Positionen vs. nur Titel) hängt als Dropdown am Vorschau-Button. */
  const canChoosePositionDetail =
    !documentIssued && !isWorkReport && !isPartialInvoice && !isFinalInvoice;
  const hasTitleRows = combinedRows.some((row) => row.kind === 'title');
  const titlesOnlyDisabledReason =
    'Ohne Titelzeilen gibt es keine Titelzusammenstellung. Zuerst einen Titel einfügen.';
  /** Der Button gibt alle Positionen aus, das Dropdown nur die Titel — beides öffnet die Vorschau. */
  const startPreviewWithDetail = (level: 'full' | 'titles') => {
    if (!canChoosePositionDetail) {
      void generatePdfPreview();
      return;
    }
    const nextLevel = level === 'titles' && !hasTitleRows ? 'full' : level;
    if (positionDetailLevel !== nextLevel) {
      setPositionDetailLevel(nextLevel);
      void persistFull({ positionDetailLevel: nextLevel });
    }
    void generatePdfPreview({ positionDetailLevel: nextLevel });
  };
  const positionToolsContent = (
    <>
      <div className="flex min-h-[44px] items-center gap-2 bg-app-surface px-3 py-2">
        <div className="flex min-w-0 flex-1 rounded-md border border-app-border p-0.5">
          <button
            type="button"
            onClick={() => setPanelMode('blocks')}
            className={`h-8 flex-1 rounded-md text-xs font-semibold ${
              panelMode === 'blocks' ? 'bg-pipedrive-blue text-white' : 'text-app-text-muted'
            }`}
          >
            Bausteine
          </button>
          {canViewStammdaten ? (
            <button
              type="button"
              onClick={() => setPanelMode('search')}
              className={`h-8 flex-1 rounded-md text-xs font-semibold ${
                panelMode === 'search' ? 'bg-pipedrive-blue text-white' : 'text-app-text-muted'
              }`}
            >
              Suche
            </button>
          ) : null}
        </div>
        {positionInsertBusy ? (
          <div aria-live="polite" aria-busy="true">
            <InlineLoadingState label="Wird eingefügt…" className="shrink-0 justify-start text-[11px]" />
          </div>
        ) : null}
      </div>
      {panelMode === 'blocks' || !canViewStammdaten ? (
        <div className="space-y-2 p-3">
          <BlockPaletteItem
            id="block-text"
            label="Text"
            kind="text"
            onInsert={() => void insertBlockRowAt('text', numberedRows.length)}
          />
          <BlockPaletteItem
            id="block-title"
            label="Titel"
            kind="title"
            onInsert={() => void insertBlockRowAt('title', numberedRows.length)}
          />
          <BlockPaletteItem
            id="block-free"
            label="Freie Position"
            kind="freePosition"
            onInsert={() => void insertBlockRowAt('freePosition', numberedRows.length)}
          />
          <BlockPaletteItem
            id="block-image"
            label="Bild"
            kind="image"
            onInsert={() => void insertBlockRowAt('image', numberedRows.length)}
          />
          <BlockPaletteItem
            id="block-surcharge"
            label="Zuschlag auf vorherige Positionen"
            kind="positionSurcharge"
            onInsert={() => setSurchargeModalState({ mode: 'insert', insertIndex: numberedRows.length })}
          />
        </div>
      ) : (
        <OfferPositionPickerPanel
          leadRouteId={leadRouteId}
          offeringId={offeringId}
          onAdded={reloadPositions}
          dragAndDropEnabled
          onInsertItem={({ kind, id, insertIndex, name, unit, gross_price, description }) =>
            insertPickerItemAt(kind, id, typeof insertIndex === 'number' ? insertIndex : numberedRows.length, {
              name,
              unit,
              gross_price,
              description,
            })
          }
        />
      )}
    </>
  );

  if (sageReads === null) {
    return (
      <div className="fixed inset-0 z-[250] flex bg-editor-desk">
        <PageLoadingState />
      </div>
    );
  }
  if (sageReads.length > 0) {
    return (
      <div className="fixed inset-0 z-[250] flex flex-col bg-editor-desk text-app-text">
        <header className="flex shrink-0 items-center gap-2 border-b border-app-border bg-app-surface px-3 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md px-3 text-sm text-app-text-muted hover:bg-app-hover"
          >
            Zurück
          </button>
          <h1 className="text-sm font-semibold md:text-base">Übernommene Belege</h1>
        </header>
        <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mx-auto flex max-w-[210mm] flex-col gap-4">
            {sageReads.map((document) => (
              <SageImportInvoiceReadView key={document.id || document.invoiceNumber} document={document} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <OfferDocumentIssuedContext.Provider value={documentIssued}>
    {imagePicker ? (
      <OfferImagePickerDialog
        leadRouteId={leadRouteId}
        remainingSlots={remainingImageSlots}
        existingObjectPaths={inlineImageObjectPaths}
        images={
          'editBlockId' in imagePicker
            ? (
                (imagePicker.zone === 'intro' ? introContent : closingContent).find(
                  (block): block is Extract<OfferDocumentContentBlock, { type: 'imageGrid' }> =>
                    block.id === imagePicker.editBlockId && block.type === 'imageGrid',
                )
              )?.images
            : undefined
        }
        onImagesChange={
          'editBlockId' in imagePicker
            ? async (next) => {
                const zone = imagePicker.zone;
                const current = zone === 'intro' ? snapRef.current.introContent : snapRef.current.closingContent;
                const persist = zone === 'intro' ? persistIntroContent : persistClosingContent;
                const previous =
                  current.find(
                    (block): block is Extract<OfferDocumentContentBlock, { type: 'imageGrid' }> =>
                      block.id === imagePicker.editBlockId && block.type === 'imageGrid',
                  )?.images ?? [];
                await persist(replaceGridImages(current, imagePicker.editBlockId, next));
                if (next.length === 0) setImagePicker(null);
                for (const image of previous.filter((item) => !next.some((keep) => keep.id === item.id))) {
                  try {
                    await deleteOfferAttachmentStorage(leadRouteId, image);
                  } catch {
                    /* draft already dropped the image */
                  }
                }
              }
            : undefined
        }
        onClose={() => setImagePicker(null)}
        onAdd={async (added) => {
          if ('editBlockId' in imagePicker) return;
          const current =
            imagePicker.zone === 'intro' ? snapRef.current.introContent : snapRef.current.closingContent;
          const persist = imagePicker.zone === 'intro' ? persistIntroContent : persistClosingContent;
          await persist(
            insertContentBlock(current, imagePicker.index, {
              id: newDocumentContentId(),
              type: 'imageGrid',
              images: added.slice(0, remainingImageSlots),
            }),
          );
        }}
      />
    ) : null}
    <div
      className="fixed inset-0 z-[250] flex flex-col bg-editor-desk text-app-text"
    >
      <header className="shrink-0 flex flex-row items-center gap-2 md:gap-3 px-3 py-2.5 bg-app-surface border-b border-app-border shadow-sm">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-app-text-muted hover:bg-app-hover shrink-0"
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-app-text text-sm md:text-base leading-snug break-words flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              <span className="tabular-nums text-app-text shrink-0">{documentExportRefPreview}</span>
              <span className="text-slate-400 font-normal shrink-0" aria-hidden>
                |
              </span>
              <span className="min-w-0 font-semibold">
                {formatOfferTitleLine(documentTypeLabel, offerTitleRest, {
                  hidePrefix: hideDocumentTypeInTitle,
                })}
              </span>
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {pdfPreview?.documentNumberCommitted ? (
                <span className={EDITOR_STATUS_BADGE_DONE}>
                  <i className={EDITOR_STATUS_DOT} aria-hidden />
                  Abgeschlossen
                </span>
              ) : (
                <span className={EDITOR_STATUS_BADGE_DRAFT}>
                  <i className={EDITOR_STATUS_DOT} aria-hidden />
                  Entwurf
                </span>
              )}
              <span className="text-xs text-app-text-subtle hidden sm:inline" aria-hidden>
                ·
              </span>
              <span className="text-xs text-app-text-subtle hidden sm:inline">Gespeichert</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 md:hidden">
          <EditorViewToggle
            value={editorView}
            onChange={setEditorView}
            calculationHintCount={documentIssued ? 0 : calculationHintCount}
          />
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {documentIssued ? null : (
            <OfferAttachmentsButton
              leadRouteId={leadRouteId}
              attachments={attachments}
              onChange={commitAttachments}
              standardAttachments={standardAttachmentsForEditor}
              onExcludeStandard={excludeStandardAttachment}
              onRestoreStandard={restoreStandardAttachment}
              className={EDITOR_HEADER_ACTION_CLASS}
            />
          )}
          {documentIssued || !offeringId ? null : (
            <OfferImportActionButton
              offeringId={offeringId}
              onImported={reloadPositions}
              className={EDITOR_HEADER_ACTION_CLASS}
            />
          )}
          {offeringId ? (
            <GaebActionButton
              offeringId={offeringId}
              exportOnly
              label={
                <>
                  <FileText className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
                  GAEB-Export
                </>
              }
              className={EDITOR_HEADER_ACTION_CLASS}
            />
          ) : null}
          {documentIssued || !idsEnabled ? null : offeringId ? (
            <IdsCartButton
              leadRouteId={leadRouteId}
              offeringId={offeringId}
              documentTypeLabel={documentTypeLabel}
              documentNo={documentExportRefPreview}
              onImported={reloadPositions}
              className={EDITOR_HEADER_ACTION_CLASS}
              autoOpen={idsAutoOpen}
              onAutoOpened={clearIdsAutoOpen}
            />
          ) : null}
          <EditorViewToggle
            value={editorView}
            onChange={setEditorView}
            calculationHintCount={documentIssued ? 0 : calculationHintCount}
          />
          <div ref={previewMenuWrapRef} className="relative flex items-center">
            <button
              type="button"
              onClick={() => startPreviewWithDetail('full')}
              disabled={pdfLoading}
              className={`inline-flex h-9 items-center bg-pipedrive-blue px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:pointer-events-none ${
                canChoosePositionDetail ? 'rounded-l-md' : 'rounded-md'
              }`}
            >
              {pdfLoading
                ? 'PDF wird erzeugt…'
                : documentIssued
                  ? 'PDF anzeigen'
                  : 'Weiter zur Vorschau'}
            </button>
            {canChoosePositionDetail ? (
              <button
                type="button"
                onClick={() => setPreviewMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={previewMenuOpen}
                aria-label="Weitere Ausgabeoption"
                title="Weitere Ausgabeoption"
                className="inline-flex h-9 items-center rounded-r-md border-l border-blue-400/60 bg-pipedrive-blue px-2 text-white hover:bg-blue-700"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            ) : null}
            {canChoosePositionDetail && previewMenuOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-full z-[300] mt-1 w-72 overflow-hidden rounded-md border border-app-border bg-app-surface shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={pdfLoading || !hasTitleRows}
                  aria-disabled={!hasTitleRows}
                  title={!hasTitleRows ? titlesOnlyDisabledReason : undefined}
                  onClick={() => {
                    if (!hasTitleRows) return;
                    setPreviewMenuOpen(false);
                    startPreviewWithDetail('titles');
                  }}
                  className={`block w-full px-3 py-2 text-left ${
                    hasTitleRows
                      ? 'hover:bg-app-hover disabled:opacity-60'
                      : 'cursor-not-allowed opacity-50'
                  }`}
                >
                  <span
                    className={`block text-sm font-medium ${
                      hasTitleRows ? 'text-app-text' : 'text-app-text-subtle'
                    }`}
                  >
                    Nur Titel ausgeben
                  </span>
                  <span className="block text-xs text-app-text-muted">
                    {hasTitleRows
                      ? 'Nur Titel mit ihren Summen im PDF'
                      : titlesOnlyDisabledReason}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <DndContext
        sensors={dndSensors}
        collisionDetection={offerDndCollisionDetection}
        onDragStart={onTableDragStart}
        onDragCancel={onTableDragCancel}
        onDragEnd={async (e) => {
          await onTableDragEnd(e);
          setActiveDragPreview(null);
        }}
      >
      <div className="flex-1 min-h-0 flex flex-col md:flex-row min-w-0">
        <div className="relative flex flex-1 min-w-0 min-h-0 flex-col">
        <div
          ref={(node) => {
            documentPreviewRef.current = node;
            setDocumentScrollEl(node);
          }}
          className={`app-scrollbar flex flex-1 min-w-0 min-h-0 flex-col overflow-y-auto ${
            documentZoomEnabled && documentZoom > 1 ? 'overflow-x-auto' : 'overflow-x-hidden'
          } overscroll-contain max-md:bg-app-surface max-md:px-0 max-md:py-0 ${
            editorView === 'calculation' ? 'md:p-0 xl:overflow-hidden' : 'md:p-4 lg:p-6'
          }`}
        >
          {loadError && (
            <div className="max-w-[210mm] mx-auto mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {loadError}
            </div>
          )}
          {isInvoiceFamilyDocumentType(documentTypeLabel) ? (
            <CustomerBlockNotice
              className="max-w-[210mm] mx-auto mb-4"
              isBlocked={invoiceDebtor.isBlocked}
              blockReason={invoiceDebtor.blockReason}
              warningText={invoiceDebtor.warningText}
            />
          ) : null}
          {stornoBootstrapError ? (
            <div className="max-w-[210mm] mx-auto mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 flex items-start justify-between gap-3">
              <span>{stornoBootstrapError}</span>
              <button
                type="button"
                onClick={() => setStornoBootstrapError(null)}
                className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100/80"
              >
                Schließen
              </button>
            </div>
          ) : null}
          {editorView === 'calculation' ? (
            <>
            <PositionQuoteOffersPopover
              open={quoteOffersRow != null}
              onClose={() => {
                if (quoteApplying) return;
                setQuoteOffersRow(null);
                setQuoteApplyError(null);
              }}
              position={quoteOffersContext}
              offers={quoteOffersContext ? quoteOffers?.byPositionId[quoteOffersContext.positionId] ?? null : null}
              applying={quoteApplying}
              canApply={!documentIssued}
              onApply={async (offer: PositionQuoteOffer | null) => {
                if (!offeringId || !quoteOffersContext) return;
                setQuoteApplying(true);
                setQuoteApplyError(null);
                try {
                  const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}/apply-quote-offer`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ position_id: quoteOffersContext.positionId, quote_id: offer?.quoteId ?? null }),
                  });
                  const body = (await res.json().catch(() => ({}))) as { error?: string };
                  if (!res.ok) throw new Error(body.error || 'Übernahme fehlgeschlagen.');
                  await Promise.all([reloadPositions(), reloadQuoteOffers()]);
                  setQuoteOffersRow(null);
                } catch (e) {
                  setQuoteApplyError(e instanceof Error ? e.message : String(e));
                } finally {
                  setQuoteApplying(false);
                }
              }}
            />
            {quoteApplyError && quoteOffersRow ? (
              <div className="mx-3 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{quoteApplyError}</div>
            ) : null}
            <OfferCalculationTable
              quoteOffersByPositionId={quoteOfferSummaries}
              onOpenQuoteOffers={(row) => {
                setQuoteApplyError(null);
                setQuoteOffersRow(row);
              }}
              rows={calculationTableRows}
              marginHints={documentIssued ? undefined : calculationMarginHints}
              onDismissMarginHints={documentIssued ? undefined : dismissCalculationHints}
              readOnly={documentIssued}
              onCommit={handleCalculationRowCommit}
              hourlyRate={calculationHourlyRate}
              realLaborCostRate={calculationRealLaborCostRate}
              listLaborSeparately={calculationLaborSeparately}
              onListLaborSeparatelyChange={(value) => {
                setCalculationLaborSeparately(value);
                void persistFull({ calculationLaborSeparately: value });
              }}
              onHourlyRateCommit={(value) => {
                setCalculationHourlyRate(value);
                setCalculationHourlyRateFromCustomer(false);
                void persistFull({
                  calculationHourlyRate: value,
                  calculationHourlyRateFromCustomer: false,
                });
              }}
              onRealLaborCostRateCommit={(value) => {
                setCalculationRealLaborCostRate(value);
                void persistFull({ calculationRealLaborCostRate: value });
              }}
              copperQuoteEurPer100kg={calculationCopperQuoteEurPer100kg}
              aluminumQuoteEurPer100kg={calculationAluminumQuoteEurPer100kg}
              metalQuoteSource={calculationMetalQuoteSource}
              metalQuoteDate={calculationMetalQuoteDate}
              metalProcurementCostPercent={calculationMetalProcurementCostPercent}
              instanceMetalQuoteSource={instanceMetalQuotes.source}
              instanceMetalQuoteDate={instanceMetalQuotes.quoteDate}
              onMetalQuotesCommit={(values) => void commitMetalQuotes(values)}
              metalQuotesSaving={metalQuotesSaving}
              onToggleEventual={treatEventualAsNormal ? undefined : toggleRowEventual}
              onImageUpload={(row, file) => onRowImageUpload(row.id, file)}
              onDeleteRow={(row) => requestRowDelete(row)}
              isRowDeleting={(row) =>
                row.isApi &&
                isPositionRowId(row.leadPositionId) &&
                isPositionRowId(deletingPositionId) &&
                positionRowIdsEqual(deletingPositionId, row.leadPositionId)
              }
              sidebarContent={documentIssued ? null : positionToolsContent}
              sidebarNeedsHeight={panelMode === 'search' && canViewStammdaten}
              tableRef={positionTableRef}
              renderDropBand={(index, columnCount) => (
                <DropBand id={`drop-${index}`} dropIndex={index} colSpan={columnCount} />
              )}
            />
            </>
          ) : layoutPreviewPending ? (
            <PageLoadingState />
          ) : (
          <>
          <div
            ref={sheetOuterRef}
            className={
              showLayoutBands
                ? `mx-auto shrink-0 ${sheetScale !== 1 ? 'overflow-hidden' : ''}`
                : undefined
            }
            style={
              showLayoutBands
                ? {
                    // Box takes the scaled size so the pane scrolls over the whole zoomed sheet.
                    width: `${PDF_A4_WIDTH_PT * (96 / 72) * sheetScale}px`,
                    // Only lock the height while scaled; the inner ResizeObserver keeps it
                    // in sync so closing text + footer are never clipped.
                    height:
                      sheetScale !== 1 && sheetLayoutHeightPx != null
                        ? `${sheetLayoutHeightPx * sheetScale}px`
                        : undefined,
                  }
                : undefined
            }
          >
            <div
              ref={sheetInnerRef}
              style={
                showLayoutBands
                  ? {
                      width: `${PDF_A4_WIDTH_PT}pt`,
                      transform: sheetScale !== 1 ? `scale(${sheetScale})` : undefined,
                      transformOrigin: 'top left',
                    }
                  : undefined
              }
            >
          <article
            className={`theme-light mx-auto bg-app-surface offer-doc-print
              ${documentIssued ? 'pointer-events-none' : ''}
              ${
                showLayoutBands
                  ? 'relative text-[#334155] shadow-[0_4px_24px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.06]'
                  : `max-w-[210mm] text-[9.5pt] leading-[1.45] text-neutral-800 max-md:text-app-text-muted
              max-md:min-h-0 max-md:w-full max-md:max-w-none max-md:mx-0 max-md:px-10 max-md:pt-9 max-md:pb-12 max-md:shadow-none max-md:ring-0 max-md:rounded-none
              md:min-h-[calc(100vh-8rem)] md:px-[56px] md:pt-10 md:pb-14 md:shadow-[0_4px_24px_rgba(0,0,0,0.08)] md:ring-1 md:ring-black/[0.06]`
              }`}
            style={
              showLayoutBands
                ? {
                    ...OFFER_DOCUMENT_FONT_STYLE,
                    width: `${PDF_A4_WIDTH_PT}pt`,
                    minHeight: `${PDF_A4_HEIGHT_PT}pt`,
                    padding: 0,
                    fontSize: `${bodyFontSizePt}pt`,
                    lineHeight: 1.45,
                  }
                : OFFER_DOCUMENT_FONT_STYLE
            }
          >
            {showLayoutBands && documentLayout && offerBandData ? (
              <>
                <HtmlHeaderBand
                  layout={documentLayout}
                  data={offerBandData}
                  renderOverride={renderBandOverride}
                />
                <HtmlFoldMarks layout={documentLayout} />
              </>
            ) : null}
            <div
              style={
                showLayoutBands && documentLayout
                  ? {
                      ...OFFER_DOCUMENT_FONT_STYLE,
                      paddingTop: `${Math.max(documentLayout.header.heightPt + 8, pageMargins.top)}pt`,
                      paddingLeft: `${pageMargins.left}pt`,
                      paddingRight: `${pageMargins.right}pt`,
                      paddingBottom: `${Math.max(
                        documentLayout.footer.heightPt + 12,
                        pageMargins.bottom,
                      )}pt`,
                    }
                  : OFFER_DOCUMENT_FONT_STYLE
              }
            >
            {!showLayoutBands ? (
            <>
            <div className="flex flex-row flex-nowrap gap-5 sm:gap-x-10 items-start mb-7 max-md:gap-4">
              <div className="min-w-0 flex-[1_1_0%] flex flex-col">
                {company ? (
                  <div
                    className={
                      company.has_logo
                        ? 'flex min-h-[calc(56px+1.25rem+8px)] sm:min-h-[calc(64px+1.25rem+8px)] items-end mb-4'
                        : 'mb-4'
                    }
                  >
                    <p className="text-[7.5pt] font-bold leading-[1.45] text-neutral-900 tracking-wide w-full pt-6 sm:pt-7">
                      {(company.company_name ?? '').trim() || 'Firma'}
                      {companyLines.length > 0 ? ` | ${companyLines.join(' | ')}` : ''}
                    </p>
                  </div>
                ) : null}
                <div className="text-[9.5pt] text-neutral-800 font-normal leading-[1.45] space-y-0">
                  <OfferInlineEdit
                    value={recipientLayout.nameLines.join('\n')}
                    onSave={async (v) => {
                      const next = v
                        .split(/\r?\n/)
                        .map((x) => x.trim())
                        .filter(Boolean);
                      setRecipientNameLinesEdit(next);
                      await persistFull({ recipientNameLines: next });
                    }}
                    multiline
                    viewTextClassName="text-[9.5pt] text-neutral-800 leading-[1.45]"
                    aria-label="Empfänger Name/Firma"
                  />
                  <OfferInlineEdit
                    value={recipientLayout.streetLine}
                    onSave={async (v) => {
                      setRecipientStreetLineEdit(v);
                      await persistFull({ recipientStreetLine: v });
                    }}
                    multiline={false}
                    viewTextClassName="text-[9.5pt] text-neutral-800 leading-[1.45]"
                    emptyHint="Straße und Hausnummer"
                    aria-label="Empfänger Straße"
                  />
                  <OfferInlineEdit
                    value={recipientLayout.plzOrtLine}
                    onSave={async (v) => {
                      setRecipientPlzOrtLineEdit(v);
                      await persistFull({ recipientPlzOrtLine: v });
                    }}
                    multiline={false}
                    viewTextClassName="text-[9.5pt] text-neutral-800 leading-[1.45]"
                    emptyHint="PLZ Ort"
                    aria-label="Empfänger PLZ Ort"
                  />
                </div>
                {company && showAnsprechBlock ? (
                  <div className="mt-3 text-[9.5pt] leading-[1.45] text-neutral-800 font-normal">
                    <p className="m-0 leading-[1.45] text-transparent select-none" aria-hidden>
                      &nbsp;
                    </p>
                    <div className="flex flex-col gap-1 leading-[1.45]">
                      <p className="font-bold m-0">Ihr Ansprechpartner</p>
                      <p className="m-0">
                        {ansprechRole && ansprechNames
                          ? `${ansprechRole}: ${ansprechNames}`
                          : ansprechNames || ansprechRole}
                      </p>
                      {ansprechMail ? (
                        <p className="m-0">
                          <a
                            href={`mailto:${ansprechMail}`}
                            className="text-inherit no-underline hover:no-underline break-all"
                          >
                            {ansprechMail}
                          </a>
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div
                className="min-w-0 shrink-0 w-[42%] max-w-[160px] text-left text-[9.5pt] text-neutral-800 font-normal leading-[1.45]"
              >
                {company?.has_logo ? (
                  <div className="mb-5 flex justify-end w-full">
                    <img
                      src={companyLogoSrc(urlInstanceId, logoBust)}
                      alt="Logo"
                      className="max-h-[56px] sm:max-h-[64px] max-w-[160px] w-auto object-contain object-right"
                    />
                  </div>
                ) : null}
                {company ? null : (
                  <p className="text-slate-400 italic text-[9.5pt]">Firmendaten werden geladen…</p>
                )}
              </div>
            </div>

            <div className="mb-7 leading-tight">
              <p className="text-[12.5pt] font-bold text-neutral-900 tracking-tight m-0 tabular-nums">
                {documentTypeLabel} {documentExportRefPreview}
              </p>
              <h1 className="text-[12.5pt] font-bold text-neutral-900 tracking-tight mt-1.5 m-0">
                <OfferInlineEdit
                  value={offerTitleEditValue}
                  onSave={saveOfferTitleLine}
                  multiline={false}
                  viewContent={
                    <span className="flex flex-wrap items-baseline">
                      {offerTitleParts.prefix ? (
                        <span style={{ whiteSpace: 'pre' }}>{offerTitleParts.prefix}</span>
                      ) : null}
                      <span className="min-w-0">{offerTitleParts.title}</span>
                    </span>
                  }
                  viewTextClassName="text-[12.5pt] font-bold text-neutral-900"
                  viewWrapperClassName="!w-full max-w-full align-baseline"
                  editInputClassName="text-[12.5pt] font-bold"
                  aria-label="Angebotstitel im Dokument"
                />
              </h1>
            </div>

            <div className="mb-8 max-md:gap-3">
              <div className="flex flex-row flex-nowrap justify-between items-start gap-4 sm:gap-6">
                <div className="min-w-0 flex-1 flex flex-row flex-nowrap items-start gap-4 sm:gap-5 pr-2 sm:pr-4">
                  <div className={`min-w-0 ${showSecondaryMeta ? 'flex-[2]' : 'flex-1'}`}>
                    <span className={metaLabelClass}>Leistungsort</span>
                    <div className="mt-0.5">
                      <OfferInlineEdit
                        value={leistungsortEdit}
                        onSave={async (v) => {
                          setLeistungsortEdit(v);
                          await persistFull({ leistungsort: v });
                        }}
                        multiline
                        viewTextClassName="font-bold text-neutral-900 text-[9.5pt] leading-tight"
                        aria-label="Leistungsort"
                      />
                    </div>
                  </div>
                  {showSecondaryMeta ? (
                    <div className="min-w-0 flex-1 shrink">
                      <div className="flex flex-row flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                        <span className={metaLabelClass}>{secondaryMetaLabel}</span>
                        {canHideSecondaryMeta ? (
                          <button
                            type="button"
                            className={metaHideBtnClass}
                            onClick={() => {
                              if (secondaryMetaKind === 'lieferdatum') {
                                setHideLieferdatum(true);
                                void persistFull({ hideLieferdatum: true });
                              } else {
                                setHideLeistungszeitraum(true);
                                void persistFull({ hideLeistungszeitraum: true });
                              }
                            }}
                          >
                            Ausblenden
                          </button>
                        ) : null}
                      </div>
                      <div className="mt-0.5">
                        <OfferLeistungszeitraumEdit
                          value={secondaryMetaValue}
                          onSave={async (v) => {
                            if (secondaryMetaKind === 'lieferdatum') {
                              setLieferdatumEdit(v);
                              await persistFull({ lieferdatum: v });
                            } else {
                              setLeistungszeitraumEdit(v);
                              await persistFull({ leistungszeitraum: v });
                            }
                          }}
                          viewTextClassName="font-bold text-neutral-900 text-[9.5pt] leading-tight"
                          aria-label={secondaryMetaLabel}
                          {...lzEditInvoiceProps}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="shrink-0 text-right pl-1">
                  <span className={metaLabelClass}>Ausstellungsdatum</span>
                  <OfferInlineEdit
                    value={issueDateEdit}
                    onSave={async (v) => {
                      setIssueDateEdit(formatDDMMYYYYInput(v));
                    }}
                    multiline={false}
                    viewTextClassName="font-bold text-neutral-900 text-[9.5pt] leading-tight mt-0.5 tabular-nums"
                    aria-label="Ausstellungsdatum"
                  />
                </div>
              </div>
              {!showSecondaryMeta && secondaryMetaKind ? (
                <div className="mt-1.5">
                  <button
                    type="button"
                    className={metaHideBtnClass}
                    onClick={() => {
                      if (secondaryMetaKind === 'lieferdatum') {
                        setHideLieferdatum(false);
                        void persistFull({ hideLieferdatum: false });
                      } else {
                        setHideLeistungszeitraum(false);
                        void persistFull({ hideLeistungszeitraum: false });
                      }
                    }}
                  >
                    {secondaryMetaLabel} einblenden
                  </button>
                </div>
              ) : null}
            </div>
            </>
            ) : null}

            <div className="mb-8">
              <OfferDocumentContentList
                leadRouteId={leadRouteId}
                blocks={introContent}
                minTextBlocks={0}
                remainingImageSlots={remainingImageSlots}
                onCommitText={async (id, content) => {
                  await persistIntroContent(updateContentText(snapRef.current.introContent, id, content));
                }}
                onRemoveBlock={async (id) => {
                  await persistIntroContent(removeContentBlock(snapRef.current.introContent, id));
                }}
                onAddText={async (index) => {
                  await persistIntroContent(
                    insertContentBlock(snapRef.current.introContent, index, {
                      id: newDocumentContentId(),
                      type: 'text',
                      content: '',
                    }),
                  );
                }}
                onAddImages={(index) => setImagePicker({ zone: 'intro', index })}
                onRemoveImage={async (attachment) => {
                  await persistIntroContent(
                    removeAttachmentFromContent(snapRef.current.introContent, attachment.id),
                  );
                  try {
                    await deleteOfferAttachmentStorage(leadRouteId, attachment);
                  } catch {
                    /* draft already dropped the image */
                  }
                }}
                onReplaceImage={(attachment) => {
                  const blockId = findImageGridBlockId(snapRef.current.introContent, attachment.id);
                  if (blockId) setImagePicker({ zone: 'intro', editBlockId: blockId });
                }}
              />
            </div>

            {positionMutateError ? (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[9.5pt] text-red-800">
                {positionMutateError}
              </div>
            ) : null}

            {!tableStartsOnPage2 ? positionTableToolbar : null}

            {tableStartsOnPage2 ? (
              <div
                className="mt-4 mb-3 flex items-center gap-2 text-[8.5pt] font-semibold tracking-wide text-app-text-subtle"
                aria-hidden
              >
                <span className="h-px flex-1 border-t border-dashed border-app-border" />
                Seite 2
                <span className="h-px flex-1 border-t border-dashed border-app-border" />
              </div>
            ) : null}

            {tableStartsOnPage2 ? positionTableToolbar : null}

            <div
              ref={(node) => {
                positionTableRef.current = node;
                setDocumentTableEl(node);
              }}
              className={`app-scrollbar mt-2 mb-8 ${showLayoutBands ? 'overflow-visible' : 'overflow-x-auto -mx-1'}`}
            >
              <table
                className={`w-full leading-tight border-collapse table-fixed font-inherit ${showLayoutBands ? '' : 'text-[9.5pt]'}`}
                style={showLayoutBands ? { fontSize: `${bodyFontSizePt}pt` } : undefined}
              >
                <colgroup>
                  {hidePositionPrices ? (
                    showArticleNumberColumn ? (
                      <>
                        <col className="w-[6%]" style={positionColumnStyle} />
                        <col className="w-[8%]" />
                        <col style={unitColumnStyle} />
                        <col className="w-[14%]" />
                        <col />
                        <col className={showLayoutBands ? 'w-0' : 'w-[4%]'} />
                      </>
                    ) : (
                      <>
                        <col className="w-[7%]" style={positionColumnStyle} />
                        <col className="w-[8%]" />
                        <col style={unitColumnStyle} />
                        <col />
                        <col className={showLayoutBands ? 'w-0' : 'w-[4%]'} />
                      </>
                    )
                  ) : showPositionBillingColumns ? (
                    <>
                      <col className="w-[6%]" style={positionColumnStyle} />
                      <col className="w-[10%]" />
                      <col className="w-[10%]" />
                      <col style={unitColumnStyle} />
                      <col />
                      <col style={amountColumnStyle} />
                      <col style={amountColumnStyle} />
                      <col className={showLayoutBands ? 'w-0' : 'w-[32px]'} />
                    </>
                  ) : (
                    <>
                      <col className="w-[7%]" style={positionColumnStyle} />
                      <col className="w-[8%]" />
                      <col style={unitColumnStyle} />
                      <col />
                      <col style={amountColumnStyle} />
                      <col style={amountColumnStyle} />
                      <col className={showLayoutBands ? 'w-0' : 'w-[32px]'} />
                    </>
                  )}
                </colgroup>
                <thead>
                  <tr
                    className="text-[9.5pt] font-normal text-neutral-900 leading-[1.45]"
                    style={{ backgroundColor: tableHeaderBg }}
                  >
                    <th className="py-1.5 pl-[8pt] pr-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90">
                      Pos.
                    </th>
                    <th className="py-1.5 px-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90">
                      {showPositionBillingColumns ? (
                        <div className="leading-[1.2]">
                          <div>Menge</div>
                          <div className="text-[8pt] font-normal text-app-text-subtle">Bereits abg.</div>
                        </div>
                      ) : (
                        'Menge'
                      )}
                    </th>
                    {showPositionBillingColumns ? (
                      <th className="py-1.5 px-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90">
                        <div className="leading-[1.2]">
                          <div>Diese</div>
                          <div>Rechnung</div>
                        </div>
                      </th>
                    ) : null}
                    <th className="py-1.5 px-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90">Einheit</th>
                    {showArticleNumberColumn ? (
                      <th className="py-1.5 px-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90 whitespace-nowrap">
                        Artikel-Nr.
                      </th>
                    ) : null}
                    <th className="py-1.5 px-2 text-left align-bottom border-0 border-b border-b-app-border-strong/90">
                      Beschreibung
                    </th>
                    {!hidePositionPrices ? (
                      <th className="py-1.5 pl-2 pr-0 text-right align-bottom border-0 border-b border-b-app-border-strong/90 whitespace-nowrap">
                        Preis
                      </th>
                    ) : null}
                    {!hidePositionPrices ? (
                      <th className="py-1.5 pl-2 pr-[4pt] text-right align-bottom border-0 border-b border-b-app-border-strong/90 whitespace-nowrap">
                        Gesamt
                      </th>
                    ) : null}
                    <th
                      className={`py-1.5 pl-1 pr-0 text-center align-bottom border-0 border-b border-b-app-border-strong/90 ${showLayoutBands ? 'w-0 p-0 border-0' : 'w-8'}`}
                      aria-label="Entfernen"
                    />
                  </tr>
                </thead>
                <tbody>
                  {documentRowWindow.paddingTop > 0 ? (
                    <tr aria-hidden style={{ height: documentRowWindow.paddingTop }}>
                      <td
                        colSpan={positionTableColSpan}
                        style={{
                          height: documentRowWindow.paddingTop,
                          padding: 0,
                          border: 0,
                          lineHeight: 0,
                          fontSize: 0,
                        }}
                      />
                    </tr>
                  ) : null}
                  {tableRows.slice(documentRowWindow.start, documentRowWindow.end).map((row, offset) => {
                    const idx = documentRowWindow.start + offset;
                    const lt = lineTotal(row.quantity, row.unit_price_vk);
                    const isMeta = row.kind === 'title' || row.kind === 'text' || row.kind === 'image';
                    const isTitle = row.kind === 'title';
                    const metaPlaceholder = null;
                    const isSurcharge = row.kind === 'positionSurcharge';
                    const isVirtualLabor = isVirtualTitleLaborRow(row);
                    const hourLaborEpEditable =
                      isHourLaborPricedRow(row) && !calculationLaborSeparately && !isVirtualLabor;
                    const laborBakedIntoPrice =
                      !calculationLaborSeparately &&
                      laborSharePerUnit(
                        effectiveLaborMinutes(row),
                        effectiveHourlyRate(row, calculationHourlyRate),
                      ) > 0;
                    const lockUnitPrice =
                      isSurcharge || isVirtualLabor || (laborBakedIntoPrice && !hourLaborEpEditable);
                    const titleSubtotals = titleSubtotalByAfterRowId.get(row.id) ?? [];
                    const billingState = positionBillingStateByRowId.get(row.id);
                    const billingLineTotal =
                      billingState && showPositionBillingColumns
                        ? lineTotal(billingState.invoiceQty, row.unit_price_vk)
                        : lt;
                    if (isLumpSumPartialInvoice) {
                      return (
                        <tr key={row.id} className="border-b border-b-app-border/90 bg-app-surface">
                          <td className="py-2 pl-[8pt] pr-2 align-top text-neutral-600 tabular-nums leading-tight">1</td>
                          <td className="py-2 px-2 align-top text-left tabular-nums text-neutral-900 leading-tight">1,00</td>
                          <td className="py-2 px-2 align-top text-neutral-800 leading-tight">psch</td>
                          <td className="py-2 px-2 align-top text-neutral-900 leading-tight">
                            <OfferInlineEdit
                              value={effectivePartialInvoiceLineName}
                              onSave={async (v) => {
                                const customTitle =
                                  v.trim() === partialInvoiceLineNameFallback.trim() ? '' : v;
                                setPartialInvoiceLineName(customTitle);
                                await persistFull({ partialInvoiceLineName: customTitle });
                              }}
                              emptyHint={partialInvoiceLineNameFallback}
                              aria-label="Titel der Abschlagsposition"
                            />
                            <OfferInlineEdit
                              value={effectivePartialDescription}
                              onSave={async (v) => {
                                setPartialInvoiceDescription(v);
                                await persistFull({ partialInvoiceDescription: v });
                              }}
                              multiline
                              viewTextClassName="text-[9.5pt] text-app-text-muted leading-tight mt-1"
                              emptyHint={partialInvoiceDescriptionFallback}
                              aria-label="Abschlagsrechnung Beschreibung"
                            />
                          </td>
                          <td className="py-2 pl-2 pr-0 align-top text-right tabular-nums text-neutral-900 leading-tight">
                            {formatEuro(row.unit_price_vk)}
                          </td>
                          <td className="py-2 pl-2 pr-[4pt] align-top text-right tabular-nums font-normal text-neutral-900 leading-tight">
                            {formatEuro(lt)}
                          </td>
                          <td className={`py-2 pl-1 pr-0 align-top text-center text-slate-300 ${showLayoutBands ? 'w-0 p-0 relative overflow-visible' : ''}`}>
                            {showLayoutBands ? null : '—'}
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <React.Fragment key={row.id}>
                        <DropBand id={`drop-${idx}`} dropIndex={idx} colSpan={positionTableColSpan} />
                        <DraggableOfferRow id={`row-${row.id}`} rowId={row.id}>
                          {({ setNodeRef, style, attributes, listeners }) => (
                          <tr
                            ref={(node) => {
                              setNodeRef(node);
                              documentRowWindow.measureRow(row.id, node);
                            }}
                            style={style}
                            data-offer-row-index={idx}
                            className={`group/offer-row border-b border-b-app-border/90 bg-app-surface ${showLayoutBands ? 'relative' : ''}`}
                          >
                              <td className="py-2 pl-[8pt] pr-2 align-top text-neutral-600 tabular-nums leading-tight">
                                <div className="flex items-start gap-1">
                                  {documentIssued || isVirtualLabor ? null : (
                                  <span
                                    {...attributes}
                                    {...listeners}
                                    className="mt-0.5 inline-flex shrink-0 cursor-grab touch-none rounded p-0.5 text-slate-400 hover:text-app-text-muted active:cursor-grabbing"
                                    aria-label="Position verschieben"
                                  >
                                    <GripVertical className="w-3.5 h-3.5 pointer-events-none" />
                                  </span>
                                  )}
                                  <span className={`min-w-0 pt-0.5 leading-tight ${isTitle ? 'font-bold text-neutral-900' : ''}`}>
                                    {(row.kind === 'text' || row.kind === 'image') ? null : row.numberLabel}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 px-2 align-top text-left tabular-nums text-neutral-900 leading-tight">
                                {isMeta ? (
                                  metaPlaceholder
                                ) : showPositionBillingColumns ? (
                                  <div className="space-y-0.5">
                                    <span className="block text-[9.5pt] tabular-nums">
                                      {formatGermanNumber(row.quantity, { maximumFractionDigits: 3 })}
                                    </span>
                                    {billingState ? (
                                      <>
                                        <span className="block text-[8pt] text-app-text-subtle tabular-nums">
                                          {formatGermanNumber(billingState.alreadyInvoicedQty, {
                                            maximumFractionDigits: 3,
                                          })}
                                        </span>
                                        {billingState.overInvoiced ? (
                                          <span className="block text-[7.5pt] text-amber-700">über Angebot</span>
                                        ) : null}
                                      </>
                                    ) : null}
                                  </div>
                                ) : isSurcharge || isVirtualLabor ? (
                                  <span className="inline-block px-2 py-1 text-[9.5pt]">
                                    {isVirtualLabor
                                      ? formatGermanNumber(row.quantity, { maximumFractionDigits: 2 })
                                      : '1'}
                                  </span>
                                ) : (
                                  <DecimalInput
                                    value={row.quantity}
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={3}
                                    onChange={(quantity) => {
                                      if (row.isApi && row.leadPositionId != null) {
                                        updateApiRowLocal(row.leadPositionId, { quantity });
                                      } else {
                                        updateDraftRow(row.id, { quantity });
                                      }
                                    }}
                                    onFocus={
                                      row.isApi ? () => handleApiPositionFieldFocus(row.leadPositionId) : undefined
                                    }
                                    onBlur={(committed) => handlePositionFieldBlur(row, { quantity: committed })}
                                    className="w-full min-w-0 font-inherit border border-app-border/90 rounded-md px-2 py-1 text-[9.5pt]"
                                  />
                                )}
                              </td>
                              {showPositionBillingColumns ? (
                                <td className="py-2 px-2 align-top text-left tabular-nums text-neutral-900 leading-tight">
                                  {isMeta || !billingState ? (
                                    metaPlaceholder
                                  ) : (
                                    <DecimalInput
                                      value={billingState.invoiceQty}
                                      minimumFractionDigits={0}
                                      maximumFractionDigits={3}
                                      max={billingState.remainingQty}
                                      onChange={(qty) =>
                                        setPositionInvoiceQty(
                                          billingState.rowKey,
                                          qty,
                                          billingState.remainingQty,
                                        )
                                      }
                                      className="w-full min-w-0 font-inherit border border-app-border/90 rounded-md px-2 py-1 text-[9.5pt] text-left"
                                    />
                                  )}
                                </td>
                              ) : null}
                              <td className="py-2 px-2 align-top text-neutral-800 leading-tight">
                                {isMeta ? null : isSurcharge || isVirtualLabor ? (
                                  documentIssued ? (
                                    <span className="inline-block py-1 text-[9.5pt] text-app-text-muted">
                                      {isVirtualLabor ? row.unit : 'psch'}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 text-[9.5pt] rounded-md border border-app-border bg-app-surface-muted text-app-text-muted">
                                      {isVirtualLabor ? row.unit : 'psch'}
                                    </span>
                                  )
                                ) : documentIssued ? (
                                  <span className="block min-w-0 py-1 text-[9.5pt]">{row.unit}</span>
                                ) : (
                                  <OfferUnitPicker
                                    value={row.unit}
                                    variant="page"
                                    ariaLabel={`Einheit Position ${row.numberLabel || ''}`.trim()}
                                    triggerClassName="!border-app-border/90 py-1 text-[9.5pt]"
                                    onChange={(unit) => {
                                      const patch = patchForUnitChange(unit, row.unit);
                                      if (row.isApi && row.leadPositionId != null) {
                                        handleApiPositionFieldFocus(row.leadPositionId);
                                        updateApiRowLocal(row.leadPositionId, patch);
                                      } else {
                                        updateDraftRow(row.id, patch);
                                      }
                                      handlePositionFieldBlur(row, patch);
                                    }}
                                  />
                                )}
                              </td>
                              {showArticleNumberColumn ? (
                                <td className="py-2 px-2 align-top text-neutral-700 leading-tight tabular-nums text-[9.5pt]">
                                  {isMeta ? (
                                    metaPlaceholder
                                  ) : (
                                    <span className="inline-flex items-center gap-1 min-w-0">
                                      <span className="truncate">{row.article_number?.trim() || ''}</span>
                                      {!documentIssued && idsEnabled && offeringId && row.article_number?.trim() ? (
                                        <button
                                          type="button"
                                          className="p-0.5 rounded text-pipedrive-blue hover:bg-app-hover"
                                          title="Im Großhändler-Shop öffnen"
                                          onClick={() =>
                                            void launchIdsArticleDeeplink({
                                              leadRouteId,
                                              offeringId,
                                              articleNumber: row.article_number!.trim(),
                                            }).catch(() => undefined)
                                          }
                                        >
                                          <Store className="w-3 h-3" />
                                        </button>
                                      ) : null}
                                    </span>
                                  )}
                                </td>
                              ) : null}
                              <td className="py-2 px-2 align-top text-neutral-900 leading-tight" style={{ paddingLeft: 8 + titleIndentPx(row.rowDepth) }}>
                                {row.kind === 'image' ? (
                                  <OfferRowImageUpload
                                    image={row.image}
                                    readOnly={documentIssued}
                                    onUpload={(file) => onRowImageUpload(row.id, file)}
                                  />
                                ) : (
                                  <>
                                    <div className="flex items-start gap-1.5 mb-1.5">
                                      {isVirtualLabor ? (
                                        documentIssued ? (
                                          <span className="block min-w-0 flex-1 py-1 text-[9.5pt]">
                                            {row.item_name}
                                          </span>
                                        ) : (
                                          <VirtualLaborNameInput
                                            value={row.item_name}
                                            onCommit={(next) => commitLaborRowName(row.id, next, row.quantity)}
                                            className="w-full min-w-0 flex-1 font-inherit border border-app-border/90 rounded-md px-2 py-1 text-[9.5pt]"
                                          />
                                        )
                                      ) : documentIssued ? (
                                        <span className={`block min-w-0 flex-1 py-1 ${isTitle ? 'font-bold' : ''} ${(row.titleLevel ?? 1) > 1 ? 'text-[9pt]' : 'text-[9.5pt]'}`}>
                                          {row.item_name}
                                        </span>
                                      ) : (
                                        <AutoResizeTextarea
                                          value={row.item_name}
                                          rows={1}
                                          onChange={(e) => {
                                            // Kurztext bleibt einzeilig (eingefügte Umbrüche → Leerzeichen), bricht aber sichtbar um.
                                            const item_name = e.target.value.replace(/\r?\n/g, ' ');
                                            if (row.isApi && row.leadPositionId != null) {
                                              updateApiRowLocal(row.leadPositionId, { item_name });
                                            } else {
                                              updateDraftRow(row.id, { item_name });
                                            }
                                          }}
                                          onFocus={
                                            row.isApi ? () => handleApiPositionFieldFocus(row.leadPositionId) : undefined
                                          }
                                          onBlur={() => handlePositionFieldBlur(row)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              return;
                                            }
                                            if (isTitle && e.key === 'Tab') {
                                              e.preventDefault();
                                              applyTitleLevel(
                                                row,
                                                (row.titleLevel ?? MIN_TITLE_LEVEL) + (e.shiftKey ? -1 : 1),
                                              );
                                            }
                                          }}
                                          placeholder={row.kind === 'title' ? 'Titel' : 'Kurztext'}
                                          className={`w-full min-w-0 flex-1 font-inherit border border-app-border/90 rounded-md px-2 py-1 resize-none overflow-hidden ${isTitle ? 'font-bold' : ''} ${(row.titleLevel ?? 1) > 1 ? 'text-[9pt]' : 'text-[9.5pt]'}`}
                                        />
                                      )}
                                      {!documentIssued && isTitle ? (
                                        <span className="inline-flex shrink-0 items-center gap-0.5">
                                          <button
                                            type="button"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                                            title="Ausrücken"
                                            aria-label="Titel ausrücken"
                                            disabled={(row.titleLevel ?? MIN_TITLE_LEVEL) <= MIN_TITLE_LEVEL}
                                            onClick={() => applyTitleLevel(row, (row.titleLevel ?? MIN_TITLE_LEVEL) - 1)}
                                          >
                                            <IndentDecrease className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-app-text-muted hover:bg-app-hover disabled:opacity-40"
                                            title="Einrücken"
                                            aria-label="Titel einrücken"
                                            disabled={
                                              (row.titleLevel ?? MIN_TITLE_LEVEL) >=
                                              maxAllowedTitleLevelAt(
                                                numberedRows,
                                                numberedRows.findIndex((r) => r.id === row.id),
                                              )
                                            }
                                            onClick={() => applyTitleLevel(row, (row.titleLevel ?? MIN_TITLE_LEVEL) + 1)}
                                          >
                                            <IndentIncrease className="h-3.5 w-3.5" />
                                          </button>
                                        </span>
                                      ) : null}
                                      {!documentIssued && isSurcharge ? (
                                        <button
                                          type="button"
                                          onClick={() => setSurchargeModalState({ mode: 'edit', rowId: row.id })}
                                          className="shrink-0 inline-flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 p-1.5 text-blue-400 hover:bg-blue-100 hover:text-blue-500"
                                          title="Zuschlag bearbeiten"
                                          aria-label="Zuschlag bearbeiten"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                      ) : null}
                                    </div>
                                    {row.kind !== 'title' && row.kind !== 'text' && !isVirtualLabor ? (
                                      <>
                                        {row.isEventual ? (
                                          <p className="mb-1 text-[9.5pt] leading-tight text-app-text-subtle select-none">
                                            {EVENTUAL_POSITION_HINT}
                                          </p>
                                        ) : null}
                                        {documentIssued ? (
                                          !hidePositionDescriptions && row.description.trim() ? (
                                            <p className="whitespace-pre-wrap text-[9.5pt] text-app-text-muted">
                                              {row.description}
                                            </p>
                                          ) : null
                                        ) : (
                                          <>
                                            {!hidePositionDescriptions ? (
                                              <AutoResizeTextarea
                                                value={row.description}
                                                onChange={(e) => {
                                                  const description = e.target.value;
                                                  if (row.isApi && row.leadPositionId != null) {
                                                    updateApiRowLocal(row.leadPositionId, { description });
                                                  } else {
                                                    updateDraftRow(row.id, { description });
                                                  }
                                                }}
                                                onFocus={
                                                  row.isApi ? () => handleApiPositionFieldFocus(row.leadPositionId) : undefined
                                                }
                                                onBlur={() => handlePositionFieldBlur(row)}
                                                placeholder="Beschreibung"
                                                rows={1}
                                                className="w-full font-inherit border border-app-border/90 rounded-md px-2 py-1 text-[9.5pt] text-app-text-muted resize-none overflow-hidden"
                                              />
                                            ) : null}
                                            {row.internal_note?.trim() || openInternalNoteRowIds.has(row.id) ? (
                                              <AutoResizeTextarea
                                                value={row.internal_note}
                                                autoFocus={!row.internal_note?.trim()}
                                                onChange={(e) => {
                                                  const internal_note = e.target.value;
                                                  if (row.isApi && row.leadPositionId != null) {
                                                    updateApiRowLocal(row.leadPositionId, { internal_note });
                                                  } else {
                                                    updateDraftRow(row.id, { internal_note });
                                                  }
                                                }}
                                                onFocus={
                                                  row.isApi ? () => handleApiPositionFieldFocus(row.leadPositionId) : undefined
                                                }
                                                onBlur={() => {
                                                  if (!row.internal_note?.trim()) setInternalNoteOpen(row.id, false);
                                                  handlePositionFieldBlur(row);
                                                }}
                                                placeholder="Interne Notiz (nicht im Kundendokument)"
                                                rows={1}
                                                className="mt-1 w-full font-inherit border border-amber-200 rounded-md px-2 py-1 text-[9pt] text-amber-800 bg-amber-50/50 resize-none overflow-hidden"
                                              />
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => setInternalNoteOpen(row.id, true)}
                                                className="mt-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[8.5pt] text-amber-700 opacity-0 transition-opacity hover:bg-amber-50 focus-visible:opacity-100 group-hover/offer-row:opacity-100 [@media(hover:none)]:opacity-100"
                                              >
                                                <Plus className="h-3 w-3" />
                                                Interne Notiz
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : null}
                                    {(row.components?.length ?? 0) > 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExpandedComponentRowIds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(row.id)) next.delete(row.id);
                                            else next.add(row.id);
                                            return next;
                                          });
                                        }}
                                        className="mt-1.5 inline-flex items-center gap-1 text-[8.5pt] text-app-text-subtle hover:text-app-text-muted"
                                        aria-expanded={expandedComponentRowIds.has(row.id)}
                                      >
                                        <ChevronRight
                                          className={`h-3.5 w-3.5 shrink-0 transition-transform ${expandedComponentRowIds.has(row.id) ? 'rotate-90' : ''}`}
                                        />
                                        Stückliste · {row.components!.length} Zeilen · EK {formatEuro(row.unit_cost_ek)} / Einh.
                                      </button>
                                    ) : null}
                                  </>
                                )}
                              </td>
                              {!hidePositionPrices ? (
                                <td className="py-2 pl-2 pr-0 align-top text-right tabular-nums text-neutral-900 leading-tight">
                                  {isMeta ? metaPlaceholder : lockUnitPrice ? (
                                    formatEuro(row.unit_price_vk)
                                  ) : (
                                    <DecimalInput
                                      value={row.unit_price_vk}
                                      clearOnFocusWhenZero
                                      onChange={(unit_price_vk) => {
                                        if (hourLaborEpEditable) {
                                          applyHourLaborEp(row, unit_price_vk, false);
                                          return;
                                        }
                                        if (row.isApi && row.leadPositionId != null) {
                                          updateApiRowLocal(row.leadPositionId, { unit_price_vk });
                                        } else {
                                          updateDraftRow(row.id, { unit_price_vk });
                                        }
                                      }}
                                      onFocus={
                                        row.isApi ? () => handleApiPositionFieldFocus(row.leadPositionId) : undefined
                                      }
                                      onBlur={(committed) => {
                                        if (hourLaborEpEditable) {
                                          applyHourLaborEp(row, committed, true);
                                          return;
                                        }
                                        handlePositionFieldBlur(row, {
                                          unit_price_vk: committed,
                                          surcharge_percent: surchargeForVk(
                                            row.unit_cost_ek,
                                            row.surcharge_percent,
                                            committed,
                                          ),
                                        });
                                      }}
                                      className="w-full min-w-0 font-inherit border border-app-border/90 rounded-md px-2 py-1 text-[9.5pt] text-right"
                                    />
                                  )}
                                </td>
                              ) : null}
                              {!hidePositionPrices ? (
                                <td className="py-2 pl-2 pr-[4pt] align-top text-right tabular-nums font-normal text-neutral-900 leading-tight">
                                  {isMeta
                                    ? metaPlaceholder
                                    : row.isEventual
                                      ? formatEventualGesamt(showPositionBillingColumns ? billingLineTotal : lt)
                                      : formatEuro(showPositionBillingColumns ? billingLineTotal : lt)}
                                </td>
                              ) : null}
                              <td
                                className={`py-2 pl-1 pr-0 align-top text-center ${showLayoutBands ? 'relative w-0 p-0 overflow-visible border-0' : ''}`}
                              >
                                {documentIssued || isVirtualLabor ? null : (
                                <div
                                  className={
                                    showLayoutBands
                                      ? 'absolute top-2 left-0 z-10 flex translate-x-2 flex-col items-center gap-0.5 print:hidden'
                                      : 'inline-flex flex-col items-center gap-0.5'
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={() => requestRowDelete(row)}
                                    disabled={
                                      row.isApi &&
                                      isPositionRowId(row.leadPositionId) &&
                                      isPositionRowId(deletingPositionId) &&
                                      positionRowIdsEqual(deletingPositionId, row.leadPositionId)
                                    }
                                    className="inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-40"
                                    title="Position entfernen"
                                    aria-label="Position entfernen"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                  {shouldShowLaborCostKindSwitcher(row) ? (
                                    <OfferCostKindSwitcher
                                      value={resolveLaborCostKind(row)}
                                      onChange={(next) => {
                                        setDraftRows((prev) => {
                                          const hasRow = prev.some((d) => d.id === row.id);
                                          const nextRows = hasRow
                                            ? prev.map((d) => (d.id === row.id ? { ...d, costKind: next } : d))
                                            : [
                                                ...prev,
                                                {
                                                  id: row.id,
                                                  kind: row.kind,
                                                  leadPositionId: row.leadPositionId,
                                                  sourceType: row.sourceType,
                                                  quantity: row.quantity,
                                                  unit: row.unit,
                                                  item_name: row.item_name,
                                                  description: row.description,
                                                  internal_note: row.internal_note,
                                                  unit_price_vk: row.unit_price_vk,
                                                  unit_cost_ek: row.unit_cost_ek,
                                                  surcharge_percent: row.surcharge_percent,
                                                  image: row.image,
                                                  positionSurcharge: row.positionSurcharge ?? null,
                                                  isEventual: row.isEventual,
                                                  costKind: next,
                                                },
                                              ];
                                          void persistFull({ draftRows: nextRows });
                                          return nextRows;
                                        });
                                      }}
                                    />
                                  ) : null}
                                  {!isMeta && !treatEventualAsNormal ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleRowEventual(row)}
                                      className={
                                        row.isEventual
                                          ? 'inline-flex h-5 w-5 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600'
                                          : 'inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-400 hover:bg-blue-50 hover:text-blue-500'
                                      }
                                      title={
                                        row.isEventual
                                          ? 'Eventualposition — Betrag nicht in der Angebotssumme. Klicken zum Aufheben.'
                                          : 'Als Eventualposition markieren — Einheitspreis bleibt, Betrag nicht in der Summe'
                                      }
                                      aria-pressed={row.isEventual}
                                      aria-label={
                                        row.isEventual
                                          ? 'Eventualposition aufheben'
                                          : 'Als Eventualposition markieren'
                                      }
                                    >
                                      <CircleHelp className="h-3.5 w-3.5" />
                                    </button>
                                  ) : null}
                                </div>
                                )}
                              </td>
                          </tr>
                          )}
                        </DraggableOfferRow>
                        {(row.components?.length ?? 0) > 0 && expandedComponentRowIds.has(row.id) ? (
                          <tr className="border-b border-b-app-border/90 bg-slate-50">
                            <td colSpan={positionTableColSpan} className="py-2 pl-7 pr-2">
                              <p className="mb-1.5 text-[8.5pt] font-semibold text-app-text-muted">
                                Stückliste (intern, nicht im PDF)
                              </p>
                              <table className="w-full text-[8.5pt] text-neutral-800">
                                <thead>
                                  <tr className="text-app-text-subtle">
                                    <th className="py-0.5 pr-2 text-left font-normal">Art.-Nr.</th>
                                    <th className="py-0.5 pr-2 text-left font-normal">Bezeichnung</th>
                                    <th className="py-0.5 pr-2 text-right font-normal">Menge</th>
                                    <th className="py-0.5 pr-2 text-left font-normal">Einh.</th>
                                    <th className="py-0.5 text-right font-normal">EK</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.components!.map((component) => {
                                    const role = uglComponentRoleLabel(component.ugl_role, component.surcharge_type);
                                    return (
                                      <tr key={component.id}>
                                        <td className="py-0.5 pr-2 tabular-nums">{component.article_number || '—'}</td>
                                        <td className="py-0.5 pr-2">
                                          {component.item_name}
                                          {role ? <span className="ml-1 text-app-text-subtle">({role})</span> : null}
                                          {component.description.trim() ? (
                                            <div className="whitespace-pre-wrap text-app-text-subtle">
                                              {component.description}
                                            </div>
                                          ) : null}
                                        </td>
                                        <td className="py-0.5 pr-2 text-right tabular-nums">
                                          {formatGermanNumber(component.quantity, { maximumFractionDigits: 3 })}
                                        </td>
                                        <td className="py-0.5 pr-2">{component.unit || ''}</td>
                                        <td className="py-0.5 text-right tabular-nums">
                                          {formatEuro(component.line_total_ek)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        ) : null}
                        {titleSubtotals.map((titleSubtotal) => (
                          <tr key={`${row.id}-sum-${titleSubtotal.titleRowId}`} className="border-b border-b-app-border/90 bg-app-surface">
                            <td className="py-2 pl-[8pt] pr-2 align-top" />
                            <td className="py-2 px-2 align-top" />
                            {showPositionBillingColumns ? <td className="py-2 px-2 align-top" /> : null}
                            <td className="py-2 px-2 align-top" />
                            {showArticleNumberColumn ? <td className="py-2 px-2 align-top" /> : null}
                            <td
                              className={`py-2 px-2 align-top leading-tight text-neutral-900 ${
                                titleSubtotal.titleLevel <= 1 ? 'text-[9.5pt] font-bold' : 'text-[9pt] font-semibold'
                              }`}
                              style={{ paddingLeft: 8 + titleIndentPx(titleSubtotal.titleLevel) }}
                            >
                              {titleSubtotalLabel(titleSubtotal)}
                            </td>
                            {!hidePositionPrices ? <td className="py-2 pl-2 pr-0 align-top" /> : null}
                            {!hidePositionPrices ? (
                              <td
                                className={`py-2 pl-2 pr-[4pt] align-top text-right tabular-nums text-neutral-900 leading-tight ${
                                  titleSubtotal.titleLevel <= 1 ? 'font-bold' : 'font-semibold'
                                }`}
                              >
                                {formatEuro(titleSubtotal.net)}
                              </td>
                            ) : null}
                            <td className={`py-2 pl-1 pr-0 align-top ${showLayoutBands ? 'w-0 p-0' : ''}`} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {documentRowWindow.paddingBottom > 0 ? (
                    <tr aria-hidden style={{ height: documentRowWindow.paddingBottom }}>
                      <td
                        colSpan={positionTableColSpan}
                        style={{
                          height: documentRowWindow.paddingBottom,
                          padding: 0,
                          border: 0,
                          lineHeight: 0,
                          fontSize: 0,
                        }}
                      />
                    </tr>
                  ) : null}
                  {!isLumpSumPartialInvoice && documentRowWindow.end === tableRows.length ? (
                    <DropBand
                      id={`drop-${numberedRows.length}`}
                      dropIndex={numberedRows.length}
                      colSpan={positionTableColSpan}
                    />
                  ) : null}
                  {tableRows.length === 0 ? (
                    <tr>
                      <td colSpan={positionTableColSpan} className="py-7 text-center text-slate-400 text-[9.5pt] border-b border-app-border leading-tight">
                        Keine Positionen im {documentTypeLabel}. Nutzen Sie die rechte Spalte (Materialien / Leistungen) oder den Tab „Angebot“.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {isPartialInvoice ? (
              <p className={`${showPaidPartialsSummary ? 'mb-2' : 'mb-4'} text-[9.5pt] font-semibold text-neutral-900`}>
                Auftragssumme netto: <span className="tabular-nums">{formatEuro(effectiveOrderTotalNet)}</span>
              </p>
            ) : null}
            {showPaidPartialsSummary ? (
              <div className="mb-6">
                <h3 className="mb-2 text-[9.5pt] font-semibold text-neutral-900">Bisherige Abschlagsrechnungen</h3>
                <table className="w-full border-collapse text-[9pt] text-neutral-800">
                  <thead>
                    <tr className="bg-app-surface-muted border-b border-app-border">
                      <th className="text-left font-semibold px-2 py-1">Rechnung</th>
                      <th className="text-left font-semibold px-2 py-1">Datum</th>
                      <th className="text-right font-semibold px-2 py-1">Netto</th>
                      <th className="text-right font-semibold px-2 py-1">USt</th>
                      <th className="text-right font-semibold px-2 py-1">Brutto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previousPartialInvoicesSummary.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-app-border">
                        <td className="px-2 py-1">{invoice.referenceId}</td>
                        <td className="px-2 py-1">{invoice.issueDate || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatEuro(invoice.netAmount)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatEuro(invoice.vatAmount)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatEuro(invoice.grossAmount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-app-surface-muted border-t border-app-border">
                      <td className="px-2 py-1 font-semibold" colSpan={2}>Summe Abschläge</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatEuro(previousPartialsSummaryTotals.paidPartialNetTotal)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatEuro(previousPartialsSummaryTotals.paidPartialVatTotal)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">{formatEuro(previousPartialsSummaryTotals.paidPartialGrossTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}
            {(isFinalInvoice || isPartialInvoice) && paidPartialsError ? (
              <p className="mb-4 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[8.5pt] text-amber-800">
                {paidPartialsError}
              </p>
            ) : null}
            {billingLedgerError && showPositionBillingColumns ? (
              <p className="mb-4 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[8.5pt] text-amber-800">
                {billingLedgerError}
              </p>
            ) : null}
            {isPartialInvoice && !documentIssued ? (
              <div className="mb-3 flex flex-col items-end gap-2">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {isLumpSumPartialInvoice ? (
                    <div
                      className="inline-flex rounded-md border border-app-border bg-app-surface-muted p-0.5 text-[8.5pt]"
                      title="Kumuliert: Prozent/Betrag ist der Leistungsstand insgesamt, bisherige Abschläge werden abgezogen."
                    >
                      {([true, false] as const).map((cumulative) => (
                        <button
                          key={cumulative ? 'cumulative' : 'single'}
                          type="button"
                          onClick={() => {
                            setPartialInvoiceCumulative(cumulative);
                            void persistFull({ partialInvoiceCumulative: cumulative });
                          }}
                          className={`rounded px-2 py-0.5 transition-colors ${
                            isCumulativeLumpSumPartialInvoice === cumulative
                              ? 'bg-app-surface text-neutral-900 shadow-sm'
                              : 'text-app-text-muted hover:text-neutral-900'
                          }`}
                        >
                          {cumulative ? 'Kumuliert' : 'Nur dieser Abschlag'}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="inline-flex rounded-md border border-app-border bg-app-surface-muted p-0.5 text-[8.5pt]">
                    <button
                      type="button"
                      onClick={() => {
                        setPartialInvoiceMode('position');
                        void persistFull({ partialInvoiceMode: 'position' });
                      }}
                      className={`rounded px-2 py-0.5 transition-colors ${
                        partialInvoiceMode === 'position'
                          ? 'bg-app-surface text-neutral-900 shadow-sm'
                          : 'text-app-text-muted hover:text-neutral-900'
                      }`}
                    >
                      Nach Position
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPartialInvoiceMode('percent');
                        void persistFull({ partialInvoiceMode: 'percent' });
                      }}
                      className={`rounded px-2 py-0.5 transition-colors ${
                        partialInvoiceMode === 'percent'
                          ? 'bg-app-surface text-neutral-900 shadow-sm'
                          : 'text-app-text-muted hover:text-neutral-900'
                      }`}
                    >
                      Nach Prozent
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPartialInvoiceMode('amount');
                        void persistFull({ partialInvoiceMode: 'amount' });
                      }}
                      className={`rounded px-2 py-0.5 transition-colors ${
                        partialInvoiceMode === 'amount'
                          ? 'bg-app-surface text-neutral-900 shadow-sm'
                          : 'text-app-text-muted hover:text-neutral-900'
                      }`}
                    >
                      Nach Betrag
                    </button>
                  </div>
                </div>
                {isPercentPartialInvoice ? (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    {ABSCHLAG_PERCENT_PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          setAbschlagPercent(p);
                          void persistFull({ abschlagPercent: p });
                        }}
                        className={`inline-flex items-center whitespace-nowrap rounded border px-2 py-0.5 text-[8.5pt] transition-colors ${
                          isAbschlagPresetPercent(abschlagPercent) && abschlagPercent === p
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-app-border bg-app-surface text-app-text hover:bg-app-hover'
                        }`}
                      >
                        {p} %
                      </button>
                    ))}
                    <label className="ml-1 inline-flex items-center gap-1 text-[9.5pt] text-neutral-600">
                      <DecimalInput
                        value={abschlagPercent}
                        min={0}
                        max={100}
                        minimumFractionDigits={0}
                        maximumFractionDigits={ABSCHLAG_PERCENT_MAX_FRACTION_DIGITS}
                        className="h-7 w-[4.75rem] rounded-md border border-app-border bg-app-surface px-1.5 text-right text-[9.5pt] tabular-nums"
                        onChange={(v) => setAbschlagPercent(roundAbschlagPercent(v))}
                        onBlur={(clamped) => {
                          const next = roundAbschlagPercent(clamped);
                          setAbschlagPercent(next);
                          void persistFull({ abschlagPercent: next });
                        }}
                      />
                      <span>%</span>
                    </label>
                  </div>
                ) : isAmountPartialInvoice ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[9.5pt] text-neutral-600">
                      <div className="inline-flex rounded-md border border-app-border bg-app-surface-muted p-0.5 text-[8.5pt]">
                        {([false, true] as const).map((gross) => (
                          <button
                            key={gross ? 'gross' : 'net'}
                            type="button"
                            onClick={() => {
                              setPartialInvoiceAmountIsGross(gross);
                              void persistFull({ partialInvoiceAmountIsGross: gross });
                            }}
                            className={`rounded px-2 py-0.5 transition-colors ${
                              partialInvoiceAmountIsGross === gross
                                ? 'bg-app-surface text-neutral-900 shadow-sm'
                                : 'text-app-text-muted hover:text-neutral-900'
                            }`}
                          >
                            {gross ? 'Brutto' : 'Netto'}
                          </button>
                        ))}
                      </div>
                      <DecimalInput
                        // Gespeichert wird immer Netto; Brutto-Eingabe wird exakt zurückgerechnet.
                        value={
                          partialInvoiceAmountIsGross
                            ? grossFromNetAmount(partialInvoiceAmount, vatRatePercent)
                            : partialInvoiceAmount
                        }
                        min={0}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                        className="h-7 w-24 rounded-md border border-app-border bg-app-surface px-1.5 text-right text-[9.5pt] tabular-nums"
                        onChange={(v) => {
                          const amount = Math.max(0, v);
                          setPartialInvoiceAmount(
                            partialInvoiceAmountIsGross ? netFromGrossAmount(amount, vatRatePercent) : amount,
                          );
                        }}
                        onBlur={(clamped) => {
                          const amount = Math.max(0, clamped);
                          const next = partialInvoiceAmountIsGross
                            ? netFromGrossAmount(amount, vatRatePercent)
                            : amount;
                          setPartialInvoiceAmount(next);
                          void persistFull({ partialInvoiceAmount: next });
                        }}
                      />
                      <span>€</span>
                      {isCumulativeLumpSumPartialInvoice ? (
                        paidPartialInvoices.length > 0 ? (
                          <span className="text-app-text-subtle">
                            · abzgl. bisher {formatEuro(finalInvoiceTotals.paidPartialNetTotal)} netto
                          </span>
                        ) : null
                      ) : hasPartialOfferBasis ? (
                        <span className="text-app-text-subtle">
                          · verbleibend{' '}
                          {formatEuro(
                            remainingPartialInvoiceNet(
                              discountedOfferNetTotal,
                              previousPartialsSummaryTotals.paidPartialNetTotal,
                            ),
                          )}{' '}
                          netto
                        </span>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="text-app-text-subtle text-[8.5pt]">
                    Menge je Position in der Spalte „Diese Rechnung“.
                  </p>
                )}
                <label className="flex flex-wrap items-center justify-end gap-1.5 text-[9.5pt] text-neutral-600">
                  <span>Auftragssumme netto (optional, sonst Angebotssumme)</span>
                  <DecimalInput
                    value={effectiveOrderTotalNet}
                    min={0}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                    className="h-7 w-[7.5rem] rounded-md border border-app-border bg-app-surface px-1.5 text-right text-[9.5pt] tabular-nums"
                    onChange={() => {}}
                    onBlur={(committed) => {
                      const offerNet = Math.round(discountedOfferNetTotal * 100) / 100;
                      // Gleicher Wert wie das Angebotsnetto = kein Override (folgt späteren Angebotsänderungen).
                      const next = Math.round(committed * 100) / 100 === offerNet ? null : committed;
                      if (next === orderTotalNet) return;
                      setOrderTotalNet(next);
                      void persistFull({ orderTotalNet: next });
                    }}
                  />
                  <span>€</span>
                  {orderTotalNet != null ? (
                    <button
                      type="button"
                      className="text-[8.5pt] text-app-text-muted underline hover:text-neutral-900"
                      onClick={() => {
                        setOrderTotalNet(null);
                        void persistFull({ orderTotalNet: null });
                      }}
                    >
                      Angebotssumme verwenden
                    </button>
                  ) : null}
                </label>
              </div>
            ) : null}

            <div className="mb-10 text-[9.5pt] text-neutral-800">
              {!totalsHidden && titleTotalsSummaryList.length > 0 ? (
                <div className="mb-3 flex w-full flex-col gap-1.5">
                  {titleTotalsSummaryList.map((sub) => (
                    <div
                      key={`title-summary-${sub.titleRowId}`}
                      className="flex w-full items-start"
                    >
                      <div
                        className="shrink-0"
                        // Label startet bündig mit der Beschreibungsspalte (Pos. + Menge 8 % + Einheit).
                        style={{ width: `calc(${positionColumnStyle.width} + 8% + ${unitColumnStyle.width})` }}
                        aria-hidden
                      />
                      <span
                        className="min-w-0 flex-1 pr-2 text-left text-neutral-500"
                        style={{ paddingLeft: titleIndentPx(sub.titleLevel) }}
                      >
                        {titleSubtotalLabel(sub)}
                      </span>
                      <span
                        className="shrink-0 tabular-nums text-right text-neutral-500"
                        style={{ width: `${OFFER_TITLE_TOTALS_AMOUNT_PCT}%` }}
                      >
                        {formatEuro(sub.net)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex flex-col items-end gap-1.5">
              {!totalsHidden ? (
                <>
                  {/* Zwischensumme row — only visible when a discount is active */}
                  {!isPartialInvoice && discount && discount.value > 0 && (
                    <div className="flex justify-between gap-10 min-w-[220px]">
                      <span className="text-neutral-500">Zwischensumme</span>
                      <span className="tabular-nums text-neutral-500">{formatEuro(rawNetTotal)}</span>
                    </div>
                  )}

                  {/* Discount row — shown when active; click to re-edit */}
                  {!isPartialInvoice && discount && discount.value > 0 && !discountEditing && (
                    documentIssued ? (
                      <div className="flex justify-between gap-10 min-w-[220px]">
                        <span className="text-neutral-600">
                          Rabatt{discount.type === 'percent' ? ` (${discount.value.toLocaleString('de-DE')} %)` : ''}
                        </span>
                        <span className="tabular-nums text-neutral-600">
                          −{formatEuro(discountAmount)}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDiscountEditing(true)}
                        className="flex justify-between gap-10 min-w-[220px] group hover:opacity-70 transition-opacity"
                        title="Rabatt bearbeiten"
                      >
                        <span className="text-neutral-600 group-hover:text-neutral-900">
                          Rabatt{discount.type === 'percent' ? ` (${discount.value.toLocaleString('de-DE')} %)` : ''}
                        </span>
                        <span className="tabular-nums text-neutral-600 group-hover:text-neutral-900">
                          −{formatEuro(discountAmount)}
                        </span>
                      </button>
                    )
                  )}
                </>
              ) : null}

              {/* Inline discount editor */}
              {!isPartialInvoice && discountEditing && !documentIssued && (
                <div className="flex items-center gap-2 min-w-[220px]">
                  <span className="text-neutral-600 shrink-0">Rabatt</span>
                  <div className="flex items-center gap-1 ml-auto">
                    <DecimalInput
                      value={discount?.value ?? 0}
                      min={0}
                      max={discount?.type === 'percent' ? 100 : rawNetTotal}
                      minimumFractionDigits={discount?.type === 'percent' ? 0 : 2}
                      maximumFractionDigits={2}
                      className="w-20 border border-app-border-strong rounded-md px-2 py-0.5 text-[9.5pt] text-right"
                      onChange={(v) => {
                        const type = discount?.type ?? 'percent';
                        const clamped = type === 'percent' ? Math.min(100, v) : Math.min(rawNetTotal, v);
                        const next: OfferDiscount = { type, value: clamped };
                        setDiscount(next);
                      }}
                      onBlur={() => {
                        const next = snapRef.current.discount;
                        const finalDiscount = (!next || next.value <= 0) ? null : next;
                        setDiscount(finalDiscount);
                        setDiscountEditing(false);
                        void persistFull({ discount: finalDiscount ?? undefined });
                      }}
                    />
                    {/* % / € toggle */}
                    <div className="flex rounded-md border border-blue-300 overflow-hidden text-[8.5pt]">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const next: OfferDiscount = { type: 'percent', value: discount?.value ?? 0 };
                          setDiscount(next);
                        }}
                        className={`px-1.5 py-0.5 ${(discount?.type ?? 'percent') === 'percent' ? 'bg-blue-500 text-white' : 'bg-app-surface text-blue-400 hover:bg-blue-50'}`}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const next: OfferDiscount = { type: 'absolute', value: discount?.value ?? 0 };
                          setDiscount(next);
                        }}
                        className={`px-1.5 py-0.5 ${(discount?.type ?? 'percent') === 'absolute' ? 'bg-blue-500 text-white' : 'bg-app-surface text-blue-400 hover:bg-blue-50'}`}
                      >
                        €
                      </button>
                    </div>
                    {/* Remove discount */}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setDiscount(null);
                        setDiscountEditing(false);
                        void persistFull({ discount: undefined });
                      }}
                      className="text-neutral-400 hover:text-red-500 transition-colors p-0.5"
                      title="Rabatt entfernen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}

              {/* VAT picker + discount actions (panel floats over content below, no layout shift) */}
              {documentIssued ? null : (
              <div ref={vatPickerWrapRef} className="relative z-50 flex min-w-[320px] justify-end">
                <div className="flex justify-end gap-2">
                  {!totalsHidden ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setVatModeEditing((v) => !v)}
                        className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
                      >
                        USt. anpassen
                      </button>
                      {!isPartialInvoice && (!discount || discount.value <= 0) && !discountEditing && (
                        <button
                          type="button"
                          onClick={() => {
                            setDiscount({ type: 'percent', value: 0 });
                            setDiscountEditing(true);
                          }}
                          className="inline-flex items-center rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[8pt] font-normal text-blue-800 transition-colors hover:bg-blue-100"
                        >
                          + Rabatt hinzufügen
                        </button>
                      )}
                    </>
                  ) : null}
                  {isFinalInvoice || isPartialInvoice ? (
                    <button
                      type="button"
                      onClick={() => setDeductionPickerOpen(true)}
                      className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
                    >
                      Abschlag auswählen
                    </button>
                  ) : null}
                  {canToggleDocumentTotals ? (
                    <button
                      type="button"
                      onClick={() => {
                        const next = !hideTotals;
                        setHideTotals(next);
                        if (next) setVatModeEditing(false);
                        void persistFull({ hideTotals: next });
                      }}
                      className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
                      title={hideTotals ? 'Netto-, USt- und Bruttosummen wieder anzeigen' : 'Netto-, USt- und Bruttosummen ausblenden'}
                    >
                      {hideTotals ? 'Summen einblenden' : 'Summen ausblenden'}
                    </button>
                  ) : null}
                  {isLaborCostShareEligibleDocument(documentTypeLabel) ? (
                    <button
                      type="button"
                      onClick={() => {
                        const next: LaborCostShareMode = laborCostShareToggleOn ? 'off' : 'on';
                        setLaborCostShare(next);
                        void persistFull({ laborCostShare: next });
                      }}
                      className="inline-flex items-center rounded border border-blue-300 bg-app-surface px-2 py-0.5 text-[8pt] font-normal text-blue-600 transition-colors hover:border-blue-500 hover:text-blue-800"
                      title={
                        laborCostShareToggleOn
                          ? 'Lohnkostenanteil-Hinweis ausblenden'
                          : 'Lohnkostenanteil-Hinweis ausweisen'
                      }
                    >
                      {laborCostShareToggleOn ? 'Lohnkosten ausblenden' : 'Lohnkosten ausweisen'}
                    </button>
                  ) : null}
                </div>
                {vatModeEditing && (
                  <div className="absolute right-0 top-full mt-1">
                    <OfferVatPickerPanel
                      value={vatMode}
                      onChange={(nextVatMode) => {
                        setVatMode(nextVatMode);
                        void persistFull({ vatMode: nextVatMode });
                        setVatModeEditing(false);
                      }}
                    />
                  </div>
                )}
              </div>
              )}

              {!totalsHidden ? (
                <div
                  className={`flex w-full items-end gap-4 ${paymentQrBesideTotals ? 'justify-between' : 'justify-end'}`}
                >
                  {paymentQrBesideTotals ? paymentQrPreview : null}
                <div className="flex flex-col gap-1.5" style={{ width: `${OFFER_TOTALS_COLUMN_WIDTH_PT}pt` }}>
                <>
                  {showDeductionTotals || isFinalInvoice ? (
                    <>
                      <div className="flex w-full justify-between gap-4">
                        <span className="text-neutral-600">Nettobetrag (Leistungen)</span>
                        <span className="tabular-nums text-neutral-900">{formatEuro(baseNetTotal)}</span>
                      </div>
                      {paidPartialInvoices.length > 0 ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">abzgl. bisherige Abschläge (Netto)</span>
                          <span className="tabular-nums text-neutral-900">−{formatEuro(finalInvoiceTotals.paidPartialNetTotal)}</span>
                        </div>
                      ) : null}
                      {payableTotals.siteLevyNet > 0 ? (
                        <>
                          <div className="flex w-full justify-between gap-4">
                            <span className="text-neutral-600">Netto vor Umlage</span>
                            <span className="tabular-nums text-neutral-900">{formatEuro(netTotalBeforeLevy)}</span>
                          </div>
                          <div className="flex w-full justify-between gap-4">
                            <span className="text-neutral-600">
                              ./. {siteLevy?.label || DEFAULT_SITE_LEVY_LABEL}
                              {siteLevy?.percent != null ? ` (${siteLevy.percent.toLocaleString('de-DE')} %)` : ''}
                            </span>
                            <span className="tabular-nums text-neutral-900">−{formatEuro(payableTotals.siteLevyNet)}</span>
                          </div>
                        </>
                      ) : null}
                      <div className="flex w-full justify-between gap-4">
                        <span className="font-bold text-neutral-900">
                          Nettobetrag ({isFinalInvoice ? 'Schlussrechnung' : 'Abschlagsrechnung'})
                        </span>
                        <span className="tabular-nums font-bold text-neutral-900">{formatEuro(netTotal)}</span>
                      </div>
                      {!hideVatAndGross && payableTotals.siteLevyNet <= 0 ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">zzgl. {vatRatePercent} % USt. (Leistungen)</span>
                          <span className="tabular-nums text-neutral-900">{formatEuro(baseVatAmount)}</span>
                        </div>
                      ) : null}
                      {!hideVatAndGross && payableTotals.siteLevyNet <= 0 && paidPartialInvoices.length > 0 ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">abzgl. bisherige Abschläge (USt.)</span>
                          <span className="tabular-nums text-neutral-900">−{formatEuro(finalInvoiceTotals.paidPartialVatTotal)}</span>
                        </div>
                      ) : null}
                      {!hideVatAndGross ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="font-bold text-neutral-900">
                            USt. ({isFinalInvoice ? 'Schlussrechnung' : 'Abschlagsrechnung'})
                          </span>
                          <span className="tabular-nums font-bold text-neutral-900">{formatEuro(vatAmount)}</span>
                        </div>
                      ) : null}
                      {!hideVatAndGross ? (
                        <div className="flex w-full justify-between gap-4 pt-2.5 mt-1 border-t-[3px] border-double border-neutral-900 text-[9.5pt] font-bold text-neutral-900">
                          <span>{isFinalInvoice ? 'Finaler Rechnungsbetrag' : 'Bruttobetrag'}</span>
                          <span className="tabular-nums">{formatEuro(grossTotal)}</span>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {payableTotals.siteLevyNet > 0 ? (
                        <>
                          <div className="flex w-full justify-between gap-4">
                            <span className="text-neutral-600">Netto vor Umlage</span>
                            <span className="tabular-nums text-neutral-900">{formatEuro(netTotalBeforeLevy)}</span>
                          </div>
                          <div className="flex w-full justify-between gap-4">
                            <span className="text-neutral-600">
                              ./. {siteLevy?.label || DEFAULT_SITE_LEVY_LABEL}
                              {siteLevy?.percent != null ? ` (${siteLevy.percent.toLocaleString('de-DE')} %)` : ''}
                            </span>
                            <span className="tabular-nums text-neutral-900">−{formatEuro(payableTotals.siteLevyNet)}</span>
                          </div>
                        </>
                      ) : null}
                      <div className="flex w-full justify-between gap-4">
                        <span className="font-bold text-neutral-900">Nettobetrag</span>
                        <span className="tabular-nums font-bold text-neutral-900">{formatEuro(netTotal)}</span>
                      </div>

                      {!hideVatAndGross && (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">zzgl. {vatRatePercent} % USt.</span>
                          <span className="tabular-nums font-normal text-neutral-900">{formatEuro(vatAmount)}</span>
                        </div>
                      )}
                      {!hideVatAndGross && (
                        <div className="flex w-full justify-between gap-4 pt-2.5 mt-1 border-t-[3px] border-double border-neutral-900 text-[9.5pt] font-bold text-neutral-900">
                          <span>Bruttobetrag</span>
                          <span className="tabular-nums">{formatEuro(grossTotal)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {showPayableExtras || constructionDeductionsEditing || showConstructionDeductionsUi ? (
                    <>
                      {payableTotals.retentionGross > 0 ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">
                            ./. Sicherheitseinbehalt
                            {retention?.percent != null
                              ? ` (${retention.percent.toLocaleString('de-DE')} %)`
                              : ''}
                          </span>
                          <span className="tabular-nums text-neutral-900">
                            −{formatEuro(payableTotals.retentionGross)}
                          </span>
                        </div>
                      ) : null}
                      {payableTotals.priorRetentionsDueGross > 0 ? (
                        <div className="flex w-full justify-between gap-4">
                          <span className="text-neutral-600">
                            zzgl. fällige Sicherheitseinbehalte
                            {heldPriorRetentions.length > 0
                              ? ` (${heldPriorRetentions.map((r) => r.referenceId).join(', ')})`
                              : ''}
                          </span>
                          <span className="tabular-nums text-neutral-900">
                            {formatEuro(payableTotals.priorRetentionsDueGross)}
                          </span>
                        </div>
                      ) : null}
                      {(payableTotals.retentionGross > 0 ||
                        payableTotals.priorRetentionsDueGross > 0 ||
                        payableTotals.skontoAmount > 0) && (
                        <div className="flex w-full justify-between gap-4 font-bold text-neutral-900">
                          <span>Zahlbetrag</span>
                          <span className="tabular-nums">{formatEuro(payableTotals.payableAmount)}</span>
                        </div>
                      )}
                      {payableTotals.skontoAmount > 0 ? (
                        <>
                          <div className="flex w-full justify-between gap-4 text-neutral-600">
                            <span>
                              Skonto
                              {skontoPercentForPayable != null
                                ? ` (${skontoPercentForPayable.toLocaleString('de-DE')} %)`
                                : ''}
                            </span>
                            <span className="tabular-nums">−{formatEuro(payableTotals.skontoAmount)}</span>
                          </div>
                          <div className="flex w-full justify-between gap-4 text-neutral-600">
                            <span>Zahlbetrag bei Skonto</span>
                            <span className="tabular-nums">{formatEuro(payableTotals.payableWithSkonto)}</span>
                          </div>
                        </>
                      ) : null}
                      {!documentIssued && showConstructionDeductionsUi ? (
                        <div className="mt-1 flex flex-col items-end gap-1">
                          {!constructionDeductionsEditing ? (
                            <button
                              type="button"
                              onClick={() => setConstructionDeductionsEditing(true)}
                              className="text-[8.5pt] text-blue-600 hover:text-blue-800"
                            >
                              Umlage / Einbehalt
                            </button>
                          ) : (
                            <div className="flex flex-wrap items-center justify-end gap-2 rounded-md border border-app-border bg-app-surface-muted p-2 text-[8.5pt]">
                              <label className="inline-flex items-center gap-1">
                                <span>Umlage %</span>
                                <DecimalInput
                                  value={siteLevy?.percent ?? 0}
                                  min={0}
                                  max={100}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={2}
                                  className="h-7 w-14 rounded border border-app-border bg-app-surface px-1 text-right"
                                  onChange={(v) => {
                                    const next =
                                      v > 0
                                        ? {
                                            kind: 'site_levy' as const,
                                            label: siteLevy?.label || DEFAULT_SITE_LEVY_LABEL,
                                            percent: Math.min(100, Math.round(v * 100) / 100),
                                            amountNet: null,
                                          }
                                        : null;
                                    setSiteLevy(next);
                                  }}
                                  onBlur={(clamped) => {
                                    const next =
                                      clamped > 0
                                        ? {
                                            kind: 'site_levy' as const,
                                            label: siteLevy?.label || DEFAULT_SITE_LEVY_LABEL,
                                            percent: Math.min(100, Math.round(clamped * 100) / 100),
                                            amountNet: null,
                                          }
                                        : null;
                                    setSiteLevy(next);
                                    setConstructionDeductionsCustomized(true);
                                    void persistFull({
                                      siteLevy: next,
                                      constructionDeductionsCustomized: true,
                                    });
                                  }}
                                />
                              </label>
                              <label className="inline-flex items-center gap-1">
                                <span>{isFinalInvoice ? 'Einbehalt Schluss %' : 'Einbehalt %'}</span>
                                <DecimalInput
                                  value={retention?.percent ?? 0}
                                  min={0}
                                  max={100}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={2}
                                  className="h-7 w-14 rounded border border-app-border bg-app-surface px-1 text-right"
                                  onChange={(v) => {
                                    const next =
                                      v > 0
                                        ? {
                                            kind: 'performance' as const,
                                            percent: Math.min(100, Math.round(v * 100) / 100),
                                            amountGross: null,
                                          }
                                        : null;
                                    setRetention(next);
                                  }}
                                  onBlur={(clamped) => {
                                    const next =
                                      clamped > 0
                                        ? {
                                            kind: 'performance' as const,
                                            percent: Math.min(100, Math.round(clamped * 100) / 100),
                                            amountGross: null,
                                          }
                                        : null;
                                    setRetention(next);
                                    setConstructionDeductionsCustomized(true);
                                    void persistFull({
                                      retention: next,
                                      constructionDeductionsCustomized: true,
                                    });
                                  }}
                                />
                              </label>
                              {isFinalInvoice ? (
                                <p className="w-full text-right text-[8pt] text-app-text-subtle">
                                  Offene Einbehalte aus Vor-Rechnungen werden automatisch addiert. Hier nur ein
                                  zusätzlicher Einbehalt dieser Schlussrechnung.
                                </p>
                              ) : null}
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-800"
                                onClick={() => setConstructionDeductionsEditing(false)}
                              >
                                Fertig
                              </button>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </>
              {laborCostShareNote ? (
                <div
                  className="w-full pt-1 text-[8pt] leading-[1.4] text-app-text-subtle"
                  style={OFFER_DOCUMENT_FONT_STYLE}
                >
                  {laborCostShareNote}
                </div>
              ) : null}
                </div>
                </div>
              ) : laborCostShareNote ? (
                <div
                  className="pt-1 text-[8pt] leading-[1.4] text-app-text-subtle"
                  style={{ width: `${OFFER_TOTALS_COLUMN_WIDTH_PT}pt` }}
                >
                  {laborCostShareNote}
                </div>
              ) : null}
              {hideVatAndGross && vatLegalNote ? (
                <div className="self-stretch text-[9.5pt] text-neutral-800 leading-[1.45] pt-1">
                  {vatLegalNote}
                </div>
              ) : null}
              </div>
            </div>

            <div className="mb-6">
              <OfferDocumentContentList
                leadRouteId={leadRouteId}
                blocks={closingContent}
                minTextBlocks={isCreditOrStornoDocument ? 0 : 1}
                variant="muted"
                insertPlacement="beforeFirstText"
                remainingImageSlots={remainingImageSlots}
                onCommitText={async (id, content) => {
                  if (isFirstClosingText(id)) markPaymentTermsCustomized();
                  await persistClosingContent(
                    updateContentText(snapRef.current.closingContent, id, content),
                    isFirstClosingText(id) ? { paymentTermsCustomized: true } : undefined,
                  );
                }}
                onRemoveBlock={async (id) => {
                  const textCount = snapRef.current.closingContent.filter((b) => b.type === 'text').length;
                  if (!isCreditOrStornoDocument && textCount <= 1 && snapRef.current.closingContent.find((b) => b.id === id)?.type === 'text') {
                    return;
                  }
                  if (isFirstClosingText(id)) markPaymentTermsCustomized();
                  await persistClosingContent(
                    removeContentBlock(snapRef.current.closingContent, id),
                    isFirstClosingText(id) ? { paymentTermsCustomized: true } : undefined,
                  );
                }}
                onAddText={async (index) => {
                  await persistClosingContent(
                    insertContentBlock(snapRef.current.closingContent, index, {
                      id: newDocumentContentId(),
                      type: 'text',
                      content: '',
                    }),
                  );
                }}
                onAddImages={(index) => setImagePicker({ zone: 'closing', index })}
                onRemoveImage={async (attachment) => {
                  await persistClosingContent(
                    removeAttachmentFromContent(snapRef.current.closingContent, attachment.id),
                  );
                  try {
                    await deleteOfferAttachmentStorage(leadRouteId, attachment);
                  } catch {
                    /* draft already dropped the image */
                  }
                }}
                onReplaceImage={(attachment) => {
                  const blockId = findImageGridBlockId(snapRef.current.closingContent, attachment.id);
                  if (blockId) setImagePicker({ zone: 'closing', editBlockId: blockId });
                }}
                paymentTermsEdit={
                  documentIssued || !paymentTermsBlockId
                    ? null
                    : {
                        blockId: paymentTermsBlockId,
                        terms: documentPaymentTerms,
                        wording: paymentTermsWording,
                        context: paymentTermsContextFromDocumentType(documentTypeLabel),
                        onSave: ({ terms, wording }) => persistDocumentPaymentTerms(terms, wording),
                      }
                }
              />
            </div>

            {!isWorkReport ? (
              <div className="mb-12">
                <OfferInlineEdit
                  value={closingGreeting}
                  onSave={async (v) => {
                    setClosingGreeting(v);
                    await persistFull({ closingGreeting: v });
                  }}
                  multiline
                  viewTextClassName="text-[9.5pt] text-neutral-900 leading-[1.45]"
                  aria-label="Grußformel"
                />
              </div>
            ) : null}
            {paymentQrAfterGreeting ? (
              <div className={`mb-12 ${paymentQrAlign === 'center' ? 'flex justify-center' : ''}`}>
                {paymentQrPreview}
              </div>
            ) : null}
            {shouldShowSignature ? (
              <div className="mb-14 max-w-md">
                <div className="border-t-2 border-neutral-900 pt-2.5">
                  <OfferInlineEdit
                    value={signatureHint}
                    onSave={async (v) => {
                      setSignatureHint(v);
                      await persistFull({ signatureHint: v });
                    }}
                    multiline
                    viewTextClassName="text-[9.5pt] text-neutral-500 leading-snug"
                    aria-label="Hinweis Unterschrift"
                  />
                </div>
              </div>
            ) : null}

            {company && !showLayoutBands ? (
              <footer className="pt-10 mt-4 text-[7.5pt] text-neutral-600 leading-[1.35] text-left">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 sm:gap-x-8 gap-y-4 items-start">
                  <div className="space-y-0.5 min-w-0 text-left">
                    {directorLines.map(
                      (ln, i) =>
                        (ln ?? '').trim() ? (
                          <p key={`dl-${i}`} className="text-neutral-700">
                            {ln}
                          </p>
                        ) : null,
                    )}
                    {registryLine?.trim() ? (
                      <p className="text-neutral-700">{registryLine}</p>
                    ) : null}
                    {(company.vat_id ?? '').trim() ? (
                      <p>
                        <span className="text-neutral-700">USt-ID</span>{' '}
                        <span className="text-neutral-700">{company.vat_id.trim()}</span>
                      </p>
                    ) : null}
                    {(company.tax_number ?? '').trim() ? (
                      <p>
                        <span className="text-neutral-700">Steuernummer</span>{' '}
                        <span className="text-neutral-700">{company.tax_number.trim()}</span>
                      </p>
                    ) : null}
                  </div>
                  {websiteUrl || (company.phone ?? '').trim() || (company.email ?? '').trim() ? (
                    <div className="space-y-0.5 min-w-0 text-left text-neutral-700">
                      {websiteUrl ? (
                        <p>
                          <a
                            href={websiteUrl}
                            className="no-underline hover:underline break-all"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {company.website?.replace(/^https?:\/\//i, '') || company.website}
                          </a>
                        </p>
                      ) : null}
                      {(company.phone ?? '').trim() ? (
                        <p>
                          <span className="text-neutral-600">Telefon </span>
                          {company.phone.trim()}
                        </p>
                      ) : null}
                      {(company.email ?? '').trim() ? (
                        <p>
                          <span className="text-neutral-600">E-Mail </span>
                          <a
                            href={`mailto:${company.email.trim()}`}
                            className="no-underline hover:underline break-all"
                          >
                            {company.email.trim()}
                          </a>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="hidden sm:block min-w-0" aria-hidden />
                  )}
                  <div className="space-y-0.5 min-w-0 text-left">
                    {(company.bank_name ?? '').trim() ? (
                      <p className="text-neutral-700">{company.bank_name.trim()}</p>
                    ) : null}
                    {(company.bank_account_holder ?? '').trim() ? (
                      <p className="text-neutral-700">{company.bank_account_holder.trim()}</p>
                    ) : null}
                    {(company.iban ?? '').trim() ? (
                      <p className="text-neutral-700 tabular-nums tracking-tight break-all">
                        IBAN {company.iban.trim()}
                      </p>
                    ) : null}
                    {(company.bic ?? '').trim() ? (
                      <p className="text-neutral-700 tabular-nums tracking-tight">
                        BIC {company.bic.trim()}
                      </p>
                    ) : null}
                  </div>
                </div>
                <p className="text-right mt-5 text-[7.5pt] text-neutral-400 tracking-wide leading-[1.15]">
                  Seite 1 von {1 + attachments.length}
                </p>
              </footer>
            ) : null}
            </div>
            {showLayoutBands && documentLayout && offerBandData ? (
              <HtmlFooterBand
                layout={documentLayout}
                data={offerBandData}
                renderOverride={renderBandOverride}
              />
            ) : null}
          </article>
            </div>
          </div>
          <OfferAttachmentPreviewPages leadRouteId={leadRouteId} attachments={attachments} />
          </>
          )}
        </div>
        {documentZoomEnabled ? (
          <DocumentZoomControls
            zoom={documentZoom}
            onZoomOut={() => applyDocumentZoom((z) => stepDocumentZoom(z, -1))}
            onZoomIn={() => applyDocumentZoom((z) => stepDocumentZoom(z, 1))}
            onReset={() => applyDocumentZoom(1)}
            onFitWidth={zoomDocumentToPageWidth}
          />
        ) : null}
        </div>

        {documentIssued || editorView === 'calculation' ? null : (
        <aside className="hidden md:flex w-[320px] shrink-0 border-l border-app-border bg-app-surface-muted flex-col min-h-0">
          {positionToolsContent}
        </aside>
        )}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeDragPreview ? (
          <OfferDragOverlayPreview preview={activeDragPreview} />
        ) : null}
      </DragOverlay>
      </DndContext>

      <div className="md:hidden shrink-0 border-t border-app-border bg-app-surface px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_14px_rgba(0,0,0,.06)]">
        {documentIssued ? (
          offeringId ? (
            <div className="mb-2">
              <GaebActionButton
                offeringId={offeringId}
                onImported={reloadPositions}
                exportOnly
                label={
                  <>
                    <FileText className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
                    GAEB
                  </>
                }
                className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover"
              />
            </div>
          ) : null
        ) : (
        <div className="grid grid-cols-2 gap-2">
          <OfferAttachmentsButton
            leadRouteId={leadRouteId}
            attachments={attachments}
            onChange={commitAttachments}
            standardAttachments={standardAttachmentsForEditor}
            onExcludeStandard={excludeStandardAttachment}
            onRestoreStandard={restoreStandardAttachment}
            className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover"
          />
          {offeringId ? (
            <OfferImportActionButton
              offeringId={offeringId}
              onImported={reloadPositions}
              className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover"
            />
          ) : null}
          {offeringId ? (
            <GaebActionButton
              offeringId={offeringId}
              exportOnly
              label={
                <>
                  <FileText className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
                  GAEB-Export
                </>
              }
              className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover"
            />
          ) : null}
          {offeringId && idsEnabled ? (
            <IdsCartButton
              leadRouteId={leadRouteId}
              offeringId={offeringId}
              documentTypeLabel={documentTypeLabel}
              documentNo={documentExportRefPreview}
              onImported={reloadPositions}
              className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg border border-app-border bg-app-surface text-xs sm:text-sm font-semibold text-app-text hover:bg-app-hover"
            />
          ) : null}
        </div>
        )}
        <button
          type="button"
          onClick={() => void generatePdfPreview()}
          disabled={pdfLoading}
          className="mt-2 w-full py-3 rounded-xl bg-pipedrive-blue text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:pointer-events-none"
        >
          {pdfLoading ? 'PDF wird erzeugt…' : documentIssued ? 'PDF anzeigen' : 'Weiter'}
        </button>
      </div>

      {/* Mobile: FAB öffnet Positions-Auswahl im Sheet */}
      {!documentIssued && !pdfOverlayOpen && canViewStammdaten && (
        <button
          type="button"
          onClick={() => setShowMobilePositionPicker(true)}
          className="md:hidden fixed right-4 z-[252] h-14 w-14 rounded-full bg-emerald-600 text-white shadow-lg flex items-center justify-center hover:bg-emerald-700 active:bg-emerald-800 bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))]"
          aria-label="Position hinzufügen"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {showMobilePositionPicker ? (
        <div
          className="md:hidden fixed inset-0 z-[253] flex flex-col justify-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="offer-mobile-positions-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40 border-0 cursor-default"
            aria-label="Schließen"
            onClick={() => setShowMobilePositionPicker(false)}
          />
          <div
            className="relative bg-app-surface-muted rounded-t-2xl shadow-xl border-t border-app-border max-h-[min(88vh,720px)] flex flex-col min-h-0 mx-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-app-border bg-app-surface rounded-t-2xl">
              <h2
                id="offer-mobile-positions-title"
                className="text-base font-semibold text-app-text inline-flex items-center gap-2 min-w-0"
              >
                <span className="truncate">Position hinzufügen</span>
                {positionInsertBusy ? (
                  <LoadingSpinner className="shrink-0 text-app-text-subtle" />
                ) : null}
              </h2>
              <button
                type="button"
                onClick={() => setShowMobilePositionPicker(false)}
                className="p-2 rounded-lg text-app-text-subtle hover:bg-app-hover"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden max-h-[min(75vh,620px)]">
              <OfferPositionPickerPanel
                leadRouteId={leadRouteId}
                offeringId={offeringId}
                dragAndDropEnabled={false}
                onInsertItem={({ kind, id, name, unit, gross_price, description }) =>
                  insertPickerItemAt(kind, id, undefined, { name, unit, gross_price, description })
                }
                onAdded={async () => {
                  await reloadPositions();
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <AppModal
        open={Boolean(offerPdfSendNotice)}
        onClose={() => setOfferPdfSendNotice(null)}
        overlayClassName="z-[271]"
        panelClassName="md:max-w-md"
        ariaLabelledBy="offer-pdf-send-notice-title"
      >
        {offerPdfSendNotice ? (
          <>
            <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3 shrink-0">
              <div className="w-8" aria-hidden="true" />
              <h2
                id="offer-pdf-send-notice-title"
                className="text-sm font-semibold text-app-text text-center flex-1"
              >
                {offerPdfSendNotice.type === 'need_mail'
                  ? 'E-Mail-Konto fehlt'
                  : offerPdfSendNotice.type === 'success'
                    ? 'E-Mail erfolgreich versendet'
                    : offerPdfSendNotice.type === 'partial'
                      ? 'E-Mail versendet — Nachbearbeitung fehlgeschlagen'
                      : offerPdfSendNotice.type === 'error' && offerPdfSendNotice.title
                        ? offerPdfSendNotice.title
                        : 'Senden nicht möglich'}
              </h2>
              <button
                type="button"
                onClick={() => setOfferPdfSendNotice(null)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text text-xs"
                aria-label="Hinweis schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4">
              {offerPdfSendNotice.type === 'success' ? (
                <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              ) : offerPdfSendNotice.type === 'partial' ? (
                <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-amber-100 text-amber-700">
                  <Info className="h-5 w-5" />
                </div>
              ) : null}
            </div>
            <div className="px-4 pb-4 text-sm text-app-text-muted leading-relaxed text-center">
              {offerPdfSendNotice.type === 'need_mail' ? (
                <p>
                  Um das Dokument per E-Mail zu versenden, verbinden Sie bitte zuerst ein Postfach in
                  den Einstellungen. Danach können Sie es hier erneut versuchen.
                </p>
              ) : (
                <p className="whitespace-pre-wrap text-center">{offerPdfSendNotice.message}</p>
              )}
            </div>
            <div className="px-4 py-3 border-t border-app-border flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOfferPdfSendNotice(null)}
                className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
              >
                Schließen
              </button>
              {offerPdfSendNotice.type === 'need_mail' ? (
                <Link
                  to={`${instancePrefix}/settings/email-dispatch`}
                  onClick={() => setOfferPdfSendNotice(null)}
                  className="inline-flex justify-center items-center px-3 py-2 rounded-lg bg-pipedrive-blue text-white text-sm hover:bg-blue-700"
                >
                  E-Mail einrichten
                </Link>
              ) : null}
            </div>
          </>
        ) : null}
      </AppModal>

      <FinalInvoiceDeductionPickerModal
        open={deductionPickerOpen}
        onClose={() => setDeductionPickerOpen(false)}
        organizationId={lead.organization?.id}
        contactId={lead.contact_id}
        currentOfferingId={offeringId ?? null}
        currentDocument={currentDeductionDocument}
        fallbackVatRatePercent={getOfferVatOption(vatMode).ratePercent}
        selectedIds={selectedDeductionInvoiceIds}
        onConfirm={handleDeductionPickerConfirm}
      />

      <PositionSurchargePickerModal
        open={surchargeModalState != null}
        mode={surchargeModalState?.mode ?? 'insert'}
        onClose={() => setSurchargeModalState(null)}
        candidates={surchargeModalCandidates}
        initialPercent={surchargeModalInitial.percent}
        initialAmount={surchargeModalInitial.amount}
        initialSurchargeMode={surchargeModalInitial.surchargeMode}
        initialTargetRowIds={surchargeModalInitial.targetRowIds}
        onConfirm={handleSurchargeModalConfirm}
      />

      {offerMailComposeOpen && offerMailComposeInitial ? (
        <ComposeEmailModal
          key={`offer-mail-${offerMailComposeResetKey}`}
          open
          accountId={offerMailComposeAccountId}
          resetKey={offerMailComposeResetKey}
          initialTo={offerMailComposeInitial.to}
          initialCc={offerMailComposeInitial.cc}
          initialSubject={offerMailComposeInitial.subject}
          initialBody={offerMailComposeInitial.body}
          initialFromEmail={offerMailComposeInitial.fromEmail}
          initialAttachments={offerMailComposeInitial.attachments}
          signatureHtml={offerMailComposeInitial.signatureHtml ?? null}
          overlayClassName="z-[270]"
          leadId={lead.id}
          onClose={() => {
            setOfferMailComposeOpen(false);
            setOfferMailComposeInitial(null);
          }}
          onAccountChange={(id) => setOfferMailComposeAccountId(id)}
          onSent={handleOfferDocumentSent}
        />
      ) : null}

      {(pdfPreview || pdfLoading) && pdfOverlayOpen ? (
        <div
          className="fixed inset-0 z-[260] flex items-center justify-center p-3 sm:p-6 max-md:p-0 max-md:items-stretch max-md:justify-stretch bg-slate-900/45 backdrop-blur-[2px] max-md:bg-neutral-900"
          role="dialog"
          aria-modal="true"
          aria-labelledby="offer-pdf-preview-title"
          onClick={(e) => {
            if (pdfPreviewCloseLocked) return;
            if (e.target === e.currentTarget) closePdfPreview();
          }}
        >
          <div className="flex flex-col w-full max-w-4xl h-[92vh] min-h-0 rounded-xl bg-app-surface shadow-2xl border border-app-border overflow-hidden max-md:max-w-none max-md:h-full max-md:min-h-0 max-md:rounded-none max-md:border-0 max-md:shadow-none">
            <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-app-border bg-app-surface-muted/90 max-md:pt-[max(0.75rem,env(safe-area-inset-top))] max-md:pl-[max(1rem,env(safe-area-inset-left))] max-md:pr-[max(1rem,env(safe-area-inset-right))]">
              <div className="relative flex min-w-0 flex-1 items-center gap-1.5 pr-2">
                <h2 id="offer-pdf-preview-title" className="text-sm font-semibold text-app-text shrink-0">
                  PDF-Vorschau
                </h2>
                {pdfPreview?.documentNumberCommitted ? (
                  <span className={EDITOR_STATUS_BADGE_DONE}>
                    <i className={EDITOR_STATUS_DOT} aria-hidden />
                    Abgeschlossen
                  </span>
                ) : null}
                {pdfPreview?.documentNumberCommitted ? (
                  <span className="text-sm tabular-nums text-app-text-muted">{pdfPreview?.referenceId}</span>
                ) : null}
                {pdfPreview?.zugferdWarning ? (
                  <ZugferdWarningTooltip text={pdfPreview?.zugferdWarning} />
                ) : null}
                {pdfPreview?.einvoiceBlocked && !pdfPreview?.documentNumberCommitted ? (
                  <span className="text-xs font-medium text-red-700" role="alert">
                    E-Rechnungspflicht: erst Angaben ergänzen, dann ausstellen
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  onClick={() => void confirmOrRunDocumentExport('download')}
                  disabled={!pdfPreview || duplicateExportBusy || pdfLoading || Boolean(pdfPreview?.einvoiceBlocked && !pdfPreview?.documentNumberCommitted)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-app-border bg-app-surface text-sm font-medium text-app-text-muted hover:bg-app-hover"
                >
                  <FileDown className="w-4 h-4 shrink-0" />
                  Herunterladen
                </button>
                <button
                  type="button"
                  onClick={() => void confirmOrRunDocumentExport('email')}
                  disabled={!pdfPreview || offerMailComposeBusy || duplicateExportBusy || pdfLoading || Boolean(pdfPreview?.einvoiceBlocked && !pdfPreview?.documentNumberCommitted)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-pipedrive-blue/40 bg-pipedrive-blue text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:pointer-events-none"
                >
                  <Send className="w-4 h-4 shrink-0" />
                  {offerMailComposeBusy ? '…' : `${documentTypeLabel} senden`}
                </button>
                <button
                  type="button"
                  onClick={closePdfPreview}
                  disabled={pdfPreviewCloseLocked}
                  className="p-2 rounded-lg text-app-text-muted hover:bg-slate-200/80 disabled:pointer-events-none disabled:opacity-40"
                  aria-label="Vorschau schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {pdfPreview ? (
              <div className="relative flex min-h-0 w-full flex-1 flex-col">
                <PdfDocumentViewer
                  url={pdfPreview.url}
                  title={`${documentTypeLabel} als PDF`}
                  className="min-h-0 w-full flex-1 bg-neutral-800"
                  loadingFallback={<PdfPreviewLoadingPanel phase={pdfPhase} />}
                />
                {pdfLoading ? (
                  // Altes PDF bleibt sichtbar, bis das neue da ist.
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-neutral-900/55 backdrop-blur-[1px]"
                    role="status"
                    aria-live="polite"
                  >
                    <LoadingSpinner className="text-white" />
                    <p className="text-sm font-medium text-white">
                      {pdfPhase === 'attachments'
                        ? 'Anhänge werden angefügt…'
                        : pdfPhase === 'zugferd'
                          ? 'E-Rechnung wird eingebettet…'
                          : 'PDF wird aktualisiert…'}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <PdfPreviewLoadingPanel phase={pdfPhase} />
            )}
          </div>
        </div>
      ) : null}
      <EInvoiceTaxIdDialog
        open={einvoiceTaxIdPrompt != null}
        vatMode={vatMode}
        organization={
          taxOrgId
            ? {
                id: taxOrgId,
                name:
                  invoiceDebtor.source === 'recipient_organization'
                    ? invoiceDebtor.partyName
                    : (lead.organization?.name ?? ''),
              }
            : null
        }
        initial={recipientOrgTaxIds}
        sellerMissing={missingEInvoiceTaxIds.seller}
        onClose={() => setEinvoiceTaxIdPrompt(null)}
        onSkip={() => {
          const pending = einvoiceTaxIdPrompt;
          einvoiceTaxIdSkippedRef.current = true;
          setEinvoiceTaxIdPrompt(null);
          void generatePdfPreview({ ...pending, skipTaxIdCheck: true });
        }}
        onSaved={(ids) => {
          const pending = einvoiceTaxIdPrompt;
          if (taxOrgId) setOrgTaxIdOverride({ ...ids, orgId: taxOrgId });
          setEinvoiceTaxIdPrompt(null);
          void generatePdfPreview({ ...pending, skipTaxIdCheck: true, recipientTaxIds: ids });
        }}
      />
      <AppConfirmDialog
        open={titleDeletePrompt != null}
        onClose={() => {
          if (!titleDeleteBusy) setTitleDeletePrompt(null);
        }}
        title={`Titel „${titleDeletePrompt?.title.item_name.trim() || 'ohne Namen'}“ löschen`}
        description={
          titleDeletePrompt ? (
            <>
              <p>{titleDeleteDescription(titleDeletePrompt.members)}</p>
              <p className="mt-3">Sollen die Positionen auch gelöscht werden?</p>
            </>
          ) : null
        }
        confirmLabel="Ja, Positionen löschen"
        cancelLabel="Nein, nur Titel löschen"
        destructive
        isLoading={titleDeleteBusy}
        overlayClassName="z-[300]"
        onCancel={() => {
          const pending = titleDeletePrompt;
          setTitleDeletePrompt(null);
          if (!pending) return;
          // Nur der Titel geht; seine Positionen rücken zum vorherigen Titel auf.
          if (pending.title.isApi && pending.title.leadPositionId != null) {
            void deleteApiPosition(pending.title.leadPositionId);
          } else {
            removeDraftRow(pending.title.id);
          }
        }}
        onConfirm={async () => {
          const pending = titleDeletePrompt;
          if (!pending) return;
          setTitleDeleteBusy(true);
          try {
            await deleteTableRows([pending.title, ...pending.members]);
          } finally {
            setTitleDeleteBusy(false);
            setTitleDeletePrompt(null);
          }
        }}
      />
      <AppConfirmDialog
        open={duplicateExportPrompt != null}
        onClose={() => setDuplicateExportPrompt(null)}
        tone="warning"
        title="Achtung: Dokument bereits vorhanden"
        description={
          duplicateExportPrompt ? (
            <>
              <p>
                {
                  formatDuplicateDocumentWarning({
                    documentTypeLabel,
                    existingNumbers: duplicateExportPrompt.existingNumbers,
                    nextNumber: duplicateExportPrompt.nextNumber,
                  }).body
                }
              </p>
              <p className="mt-3">{DUPLICATE_DOCUMENT_WARNING_QUESTION}</p>
            </>
          ) : null
        }
        confirmLabel={
          duplicateExportPrompt?.kind === 'email' ? 'Trotzdem senden' : 'Trotzdem herunterladen'
        }
        cancelLabel="Abbrechen"
        overlayClassName="z-[300]"
        onConfirm={() => {
          const pending = duplicateExportPrompt;
          setDuplicateExportPrompt(null);
          if (!pdfPreview) return;
          if (pending?.kind === 'email') void openOfferPdfEmailCompose();
          else if (pending?.kind === 'download') void downloadCurrentPdf();
        }}
      />
    </div>
    </OfferDocumentIssuedContext.Provider>
  );
};

/** Rich hover tooltip for ZUGFeRD / E-Rechnung warnings next to the PDF preview title. */
function ZugferdWarningTooltip({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipId = React.useId();

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const show = React.useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const hideSoon = React.useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  }, [clearCloseTimer]);

  React.useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return (
    <>
      <button
        type="button"
        onMouseEnter={show}
        onFocus={show}
        onBlur={hideSoon}
        onMouseLeave={hideSoon}
        className="inline-flex shrink-0 rounded-md p-0.5 text-amber-600 hover:bg-amber-100/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/90"
        aria-describedby={open ? tipId : undefined}
        aria-label="Hinweis zur E-Rechnung (ZUGFeRD): Details beim Darüberfahren mit der Maus"
      >
        <AlertTriangle className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div
          id={tipId}
          role="tooltip"
          onMouseEnter={show}
          onMouseLeave={hideSoon}
          className="absolute left-0 top-full z-[320] mt-1.5 w-max max-w-full min-w-0 sm:max-w-[min(32rem,calc(100vw-2rem))]"
        >
          <div className="max-h-[min(50vh,22rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-600/90 bg-slate-900 px-3.5 py-3 text-left shadow-2xl ring-1 ring-black/25">
            <p className="m-0 mb-2 text-[11px] font-semibold text-amber-200/95">
              E-Rechnung / ZUGFeRD
            </p>
            <p className="m-0 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-100">{text}</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

function DropBand({ id, dropIndex, colSpan }: { id: string; dropIndex: number; colSpan: number }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <tr ref={setNodeRef} data-offer-drop-index={dropIndex}>
      <td colSpan={colSpan} className="p-0">
        <div className={`h-2 my-0.5 rounded ${isOver ? 'bg-pipedrive-blue/30' : 'bg-transparent border border-dashed border-app-border'}`} />
      </td>
    </tr>
  );
}

function OfferDragOverlayPreview({ preview }: { preview: OfferDragPreview }) {
  const isPalette = preview.variant === 'palette';
  return (
    <div
      className={`rounded-lg border bg-app-surface px-3 py-2 text-sm text-app-text shadow-xl cursor-grabbing flex items-center gap-2 box-border ${
        isPalette ? 'border-app-border' : 'border-app-border-strong inline-flex'
      }`}
      style={isPalette ? { width: BLOCK_PALETTE_DRAG_WIDTH_PX } : undefined}
    >
      <GripVertical className="w-4 h-4 text-app-text-subtle shrink-0" />
      <span className={`min-w-0 ${isPalette ? 'text-left leading-snug' : ''}`}>{preview.label}</span>
    </div>
  );
}

function BlockPaletteItem({
  id,
  label,
  kind,
  onInsert,
}: {
  id: string;
  label: string;
  kind: 'text' | 'title' | 'freePosition' | 'image' | 'positionSurcharge';
  onInsert?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { source: 'block', kind },
  });
  const suppressClickRef = React.useRef(false);
  const wasDraggingRef = React.useRef(false);
  React.useEffect(() => {
    if (wasDraggingRef.current && !isDragging) {
      suppressClickRef.current = true;
      const timer = window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 250);
      wasDraggingRef.current = isDragging;
      return () => window.clearTimeout(timer);
    }
    wasDraggingRef.current = isDragging;
  }, [isDragging]);
  const style: React.CSSProperties | undefined = isDragging ? { opacity: 0 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false;
          return;
        }
        onInsert?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onInsert?.();
        }
      }}
      className={BLOCK_PALETTE_ITEM_CLASS}
    >
      <GripVertical className="w-4 h-4 text-app-text-subtle" />
      {label}
    </div>
  );
}
